/*
 * Picturepops configurator (Plan 6 — full-screen app mode)
 * Alpine.js factory + per-occasion defaults + localStorage persistence + canvas crop + cart submit.
 * State survives tab close so returning visitors can "Continue design" from the homepage banner.
 *
 * State model:
 *   stage   — 'entry' (no photos yet) | 'builder' (photos present). DERIVED from photos.length.
 *   tab     — 'photos' | 'words' | 'note' | 'send' — active control panel in Stage 2.
 *
 * Tab validation:
 *   tabIsValid('photos') — exactly 4 photos uploaded
 *   tabIsValid('words')  — 4 tagline lines non-empty + highlight selected
 *   tabIsValid('note')   — message length 50–250
 *   tabIsValid('send')   — sender name + valid US address fields
 *
 * "Add to cart" is enabled iff allTabsValid() is true.
 */

(function () {
  'use strict';

  /* How much the lightbox magnifier scales the picture. 2.6 puts a 900px-wide
     picture at ~2340px, which is about what a 2400px source can honestly show.
     The closed and top shots are 2400px from the print masters; the open shot
     is a 1600px render and goes a little soft past 2x. */
  const PDP_MAG_SCALE = 2.6;

  /* ── NOTHING IS REMEMBERED, 2026-08-19 ────────────────────────────────────
     Daniel's call: resuming a half-finished card was intrusive and unhelpful
     while the flow is still being designed, and it made every test start from
     whatever the last test left behind. A proper "continue where you left off"
     is being researched separately (how Mixtiles handles the journey) and will
     be designed rather than reinstated.

     So: every load starts clean. These two keys are kept ONLY to be deleted —
     a returning tester still has a draft and 4 photo blobs in their browser
     from before this change, and silently leaving that behind would be its own
     small mess. Remove the purge once it has been live long enough that nobody
     is carrying one. The old implementation is in git at 9cb4567. */
  const STORAGE_KEY = 'pp_config_v2';
  const SUBMITTED_KEY = 'pp_submitted_v1';

  // The artist's comp is 2000x2000 (SPAN in scripts/make-open-video.py), but
  // the stage is laid out at 1/LAYOUT_SCALE of geometry units — see the note
  // on LAYOUT_SCALE in card-stage.js. Anything sized in comp units and placed
  // in LAYOUT px must go through here, or it will be LAYOUT_SCALE times too
  // big. Falls back to 1 so a missing CardStage cannot silently shrink things.
  const COMP_SPAN = 2000;
  function compSpanCss() {
    const CS = window.PP && window.PP.CardStage;
    return COMP_SPAN / ((CS && CS.LAYOUT_SCALE) || 1);
  }

  // ── Analytics ───────────────────────────────────────────────────────────
  // Pushes to dataLayer only. GTM forwards these to GA4; nothing here fires a
  // GA4 tag directly, because Shopify's native Google channel already loads
  // GA4 and sends the ecommerce events — a second config tag double-counts
  // every one of them, invisibly.
  //
  // Never allowed to break the builder: analytics failing must not stop
  // someone buying a card.
  // Send a custom event to GA4.
  //
  // The lightest thing that works (Daniel, 2026-08-03). Shopify's Google
  // channel already loads gtag.js for GA4 G-FWFBHKGWWR, so there is nothing to
  // add: no GTM container, no extra script, no network weight.
  //
  // The stub matters. gtag.js loads async, and an event fired before it arrives
  // would simply be lost — pp_first_preview fires early enough to hit that.
  // This is Google's documented queue form: entries pushed as `arguments` are
  // replayed by gtag.js once it loads, and if the real gtag is already there
  // this calls straight through.
  //
  // Deliberately NOT pushing {event: name} objects for GTM to pick up: keeping
  // our events out of dataLayer in that shape means a GTM trigger cannot start
  // double-counting what gtag already sends.
  //
  // GTM *is* on the store — verified 2026-08-09, the container script loads and
  // populates dataLayer, so the older "nothing consumes them today" was out of
  // date. That makes this rule matter more, not less. Every event in the theme
  // goes through this function, the wizard's included (window.PP.track).
  //
  // Never allowed to break the builder: analytics failing must not stop someone
  // buying a card.
  // ?ppdebug=1 routes the event into GA4 DebugView. Without it there is no way
  // to prove GA4 RECEIVED an event — the browser only shows that gtag was
  // called, and a beacon that is dropped looks identical to one that landed.
  const PP_GA_DEBUG = (function () {
    try { return new URLSearchParams(location.search).has('ppdebug'); } catch (e) { return false; }
  })();

  // ── Every experiment this visitor is in, as ONE GA4 dimension ──
  //
  // Currently EMPTY — no experiment is running, so every event reports
  // `pp_experiments: 'none'`. The plumbing is kept deliberately: the GA4
  // custom dimension is already registered, and registration is NOT
  // retroactive, so tearing this out would mean the first day of the next
  // test collects nothing.
  //
  // To run a test: add its name here, and have whatever assigns the arm write
  // a `pp_ab_<name>` cookie holding it. Not one GA4 parameter per test — one
  // dimension, registered once, so every future test is code-only.
  //
  // Read off a cookie rather than component state, because this has to be
  // readable on every page — the marketing pages included — and the component
  // only exists on the card flow.
  //
  // The photos-first test was removed on 2026-08-11 (Daniel) — it was the only
  // entry in this list. See projects/pop-cards/decisions.md.
  // The home test (designs or occasions first, 2026-10-25): snippets/pp-home-test.liquid writes pp_ab_home.
  var ACTIVE_EXPERIMENTS = ['home'];

  // Format: `name:arm`, comma-separated, SORTED. Sorted because GA4 treats
  // "a:1,b:2" and "b:2,a:1" as two different dimension values, which would
  // split one cohort across two rows for no reason.
  //
  // A visitor not assigned to a test is left OUT rather than recorded as
  // "unassigned": someone who never reached the page that assigns them was
  // never in that test. 'none' means in no test at all — which is itself worth
  // being able to filter on, e.g. traffic landing straight on the product page.
  function experiments() {
    try {
      var out = [];
      ACTIVE_EXPERIMENTS.forEach(function (name) {
        var key = 'pp_ab_' + name + '=';
        var hit = document.cookie.split('; ').find(function (c) { return c.indexOf(key) === 0; });
        if (hit) out.push(name + ':' + decodeURIComponent(hit.slice(key.length)));
      });
      return out.sort().join(',') || 'none';
    } catch (e) { return 'none'; }
  }

  /* Countries already counted by onCountryChange, so one person changing
     their mind is not counted as repeated demand. Page-lifetime. */
  const blockedSeen = Object.create(null);

  /* Which builder the event came from: 'b' for direction B (?view=builder-b, and the builder itself once it switches),
     'classic' for today's. On every event, GA and PostHog alike, so the two can be compared once traffic exists
     (builder B plan §5, Analytics). Read from the page, once: the section marks B's root data-builder-ui="b". */
  let UI_KIND = null;
  function uiKind() {
    if (UI_KIND) return UI_KIND;
    try {
      if (!document.querySelector('.pp-builder-app')) return null;     // not a builder page (yet)
      UI_KIND = document.querySelector('[data-builder-ui="b"]') ? 'b' : 'classic';
    } catch (e) { return null; }
    return UI_KIND;
  }
  function withUi(params) {
    const out = Object.assign({}, params || {});
    const ui = uiKind();
    if (ui && out.ui == null) out.ui = ui;
    return out;
  }

  function track(name, params) {
    params = withUi(params);
    try {
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== 'function') {
        window.gtag = function () { window.dataLayer.push(arguments); };
      }
      const data = Object.assign({}, params || {});
      // On EVERY event, not just the cart one. A test is decided by purchases
      // per arm, and that ratio needs the denominator — how many of each arm
      // got there at all — from the same stream.
      data.pp_experiments = experiments();
      if (PP_GA_DEBUG) data.debug_mode = true;
      window.gtag('event', name, data);
    } catch (e) { /* ignore */ }

    // PostHog gets the same event, in its own try block so that one broken
    // analytics vendor cannot take the other down with it — and neither can
    // stop someone buying a card. Absent when the API key setting is blank,
    // which is the whole disable switch.
    try {
      if (window.posthog && window.posthog.capture) {
        window.posthog.capture(name, params || {});
      }
    } catch (e) { /* ignore */ }
  }

  // How long since the page started loading — for the <90s-to-preview target
  // in projects/pop-cards/CLAUDE.md, which was a manual stopwatch test.
  function msSinceLanding() {
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      return Math.round(nav ? performance.now() - nav.startTime : performance.now());
    } catch (e) { return null; }
  }

  // The only sections a customer can be in. 'front' and 'top' were removed on
  // 2026-08-03 when the covers became artist artwork; saved state from before
  // then can still name them, so loadState() migrates rather than trusting it.
  // 'send' became 'preview' on 2026-09-03: the card alone, close/open, and
  // Send — which is add-to-cart. See builder-preview.liquid.
  // 'recipients' added 2026-09-08: the OPT-IN fan-out step. Reached only from
  // Preview's "Send it to more than one person", never on the way to a
  // single-card add-to-cart — see openRecipients(). A customer sending one
  // card still never meets an address form here; Shopify's checkout asks.
  // 'inside' added 2026-10-08: the inside top, chosen by the customer on a generic-cover design
  // (assets/inside-top.js). Reached by tapping the inside top or from the dock, only when the design
  // has a text-free top (hasTopChoice).
  const SECTIONS = ['card', 'photos', 'bottom', 'inside', 'preview', 'recipients'];

  /* ── THE ADDRESS FIELDS THAT MUST BE FILLED ────────────────────────────
     IN SCREEN ORDER, and that is load-bearing: a failed Add-to-cart focuses
     the FIRST one still empty, so this array decides whether the customer is
     sent up their form or around it. Reorder the markup and reorder this.

     `label` is how the field is named back to them ("Still need the city"),
     so it reads as a sentence rather than as a field name. Deliberately not
     the same string as the visible <label>: "Recipient street address" is a
     form label, "the street address" is English.

     line2 (Apt / suite) is absent on purpose — it is optional, and a form
     that counts optional fields as missing is a form that is never done. */
  const SEND_REQUIRED = [
    { key: 'firstName', label: 'their first name' },
    { key: 'lastName',  label: 'their last name' },
    { key: 'line1',     label: 'the street address' },
    { key: 'city',      label: 'the city' },
    { key: 'state',     label: 'the state' },
    { key: 'zip',       label: 'the ZIP code' },
  ];

  /* THE CEILING ON THE FAN-OUT LIST — SHOPIFY'S, NOT OURS (since 2026-10-09).
     It was six, a product decision (Daniel, 2026-09-08: "short list"). On
     2026-10-09 he lifted it with the list import: "remove limits. yes, pasting
     a list or uploading a csv." What is left is a technical ceiling, and it is
     the cart's: every person is one cart line, and the card-complete checkout
     Function (picturepops-free-card) runs over every line on every cart change.
     Measured on Live, 2026-10-09: 20 lines add in 0.7 s; 200 lines in one cart
     are refused by /cart/add.js with HTTP 422 "A runtime error occurred while
     running the validation" — the Function's input outgrows Shopify's limit.
     RECIP_MAX sits under the measured edge (see pop-cards decisions.md
     2026-10-09, "No limit on people") and is said in words when it is reached.
     Raising it means making that Function's input smaller first, then
     re-measuring. Still enforced on the way in (draftFrom), on the way out
     (loadState) and at the buttons (addRecipient, the import). */
  const RECIP_MAX = 100;

  /* ⭐ ARTIST FREE-CARD MODE — /products/picturepop?artist=<preview token>.
     An artist from artists.picturepops.com builds a card from THEIR OWN design
     and sends it free (two per artist, once). Same builder, same bake, same
     Cloudinary upload, same buildCartProperties(); only the ending differs: the
     lines go to the artist platform, which creates a $0 order, instead of to
     the cart. Decided 2026-09-17 — pop-cards docs/2026-09-17-artist-platform-plan.md.

     ⛔ NO DRAFT IN THIS MODE, IN EITHER DIRECTION. The draft lives in one
     localStorage key and one IndexedDB store per browser. An artist's card must
     neither inherit a customer draft sitting in the same browser nor overwrite
     it, so loadState, saveDraft and clearDraft all stand down. */
  const ARTIST_TOKEN = (function () {
    try {
      const q = new URLSearchParams(location.search);
      if (q.get('artist') !== '1') return null;
      const d = q.get('design') || '';
      return { design: /^[a-z0-9-]{1,80}$/.test(d) ? d : null };
    } catch (e) { return null; }
  })();
  const ARTISTS_ORIGIN = 'https://artists.picturepops.com';
  /* The artist mode on the pages where cards are chosen (All cards, an occasion's page, a series'), carried in the tab
     by assets/pp-artist-mode.js: ?artistcard=1 there (never ?artist=, which is the card list's Artist filter), and a
     builder link from those pages gets ?artist=1 back. Set here too, so "Change" on Photos keeps it. */
  const ARTIST_CARDS_PARAM = 'artistcard';
  if (ARTIST_TOKEN) { try { sessionStorage.setItem('pp_artist_mode_v1', '1'); } catch (e) { /* the address carries it */ } }

  /* ⭐ ORG MODE — /products/picturepop?org=1&order=<id>&card=<default|recipient id>.
     An organization on business.picturepops.com makes one card of a bulk order:
     THEIR approved design (only), their note prefilled, photos preloaded if
     someone added them through a photo link. Same builder, same bake, same
     Cloudinary upload, same buildCartProperties() — only the ending differs:
     the card is saved into the order on the business app, not added to the
     cart (projects/pop-cards/docs/2026-09-24-business-platform-plan.md, phase 4).
     Drafts stand down exactly as in artist mode, for the same reason.

     `biz=` is accepted ONLY on a page served from localhost (shopify theme dev),
     and only if it is itself a localhost address: local QA of the business app,
     never a way to point a real customer's builder somewhere else. */
  const ORG_TOKEN = (function () {
    try {
      const q = new URLSearchParams(location.search);
      if (q.get('org') !== '1') return null;
      const order = q.get('order') || '', card = q.get('card') || '';
      if (!/^[0-9a-f-]{36}$/.test(order) || !/^(default|[0-9a-f-]{36})$/.test(card)) return null;
      let origin = 'https://business.picturepops.com';
      const biz = q.get('biz') || '';
      if (/^(localhost|127\.0\.0\.1)$/.test(location.hostname) && /^http:\/\/(localhost|127\.0\.0\.1):\d{2,5}$/.test(biz)) origin = biz;
      return { order: order, card: card, origin: origin };
    } catch (e) { return null; }
  })();

  /* One recipient, as the envelope will read them.
     ⛔ THE SEPARATOR IS THE MIDDLE DOT, and it is not cosmetic: the print
     side splits `Delivery To` on /[·\n]/ (print/envelope.mjs, addressLines)
     and print/order.mjs's deliveryToFromShipping already joins the order's
     own shipping address with exactly this string. Emitting the same shape
     means a card carrying its own address prints through the pipeline that
     exists today, with nothing changed downstream.
     Three parts, in the order a person writes them: who, where, which town. */
  function recipToLine(r) {
    if (!r) return '';
    const name = [r.firstName, r.lastName].map(function (s) { return String(s || '').trim(); })
                                          .filter(Boolean).join(' ');
    const p = recipParts(r);
    const street = [p.line1, p.line2].filter(Boolean).join(' ');
    const town = [p.city, p.state].filter(Boolean).join(', ');
    const townZip = [town, p.zip].filter(Boolean).join(' ');
    /* ⭐ THE COUNTRY IS THE LAST LINE, alone, full name, CAPITALS — and only
       abroad (print reference_geometry.json → envelope.international). */
    return [name, street, townZip, recipCountryLine(r)].filter(Boolean).join('  ·  ');
  }

  /* ── THE ADDRESS FIELDS, WITHOUT THEIR REPEATS (Yonatan, 2026-10-12: the drawn envelope read
     "4 Commodore Drive, 326 UNIT 326" and "Emeryville, CA, CA 94608") ─────────────────────────────
     The form's fields can arrive already holding their neighbour: a browser's autofill, a paste or a
     phone's keyboard puts the unit on the end of the street AND in Apt, or the state after the city.
     The pick from the suggestions never does (pp-address.js hands clean parts), so this is not about
     where the repeat came from: whatever filled the fields, the paper must not say it twice. Every
     line that joins the fields (the printed `Delivery To`, the drawn envelope, the folded rows, the
     sender's `_From`) is built from these parts. Only the repeat goes; the customer's own words, case
     and all, stay. A US state is a two-letter code and is written in capitals; a region abroad
     ("Tel Aviv District") is a name and must not be shouted. */
  function recipParts(r) {
    r = r || {};
    const t = function (x) { return String(x || '').trim(); };
    const esc = function (s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); };
    const us = recipIsUS(r);
    let line1 = t(r.line1), city = t(r.city);
    const line2 = t(r.line2), zip = t(r.zip);
    const state = us ? t(r.state).toUpperCase() : t(r.state);
    if (line2) {
      // The street ends with the apartment, or with its number alone ("4 Commodore Drive, 326" + "UNIT 326").
      const num = (line2.match(/[\w-]+$/) || [''])[0];
      const tail = new RegExp('[,\\s]+(?:#\\s*)?(?:' + esc(line2) + (num && num !== line2 ? '|' + esc(num) : '') + ')$', 'i');
      const cut = line1.replace(tail, '').trim();
      if (cut) line1 = cut;
    }
    // The city ends with the ZIP, or with the state ("Emeryville, CA" + "CA").
    if (zip) city = city.replace(new RegExp('[,\\s]+' + esc(zip) + '$'), '').trim();
    if (state) city = city.replace(new RegExp('[,\\s]+' + esc(state) + '$', 'i'), '').trim();
    return { line1: line1, line2: line2, city: city, state: state, zip: zip, us: us };
  }

  /* The sender, From you: a US address, or `abroad` and a name only (a return address must be in the US, so print
     sets the name over ours: Yonatan, reference_geometry.json envelope.shipper_frame.sender_rule). */
  function blankSender() {
    return { firstName: '', lastName: '', line1: '', line2: '', city: '', state: '', zip: '', abroad: false };
  }
  function senderFrom(x) {
    const s = blankSender();
    if (!x || typeof x !== 'object') return s;
    ['firstName', 'lastName', 'line1', 'line2', 'city', 'state', 'zip'].forEach(function (k) { s[k] = String(x[k] || ''); });
    s.abroad = !!x.abroad;
    return s;
  }

  function recipIsUS(r) {
    return String((r && r.country) || 'US').trim().toUpperCase() === 'US';
  }
  function recipCountryLine(r) {
    if (recipIsUS(r)) return '';
    const code = String(r.country).trim().toUpperCase();
    return (window.PPAddress ? window.PPAddress.countryLine(code) : code);
  }

  // What can be done to ONE picture. 'replace' is not here: it opens the file
  // picker and swaps the picture, so it is an action, not a state the dock can
  // be sitting in. 'none' is the closed state, reachable only by leaving the
  // picture entirely (closePhoto).
  //
  // 'border' is gone (Daniel, 2026-08-16). Its Shadow moved to the card-wide
  // row — a shadow on one picture out of four was never a thing anyone wanted
  // — and the Slim/Gallery black mats went with it: they fight the polaroid
  // the panel already is.
  const EDIT_SUBS = ['none', 'effect', 'crop', 'caption'];

  // Caption length. Also the maxlength on the input in builder-dock.liquid —
  // keep the two in step or the "N left" counter lies at the end.
  const CAPTION_MAX = 80;

  /* Note length. ⚠️ ENFORCED IN noteInput(), not by a `maxlength` attribute:
     the attribute refused the key natively, fired no event, and so could never
     tell anybody why their typing had stopped (Daniel, 2026-09-10: "can't see
     the ch limit message").
     ⚠️ 600, RAISED FROM 200 (Daniel, 2026-09-07, holding a proof Yonatan
     printed: "I'm surprised by how much fits in the note while still being
     legible").

     ⛔ THE CAP IS A BACKSTOP. THE FITTER IS THE LIMIT. Getting that the wrong
     way round is what 200 was: measured in a 72 x 48.75mm box at the old
     3.5mm floor, 472 characters of ordinary prose already fitted, so the cap
     was refusing notes the card had room for and the fitter never got a say.
     Yonatan's note is 415 characters and was rejected on the count alone.

     600 comes off the same measurement at the new floor: 652 characters of
     continuous prose fit at 3.0mm, so 600 leaves a margin for the ragged
     right edge and the short lines that real notes have. For prose the cap
     and the floor now bind at about the same point; for a note with a
     salutation, a gap and a signature the FITTER binds first — which is
     correct, because rows are the scarce thing and characters are not. See
     NOTE_FIT_MIN.

     Measured, not estimated: bisect on prose length in a 72 x 48.75mm box,
     Indie Flower 400, line-height 1.35, fonts.ready awaited (measure early
     and you size against fallback metrics, which reports both faces equal). */
  const NOTE_MAX = 600;

  // How many blank lines one gap may hold — between two lines of words, or
  // trailing the note while it is being typed. "Once, or even twice, or even
  // three times, that's fine" (Daniel, 2026-09-03). The gap is paid for in
  // the size like the words are, and the Enter that would make a fourth is
  // refused (see fitNote) — nothing is kept in the state and quietly dropped
  // from the card, which is what "the previous line breaks get pushed up"
  // was. Trailing blank lines are taken off on blur; nothing trailing is
  // ever printed.
  /* ⛔ THE CURSOR LABEL'S STATE LIVES HERE, NOT ON THE COMPONENT.
     Every property of the Alpine data object is reactive, and writing one per
     pointermove re-runs every binding on the page — which is how the panels
     were once drawn blank. These are plain module variables: the label is
     moved by writing its transform directly, and nothing observes them. */
  /* The label springs toward the cursor rather than tracking it exactly, and
     rotates with how far it is lagging — the dangle of Google Street View's
     pegman. `t*` is the target (cursor + offset); `p*` is where the label
     actually is; the gap between them IS the velocity, and drives the swing.
     All plain module variables: a reactive write per frame re-runs every
     binding on the page. */
  // How long the "Edit picture" pill survives a pointerleave, so the hop
  // between two adjacent panels (leave, then enter, a frame apart) does not
  // blink it. Short enough that leaving the card altogether still feels
  // immediate.
  const TAG_OFF_MS = 90;
  const NOTE_TAG_OFF = true;   // the note's cursor label, retired 2026-10-11 (noteTagOn)
  // The spring itself and its state are assets/cursor-tag.js (shared with the design lightbox since 2026-10-11).
  // A no-op stand-in only if that file failed to load: a missing label must never take the builder down.
  const TAG = (window.PP && window.PP.CursorTag) ? window.PP.CursorTag.create()
    : { el: null, rect: null, tx: 0, ty: 0, px: 0, py: 0, angle: 0, raf: 0, seen: false, loop: function () {}, on: function () {}, aim: function () {}, off: function () {} };

  const NOTE_GAP_MAX = 3;
  const NOTE_TRAIL_ALLOW = NOTE_GAP_MAX;

  // ── FULL SIZE — the card-wide layout that gives up captions ──────────
  // The picture takes the whole panel bar a 3.5mm margin on every side, so it
  // stops short of each fold and of the trim. 3mm is the bleed the rest of the
  // print file works in, so 3.5 clears it; anything less and a fold that lands
  // a millimetre out crops the photograph.
  //
  // ⚠️ 3mm until 2026-09-03 — Daniel widened the `slim` stop, and full size IS
  // that stop with captions off, so these move with it.
  //
  // As shares of the 57.5 x 80.5mm panel, which is how the CSS and the print
  // file both have to state it. Keep these three in step with .is-fullsize in
  // stage.css and MM.photoFull in print/geometry.mjs.
  // ⚠️ These three are unused now — boxAspect() reads the aspect straight from
  // wellFor() — but kept honest against the true panel so they can't mislead.
  const FULL_MARGIN_MM = 3.5;
  const FULL_W_PCT = 87.56;    // (56.25 - 7) / 56.25
  const FULL_H_PCT = 91.11;    // (78.75 - 7) / 78.75
  const FULL_ASPECT = 49.25 / 71.75;

  // ── BORDER STOPS (v4, 2026-08-25) ────────────────────────────────────
  // The paper margin around the picture is a card-wide control, and captions
  // on/off is a SECOND, independent one. Discrete stops, not a free slider:
  // every legal geometry needs a matching crop window, uploaded bake and
  // print well, and a continuous value would put a customer-set number into
  // the print path.
  //
  // ⛔ THIS TABLE IS A COPY, BUT NOT A MIRROR — and that is deliberate.
  // The original is MM.borderStops in projects/pop-cards/print/geometry.mjs
  // (venture repo), which asserts at import that every stop tiles the panel.
  //
  // ⚠️ THE NUMBERS BELOW ARE NOT PRINT'S NUMBERS AND MUST NOT BE "CORRECTED"
  // TO MATCH THEM (Daniel, 2026-10-02). This panel is 56.25 x 78.75; print's
  // is 53.5 x 74.9, and since 2026-10-02 the sheet prints that at 0.95 again.
  // Both are exactly 5:7, so the SHAPE the customer sees is the shape that
  // prints; only the absolute millimetres differ. Making them agree would
  // mean rebuilding the 3D card files, which is expensive and buys nothing —
  // the on-screen card is a picture of a card, not a measurement of one.
  //
  // So: change this table when the SHAPES change (a new stop, a different
  // proportion). Do not change it because print's millimetres moved.
  //
  // Millimetres, against the true 56.25 x 78.75 panel. ⭐ CORRECTED 2026-09-03
  // from the stale 57.5 x 80.5 (see MM.panel in print/geometry.mjs and
  // decisions.md): the old numbers were 46/45 of the truth but exactly
  // proportional, so only absolute sizes were ever wrong, never the on-screen
  // or printed SHAPE. Adding the `super` stop was the moment to fix it —
  // these match geometry.mjs stop-for-stop IN SHAPE, not in millimetres
  // (see above). `cap` is the captioned well,
  // `full` the uncaptioned one. With captions ON the 15.1mm band keeps its
  // place at the foot and the PICTURE gives up the height — the border stays
  // where the customer put it (Daniel, 2026-08-25).
  const PANEL_W_MM = 56.25, PANEL_H_MM = 78.75, CAPTION_BAND_MM = 15.1;
  const BORDER_STOPS = [
    { key: 'none',    label: 'None',    side: 0.0,  cap: { w: 56.25, h: 63.65, top: 0.0 },  full: { w: 56.25, h: 78.75, top: 0.0 } },
    { key: 'slim',    label: 'Slim',    side: 3.5,  cap: { w: 49.25, h: 60.15, top: 3.5 },  full: { w: 49.25, h: 71.75, top: 3.5 } },
    { key: 'classic', label: 'Classic', side: 5.0,  cap: { w: 46.25, h: 57.65, top: 6.0 },  full: { w: 46.25, h: 68.75, top: 5.0 } },
    { key: 'wide',    label: 'Wide',    side: 8.0,  cap: { w: 40.25, h: 54.65, top: 9.0 },  full: { w: 40.25, h: 62.75, top: 8.0 } },
    // ⭐ SUPER — the extra-big margin (Daniel, 2026-09-03). Mirrors the new
    // stop in print/geometry.mjs; 11mm of paper on every side.
    { key: 'super',   label: 'Super',   side: 11.0, cap: { w: 34.25, h: 51.65, top: 12.0 }, full: { w: 34.25, h: 56.75, top: 11.0 } }
  ];
  const BORDER_DEFAULT = 'classic';

  /* ⭐ ROUNDED PHOTO CORNERS (Yonatan, 2026-09-10; Daniel: a toggle in the
     border submenu, on every stop except `none`).
     ⛔ STATED IN MILLIMETRES, because it is a real radius on real paper — the
     same reason every other shape in this file is. It does NOT scale with the
     stop: 2 mm is 2 mm whether the picture is 46.25 mm wide or 34.25 mm.
     Three places have to agree on it and all three derive from this number:
       screen  --pp-photo-radius, written in cqw by backdropStyle()
       bake    a rounded-rect clip in bakePhoto(), corners filled white
       paper   border-radius on .photo in print/card-art.mjs, from _Rounded
     ⛔ NOT OFFERED AT THE `none` STOP. There is no paper to round against —
     the picture runs to the trim — and print lays that stop down OVERSIZE and
     trims it, so a radius would be cut through. setBorder() clears it. */
  const PHOTO_RADIUS_MM = 2.0;

  /* ⛔ THE RADIUS IS A NUMBER THE CUSTOMER CHOOSES NOW, NOT A SWITCH.
     Yonatan, 2026-09-11: "can I be greedy and ask that the corner for the
     picture will be a slider of the radius of the corner from 0 to 10 mm
     instead of just on and off?"

     PHOTO_RADIUS_MM above is no longer THE radius — it is the radius the
     slider lands on when somebody turns rounding on from zero, kept at the
     old fixed value so a card built before today looks unchanged. The three
     places that must agree (screen, bake, paper) all still derive from one
     number; that number now comes off `card.rounded` instead of the constant.

     10 mm is safe on this panel: the well is at most 56.25 mm wide and the
     bake clamps to min(w,h)/2 regardless, so the slider cannot ask for a
     circle the picture does not have room for. */
  const PHOTO_RADIUS_MAX_MM = 10;
  const PHOTO_RADIUS_STEP_MM = 0.5;

  /** The well for a stop + caption state, in mm. Falls back to the default
   *  stop rather than throwing: a card persisted with an unknown key must
   *  still render. */
  function wellFor(borderKey, captionsOn) {
    let st = BORDER_STOPS.find(function (b) { return b.key === borderKey; });
    if (!st) st = BORDER_STOPS.find(function (b) { return b.key === BORDER_DEFAULT; });
    return captionsOn ? st.cap : st.full;
  }

  /* ⭐ THE 3:4 STRIP ON SCREEN (Yonatan, 22 Oct 2026; Daniel's go 24 Oct). Part B's panel goes from 5:7 to 3:4
     (51.75 x 69 mm), and the 3D card's four photo planes are 3:4 since his renders of that day. On screen the panel is
     therefore 56.25 x 75 here: the same width, 3.75 mm shorter.

     BORDER_STOPS above stays the 5:7 table: a stop keeps its paper in millimetres on every side of the 3:4 panel, so
     its picture window is the 5:7 well 3.75 mm shorter (showWell). From 24 to 29 Oct that was the screen only, the
     bake stayed 5:7 and was drawn over the window like object-fit: cover (imgStyle, showExt). Since 2026-10-29 the
     bake follows STRIP_FORMAT below: on a 3:4 order it IS the window, and print places it in the 3:4 well. */
  const SHOW_H_MM = PANEL_W_MM * 4 / 3;              // 75
  const SHOW_CUT_MM = PANEL_H_MM - SHOW_H_MM;        // 3.75, all of it off the picture window
  function showWell(borderKey, captionsOn) {
    const w = wellFor(borderKey, captionsOn);
    return { w: w.w, h: w.h - SHOW_CUT_MM, top: w.top };
  }

  /* ⭐ THE STRIP FORMAT THE ORDER IS MADE FOR (2026-10-29). Yonatan switched Part B production to the 3:4 panel on
     27 Oct and the cutover is BY ORDER: every order says which strip it was built for (`_Strip Format`, the order
     property contract), print places and cuts it as that, and an order placed before this went live says nothing and
     prints 5:7. So this one word decides what the bake, the crop window, the sharpness number and the cut-out art
     are made from:
       '5x7'  wellFor(): the BORDER_STOPS table above, the bake overhangs the 3:4 window by 3.75 mm (showExt)
       '3x4'  showWell(): the same stops on the 56.25 x 75 panel, the bake IS the window, print's 3:4 wells
     Draft PP carries 3x4 first; Live keeps 5x7 until Yonatan has printed a 3:4 test batch end to end (his plan, step
     4), and the push that puts this on Live is the go-live. */
  const STRIP_FORMAT = '3x4';
  const STRIP_IS_34 = STRIP_FORMAT === '3x4';
  /** The well the picture is BAKED and CROPPED to, for the strip format above. */
  function bakeWell(borderKey, captionsOn) {
    return STRIP_IS_34 ? showWell(borderKey, captionsOn) : wellFor(borderKey, captionsOn);
  }
  /* How far the bake box runs past the window, top and bottom, as a share of the window's height. Nothing on a
     3:4 order: the bake is the window. */
  function showExt(borderKey, captionsOn) {
    if (STRIP_IS_34) return 0;
    return (SHOW_CUT_MM / 2) / showWell(borderKey, captionsOn).h;
  }

  /** The window on screen as percentages of the 3:4 panel, which is how the CSS states it. Width against panel width;
   *  top and height against panel height. The screen only: the bake reads wellFor(). */
  function wellPct(borderKey, captionsOn) {
    const w = showWell(borderKey, captionsOn);
    return {
      w: (w.w / PANEL_W_MM) * 100,
      h: (w.h / SHOW_H_MM) * 100,
      top: (w.top / SHOW_H_MM) * 100,
      band: captionsOn ? (CAPTION_BAND_MM / SHOW_H_MM) * 100 : 0
    };
  }

  // How far the note's type may travel from its base size (5.435cqw = 5.0mm,
  // in stage.css) before fitNote() gives up — see that method.
  //
  // NOTE_FIT_MAX is a judgement, not a measurement, and it is the one number
  // here worth arguing about: it is what stops "Love, D" growing until two
  // words fill the panel like a poster. Read at call time, not at load, so the
  // ceiling can be settled by eye on the draft theme rather than by argument:
  //   PP_NOTE_FIT_MAX = 1.8; Alpine.$data(document.querySelector('[x-data]')).fitNote()
  //
  // ⚠️ 0.60, LOWERED FROM 0.70 (Daniel, 2026-09-07). 0.60 x 5.0mm = 3.0mm.
  //
  // The rule that set 0.70 has NOT changed and is worth restating, because it
  // is what keeps this number honest (Daniel, 2026-08-29: "There needs to be a
  // cutoff point for how small it can get without it becoming illegible"):
  // the floor is the smallest size we would be HAPPY to send, and it must sit
  // clear of the smallest size that is merely readable — otherwise the last
  // legible note and the first illegible one are the same size.
  //
  // What changed is the face underneath it. Both numbers are derived, not
  // picked, and they move together:
  //
  //   absolute floor  Caveat  2.6mm (x-height 1.04mm) — print's FLOOR_MM, the
  //                   size below which it stops resolving on paper. Indie
  //                   Flower reaches that same 1.04mm x-height at 2.85mm,
  //                   because its x-height is 0.3652em against Caveat's
  //                   0.4000em. So print's floor moved 2.6 -> 2.85mm; see
  //                   FLOOR_MM in print/card-art.mjs.
  //   happy floor     3.0mm, x-height 1.10mm. One notch above the absolute
  //                   floor, the way 3.5mm was above 2.6mm.
  //
  // It IS smaller than before — 1.10mm of x-height against Caveat's 1.40mm at
  // the old floor. That is the trade Daniel took, and the evidence for it is a
  // printed proof rather than an argument: Yonatan's note is 415 characters
  // over 12 rows and needs exactly 3.01mm in this box. Indie Flower is upright,
  // monoline and open where Caveat is thin and slanted, which is why it stands
  // being set this small and Caveat would not have.
  //
  // ⛔ WHAT WOULD REVERSE IT: a printed proof at 3.0mm that reads badly. This
  // number has never been checked on the press — only on screen and against
  // one photograph of one proof.
  //
  // The character cap cannot do this job, which is Daniel's "ch count is not
  // enough": a note of ten words and fifteen Enters is well under any cap and
  // still asks for more rows than the panel has. Rows are the scarce thing,
  // not characters — so the floor is what has to hold, and fitNote() rolls
  // back anything typed that cannot be printed at it. See NOTE_MAX, which is
  // now sized as a backstop to this rather than as the limit.
  const NOTE_FIT_MIN = 0.60;
  const NOTE_FIT_MAX = 2.2;

  /* ── CENTRED WHEN SHORT, LEFT WHEN LONG (Daniel, 2026-09-07) ─────────────
     The number of RENDERED rows past which the note stops being centred and
     starts being left-aligned. Rows, not characters: what makes a centred
     note hard to read is many lines each starting somewhere different, and
     that is a row count. A long word-wrapped paragraph and the same text with
     the breaks typed by hand read the same way and get the same treatment.

     4 is the boundary between "a few words placed on the paper" and "a
     letter". At 4 rows and under, centred is right and left-aligning strands
     the words against one edge of a 72mm band; past it, centred prose is
     ragged on both edges and the eye loses the start of every line.

     ⛔ print/card-art.mjs CARRIES THIS SAME NUMBER AND THE SAME TEST. The
     preview and the paper disagreeing about alignment is the same class of
     bug as them disagreeing about size — see the note on the box in
     stage.css. Change one, change both. */
  const NOTE_ALIGN_ROWS = 4;
  /* How far back a rejected keystroke can unwind. It only ever needs to cover
     one burst of typing between two fits; 64 is far more than a human can
     enter in a 0-delay timeout and small enough that the stack is never a
     second copy of the note. */
  const NOTE_UNDO_MAX = 64;
  /* ⛔ MUST EQUAL the padding-bottom on .pp-cardnote__field, and the bottom
     inset of .pp-cardnote's clip-path, in stage.css. Measured, not chosen:
     Indie Flower's ink falls 0.0533em below its own line box at the 1.35
     leading the card uses, so a field sized to whole line boxes clips its own
     descenders. classic (-0.07em) and modern (-0.11em) do not overshoot at
     all; the handwriting face does, and it is the default. */
  const NOTE_DESC_PAD_EM = 0.06;

  /* ⛔ CAPTIONS ARE NOT OFFERED (Daniel, 2026-09-10: "I want to remove the
     option to add captions entirely from the toolbar… don't delete it from the
     code, but I want to turn it off at the moment because it's too much for
     the UI right now").
     NOTHING IS DELETED. captionsOn(), the `_Caption 1..4` properties, the
     with-captions wells in BORDER_STOPS, the caption band, its fitter and the
     print side all stay exactly as they were and still read; an order placed
     before today still prints its captions. This is the switch, and it is the
     only thing to flip to bring them back.
     ⚠️ Turn it on again and check the toolbar first: the Captions button was
     removed from builder-dock.liquid, so the flag alone gives you captions
     with no way to turn them off. */
  const CAPTIONS_OFFERED = false;

  /* ── THE CARD'S HAND (Daniel, 2026-08-29: "i also want to add fonts to the
     note and captions") ──────────────────────────────────────────────────
     ONE choice for BOTH. A note in one face and captions in another is two
     cards in an envelope, and it doubles the surface the print file has to
     agree with. Splitting them later is a second key on `card` and a second
     property — deliberately easy, deliberately not done yet.

     ⛔ EVERY FACE HERE MUST EXIST IN BOTH PLACES: font-face.liquid for the
     builder AND the FONTS table in print/make-print-file.mjs for the press.
     The print file throws on a face it cannot embed rather than falling back
     to a system one, because a silent fallback is a card that is wrong in a
     way nobody notices until it has been printed. These three are already in
     both, which is why they are these three.

     `scale` is not decoration. The 5.0mm note and 5.4mm caption are tuned to
     the HAND face — a text face set at the same mm reads much larger. The
     multiplier brings the others back to the same apparent size, and print
     applies the identical number. Tuned by eye on the real card, not
     calculated.

     ⚠️ THE REFERENCE FACE MOVED ON 2026-09-07 and the other two scales did
     NOT move with it. `hand` is Indie Flower now, whose x-height is 0.3652em
     against Caveat's 0.4000em — 91.3% — so Classic and Modern now read about
     9% LARGER than Handwritten does, where before all three matched. Holding
     the ratio would mean 0.80 -> 0.730 and 0.78 -> 0.712. Those numbers are
     written down rather than applied because these two were "tuned by eye on
     the real card" and 9% is inside what an eye should settle, not
     arithmetic. Change them off a proof, or leave them. */
  /* Each stack names its HEBREW half second (2026-09-04): Indie Flower,
     Fraunces and Figtree carry no Hebrew glyphs, so a Hebrew note fell to the
     system sans in every face — seen in the 2026-09-03 user test. The Hebrew faces
     are declared with a Hebrew unicode-range in font-face.liquid, so they
     only ever draw Hebrew; Latin is untouched. print/geometry.mjs carries
     the same pairs. */
  const CARD_FONTS = [
    /* ⚠️ WEIGHT 400, NOT 500. Indie Flower ships one weight; asking for 500
       gets a synthesised bold, which on a handwriting face reads as a smudge
       rather than as emphasis. Caveat's 500 came off a real 500 700 range. */
    { key: 'hand',    label: 'Handwritten', family: '"Indie Flower", "Playpen Sans Hebrew", cursive',
      weight: 400, scale: 1 },
    { key: 'classic', label: 'Classic',     family: '"Fraunces", "Frank Ruhl Libre", Georgia, serif',
      weight: 400, scale: 0.80 },
    { key: 'modern',  label: 'Modern',      family: '"Figtree", "Heebo", system-ui, sans-serif',
      weight: 400, scale: 0.78 },
    /* TYPEWRITER and ELEGANT (Daniel and Yonatan's sync, 19 Oct: keep Handwritten, and Typewriter and Elegant instead
       of Classic and Modern). The same keys, families, weights and scales as print/geometry.mjs CARD_FONTS (venture
       repo), which prints them (print deployed with them, 19 Oct, image 37304845111a). FOR EVERYONE since 19 Oct (Daniel:
       "put them live", not behind a switch); ?ppnotefonts=0 brings back Classic and Modern in this browser. Yonatan still
       has to register both faces in his print checker (Pop List). Classic and Modern stay in the table so every draft
       and order that chose them prints.
       Elegant is Playfair Display Italic declared as its own family at normal style (font-face.liquid), so nothing
       needs font-style. */
    { key: 'typewriter', label: 'Typewriter', family: '"Special Elite", "Heebo", "Courier New", monospace',
      weight: 400, scale: 0.80 },
    { key: 'elegant', label: 'Elegant',   family: '"PP Elegant", "Frank Ruhl Libre", Georgia, serif',
      weight: 500, scale: 0.86 },
  ];
  const NOTE_FONTS_NEW = (function () {
    try {
      const m = /[?&]ppnotefonts=(0|1)(?:&|$)/.exec(location.search);
      if (m) { if (m[1] === '0') localStorage.setItem('pp_ppnotefonts', '0'); else localStorage.removeItem('pp_ppnotefonts'); }
      return localStorage.getItem('pp_ppnotefonts') !== '0';
    } catch (e) { return !/[?&]ppnotefonts=0(?:&|$)/.test(location.search); }
  })();
  // The three the picker offers. A draft whose face is not among them keeps it (cardFont finds every key).
  const TOP_AUTO_OFF = (function () {
    try {
      const m = /[?&]pptopauto=(0|1)(?:&|$)/.exec(location.search);
      if (m) { if (m[1] === '0') localStorage.setItem('pp_pptopauto', '0'); else localStorage.removeItem('pp_pptopauto'); }
      return localStorage.getItem('pp_pptopauto') === '0';
    } catch (e) { return /[?&]pptopauto=0(?:&|$)/.test(location.search); }
  })();
  const CARD_FONTS_OFFERED = NOTE_FONTS_NEW ? ['hand', 'typewriter', 'elegant'] : ['hand', 'classic', 'modern'];
  const CARD_FONT_DEFAULT = 'hand';

  function cardFont(key) {
    return CARD_FONTS.find(function (f) { return f.key === key; })
        || CARD_FONTS.find(function (f) { return f.key === CARD_FONT_DEFAULT; });
  }

  // Click and hold to re-order. 400ms is the usual "pick this up" threshold —
  // long enough that a tap to open the editor never trips it, short enough
  // that it doesn't feel like the card is ignoring you. DRAG_SLOP is how far
  // a finger may wander before the press is read as a swipe instead.
  const HOLD_MS = 400;
  const DRAG_SLOP = 10;

  /* How much of its speed a thrown strip keeps each frame. 0.94 at 60fps is
     about half a second of coast from a brisk flick — long enough to feel
     like momentum, short enough that it never feels out of control. */
  const GLIDE_DECAY = 0.94;

  /* How much of the caption band's height the picture rises by when the
     caption is being written. 1.0 would put the band exactly where the
     picture's bottom edge was, which crops the picture harder than it needs;
     0.8 leaves a sliver of the photograph under the words so the caption is
     still visibly attached to something. */
  const CAPTION_LIFT = 0.8;

  /* The opening's speed curve — rate = MIN + (MAX-MIN)*sin(pi*progress).
     ⚠️ REWRITTEN 2026-09-03. It used to be a half-sine — slow to lift,
     quickest in the middle, settling at the end — which averaged 1.11 and
     took ~3.0s. Two things were wrong with that once the opening became the
     tail of one continuous motion (see openFromStanding): it was the slowest
     thing on screen, and it STARTED slow, right where the card had just been
     tipped into place and was already moving. "Make the whole opening faster
     ... so it feels like one smooth motion" (Daniel, 2026-09-03).

     So the rate starts at the ceiling and eases down: `MAX - (MAX-MIN)*u³`.
     The card is quickest as it breaks open, which is where the tip-down hands
     it over at speed, and settles as the last panel lands. Mean rate is
     MAX - (MAX-MIN)/4 = 1.49, so ~2.26s against 3.37s flat.

     ⛔ MAX STAYS AT 1.6, AND THAT IS A DECODER LIMIT, NOT A TASTE ONE. Above
     it the clip is asked for more than 48 source fps, starts dropping frames,
     and the geometry — which is driven from the clip's own mediaTime, on
     purpose, so the pose and the shading pixels can never disagree — follows
     it into the judder. ~2.26s is therefore the floor for a SHADED unfold.
     Preview's open is faster only because it has no shading to keep up with. */
  /* The shading canvas' backing store ceiling, in pixels per side. A MEMORY
     cap, not a resolution one — see the note in commit(). 3072² × 4 bytes is
     ~37MB for one canvas; a phone never asks for anything near it because the
     card paints small there. */
  const SHADE_CANVAS_MAX = 3072;

  const OPEN_RATE_MIN = 1.15;
  const OPEN_RATE_MAX = 1.60;

  // Distance between the first two fingers of a touch list, for the crop
  // pinch. Module scope because it is arithmetic, not state.
  /* Midpoint between the first two fingers — the handle a two-finger move
     tracks. Same reason as touchGap: arithmetic, not state. */
  function touchMid(touches) {
    const a = touches[0], b = touches[1];
    if (!a || !b) return { x: 0, y: 0 };
    return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
  }

  function touchGap(touches) {
    const a = touches[0], b = touches[1];
    if (!a || !b) return 0;
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
  }

  // The whole pre-photo flow, in order. ONE list, in one file, on one page —
  // this used to be a separate wizard at /pages/get-started with its own
  // Alpine component, its own CSS and its own copy of the occasion and photo
  // screens, handing over to this page through localStorage. Merged
  // 2026-08-11 at Daniel's instruction: you start a card, you enter the flow,
  // you build the card. There is no second door and nothing to hand off.
  // ⚠️ 'design' WAS HERE, between occasion and name. Removed 2026-08-13 at
  // Daniel's instruction: "now that I have this occasion screen, I don't need
  // this screen anymore". One design per occasion at launch, so the picker was
  // a question with one possible answer — and the occasion step's carousel
  // already shows that answer's three faces. The design is now resolved from
  // the occasion (see designForOccasion) and chosen without asking.
  //
  // A stored entryStep of 'design' from before this change is not lost: the
  // guard in loadState clamps an unknown step back to the first, and init's
  // forward-skip then puts a visitor who already has an occasion on 'name'.
  // 'product' ADDED 2026-08-15 (handoff badge 9d), REPLACED BY 'design'
  // 2026-09-02. Daniel: "we skip the product page entirely - no need for it.
  // we have occasions --> occasion page --> cards --> upon hover on the closed
  // card, we see it in it's open state --> click to enter the builder flow."
  //
  // So the card grid does the job the product page was added to do -- showing
  // the card properly before the customer commits -- and does it for FOUR
  // designs rather than one. The .pp-pdp markup is left in
  // configurator-entry.liquid, out of the flow and reachable from nothing; it
  // is deletable once the picker has earned its place.
  //
  // ⚠️ 'name' AND 'email' WERE HERE, between design and photos. Removed
  // 2026-09-03 at Daniel's instruction: "Remove name and email for launch to
  // add later as an a/b test." Every step before the photos is a toll gate in
  // front of the thing the customer came to do, and at four sessions a day
  // the captured emails are worth nothing measurable. The pages, the
  // customer-form post and the pp_email cookie all went with them (git has
  // them: commit before this one). `name` and `email` stay on the state only
  // so drafts saved with them still load. See decisions.md 2026-09-03.
  const ENTRY_STEPS = ['occasion', 'design', 'photos'];

  // Search key for the occasion list: lowercase, and apostrophes and hyphens
  // dropped rather than replaced. "Grandparents' Day" is one word away from
  // what anyone types, and "New Year's" is two — folding the punctuation out
  // of both sides makes the obvious spelling find the obvious row. Diacritics
  // are folded too, so an OS that types a curly apostrophe still matches.
  function normalizeSearch(s) {
    return String(s || '')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[’'\-]/g, '')
      .trim();
  }

  // Small cookie helpers. They used to live in onboarding-state.js, which was
  // the deleted wizard's own store; this is the only file that needs them now.
  function getCookie(name) {
    try {
      const hit = document.cookie.split('; ').find((c) => c.indexOf(name + '=') === 0);
      return hit ? decodeURIComponent(hit.slice(name.length + 1)) : null;
    } catch (e) { return null; }
  }

  function setCookie(name, value, maxAge) {
    try {
      document.cookie = name + '=' + encodeURIComponent(value)
        + '; max-age=' + maxAge + '; path=/; secure; samesite=lax';
    } catch (e) { /* ignore */ }
  }

  // The four accordion panels, left to right.
  const PANELS = ['OutLeft1', 'InLeft1', 'InRight1', 'OutRight1'];

  // How much of the visible stage each framing fills. The card and the opening
  // share a value on purpose: their extents differ by 0.5% (1835x1800 vs
  // 1840x1810 projected units), so a different margin here would show as a
  // zoom pop the instant the animation ended.
  // How much of the visible stage a framing may fill. `card` was 0.88 and is
  // 0.94 with the rest (Daniel, 2026-08-29: "it can be much larger") — the
  // whole-card view is the one that sells the object, and it was leaving a
  // 30px gap top and bottom on top of the toolbar's own clearance.
  const FRAMING_PAD = { card: 0.94, photos: 0.94, note: 0.9, photo: 0.9, top: 0.9 };

  /* ═══ THE INSIDE TOP (2026-10-08) ═══════════════════════════════════════
     Decision (venture decisions.md, 8 Oct): a card's occasion comes from its cover; on a generic
     cover the greeting printed inside was stripped and the customer chooses the inside top: a title
     and subtitle from a template, or a photo. The drawing is assets/inside-top.js (window.PPTop);
     this file holds the state, the dock, the draft and the order. Contract rows `_Top`, `_Photo 5`,
     `_Src 5`, `_Top Template/Title/Subtitle/Style` (venture repo, the order property contract).

     The words a fresh card starts with, and the template it starts on, are inside-top.js's (PPTop.words,
     PPTop.START), one copy shared with a design's lightbox (rd-design.js), so the words a visitor saw inside
     the card there are the words this opens with. They lived here until 2026-10-08 (TOP_SUBTITLE, TOP_TITLE,
     and the fallback, which was "With love / Thinking of you today" and is now the no-occasion pair, "For a
     good friend / So glad you are in my life": Daniel, 8 Oct). */
  /* THE OCCASION A VISITOR CAME FROM, carried as ?for=<occasion> (the redesign's design and occasion pages, 2026-10-08).
     ?occasion= sets the builder's occasion when the builder holds it; ?for= says which occasion the inside top's
     default WORDS are for, so a card opened from the sympathy page does not start on "Happy Birthday, Dad" even
     when the builder has no occasion of its own yet.

     ⭐ ?for=any IS "NO OCCASION" (Daniel, 8 Oct: a generic card picked from All cards, a search, a series or an
     artist's page starts on the no-occasion pair). The link still sends ?occasion=just-because for such a card,
     because the ORDER's occasion is routing and stays as it was (rd-design.js occasionFor); only the words differ.
     So the link's words are kept on the card (topWordsFrom: card.top.wordsFor, with the occasion they came with)
     and outlive the address: a reload, a restored draft, a Back. They give way the moment the builder's occasion
     is another than the one they came with, or the customer goes back to choose one (changeOccasion): from then
     on the words follow the occasion, until the customer touches the top (topOnDesign). */
  const CARRIED_OCC = (function () {
    try { const f = new URLSearchParams(location.search).get('for') || ''; return /^[a-z0-9-]{1,40}$/.test(f) ? f : null; } catch (e) { return null; }
  })();
  /* ⛔ THE OLD ENTRY NEVER SHOWS IN B (Daniel, 12 Oct: "I don't understand why we still have bits from the old website
     floating around. This can't happen. It just can't happen."). In the builder's direction B the classic entry's
     'occasion' wall and 'design' picker are never a screen: a card is chosen on All cards, an occasion's page or a
     design's page, and the builder opens on its Photos step. builderB() is that page; artist and org mode keep the
     classic builder on purpose for now (noOldEntry, below). Audit: venture repo
     projects/pop-cards/output/2026-10-12-old-site-audit/REPORT.md, section 1. */
  /* Safari's engine: every iPhone browser, and Safari on a Mac. Its opening plays the downloaded clip (startOpening). */
  const IS_WEBKIT = /AppleWebKit/.test(navigator.userAgent) && !/Chrome|Chromium|Edg|Android/.test(navigator.userAgent);
  /* The set this tab is in (assets/pp-set.js, ?set=reviewers), or null: a short list of cards, To me, one card. */
  function activeSet() {
    try { return (window.PPSet && window.PPSet.active()) || null; } catch (e) { return null; }
  }
  function builderB() {
    try { return !!document.querySelector('[data-builder-ui="b"]'); } catch (e) { return false; }
  }
  /* The free-card claim (?claim=POP-XXXX-XXXX) outlives the trip to All cards and back in this tab: kept in
     sessionStorage, read when the builder opens without one; it lasts as long as the tab, so the second free card of
     the order says Free too. Display only, as `claim` itself (the discount lives in the Shopify session). */
  const CLAIM_KEY = 'pp_claim_v1';
  const CLAIM_RE = /^POP-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/;
  /* The builder's preview switches in the address (?ppenvfx=0 and friends): remembered before the builder sends the
     visitor elsewhere, as the builder itself would have, so the link that turns one on still does (Daniel, 12 Oct). */
  function rememberSwitches(q) {
    try {
      const sw = { ppenvfx: ['pp_ppenvfx', '0'], ppflat: ['pp_ppflat', '1'], ppdeep: ['pp_ppdeep', '0'], ppkraft: ['pp_ppkraft', '1'], ppcompare: ['pp_ppcompare', '0'], ppframe: ['pp_ppframe', '0'], ppinside: ['pp_ppinside', '1'], ppbg: ['pp_ppbg', '0'], ppnotefonts: ['pp_ppnotefonts', '0'], ppachi: ['pp_ppachi', '1'] };
      Object.keys(sw).forEach(function (k) {
        if (!q.has(k)) return;
        const key = sw[k][0], on = sw[k][1];
        if (q.get(k) === on) localStorage.setItem(key, on); else localStorage.removeItem(key);
      });
      // B's side panel has four non-default values (builder-b.js SIDE_SWITCH).
      if (q.has('ppside')) { const v = q.get('ppside'); if (v === '0' || v === '1' || v === 'left' || v === 'right') localStorage.setItem('pp_ppside', v); else localStorage.removeItem('pp_ppside'); }
      // The type switch has two non-default looks (snippets/pp-type-switch.liquid).
      if (q.has('pptype')) { const v = q.get('pptype'); if (v === 'sans' || v === 'onest') localStorage.setItem('pp_pptype', v); else localStorage.removeItem('pp_pptype'); }
    } catch (e) { /* the switch stays as it was */ }
  }
  /* ── PHOTO FRAMING, DIRECTION B1: "THE PICTURE JUST MOVES" (Brief 21 part B; Daniel, 13 Oct: "for the builder photo
     framing I choose b1 from claude design"). ON BY DEFAULT since Daniel's "push b1 live" (13 Oct); ?ppframe=0 brings back
     the old Crop/Move tools in this browser, ?ppframe=b1 returns (remembered like the other pp* switches, and by
     rememberSwitches when the builder sends the visitor away). Read once, at load: the page is the unit a switch applies to. The design: design-brief/claude-design/
     reference/builder-21/claude-design-B1/. */
  const FRAME_B1 = (function () {
    try {
      const m = /[?&]ppframe=(b1|0)(?:&|$)/.exec(location.search);
      if (m) { if (m[1] === '0') localStorage.setItem('pp_ppframe', '0'); else localStorage.removeItem('pp_ppframe'); }
      return localStorage.getItem('pp_ppframe') !== '0';
    } catch (e) { return !/[?&]ppframe=0(?:&|$)/.test(location.search); }
  })();
  const FRAME_HINT_KEY = 'pp_ppframe_hint';   // the first-use hint has been shown in this browser
  const FRAME_SPILL_MS = 400;                 // the faded rest of the photo stays this long after a change stops
  const FRAME_SAY_MS = 500;                   // an announcement waits this long after the last key or wheel tick
  const TOP_PHOTO_KEY = 'pp_top_photo';      // IndexedDB slot for the inside top's photo
  /* The inside top's photo is kept larger than the four panels' (IMPORT_MAX): it fills a 98 mm
     face, and at 1600 px a landscape photo cropped to it is about 220 ppi. 2600 keeps a typical
     phone photo near 300. */
  const TOP_IMPORT_MAX = 2600;
  /* The inside-top photo's new controls (24 Oct; Achinoam via Yonatan, Daniel's go): zoom out below edge to edge, and
     see-through. Mirrored in inside-top.js (ZOOM_MIN) and in the tray's sliders. */
  const TOP_ZOOM_MIN = 0.25;
  const TOP_OPACITY_MIN = 0.2;
  const TOP_MODES = ['design', 'text', 'photo'];
  /* A refused keystroke on the inside top's words (topInput): `was` is what the field held, `v` what it would have
     held. When `v` only appends to `was` and the first added character continues the word `was` ends on, that word
     is unfinished and did not fit, so the words go back to the last whole word (trailing spaces trimmed). null when
     there is nothing to cut: an edit in the middle, a paste refused whole, or a refused space or punctuation mark. */
  const WORD_CH = /[\p{L}\p{N}\p{M}'’-]/u;
  function topCutPartialWord(was, v) {
    if (!was || v.length <= was.length || v.slice(0, was.length) !== was) return null;
    const added = v.slice(was.length);
    if (!WORD_CH.test(Array.from(added)[0] || '') || !WORD_CH.test(Array.from(was).pop() || '')) return null;
    return was.replace(/[\p{L}\p{N}\p{M}'’-]+$/u, '').replace(/\s+$/u, '');
  }
  /* The note's copy (fitNote's step-back, _rejectNoteEdit): the same test, but only spaces and tabs are trimmed after
     the cut -- a line break the customer typed is theirs, and the note's blank-line rules already budget it. */
  function noteCutPartialWord(was, v) {
    if (topCutPartialWord(was, v) === null) return null;
    return was.replace(/[\p{L}\p{N}\p{M}'’-]+$/u, '').replace(/[ \t]+$/u, '');
  }
  function emptyTop() {
    return { mode: 'design', template: (window.PPTop && window.PPTop.START) || 'classic', title: '', subtitle: '',
             font: null, color: null, photo: { zoom: 1, px: 0, py: 0 },
             // True once the customer has chosen anything here. Until then the inside top follows
             // the occasion and the design (topOnDesign); after, it is theirs and nothing resets it.
             touched: false,
             /* The occasion the link said the words are for (?for=, `any` for none), and the builder's
                occasion when it said so: see CARRIED_OCC and topWordsOcc. null: the words follow the
                builder's occasion. */
             wordsFor: null, wordsWith: null };
  }
  // A draft's inside top, checked field by field: a draft is input from outside this visit.
  function topFrom(o) {
    const t = emptyTop();
    if (!o || typeof o !== 'object') return t;
    if (TOP_MODES.indexOf(o.mode) >= 0) t.mode = o.mode;
    const P = window.PPTop;
    if (P && P.TEMPLATES.some(function (x) { return x.key === o.template; })) t.template = o.template;
    if (typeof o.title === 'string') t.title = o.title.slice(0, 80);
    if (typeof o.subtitle === 'string') t.subtitle = o.subtitle.slice(0, 120);
    if (P && P.TITLE_FONTS.indexOf(o.font) >= 0) t.font = o.font;
    if (P && P.COLOURS.some(function (c) { return c.key === o.color; })) t.color = o.color;
    if (o.photo && typeof o.photo === 'object') {
      const n = function (v, lo, hi, d) { v = Number(v); return isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d; };
      /* Zoom below 1 and Opacity since 24 Oct (inside-top.js photoPlace): a draft before it has neither and reads as
         zoom 1-3, fully opaque, exactly as it drew. */
      t.photo = { zoom: n(o.photo.zoom, TOP_ZOOM_MIN, 3, 1), px: n(o.photo.px, -1, 1, 0), py: n(o.photo.py, -1, 1, 0),
                  opacity: n(o.photo.opacity, TOP_OPACITY_MIN, 1, 1) };
    }
    t.touched = !!o.touched;
    const slugOk = function (v) { return typeof v === 'string' && (v === '' || /^[a-z0-9-]{1,40}$/.test(v)); };
    if (slugOk(o.wordsFor) && o.wordsFor && slugOk(o.wordsWith)) { t.wordsFor = o.wordsFor; t.wordsWith = o.wordsWith; }
    return t;
  }

  /* ── PREVIEW'S TWO BEATS (2026-09-03) ─────────────────────────────────
     Closing: the card FOLDS shut at the size it already is, then STANDS UP —
     the camera orbits square to the cover while closing in, so the finished
     card faces you with no keystone at all. Opening is the same two beats in
     reverse, at the same speed: "I want it to open as fast as it closed"
     (Daniel, 2026-09-03). Neither beat touches the shading clip. */
  const FOLD_MS = 900;
  const STAND_MS = 620;

  // A picture is worth editing at this size on screen and not below it. The
  // ONLY number behind "how many pictures fit" — see photosPerPage(), which
  // derives the answer from the panels' own projected width rather than from a
  // viewport breakpoint, so it stays right on any screen.
  const MIN_PICTURE_PX = 260;

  // No geometry yet — show the scene untransformed rather than guessing.
  const NEUTRAL_CAM = { x: 0, y: 0, scale: 1 };

  // Bounding box of some projected plane boxes, in the same units they are in
  // (unzoomed px relative to the scene centre). Null if there is nothing to
  // frame, which every caller must treat as "fall back", never as zero.
  function union(boxes) {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    (boxes || []).forEach(function (b) {
      if (!b) return;
      x0 = Math.min(x0, b.cx - b.w / 2); x1 = Math.max(x1, b.cx + b.w / 2);
      y0 = Math.min(y0, b.cy - b.h / 2); y1 = Math.max(y1, b.cy + b.h / 2);
    });
    if (!isFinite(x0)) return null;
    const w = x1 - x0, h = y1 - y0;
    if (!w || !h) return null;
    return { cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, w: w, h: h };
  }

  // The opening's framing. A CLOSURE variable, not component state: it is read
  // by cameraTarget() on every evaluation, and as a reactive property every
  // write would invalidate the whole style graph for a value that never
  // changes during the animation anyway.
  let openBox = null;

  const HIGHLIGHT_COLORS = {
    purple: '#C0A3DF',
    yellow: '#F5D26B',
    coral:  '#F0A48B',
    teal:   '#7DB8BE',
  };

  // Per-photo look filters (CSS filter strings — used live AND for the canvas bake later).
  // Tuned to echo the iOS app's Core Image looks.
  /* ───── THE LOOKS, AND THE WAND THAT WALKS THEM ──────────────────────────
     Yonatan, 2026-09-10: "maybe worth a 'magic' filter as the default that
     adds a bit of life. Most phone photos are grey and I don't think most
     people will bother exploring the filters."

     ⭐ A WAND, NOT A DEFAULT (Daniel). A default changes what prints for
     everyone, silently. A wand is one tap, opt-in, and reversible — and
     tapping again travels further rather than handing back what you just
     rejected.

     ⛔ FIVE DIRECTIONS, NOT ONE MOOD FIVE TIMES. My first proposal was warm,
     airy and faded; Daniel: "they all sound sort of in the same direction,
     like nostalgia, old-style photos." He was right, and it matters more for a
     wand than for a picker — a cycle that never leaves one mood is just a
     slower default. Warm and Faded are nostalgic, Bright is clean and modern,
     Punch is graphic, Cool is winter, Mono is none of the above.

     ⚠️ CSS filters have no white balance and no per-channel control, so film
     emulation is not on the table. And hue-rotate goes wrong on skin fast —
     Cool is the only one that uses it, it is kept to 8 degrees deliberately,
     and it is the one to check on faces before this goes to Live. */
  const EFFECT_FILTERS = {
    original: 'none',
    /* ⭐ PUSHED HARD ON PURPOSE (Daniel, 2026-09-10: "the filters themselves
       need to feel different enough from each other — different styles").
       The first set was polite: six variations you had to look for. A wand
       that takes a second to cast and then changes almost nothing is a worse
       trick than no wand, because the customer waited for it. */
    warm:     'sepia(0.50) saturate(1.60) brightness(1.04) contrast(1.02)',
    bright:   'brightness(1.14) contrast(1.12) saturate(1.15)',
    faded:    'contrast(0.80) brightness(1.12) saturate(0.72) sepia(0.15)',
    punch:    'saturate(1.90) contrast(1.32)',
    /* The default look on new photos (Daniel, 12 Oct: "not as deep as the filter. I just want the colours to pop a
       little bit more ... a noticeable difference between what they uploaded and what they see after the animation,
       but not crazy deep"). About a quarter of Deep's push. Baked into the pixels like every look, so print needs
       nothing (print/order.mjs: filters arrive as pixels). */
    pop:      'saturate(1.25) contrast(1.08) brightness(1.02)',
    /* ⚠️ SEPIA FIRST, THEN HUE-ROTATE — and that order is the safety.
       hue-rotate on original colour is what wrecks skin tones; sepia
       neutralises to a single hue first, so the rotate tints EVERYTHING the
       same way instead of dragging faces somewhere green. It also makes this
       an actual style — a cyanotype — rather than a slightly bluer photo. */
    cool:     'sepia(0.45) hue-rotate(165deg) saturate(1.50) contrast(1.08)',
    mono:     'grayscale(1) contrast(1.28) brightness(1.03)',

    /* ⛔ THE OLD KEYS STAY, AND ARE NOT OFFERED. A draft in somebody's
       localStorage can carry effect: 'silver' from before today, and an
       unknown key falls back to 'none' — which would silently strip the look
       off a picture they had already chosen. They are absent from
       EFFECT_ORDER, so nothing new can be set to them, and present here so
       nothing old breaks. Do not "tidy" them away. */
    silver:   'saturate(0.15) brightness(1.03) contrast(1.03)',
    noir:     'grayscale(1) contrast(1.15)',
    vivid:    'saturate(1.45) contrast(1.05)',
    dramatic: 'contrast(1.28) brightness(0.95) saturate(1.05)',
  };

  const EFFECT_ORDER = ['original', 'pop', 'warm', 'bright', 'faded', 'punch', 'cool', 'mono'];
  const EFFECT_LABELS = {
    original: 'Original', pop: 'Pop', warm: 'Warm', bright: 'Bright', faded: 'Faded',
    punch: 'Punch', cool: 'Cool', mono: 'Mono',
    silver: 'Silver', noir: 'Noir', vivid: 'Vivid', dramatic: 'Dramatic',
  };

  /* ⭐ ORDER MATTERS MORE THAN THE CONTENTS. The first tap is the safest
     flatterer; each one after travels further. That is what makes "try again"
     feel like a dial rather than a dice roll — and it is why this is a fixed
     cycle rather than Math.random(), which repeats and can hand back the look
     you just rejected (Daniel: curated, not random).

     ⚠️ Mono is LAST and never first. A tap that turns somebody's grandmother
     black and white is the opposite of magic — but that argument was against
     random, not against black and white, so it belongs in the set at the end.
     `original` is not in the cycle: the wand's job is to leave it. */
  const WAND_CYCLE = ['warm', 'bright', 'faded', 'punch', 'cool', 'mono'];

  /* ⛔ THE EFFECT TOOL IS OFF, AND NOTHING WAS DELETED (Daniel, 2026-09-10:
     "remove the effect from the toolbar (revertible)"). Same shape as
     CAPTIONS_OFFERED above: one constant, and effectsOffered() is the single
     source of truth every surface reads. Flip it to true and the tile picker,
     its toolbar button and its popover all come back exactly as they were.

     WHY IT GOES: the wand is now the whole answer. A row of tiles beside a
     one-tap button is the long way round presented as an equal, and it was the
     taller half of the popover. The looks are all still reachable — the wand
     walks every one of them. */
  const EFFECTS_OFFERED = false;

  /* How long the trick takes. Daniel: "it needs to take a second."
     The filter lands at CAST_APPLY_MS — mid-sheen, while the light is over the
     picture — so the change is REVEALED by the sweep rather than happening
     beside it. Landing it at either end makes the animation decoration bolted
     onto an instant change. */
  const CAST_MS = 1000;
  const CAST_APPLY_MS = 420;

  const DEFAULTS = {
    anniversary: {
      tagline: ['NOTHIN', 'MATTERS', 'MORE THAN', 'YOU LOVE'],
      highlight: { lineIndex: 3, wordIndex: 0, color: 'purple' },
      message: 'You make me feel like im living a teenage dream. Loving you is the best thing I do.',
    },
    birthday: {
      tagline: ['ANOTHER', 'YEAR OF', 'YOU IS', 'A GIFT'],
      highlight: { lineIndex: 3, wordIndex: 1, color: 'yellow' },
      message: 'Happy birthday. To many more years of being exactly who you are.',
    },
    wedding: {
      tagline: ['TWO BECOME', 'ONE TODAY', 'FOR ALL', 'OUR DAYS'],
      highlight: { lineIndex: 1, wordIndex: 0, color: 'coral' },
      message: 'Wishing you a marriage full of soft mornings, long laughs, and a thousand small joys.',
    },
    baby: {
      tagline: ['WELCOME', 'TO THE', 'WORLD', 'LITTLE ONE'],
      highlight: { lineIndex: 3, wordIndex: 1, color: 'coral' },
      message: 'You showed up and rearranged everything we thought we knew. We are so glad you are here.',
    },
    sympathy: {
      tagline: ['THANK YOU', 'FOR', 'EVERYTHING', 'YOU GAVE'],
      highlight: { lineIndex: 0, wordIndex: 1, color: 'teal' },
      message: 'We will not pretend we have the words. But we will try, here, every year.',
    },
    congrats: {
      tagline: ['LOOK AT', 'YOU GO', 'WE ARE', 'PROUD'],
      highlight: { lineIndex: 3, wordIndex: 0, color: 'yellow' },
      message: 'You earned this one. Take the moment in before you start chasing the next.',
    },
    'just-because': {
      tagline: ['NO REASON', 'JUST', 'THINKING', 'OF YOU'],
      highlight: { lineIndex: 1, wordIndex: 0, color: 'purple' },
      message: 'No occasion. No anniversary. Just a small note to say I am glad you are in my life.',
    },
  };

  // ───── The occasions we sell into ────────────────────────────────────
  // Daniel's launch list, 2026-08-05. Two groups in his order: the Q4 season
  // first, because that is the run we are building for, then the everyday
  // occasions that sell all year.
  //
  // ONE list, read by the occasion picker, the design picker's filter, and
  // the headline prefill. Before this there were three separate lists — this
  // file, the wizard snippet, and the artist's brief — and none of them
  // agreed, so designs.json was tagged against occasions no picker offered.
  //
  // `phrase` is stored rather than derived because the design picker heads a
  // band with "For a birthday" / "For Christmas" / "For Rosh Hashanah", and
  // English decides the article per word. There is no rule to write.
  //
  // Two of Daniel's lines were slash-pairs. "Thinking of you / Just because"
  // is ONE occasion here — same emotional slot, same card. "Sympathy / Get
  // well" is TWO: offering a sympathy cover to someone buying for a friend in
  // hospital is the worst mistake this picker could make.
  /* ⛔ LEAN CATALOGUE — 2026-09-01. Cut from fourteen to one.
   *
   * WHY, AND WHY IT IS NOT A DESIGN DECISION. The store went public (the
   * storefront password came off), and the catalogue held one real design —
   * Grumpy Cat, tagged `birthday` — against three placeholders, which were cut
   * from assets/designs.json the same day. An occasion whose slug matches no
   * design's `occasions`, and which has no universal design to fall back on,
   * gets an EMPTY PICKER: design-picker.js bands on designsMatched() (slug in
   * the list) and designsUniversal() (list empty), and neither catches it.
   * Thirteen of these fourteen were that case the moment the placeholders went.
   *
   * So the list is cut to what the catalogue can actually serve. Daniel:
   * "cut them and cut the occasions as well. we're starting lean and beefing
   * out."
   *
   * ✅ TO PUT ONE BACK: uncomment its row here AND add a design in
   * assets/designs.json carrying that slug (or `occasions: []`). Both, together,
   * or you have re-created the empty picker. Rosh Hashanah came back that way on
   * 2026-09-04 — Daniel named it as a launch occasion on 2026-09-01, Rosh
   * Hashanah 5787 is 2026-09-11, and the Shana Tova artwork landed to serve it.
   *
   * ⚠️ The rows below are kept verbatim, not deleted, because the `phrase` and
   * `headline` on each were written by hand and are easy to get subtly wrong the
   * second time — "a birthday" but "Christmas".
   *
   *  { slug: 'grandparents-day',group: 'season',   label: "Grandparents' Day", phrase: "Grandparents' Day", headline: 'For you both' },
   *  { slug: 'thanksgiving',    group: 'season',   label: 'Thanksgiving',    phrase: 'Thanksgiving',      headline: 'Happy Thanksgiving' },
   *  { slug: 'hanukkah',        group: 'season',   label: 'Hanukkah',        phrase: 'Hanukkah',          headline: 'Happy Hanukkah' },
   *  { slug: 'new-year',        group: 'season',   label: "New Year's",      phrase: 'the new year',      headline: 'Happy New Year' },
   *  { slug: 'anniversary',     group: 'everyday', label: 'Anniversary',     phrase: 'an anniversary',    headline: 'Happy anniversary' },
   *  { slug: 'thank-you',       group: 'everyday', label: 'Thank you',       phrase: 'a thank you',       headline: 'Thank you' },
   *  { slug: 'thinking-of-you', group: 'everyday', label: 'Thinking of you', phrase: 'thinking of them',  headline: 'Thinking of you' },
   *  { slug: 'sympathy',        group: 'everyday', label: 'Sympathy',        phrase: 'sympathy',          headline: 'With love' },
   *  { slug: 'get-well',        group: 'everyday', label: 'Get well',        phrase: 'getting well',      headline: 'Get well soon' },
   *  { slug: 'professional',    group: 'everyday', label: 'Professional',    phrase: 'work',              headline: 'With appreciation' },
   */
  /* `title` and `subtitle` are the OCCASION PAGE's h1 and h2 — the catalogue
     screen at /products/picturepop?occasion=<slug>. Written out per row
     rather than derived, for the same reason `phrase` and `headline` are: the
     grammar changes per occasion and a template that reads well for one reads
     wrong for the next ("Send a Happy birthday to someone you love").

     ⚠️ ROSH HASHANAH'S TITLE SPELLS IT "Rosh Hashana", WITHOUT THE FINAL H,
     while `label` — the chip, the search row — spells it "Rosh Hashanah". That
     is the handoff's verbatim copy table (2026-09-05), not a typo introduced
     here. Flagged rather than silently reconciled: one of the two is wrong and
     it is a copy call, not a code one. */
  /* ───── Occasions ────────────────────────────────────────────────────
     ⛔ THE LIST MOVED TO assets/occasions.js ON 2026-09-10, and it moved for
     one reason: /pages/occasions has to offer exactly the occasions this
     builder offers. The alternatives were loading this whole bundle on a
     directory page or keeping a second copy in Liquid — and a second copy is
     how the two drift until the page advertises destinations the flow has
     never heard of.

     ⚠️ IT IS STILL ONE HOME. The note further down still holds: "Liquid used
     to hold this list inline; it now has one home, in JS." That home is just a
     smaller file now.

     ⛔ occasions.js MUST BE IN THE DOCUMENT BEFORE THIS FILE. Both are
     `defer`, and deferred scripts run in document order — see
     product-builder.liquid. The empty fallback is deliberate: a builder with
     no occasions renders an empty picker, which is visibly broken, rather than
     throwing at parse time and taking the whole page with it. */
  const OCCASIONS = window.PP_OCCASIONS || [];

  /* ⭐ ARTIST MODE: "My designs" is an occasion of its own, first on the occasions
     page, and it holds ONLY the artist's designs — no catalogue design, not even the
     ones offered for every occasion (design-picker.js designsUniversal). Daniel,
     2026-09-17: "another occasion card that's called My Designs, where, when the
     artist clicks on that, he sees only his designs in the grid." Customers never
     get it: ARTIST_TOKEN is null for them. */
  const MY_DESIGNS = 'my-designs';
  if (ARTIST_TOKEN && !OCCASIONS.some(function (o) { return o.slug === MY_DESIGNS; })) {
    OCCASIONS.unshift({ slug: MY_DESIGNS, group: 'everyday', label: 'My designs', phrase: 'your own design',
      headline: '', title: 'My designs', subtitle: 'Only you can see these. Send one as a free card.' });
  }
  // ORG MODE: the same one-occasion shelf, holding the order's own approved design.
  if (ORG_TOKEN && !OCCASIONS.some(function (o) { return o.slug === MY_DESIGNS; })) {
    OCCASIONS.unshift({ slug: MY_DESIGNS, group: 'everyday', label: 'Your design', phrase: 'your design',
      headline: '', title: 'Your design', subtitle: 'The design your organization approved.' });
  }

  /* ───── Placeholder product shots (Daniel, 2026-08-15) ────────────────
   * Eight real photographs of the physical card, from
   * projects/pop-cards/output/product-shots/web (the @800 renders — the deck
   * draws a card 260px wide, so the full-size files would be waste).
   *
   * THE SAME EIGHT ON EVERY OCCASION, which is the point: they are stand-ins
   * for per-occasion photography that does not exist yet, and the deck can
   * be judged on real product pictures in the meantime. A design that
   * declares its own `shots` overrides them for that design alone.
   *
   * Order matters — shot 1 is the card CLOSED, the face you meet first.
   */
  const PLACEHOLDER_SHOTS = [
    { file: 'pp-shot-1-closed.webp', label: 'closed' },
    /* Shots 2-5 (the card open, front) are gone: their four photos were the
       Mixtiles dog set we may not use (Daniel, 2026-10-13). */
    { file: 'pp-shot-6-back.webp',   label: 'back' },
    { file: 'pp-shot-7-back.webp',   label: 'back, turned' },
    { file: 'pp-shot-8-back.webp',   label: 'back, angled' }
  ];

  // Which card the deck parts to before the customer touches anything.
  // Must be a slug in OCCASIONS.
  const DECK_OPENS_ON = 'birthday';

  // Occasion → headline prefill (app parity; never blank).
  const OCCASION_HEADLINE = {};
  OCCASIONS.forEach(function (o) { OCCASION_HEADLINE[o.slug] = o.headline; });

  /* The occasion page's own copy, by slug. Falls back in the picker rather
     than here, so an occasion added without copy still renders a page. */
  const OCCASION_PAGE = {};
  OCCASIONS.forEach(function (o) {
    OCCASION_PAGE[o.slug] = { title: o.title || '', subtitle: o.subtitle || '' };
  });

  function emptyState() {
    return {
      // Stage 2 model — toolbar-driven section state. 'card' | 'photos' |
      // 'bottom' | 'send'.
      //
      // 'card' is the landing state (Daniel, 2026-08-05): pick occasion, pick
      // design, pick four pictures, and then the card opens and you see the
      // WHOLE of it. Handing off into 'photos' put the camera in close on the
      // pictures before the customer had seen what they had made.
      section: 'card',
      occasion: null,
      // Where the customer is in the pre-photo questions. Explicit, not
      // derived: name and email can both legitimately be left blank, so
      // "has a name" cannot tell you whether the name step has been passed.
      // Only meaningful while `stage` is 'entry'.
      entryStep: 'occasion',

      // ── The occasion step's own state (handoff: occasion split rail,
      //    2026-08-13) ────────────────────────────────────────────────────
      // `occPick` is the PENDING occasion — highlighted in the list and
      // driving the carousel, but not yet committed. It exists because the
      // step now ends in a Continue button: until that press, choosing a row
      // is a preview, not an answer, and `occasion` must stay untouched or
      // backing out would leave a card dressed for something never confirmed.
      // Ephemeral on purpose — it is seeded from `occasion` on load (see the
      // init below), so a returning visitor still finds their choice ticked.
      occPick: null,
      occQuery: '',       // the search box, permanently visible under the pills

      // Which gallery shot the product page is showing. Ephemeral — a
      // returning visitor starts on the hero, not wherever they left the
      // thumb row.
      pdpIndex: 0,
      pdpZoom: false,   // the lightbox; never persisted
      pdpMag: false,    // the lightbox picture magnified, panning under the cursor
      pdpMagX: 50, pdpMagY: 50,   // where the cursor is on it, in percent
      designSheet: null, // the expanded view: a design id, or null. Never persisted.
      artistSheet: null, // the artist's profile: an artist id from catalogue credit, or null. Never persisted.
      occSlide: 0,        // which product photo is showing, 0..slides-1
      // Live drag bookkeeping while a finger is down on the deck. Null when
      // nothing is being dragged — which is also what tells the strip to
      // animate rather than follow the pointer.
      occDrag: null,
      // Accumulated horizontal wheel delta, spent one card at a time. A
      // trackpad emits many small deltas; a card should not turn on each.
      occWheel: 0,
      // True while a pointer is on the pill row, which parks its drift.
      occPillHeld: false,
      // Kept for drafts saved before 2026-09-03, when the name and email
      // steps were removed from the entry flow. Nothing asks for them now.
      name: '',
      email: '',
      photos: [],        // each: { dataUrl, name, size, effect?, border?, cropScale? }
      // Photo framing B1 (FRAME_B1): the faded rest of the photo (0 hidden, 1 shown, 2 fading out), the first-use hint,
      // and what the polite live region says. Display only; nothing here is saved or ordered.
      frameSpill: 0,
      frameHint: false,
      frameSaid: '',
      selectedPhoto: null,  // index of the photo being edited (null = carousel view) — ephemeral
      // How far along the strip of pictures the camera has slid in Edit
      // pictures, 0..1. CONTINUOUS, not a page number (Daniel, 2026-08-05):
      // a trackpad scroll moves it by fractions, so the pictures glide past
      // rather than jumping one to the next. The arrows step it by a window's
      // width, which is the same value moved further at once.
      // Ephemeral — a position means nothing on a viewport that may have
      // changed size since.
      photoScroll: 0,
      // True while a wheel or a finger is actually driving the camera. The
      // 520ms transition is what makes the arrows glide, and the same 520ms is
      // what would make a scroll feel like dragging something through treacle,
      // so it is switched off for the duration.
      scrubbing: false,
      // True from the moment the camera target changes until its transition
      // has had time to land. Drives `will-change: transform` on .pp-zoom,
      // which must NOT be permanent: a promoted layer keeps re-using the
      // raster it took at one device scale, so a trackpad pinch or a display
      // change leaves the card drawn from stale tiles — visibly torn, with
      // pieces missing. See the note on .pp-zoom in stage.css.
      camMoving: false,
      editSub: 'none',      // one of EDIT_SUBS — ephemeral
      // v4 (2026-08-25): which CARD-WIDE popover is open above the toolbar,
      // or null. Photo tools use editSub; this is its card-wide twin, so the
      // two can never both be showing. Ephemeral, like editSub.
      cardTool: null,
      // The inside top's photo, { dataUrl, w, h, srcW, srcH }, pixels in IndexedDB like the panels';
      // its preview (an object URL for CoverTop) and the template tiles. Only the photo is kept.
      topPhoto: null,
      topPreviewUrl: '',
      topThumbs: {},
      topAuto: null,         // the cover's colour for the layouts (_topAutoKick), or null
      topMsg: '',
      topSmall: false,
      gphotos: { loading: false },  // Google Photos import state — ephemeral
      // True when the note is longer than the card's band can hold even at the
      // fitter's floor size — see fitNote(). Drives the warning in the note
      // drawer, and nothing else: the text is never edited or silently cut.
      // Only ever true OUTSIDE typing — a restored draft, or a font or border
      // change that made an already-written note too big. While the customer
      // is typing, fitNote() rolls the edit back instead, so this cannot
      // become true and there is nothing to warn about. No UI reads it; it is
      // kept because fitNote uses the same test to pick the floor size.
      noteOverflow: false,
      // Whether the card should be showing "Your note" instead of a note. NOT
      // the same as an empty string: a note of nothing but Enters has no words
      // on it and reads as empty on the paper. fitNote() decides, because it is
      // the thing that knows which lines survive.
      noteEmpty: true,
      // True while the note's field is open on the card — the field is what you
      // see then, and the printed <p> waits. Ephemeral: an editor is not a
      // thing to restore someone into on their next visit.
      noteFieldOpen: false,
      // Which picture the mouse is over, or null. Desktop only — see
      // setHoverPhoto(). Ephemeral: never stored, never in the order.
      hoverPhoto: null,

      // ── FULL-SPREAD PROTOTYPE (?pano=1) — 2026-08-20 ──────────────────
      // One photo outpainted across all four accordion panels. Prototype
      // only: URL-gated, never persisted, never in the order. The demo
      // image is a FLUX.2 Pro Outpaint result (the front-runner model);
      // live generation uses Cloudinary gen_fill as a stand-in because it
      // is the only fill reachable without a backend. See the shootout doc.
      pano: /[?&]pano=1/.test(location.search),  // chip + mode visible at all
      panoOn: true,                              // toggled from the chip
      panoUrl: (window.PP_PANO_DEMO || ''),      // the spread being shown
      panoBusy: false,
      panoError: '',
      card: {
        occasion: null,        // mirrors top-level occasion for the future 3D card front
        topSentence: '',       // big sentence above the accordion (no highlight)
        captions: ['', '', '', ''],  // per-photo captions under the accordion
        bottomNote: '',        // freeform note below the accordion
        /* THE CUSTOMER HAS TAKEN THE PANEL OVER. Only meaningful for designs
           that bring their own message (art.CoverBottom). False means the
           design's words are showing; true means the customer replaced them.
           Kept on `card` rather than in UI state because it survives a save —
           coming back to a draft must not silently restore the design's line
           over a note somebody wrote. */
        noteOwn: false,
        // The inside top (2026-10-08): see emptyTop() and assets/inside-top.js.
        top: emptyTop(),
        section: 'card',       // also mirrored here so wizard/handoff can pre-set entry

        // ── The two card-wide picture settings (Daniel, 2026-08-16) ────
        // They belong to the CARD, not to a photo: every panel is printed on
        // one strip of paper, and one picture bleeding to the fold while its
        // neighbour carries a caption reads as a mistake rather than a choice.
        //
        // Both are in the order and both reach print — see buildCartProperties
        // and print/make-print-file.mjs.
        // TRUE means captions OFF — the uncaptioned well. Off by default
        // since 2026-09-03 (Daniel: "default - captions off in builder");
        // the switch in the dock turns them on. Drafts saved before that
        // day keep whatever they had — loadState reads the stored value.
        fullSize: true,
        /* ⭐ BOTH ON BY DEFAULT (Daniel, 2026-09-20). They were opt-in, which
           meant the card most people bought was the plainest one the builder
           can make — a square-cornered picture with no lift. These are the
           two settings that make it look like a printed object rather than a
           photo dropped on paper, and almost nobody found them.

           2.0 mm is PHOTO_RADIUS_MM, the radius Yonatan's bench and print
           already round to; stored as a NUMBER because that is the shape
           roundedMm() wants — `true` still coerces for drafts saved before
           2026-09-11, but a fresh card should not lean on that path.

           ⚠️ Both are INTENT, not effect. At the `none` border stop the
           picture runs to the trim and neither can print, so roundedActive()
           and shadowActive() both return falsy there whatever is stored here.
           BORDER_DEFAULT is `classic`, so a fresh card does show both. */
        shadow: true,          // soft drop shadow under every picture
        rounded: PHOTO_RADIUS_MM,   // 2.0 mm — see PHOTO_RADIUS_MM
        // v4, 2026-08-25: the paper margin around each picture, as a stop key
        // from BORDER_STOPS. Independent of captions — the customer sets the
        // border once and turning captions on takes the band out of the
        // PICTURE's height, not out of the border.
        //
        // `classic` + captions is exactly the layout that shipped before this
        // control existed. An untouched card no longer renders that way —
        // captions default off as of 2026-09-03 — but the well itself is
        // unchanged for anyone who turns them on.
        border: BORDER_DEFAULT,
        // The hands the note and the captions are printed in. TWO choices, not
        // one (Daniel, 2026-08-29: "if I want to change the font on the note,
        // it also changes the font on the captions. I don't want that").
        //
        // `font` is kept as the legacy single value so a draft or an order
        // written before the split still reads — see loadState and
        // print/order.mjs. Nothing writes it any more.
        font: CARD_FONT_DEFAULT,
        noteFont: CARD_FONT_DEFAULT,
        captionFont: CARD_FONT_DEFAULT,
      },

      // ── Cover designs ─────────────────────────────────────────────────
      // The catalogue, loaded from assets/designs.json. Empty until it lands.
      designs: [],
      // Which design's ART has finished decoding. The opening must not start
      // before it has: CoverTop is only revealed at frame 57, so an undecoded
      // cover would pop in halfway through the unfold.
      designArtReady: false,
      // Which tile is showing its open preview on a touch screen. Hover does
      // this on a desktop and needs no state; a phone has no hover, so the
      // tile carries a "see inside" button that sets this. Ephemeral — never
      // persisted, and cleared whenever the occasion changes.
      designPeek: null,
      // A stripped design's text-free inside top, drawn as the catalogue's top shot, by its CoverTopPlain URL
      // (design-picker.js designTopShot, assets/top-shot.js). Ephemeral: blob URLs for this page only.
      topShots: {},
      // Who each design is for (catalogue-facts.json), fetched only on a person's day (design-picker.js designsMatched).
      designWho: null,

      // ── Card stage, rebuilt 2026-08-04 ────────────────────────────────
      // Only what the rebuilt stage actually needs. The old version also
      // carried activePanel, dragIndex and narrow for a per-panel camera;
      // this camera frames the whole card and needs none of them.
      geom: null,
      // Frame 0 is the card fully OPEN. The artist's pass runs open -> closed,
      // so the opening plays it in reverse.
      cardFrame: 0,
      // Preview's fold state (2026-09-03). `cardClosed` is true while the card
      // rests closed on the preview screen; `closing` while the clip is being
      // scrubbed shut. Both ephemeral. The shading canvas stays up for both —
      // see openVideoStyle — so the closed card keeps the artist's pass.
      cardClosed: false,
      closing: false,
      artWarm: true,             // every picture on the card is decoded
      /* ⛔ THE USER FOLDED IT, versus the entry animation folding it. The
         opening itself stands the card up SHUT and reveals it closed for an
         instant (openFromStanding), so `cardClosed` is true during every
         page load — which flashed the closed dock's "Open the card" at the
         bottom before the card had even opened (Daniel, 2026-09-03). The
         closed toolbar and the fold pill key off THIS flag, set only when the
         customer presses "See it closed", so a fresh load, or arriving from
         another page, only ever shows the normal toolbar. */
      foldedInEditor: false,
      // The open toolbar's width, captured at fold time so the shut bar keeps
      // the toolbar's exact footprint (Daniel: same width as the toolbar).
      _dockW: 0,
      // ── The Open/Closed toggle (the bottom-left "pill") ──────────────
      // _foldAnimated gates the thumb squash + label bounce: false on mount
      // so the toggle appears at rest, true from the first user tap on.
      // _foldBurst holds the confetti particle specs while a burst is in
      // flight (empty otherwise). Both are UI-only; the fold state itself
      // lives in foldedInEditor. See toggleFold() and .pp-cardfold in stage.css.
      _foldAnimated: false,
      _foldBurst: [],
      // The one-time attention ring on the nav toggle after the opening —
      // see _foldNudgeArm and .pp-cardfold.is-nudge in stage.css.
      _foldNudge: false,
      /* The toolbar's own one-time hint — the violet flash and the "Start
         here" arrow. Ephemeral; whether it has been SEEN lives in
         localStorage under TB_HINT_KEY. See _armToolbarHint(). */
      tbHint: false,
      _tbHintT: 0,
      // Handoff particle palette (design_handoff_toggle): size px, radius, colour, seconds.
      _foldParticles: [
        { s: 7, r: '50%', c: '#7813EC', d: 0.55 },
        { s: 6, r: '2px', c: '#D96C57', d: 0.55 },
        { s: 7, r: '50%', c: '#D4A017', d: 0.55 },
        { s: 6, r: '2px', c: '#4E8A5E', d: 0.6 },
        { s: 6, r: '2px', c: '#2F5586', d: 0.6 },
        { s: 5, r: '50%', c: '#7813EC', d: 0.65 },
      ],
      // True only while the opening is running. Ephemeral. It used to play
      // once per session (sessionStorage `pp_opened_v2`); since 2026-09-03
      // it plays on every entry to the builder (Daniel: "Always show opening
      // animation on builder — always"). Reduced-motion still skips it.
      opening: false,
      // ⚠️ The card stays HIDDEN until we know whether it is about to open.
      // Revealing it first shows the FINISHED card, which then snaps closed
      // and unfolds — "you can see what was on the page before". Invisible on
      // a warm cache, because the decision is instant; on a cold load the
      // clip has to buffer first and the finished card sat there for the whole
      // wait. Set on every path that decides, including the ones that decline.
      _openingDecided: false,
      // Bumped to force a camera recompute — Alpine can't see geometry change.
      camNonce: 0,
      // Projected boxes for the current frame, written by applyFrame().
      // Framing reads these rather than measuring element rects, which feeds
      // back on itself through the very transform it is trying to set.
      frameBoxes: null,
      // WHO THE CARD IS FOR. Every field here describes the RECIPIENT.
      //
      // ⚠️ It used to carry `senderName`, and that name was written into
      // checkout[shipping_address][first_name] — so the shipping label read
      // the SENDER's name at the RECIPIENT's address. For a gifting product
      // that is the worst possible defect, and it shipped for weeks.
      //
      // `senderName` and `giftMessage` were both removed 2026-08-15. The buyer
      // is already identified by Shopify at checkout (email + billing name)
      // and again by the entry step's name question, and the message to the
      // recipient is the note printed ON the card — a second message field was
      // asking twice for one thing, and nothing ever printed or packed it.
      recipient: {
        firstName: '', lastName: '',
        line1: '', line2: '',
        city: '', state: '', zip: '', country: 'US',
      },

      /* ── THE FAN-OUT LIST (2026-09-08) ────────────────────────────────
         One card, several people. EMPTY IS THE NORMAL STATE and means
         "one card, and Shopify's checkout asks where it goes" — exactly
         the behaviour since 2026-09-03, untouched. A customer only ever
         gets a list by asking for one, from Preview.

         ⛔ THE LIST IS THE WHOLE FEATURE, AND ITS LENGTH IS THE ONLY
         THING THAT CHANGES WHAT ADD-TO-CART DOES:
           0  →  one cart line, no address on it (today)
           N  →  N cart lines, one per person, each carrying its own
                 `Delivery To`.
         There is no third mode and no "quantity" — a copy without a
         person to send it to is a card nobody receives.

         Every copy is the SAME card: same photos, same headline, same
         note (Daniel, 2026-09-08: "shared note only if it's same card to
         multiple people, obv"). Per-recipient notes were raised and
         parked in the same breath — see decisions.md 2026-09-08. If they
         ever land, they land as a field ON this object, which is why each
         entry is an object rather than a bare address string.

         `recipient{}` above is the retired single-address form's state.
         Left alone: it is still persisted in old drafts and reading it
         back is free. Nothing writes it any more. */
      recipients: [],
      /* Direction B's Send (2026-10-09, builder B phase 4): who the card goes to ('me' | 'other' | 'several'; '' until
         B's Send step has been opened, and always in today's builder, which never asks), and the sender's own return
         address, From you. Kept in the draft (still v: 3), so a card started in either builder keeps them. Only B writes
         `_From` from them (builder-b.js buildCartProperties): today's builder has no From you, so its orders are
         exactly what they were. Contract: docs/2026-09-08-order-property-contract.md, "The sender on the envelope". */
      sendTo: '',
      sender: blankSender(),
      /* Bumped when the US ZIP table finishes loading — see recipBad(). It is
         a reactivity trigger and nothing else; its value is meaningless. */
      _zipTick: 0,
      // Which entry is expanded for editing. -1 = all collapsed.
      recipOpen: -1,
      // Raised by the first refused Add-to-cart, never lowered — same rule
      // as the old sendTried: an untouched field is untouched until they
      // have been asked, and marking a form someone has just arrived at is
      // telling them off for not having finished it.
      recipTried: false,

      errors: {},
      /* Replays the pill's nudge on an identical second refusal. Picking five
         twice in a row sets the same message, and Alpine correctly does
         nothing with an unchanged string — so without this the second attempt
         changes nothing on screen at all, which is the bug being fixed. */
      photoBump: false,
      submitting: false,
      // True once this card is in the cart, so Preview's button reads "Already
      // added to cart" and clicking it views the cart instead of adding again.
      // Survives a bfcache Back; cleared on Back to edit (see setSection).
      cartAdded: false,
      // Preview's look-close mode: click the card to zoom in and roam it with
      // the cursor; click again to zoom back out. See previewZoomToggle.
      previewZoom: false,
      _pzMX: 0, _pzMY: 0, _pzRaf: 0,
      // Mobile look-close: relative drag, not the desktop cursor-follow.
      _pzPanX: 0, _pzPanY: 0, _pzDrag: null, _pzMoved: false,
      converting: false,   // a photo is being converted server-side
      card3dOpen: true,
      // Plan 8 fields — dormant. Retained on the type so old sessionStorage
      // rehydrates cleanly; not read by the Stage 2 builder.
      tab: 'photos',
      details: [],
      tagline: [],
      highlight: { lineIndex: 0, wordIndex: 0, color: 'purple' },
      message: '',
    };
  }

  /* ═══ THE DRAFT ═══════════════════════════════════════════════════════
     Daniel, 2026-08-29: "Needs fixing."

     Between 2026-08-19 and now the builder remembered NOTHING, while the ×
     button said "Your work is saved here — come back any time in this tab."
     Four uploads, four captions and a note, gone on a refresh, an iOS tab
     eviction, or anyone who believed that sentence.

     TWO STORES, because they hold different-sized things:
       localStorage  the light state — occasion, design, captions, note,
                     border, recipient. A few KB, read synchronously at boot
                     so the first paint already knows there is a draft.
       IndexedDB     the photographs, as data URLs under pp_photo_<n>.
                     localStorage caps at ~5MB per origin and four builder
                     photos blow that on their own (see idb-photos.js).

     localStorage therefore holds only `idbKey` refs; init's rehydrate step
     pulls the pixels in afterwards, so the card can be framed and the toolbar
     drawn before the photos land.

     ⚠️ THE FLAG IS SEPARATE AND IT IS TINY. pp_draft_flag_v1 is what the
     marketing pages read to decide whether their CTA says "Continue card" —
     see snippets/pp-draft-cta.liquid. It is a separate key on purpose: those
     pages must not parse, or even load, the whole draft to answer one
     yes/no question. */
  /* ⭐ THE ONE-TIME "START HERE" ON THE TOOLBAR (Daniel, 2026-09-20: "we see
     people missing it"). Shown once per browser, after the opening animation
     has finished — never over it, because the card unfolding is the thing we
     want looked at first. Three flashes and it retires itself. */
  const TB_HINT_KEY = 'pp_tb_hint_v1';
  const TB_HINT_MS = 3600;      /* 3 x the 1.15s flash, plus a breath */

  const DRAFT_KEY = 'pp_draft_v3';
  const DRAFT_FLAG = 'pp_draft_flag_v1';
  const PHOTO_KEY = function (i) { return 'pp_photo_' + i; };

  /* What a draft is allowed to carry. An allow-list, not a blanket dump of
     the component: `stage`, `section`, `selectedPhoto`, `editSub`, `opening`
     and the pano fields are all about WHERE THE CUSTOMER IS, and restoring
     those puts a returning visitor back inside a half-open tool with no idea
     how they got there. What persists is the CARD. */
  function draftFrom(c) {
    return {
      v: 3,
      ts: Date.now(),
      occasion: c.occasion || null,
      name: c.name || '',
      email: c.email || '',
      recipient: c.recipient || {},
      /* The fan-out list rides in the draft, so someone who typed three
         addresses (or imported eighty) and closed the tab gets them back with
         the card. Capped at RECIP_MAX on the way in as well as the way out: the
         cart's own ceiling, see RECIP_MAX. */
      recipients: (c.recipients || []).slice(0, RECIP_MAX).map(function (r) {
        return {
          firstName: r.firstName || '', lastName: r.lastName || '',
          line1: r.line1 || '', line2: r.line2 || '',
          city: r.city || '', state: r.state || '', zip: r.zip || '',
          country: r.country || 'US',
        };
      }),
      // Direction B's Send: see emptyState. Absent on older drafts, which load as not chosen and no sender.
      sendTo: ['me', 'other', 'several'].indexOf(c.sendTo) !== -1 ? c.sendTo : '',
      sender: senderFrom(c.sender),
      card: {
        occasion: c.card.occasion || null,
        // The whole design object, id included: design-picker's init calls
        // chooseDesign(card.design.id, true) when it sees one, which
        // re-resolves the artwork URLs against the CURRENT asset base rather
        // than trusting CDN links that were fingerprinted months ago.
        design: c.card.design || null,
        captions: (c.card.captions || []).slice(0, 4),
        bottomNote: c.card.bottomNote || '',
        noteOwn: !!c.card.noteOwn,
        topSentence: c.card.topSentence || '',
        border: c.card.border || BORDER_DEFAULT,
        noteFont: c.card.noteFont || CARD_FONT_DEFAULT,
        captionFont: c.card.captionFont || CARD_FONT_DEFAULT,
        fullSize: !!c.card.fullSize,
        shadow: !!c.card.shadow,
        /* ⛔ THE RADIUS, NOT A BOOLEAN. `card.rounded` has held millimetres
           since 2026-09-11 and this line went on writing `!!` over it: a card
           set to 6.5 mm saved as `true`, a reload handed that to roundedMm()
           as the pre-slider 2.0 mm, and the cart sent `_Corner Radius: "2.0"`
           for a card the customer had set to 6.5. Stored as is, the way
           orgDraft() does it — the intent, not the effect. 0 stays 0 (Square),
           and roundedMm() still coerces the booleans older drafts hold. */
        rounded: c.card.rounded == null ? false : c.card.rounded,
        top: topFrom(c.card.top),
      },
      // The inside top's photo, everything but its pixels (those are in IndexedDB, TOP_PHOTO_KEY).
      topPhoto: c.topPhoto ? { idbKey: TOP_PHOTO_KEY, w: c.topPhoto.w, h: c.topPhoto.h,
                               srcW: c.topPhoto.srcW, srcH: c.topPhoto.srcH } : null,
      // Everything about a photo EXCEPT its pixels.
      photos: (c.photos || []).map(function (p, i) {
        return {
          idbKey: PHOTO_KEY(i),
          w: p.w, h: p.h, name: p.name, size: p.size,
          // ⛔ srcW/srcH BELONG IN THE DRAFT. They are the pre-downscale
          // dimensions of the original file, and the only input to the
          // resolution check — a draft that drops them comes back as
          // "resolution unknown" after any reload, which is most of the time.
          // Two allowlists strip photo fields (here and in the rehydrate
          // below); a field added to the photo object must be added to both
          // or it silently does not survive a refresh.
          srcW: p.srcW, srcH: p.srcH,
          cropScale: p.cropScale, panX: p.panX, panY: p.panY, effect: p.effect, tilt: p.tilt, rot: p.rot,
          // The looks this picture had, newest last: the builder's direction B steps back through them (Undo).
          // Only B writes it, and only when it has some; never an order property.
          ...(Array.isArray(p.looks) && p.looks.length ? { looks: p.looks.slice(-12) } : {}),
        };
      }),
    };
  }

  function saveDraft(c) {
    if (ARTIST_TOKEN || ORG_TOKEN) return;
    // ⛔ Never a placeholder. A reload mid-download would restore a card of
    // blank panels for good. The last swap calls queueSave() (_googleLanded).
    if ((c.photos || []).some(function (p) { return p && p._loading; })) return;
    let d;
    try { d = draftFrom(c); } catch (e) { return; }
    // Nothing worth keeping: no photos and nothing typed. Writing an empty
    // draft would light "Continue card" on the marketing site for someone who
    // only ever opened the builder and left.
    const typed = (d.card.bottomNote || '').length
      || (d.card.captions || []).some(function (s) { return s && s.length; })
      || (d.card.top && d.card.top.touched);
    if (!d.photos.length && !typed) { clearDraft(); return; }
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
      localStorage.setItem(DRAFT_FLAG, '1');
    } catch (e) {
      // Quota or private mode. The builder still works for this visit; it
      // just will not survive it, and the CTA must not claim otherwise.
      try { localStorage.removeItem(DRAFT_FLAG); } catch (e2) { /* nothing left to try */ }
      return;
    }
    /* ⛔ ONLY WHEN THE PIXELS ACTUALLY CHANGED.
       This used to put all four photos on EVERY save, and the save watcher
       includes cropScale/panX/panY — so a single crop drag scheduled four
       ~240KB structured clones 400ms later, over and over. (An earlier commit
       message of mine claimed this was already conditional. It was not; I
       described the intention and shipped the loop.)

       The pixels are immutable once imported: crop, pan and effect are
       numbers, they live in localStorage, and the bake re-reads them at
       checkout. So the only thing that can make an IndexedDB write necessary
       is a photo being added, replaced or re-ordered — which is exactly what
       a change of dataUrl identity means. */
    if (window.PP && window.PP.idb) {
      (c.photos || []).forEach(function (p, i) {
        if (!p || !p.dataUrl) return;
        const key = PHOTO_KEY(i);
        if (idbWritten[key] === p.dataUrl) return;
        idbWritten[key] = p.dataUrl;
        window.PP.idb.put(key, p.dataUrl).catch(function () { delete idbWritten[key]; });
      });
      const tp = c.topPhoto;
      if (tp && tp.dataUrl && idbWritten[TOP_PHOTO_KEY] !== tp.dataUrl) {
        idbWritten[TOP_PHOTO_KEY] = tp.dataUrl;
        window.PP.idb.put(TOP_PHOTO_KEY, tp.dataUrl).catch(function () { delete idbWritten[TOP_PHOTO_KEY]; });
      }
    }
  }

  /* Which dataUrl each IndexedDB slot is known to hold, so saveDraft can skip
     a write it has already done. The comparison is by VALUE — these are
     strings — which is the behaviour we want anyway: a slot holding the same
     pixels does not need writing again, however it came to hold them. Four
     string compares per save against four ~240KB structured clones is not a
     trade worth thinking about. Cleared with the draft. */
  let idbWritten = Object.create(null);

  function clearDraft() {
    if (ARTIST_TOKEN || ORG_TOKEN) return;
    try {
      localStorage.removeItem(DRAFT_KEY);
      localStorage.removeItem(DRAFT_FLAG);
    } catch (e) { /* storage disabled — there was nothing to clear */ }
    idbWritten = Object.create(null);
    if (window.PP && window.PP.idb) window.PP.idb.clear().catch(function () {});
  }

  /* Reads the draft and merges it over a clean state. Anything malformed is
     thrown away rather than half-applied: a partly-restored card is worse
     than a fresh one, because the customer cannot tell which half is theirs. */
  function loadState() {
    if (ARTIST_TOKEN || ORG_TOKEN) return emptyState();
    /* Retire the two keys the pre-2026-08-19 builder wrote. Their shape is
       not this one and there is no migration worth writing — the flow they
       described (a separate wizard at /pages/get-started) no longer exists. */
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(SUBMITTED_KEY);
    } catch (e) { /* private mode, or storage disabled — nothing to purge */ }

    const s = emptyState();
    let d = null;
    try { d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); } catch (e) { d = null; }
    if (!d || d.v !== 3 || !d.card) return s;

    s.occasion = d.occasion || s.occasion;
    s.name = d.name || s.name;
    s.email = d.email || s.email;
    if (d.recipient) Object.assign(s.recipient, d.recipient);
    /* Drafts written before 2026-09-08 have no `recipients` at all, and the
       empty list is exactly right for them: one card, address at checkout. */
    if (Array.isArray(d.recipients)) {
      s.recipients = d.recipients.slice(0, RECIP_MAX).map(function (r) {
        return {
          firstName: String(r.firstName || ''), lastName: String(r.lastName || ''),
          line1: String(r.line1 || ''), line2: String(r.line2 || ''),
          city: String(r.city || ''), state: String(r.state || ''), zip: String(r.zip || ''),
          country: String(r.country || 'US'),
        };
      });
    }
    s.sendTo = ['me', 'other', 'several'].indexOf(d.sendTo) !== -1 ? d.sendTo : '';
    s.sender = senderFrom(d.sender);
    Object.assign(s.card, d.card);
    // BORDER_STOPS is the authority, not the draft: a stop that has since been
    // removed from the table would otherwise reach bakePhoto() and the print
    // path. Same guard the setter uses.
    if (!BORDER_STOPS.some(function (b) { return b.key === s.card.border; })) {
      s.card.border = BORDER_DEFAULT;
    }
    // Same guard for both hands: a face retired since the draft was written
    // must not reach the print file, which throws on one it cannot embed.
    //
    // A draft from before the split carries a single `font`; it becomes both,
    // which is what that card looked like.
    const known = function (k) { return CARD_FONTS.some(function (f) { return f.key === k; }); };
    if (!known(s.card.noteFont)) s.card.noteFont = known(d.card.font) ? d.card.font : CARD_FONT_DEFAULT;
    if (!known(s.card.captionFont)) s.card.captionFont = known(d.card.font) ? d.card.font : CARD_FONT_DEFAULT;
    s.card.top = topFrom(d.card.top);
    s.topPhoto = d.topPhoto && d.topPhoto.idbKey === TOP_PHOTO_KEY
      ? { idbKey: TOP_PHOTO_KEY, w: d.topPhoto.w, h: d.topPhoto.h, srcW: d.topPhoto.srcW, srcH: d.topPhoto.srcH } : null;
    s.photos = (d.photos || []).slice(0, 4).map(function (p) {
      return {
        idbKey: p.idbKey, w: p.w, h: p.h, name: p.name, size: p.size,
        // See draftFrom(). Left undefined rather than defaulted when the draft
        // predates them — cropPixels() returns null, and the card reads as
        // "not measured", which is the truth.
        srcW: p.srcW, srcH: p.srcH,
        cropScale: p.cropScale || 1,
        panX: p.panX == null ? 50 : p.panX,
        panY: p.panY == null ? 50 : p.panY,
        effect: p.effect || 'original',
        tilt: p.tilt || 0,
        rot: p.rot || 0,
        // See draftFrom(): direction B's Undo. Absent on older drafts and from today's builder, which is fine.
        looks: Array.isArray(p.looks) ? p.looks.filter(function (k) { return typeof k === 'string' && EFFECT_FILTERS[k]; }).slice(-12) : [],
      };
    });
    s.details = s.photos.map(function () { return { date: todayISO(), caption: '' }; });
    s._restored = true;
    return s;
  }


  function blobToDataUrl(blob) {
    return new Promise(function (res, rej) {
      const r = new FileReader();
      r.onload = function () { res(r.result); };
      r.onerror = function () { rej(new Error('read_failed')); };
      r.readAsDataURL(blob);
    });
  }

  // Printed photo box = 47.5 × 59.4 mm → 4:5 portrait (see pop-cards/decisions.md). Crop canvas
  // matches that aspect so what the customer frames is what prints (WYSIWYG, app parity).
  // NOTE: still named "…Square" from when the crop was square. The shape is
  // 4:5 portrait now; PP.cropToSquare is kept as the exported name because
  // idb-photos.js and gphotos.js both call it.
  const PANEL_W = 720, PANEL_H = 900;   // 720 : 900 = 4:5

  // Draw the largest centred 4:5-portrait region of a decoded <img> onto a dw × dh canvas ctx.
  function drawPanelCrop(img, ctx, dw, dh) {
    const targetAspect = dw / dh;                              // width / height (0.8)
    const rw = Math.min(img.width, img.height * targetAspect); // region width
    const rh = rw / targetAspect;                              // region height
    const sx = (img.width - rw) / 2, sy = (img.height - rh) / 2;
    ctx.drawImage(img, sx, sy, rw, rh, 0, 0, dw, dh);
  }

  // ── Why a photo was refused, and what to say about it ──
  //
  // Both importers below reject with one of these codes. They used to reject
  // with bare strings ('too large'), which the builder then showed to the
  // customer verbatim and the wizard swallowed into one catch-all — so every
  // refusal, for every reason, read "Something went wrong loading your photos."
  // The code travels with the error; the wording is decided in one place.
  function photoError(code) {
    var e = new Error(code);
    e.code = code;
    return e;
  }

  // 10 MB rejected ordinary photographs — a 12 MP iPhone JPEG or anything off a
  // real camera clears it easily (Daniel's own sample set is 9.6–11.5 MB). The
  // file is downscaled to at most 1600px on import and never kept at full size,
  // so the cap only exists to stop a decode that would exhaust memory.
  const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

  function isHeic(file) {
    return /image\/hei[cf]/i.test(file.type || '') || /\.hei[cf]$/i.test(file.name || '');
  }

  // Guard shared by both importers. Returns an Error to reject with, or null.
  function rejectReason(file) {
    if (!/^image\//.test(file.type) && !isHeic(file)) return photoError('not_an_image');
    if (file.size > MAX_UPLOAD_BYTES) return photoError('too_large');
    return null;
  }

  // The customer-facing sentence for a refused photo. Sentimental register:
  // says what happened and what to do, never blames them, never shows a code.
  function photoErrorMessage(err, file) {
    var code = (err && err.code) || '';
    var name = (file && file.name) || 'That photo';
    if (code === 'not_an_image') {
      return name + ' isn\'t a photo — pick an image file.';
    }
    if (code === 'too_large') {
      var mb = file && file.size ? (file.size / 1048576).toFixed(1) + ' MB' : 'too big';
      return name + ' is ' + mb + '. Photos need to be under 25 MB.';
    }
    if (code === 'convert_offline') {
      return 'We couldn\'t reach the network to open ' + name + '. Check your connection and try again.';
    }
    if (code === 'convert_too_large') {
      return name + ' is too large to open here. Try a smaller copy, or export it as a JPEG.';
    }
    if (code === 'decode_failed') {
      return name + ' couldn\'t be opened. Try a JPEG or PNG.';
    }
    if (code === 'read_failed') {
      return name + ' couldn\'t be read off your device. Try picking it again.';
    }
    if (err && /indexeddb|InvalidStateError|QuotaExceeded/i.test(String(err.name || err.message || ''))) {
      return 'Your browser is blocking file storage (private mode?). Try a normal window.';
    }
    return 'Something went wrong loading ' + name + '.';
  }

  // ── Decoding a photo from any device ──
  //
  // Browsers do not agree on what an image is. Chromium cannot decode HEIC at
  // all — verified 2026-08-08 against a real iPhone file, in an <img> and via
  // createImageBitmap — and HEIC is what an Apple device hands over on a Mac.
  // Safari reads it; Android and Windows send their own assortment. So the
  // format is not something we can require the customer to know about.
  //
  // Native decode is tried first: it is instant, works offline, and covers
  // JPEG, PNG, WebP, GIF and (on Safari) HEIC. Whatever the browser refuses
  // goes to Cloudinary, which converts server-side and returns a JPEG the
  // browser can read. Cloudinary is already this venture's image host and the
  // same unsigned preset the print bake uses.
  function loadImage(src, crossOrigin) {
    return new Promise(function (resolve, reject) {
      const img = new Image();
      if (crossOrigin) img.crossOrigin = 'anonymous';   // or the canvas taints
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(photoError('decode_failed')); };
      img.src = src;
    });
  }

  function readAsDataUrl(file) {
    return new Promise(function (resolve, reject) {
      const reader = new FileReader();
      reader.onerror = function () { reject(photoError('read_failed')); };
      reader.onload = function () { resolve(reader.result); };
      reader.readAsDataURL(file);
    });
  }

  // ⛔ CLOUDINARY'S FREE PLAN REFUSES ANY IMAGE OVER 10 MB, and conversion is an
  // upload of the ORIGINAL. Measured 2026-10-11 with a 14.5 MB HEIC: 400, "File
  // size too large. Got 14461599. Maximum is 10485760." Before this check the
  // customer waited through the upload and then read "couldn't be opened. Try a
  // JPEG or PNG" about a perfectly good photograph.
  //
  // Only files the browser cannot decode get here (HEIC on Chromium and
  // Firefox, mostly), so JPEG/PNG/WebP of any size up to MAX_UPLOAD_BYTES are
  // untouched. Decoding HEIC in the page instead was weighed and refused: every
  // browser HEIC decoder (heic2any, heic-to, libheif-js, elheif) is libheif +
  // libde265 compiled to WASM, LGPL-3.0 whatever the wrapper's own label says,
  // 1.5-2.7 MB, and heic2any is unmaintained since 2020. A permissive licence
  // was the bar (Daniel, 2026-10-11). Raise this if the plan changes; the 400
  // is also mapped below, so a lower limit than this still reads right.
  //
  // 10,000,000 rather than 10,485,760: a margin, so a file at the edge is
  // refused here in a moment rather than after a full upload.
  const CONVERT_MAX_BYTES = 10 * 1000 * 1000;

  function tooLargeToConvert(file) {
    track('pp_photos_rejected', {
      count: 1, reason: 'convert_too_large', heic: isHeic(file),
      mb: Math.round((file.size || 0) / 104857.6) / 10,
    });
    return photoError('convert_too_large');
  }

  // Convert anything the browser can't read into a JPEG it can.
  async function convertViaCloudinary(file) {
    const fd = new FormData();
    fd.append('upload_preset', PP_CLOUDINARY.preset);
    fd.append('file', file, file.name || 'photo');
    let json;
    try {
      const res = await fetch('https://api.cloudinary.com/v1_1/' + PP_CLOUDINARY.cloud + '/image/upload', {
        method: 'POST', body: fd,
      });
      json = await res.json();
      if (res.status === 400 && /file size too large/i.test((json && json.error && json.error.message) || '')) {
        throw tooLargeToConvert(file);
      }
      if (!res.ok || !json.secure_url) throw photoError('decode_failed');
    } catch (e) {
      // Offline or blocked: a network failure and a rejected file are not the
      // same thing to a customer, so they don't get the same sentence.
      if (e && e.code) throw e;
      throw photoError('convert_offline');
    }
    // f_jpg does the conversion; the delivery URL is CORS-enabled, so the
    // decoded pixels can still be drawn to a canvas and read back.
    const jpg = json.secure_url.replace('/upload/', '/upload/f_jpg,q_auto/');
    return loadImage(jpg, true);
  }

  // One decode path for every importer. Resolves to a drawable <img>.
  async function decodeImageFile(file) {
    try {
      return await loadImage(await readAsDataUrl(file), false);
    } catch (e) {
      if (e && e.code === 'read_failed') throw e;   // never even got the bytes
      // Too big for the converter: say so now, without the "converting" wait.
      if (file.size > CONVERT_MAX_BYTES) throw tooLargeToConvert(file);
      notifyConverting(file, true);
      try { return await convertViaCloudinary(file); }
      finally { notifyConverting(file, false); }
    }
  }

  // A HEIC can be up to 10 MB (CONVERT_MAX_BYTES) and the round trip is not instant, so the
  // flows get told when a photo is being converted and can say so.
  function notifyConverting(file, active) {
    window.dispatchEvent(new CustomEvent('pp:photo-converting', {
      detail: { active: active, name: (file && file.name) || '' },
    }));
  }

  // Import a photo WITHOUT cropping it.
  //
  // cropToSquare() centre-cropped to 720x900 at upload and threw the rest
  // away, so panning could only ever move inside a crop that had already been
  // decided for the customer — there was nothing left to pan to. The whole
  // frame is kept now (downscaled), and the 4:5 window is chosen at display
  // time and applied for real at bake time.
  const IMPORT_MAX = 1600;

  // The 4:5 window onto a photo, in percentages of that window.
  //
  // Cover the box, apply the zoom, then slide by the pan. Returns the numbers
  // for BOTH the on-screen <img> and the print bake, so they cannot disagree.
  const BOX_ASPECT = 4 / 5;

  // ⚠️ THE WINDOW IS NO LONGER ALWAYS 4:5. Full size makes it 50.5 x 73.5mm,
  // and the cover maths below is entirely about the box's aspect — left at 4:5
  // it would letterbox or over-crop every picture in that layout. Every caller
  // passes the aspect the picture is actually being shown in, including the
  // print bake, which is why they cannot disagree.
  function photoBox(photo, boxAspect) {
    const p = photo || {};
    const s = Math.max(1, Math.min(4, p.cropScale || 1));
    const box = boxAspect || BOX_ASPECT;
    const a = (p.w && p.h) ? (p.w / p.h) : box;
    // Tilt (±45°): the cover has to GROW so a rotated picture still reaches
    // the corners of the window. K is the exact factor for a window of this
    // aspect turned by `deg` (|cos| + |sin|·max(box,1/box)); returned as `deg`
    // so imgStyle and bakePhoto rotate by the SAME angle around the SAME
    // centre — the only way display and the print bake cannot drift.
    const deg = Math.max(-45, Math.min(45, p.tilt || 0));
    const th = Math.abs(deg) * Math.PI / 180;
    const K = Math.cos(th) + Math.sin(th) * Math.max(box, 1 / box);
    // cover: whichever axis is short gets stretched to fill, then × K for tilt
    const w = 100 * s * Math.max(1, a / box) * K;
    const h = 100 * s * Math.max(1, box / a) * K;
    const panX = p.panX != null ? p.panX : 50;
    const panY = p.panY != null ? p.panY : 50;
    return { w: w, h: h, x: -(w - 100) * panX / 100, y: -(h - 100) * panY / 100, deg: deg };
  }

  /* The pan that brings these faces (PPVision.faces: fractions of the picture) inside the window of box `b`
     (photoBox at the picture's current zoom), moving no further than it must: per axis, the faces' span, with room
     for hair above and chin below, is kept inside the part of the picture the window shows; a span wider than the
     window is centred. In photoBox's units: the window shows [v0, v0 + 100/w] of the picture, v0 = (w-100)·pan/w.
     null when nothing moves. */
  function facePan(b, faces) {
    let x0 = 1, x1 = 0, y0 = 1, y1 = 0;
    faces.forEach(function (f) {
      x0 = Math.min(x0, f.x - f.w * 0.25); x1 = Math.max(x1, f.x + f.w * 1.25);
      y0 = Math.min(y0, f.y - f.h * 0.5); y1 = Math.max(y1, f.y + f.h * 1.3);
    });
    const axis = function (size, a, z) {
      if (size - 100 < 0.5) return 50;                 // the picture does not overflow this way: nothing to move
      a = Math.max(0, a); z = Math.min(1, z);
      const win = 100 / size, now = (size - 100) * 0.5 / size;
      let v0 = now;
      if (z - a >= win) v0 = (a + z) / 2 - win / 2;
      else if (a < v0) v0 = a;
      else if (z > v0 + win) v0 = z - win;
      return Math.max(0, Math.min(100, v0 * size * 100 / (size - 100)));
    };
    const x = Math.round(axis(b.w, x0, x1) * 10) / 10, y = Math.round(axis(b.h, y0, y1) * 10) / 10;
    return (x === 50 && y === 50) ? null : { x: x, y: y };
  }

  /* A cut-out's mask on the picture's own <img> (imgStyle). The mask is the picture's size and stretched to the
     element's box, which IS the picture at its crop, zoom and tilt, so it cannot drift from the pixels. */
  function cutMaskCss(url) {
    return '-webkit-mask-image:url("' + url + '");mask-image:url("' + url + '");' +
           '-webkit-mask-size:100% 100%;mask-size:100% 100%;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;';
  }

  async function importPhoto(file) {
    const bad = rejectReason(file);
    if (bad) throw bad;
    const img = await decodeImageFile(file);
    const k = Math.min(1, IMPORT_MAX / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * k));
    const h = Math.max(1, Math.round(img.height * k));
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    c.getContext('2d').drawImage(img, 0, 0, w, h);
    // ⛔ srcW/srcH ARE THE ONLY RECORD OF THE REAL PHOTOGRAPH.
    //
    // Everything from here down works on the downscaled copy, and bakePhoto()
    // then SCALES the chosen crop up to the bake size whatever few pixels it
    // held — so the delivered file is always exactly bake.w x bake.h and says
    // nothing about how sharp the picture actually is. A print-side check that
    // measures the delivered file therefore cannot fail; ours could not, for
    // every web order ever placed.
    //
    // These two numbers are captured BEFORE the downscale on purpose. Combined
    // with the crop fractions they give the true pixel count inside the crop,
    // which is the only honest input to an effective-ppi test.
    return { dataUrl: c.toDataURL('image/jpeg', 0.86), w: w, h: h,
             srcW: img.width, srcH: img.height };
  }

  /* A photo's stand-in while its pixels are still downloading (Google Photos,
     2026-09-29): the right SHAPE, painted paper-plain. Tiny on purpose — only
     the aspect matters, and it is never saved or baked (see photosLoading).
     Filled before encoding, though PNG keeps alpha anyway: an unfilled canvas
     is black in JPEG, and a black panel would read as a broken photo. */
  function placeholderDataUrl(w, h) {
    const c = document.createElement('canvas');
    c.width = 40;
    c.height = Math.max(1, Math.round(40 * h / Math.max(1, w)));
    const x = c.getContext('2d');
    x.fillStyle = '#EDE6DC';
    x.fillRect(0, 0, c.width, c.height);
    return c.toDataURL('image/png');
  }

  /**
   * The customer's crop, counted in ORIGINAL image pixels.
   *
   * photoBox() draws the image at w% of the box, so the box shows 100/w of the
   * image's width — invert that against the true source dimensions and you have
   * the pixels that actually land inside the crop. Mirrors photoBox() exactly;
   * if one changes the other must.
   *
   * Returns null when srcW/srcH are missing — a draft rehydrated from an older
   * session. Absent must stay distinguishable from "measured and fine".
   */
  /* ── THE SHARE TOKEN ────────────────────────────────────────────────────
     Rolled here, in the customer's own browser, at the moment the card enters
     the cart — and stapled to the line as `_Share`, exactly the way the baked
     Cloudinary URLs are. It therefore rides into Shopify with the order.

     WHY IT IS MINTED HERE AND NOT ON THE SERVER. Whoever mints it has to tell
     everyone else. Minted on the Worker, only the Worker would know it, and the
     order confirmation email would have to go and ask — which on Basic means
     building a Shopify app. Minted here, the token is ON THE ORDER, so plain
     Liquid in the notification template can print the link with no app at all.

     Alphabet and length match SHARE_ALPHABET / SHARE_TOKEN_LENGTH in
     projects/pop-cards/fulfilment/worker.js. Change one and change both: the
     public /s/ route rejects anything that is not 22 alphanumerics before it
     touches Shopify. No I/l/O/0-style folding, because a share link is clicked
     and never typed. ~128 bits. */
  var SHARE_ALPHABET =
    'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  var SHARE_TOKEN_LENGTH = 22;

  function mintShareToken() {
    var out = '';
    var limit = 256 - (256 % SHARE_ALPHABET.length);   /* reject the tail, or the
                                                          first characters of the
                                                          alphabet come up more */
    while (out.length < SHARE_TOKEN_LENGTH) {
      var bytes = new Uint8Array(SHARE_TOKEN_LENGTH * 2);
      crypto.getRandomValues(bytes);
      for (var i = 0; i < bytes.length && out.length < SHARE_TOKEN_LENGTH; i++) {
        if (bytes[i] >= limit) continue;
        out += SHARE_ALPHABET[bytes[i] % SHARE_ALPHABET.length];
      }
    }
    return out;
  }

  function cropPixels(photo, boxAspect) {
    const p = photo || {};
    if (!p.srcW || !p.srcH) return null;
    const s = Math.max(1, Math.min(4, p.cropScale || 1));
    const box = boxAspect || BOX_ASPECT;
    const a = (p.w && p.h) ? (p.w / p.h) : box;
    return {
      w: Math.round(p.srcW / (s * Math.max(1, a / box))),
      h: Math.round(p.srcH / (s * Math.max(1, box / a))),
      srcW: p.srcW, srcH: p.srcH
    };
  }

  async function cropToSquare(file) {
    const bad = rejectReason(file);
    if (bad) throw bad;
    const img = await decodeImageFile(file);
    const c = document.createElement('canvas');
    c.width = PANEL_W; c.height = PANEL_H;
    drawPanelCrop(img, c.getContext('2d'), PANEL_W, PANEL_H);
    return c.toDataURL('image/jpeg', 0.85);
  }

  // ── Cloudinary (same account/preset as the iOS app) ──
  const PP_CLOUDINARY = { cloud: 'sg5ibhwe', preset: 'picturepops_unsigned' };
  // ⛔ THE BAKE IS WHY THE FILTERS AND THE CROP REACH PAPER AT ALL. They are
  // not order properties — they are pixels. Each photo is redrawn here through
  // its effect and its crop window and THAT is what is uploaded, so the print
  // file receives a picture that is already what the customer saw. Anything
  // this function stops doing silently stops being printed.
  //
  // The window's aspect is the whole of the cover maths. One box is pinned:
  //   with captions  47.5 x 59.4mm  (4:5)  -> 1122 x 1402
  // at 600 DPI — 2x the 300 the printer needs, for headroom. Every other stop
  // derives from its own well through BAKE_PX_PER_MM below.
  //
  // ⚠️ Full size was pinned here too, at 1217 x 1760 (51.5 x 74.5mm). The
  // `slim` stop widened to a 3.5mm border on 2026-09-03 and full size IS that
  // stop, so the constant described a box that no longer exists and is gone.
  const BAKE_W = 1122, BAKE_H = 1402;

  // 600 DPI, the resolution both boxes above are stated at:
  // 47.5mm -> 1122px is 23.622 px/mm. Every stop bakes from its own well
  // through this, so the constants above are simply two of its answers.
  const BAKE_PX_PER_MM = 600 / 25.4;
  function bakeSizeFor(borderKey, captionsOn) {
    // classic + captions keeps its exact historical constant. Deriving it
    // lands 1px off on the height (59.4mm rounds to 1403, the constant says
    // 1402) — optically nothing, but "the captioned shipping layout does not
    // move" is an invariant worth keeping literally true.
    if (borderKey === 'classic' && captionsOn && !STRIP_IS_34) return { w: BAKE_W, h: BAKE_H };
    const w = bakeWell(borderKey, captionsOn);
    return {
      w: Math.round(w.w * BAKE_PX_PER_MM),
      h: Math.round(w.h * BAKE_PX_PER_MM)
    };
  }

  // Bake a photo's edit stack (effect via canvas filter → crop zoom) to a JPEG
  // Blob. `layout` is the card's picture geometry at the moment of upload —
  // { border, captionsOn } — and must be the card's real setting or the
  // printed picture is cropped to a box it is not being placed in. The drop
  // shadow is deliberately NOT baked: it belongs outside the picture's own
  // edges, so print draws it around the photo.
  //
  // v4, 2026-08-25: was a single `fullSize` boolean when there were two legal
  // wells. There are eight now (4 stops x captions on/off), so the window
  // comes from bakeSizeFor() and the crop from the same wellFor() the screen
  // uses — one definition, three consumers.
  /* ✂ A CUT-OUT PICTURE (?ppbg=1, the "Remove background" try, 19 Oct). The order does not change shape: the
     picture is still one opaque JPEG cropped to the well. What changes is what is in it. On screen the person
     stands on the strip's own art (the panel's background, panelStrip), so the bake draws that same art, the same
     part of it this window sits on, behind the person: print lays the photo over the art in that very place
     (print/card-art.mjs stripArt), so it prints as it showed. At the `none` stop print draws no art, but the picture
     covers the whole panel, so the art behind the person comes only from the bake (since 19 Oct, Daniel). A design
     with no strip art bakes white paper behind the person, as the screen shows.
     ⚠️ IF THE ART CANNOT BE READ (no CORS on its host: a tainted canvas would throw and lose the order), the WHOLE
     PICTURE is baked instead, background and all: a card that prints differently from the screen, but never a
     lost order. Which is one reason the switch stays off for customers (projects/pop-cards/decisions.md, 19 Oct). */
  /* ⛔ MIRRORED FROM print/geometry.mjs (MM.panel, MM.imposed.partB.bleed, MM.borderStops; 19 Oct 2026) and read
     ONLY to place the strip's art behind a cut-out. [w, h, top] in mm, captions on then off. If print's geometry
     moves, this moves with it, or a cut-out's background lands a few mm off the art around it. */
  /* Both strip formats since 2026-10-29 (print/geometry.mjs STRIP_FORMATS and geometryFor('3x4').borderStops);
     STRIP_FORMAT picks. */
  const PRINT_FORMATS = {
    '5x7': {
      strip: { panel: 50.825, h: 71.155, bleed: 1.9 },
      wells: {
        none: [[50.825, 56.81, 0], [50.825, 71.155, 0]],
        slim: [[44.175, 53.485, 3.325], [44.175, 64.505, 3.325]],
        classic: [[41.325, 51.11, 5.7], [41.325, 61.655, 4.75]],
        wide: [[35.625, 48.26, 8.55], [35.625, 55.955, 7.6]],
        super: [[29.925, 45.41, 11.4], [29.925, 50.255, 10.45]],
      },
    },
    '3x4': {
      strip: { panel: 51.75, h: 69, bleed: 1.9 },
      wells: {
        none: [[51.75, 54.655, 0], [51.75, 69, 0]],
        slim: [[45.1, 51.33, 3.325], [45.1, 62.35, 3.325]],
        classic: [[42.25, 48.955, 5.7], [42.25, 59.5, 4.75]],
        wide: [[36.55, 46.105, 8.55], [36.55, 53.8, 7.6]],
        super: [[30.85, 43.255, 11.4], [30.85, 48.1, 10.45]],
      },
    },
  };
  const PRINT_STRIP = PRINT_FORMATS[STRIP_FORMAT].strip;
  const PRINT_WELLS = PRINT_FORMATS[STRIP_FORMAT].wells;
  function loadForBake(src, cors) {
    return new Promise(function (resolve) {
      if (!src) { resolve(null); return; }
      const im = new Image();
      if (cors) im.crossOrigin = 'anonymous';
      im.onload = function () { resolve(im); };
      im.onerror = function () { resolve(null); };
      im.src = src;
    });
  }
  function bakeCut(photo, layout) {
    if (!photo.cutUrl) return Promise.resolve(null);
    const want = layout && layout.strip;
    return Promise.all([loadForBake(photo.cutUrl, false), want ? loadForBake(want, true) : Promise.resolve(null)])
      .then(function (got) {
        if (!got[0] || (want && !got[1])) return null;
        return { mask: got[0], art: got[1] };
      });
  }
  // The person alone: the picture's pixels where the mask is, transparent elsewhere, at the picture's own size.
  function cutPixels(img, mask) {
    const c = document.createElement('canvas');
    c.width = img.naturalWidth || img.width; c.height = img.naturalHeight || img.height;
    const x = c.getContext('2d');
    x.drawImage(img, 0, 0, c.width, c.height);
    x.globalCompositeOperation = 'destination-in';
    x.drawImage(mask, 0, 0, c.width, c.height);
    return c;
  }
  function bakePhoto(photo, layout, index) {
    return bakeCut(photo, layout).then(function (cut) { return bakePhotoWith(photo, layout, index, cut); });
  }
  function bakePhotoWith(photo, layout, index, cut) {
    const captionsOn = !(layout && layout.captionsOn === false);
    const borderKey = (layout && layout.border) || BORDER_DEFAULT;
    return new Promise(function (resolve, reject) {
      const img = new Image();
      img.onload = function () {
        try {
          const bake = bakeSizeFor(borderKey, captionsOn);
          const cw = bake.w;
          const ch = bake.h;
          const c = document.createElement('canvas');
          c.width = cw; c.height = ch;
          const ctx = c.getContext('2d');

          // Same window as the screen — photoBox() is the single definition,
          // and it is handed the SAME well aspect the panel is rendering.
          // The old bake centre-cropped by cropScale and ignored the pan, so
          // the printed card would not have matched what was on screen.
          const b = photoBox(Object.assign({}, photo, {
            w: photo.w || img.width, h: photo.h || img.height,
          }), cw / ch);
          const dw = cw * b.w / 100, dh = ch * b.h / 100;
          const dx = cw * b.x / 100, dy = ch * b.y / 100;

          /* ⛔ WHITE FIRST, ALWAYS. The canvas starts TRANSPARENT and this is
             encoded as JPEG, which has no alpha — a browser composites the
             transparent corners onto BLACK. With rounded corners on, the
             untouched corners are exactly what is left showing, so without
             this fill every picture would ship with four black wedges.
             The panel behind the photo is #fff on paper (print/card-art.mjs
             `.panel`), so white here IS the paper: on an inkjet, white is no
             ink at all. That is what makes the corners real rather than
             painted, and it is why print can ignore _Rounded and still be
             right. */
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, cw, ch);

          /* ✂ The strip's art behind a cut-out (see bakeCut), cut from the art exactly where PRINT puts this window
             over it, in print's own millimetres: print stretches the art over the strip plus its bleed and places
             photo `index` in its panel, centred across, at the well's top (print/card-art.mjs stripArt). The
             builder's 3D card is drawn on an older, larger spec (PANEL_W_MM), so its numbers would register the
             art a few mm off. Not tilted with the picture: the art belongs to the card. */
          if (cut && cut.art) {
            const well = PRINT_WELLS[borderKey] ? PRINT_WELLS[borderKey][captionsOn ? 0 : 1] : PRINT_WELLS.classic[0];
            const W = PRINT_STRIP.panel * 4 + PRINT_STRIP.bleed * 2, H = PRINT_STRIP.h + PRINT_STRIP.bleed * 2;
            /* At `none` print lays the picture OVERSIZE by the bleed on every side (stripArt's `place`), so the art
               behind the person is that larger box, which is the whole art file across the strip's height. */
            const ob = borderKey === 'none' ? PRINT_STRIP.bleed : 0;
            const xMm = PRINT_STRIP.bleed + (index || 0) * PRINT_STRIP.panel + (PRINT_STRIP.panel - well[0]) / 2 - ob;
            const yMm = PRINT_STRIP.bleed + well[2] - ob;
            const aw = cut.art.naturalWidth, ah = cut.art.naturalHeight;
            ctx.drawImage(cut.art, xMm / W * aw, yMm / H * ah, (well[0] + 2 * ob) / W * aw, (well[1] + 2 * ob) / H * ah, 0, 0, cw, ch);
          }

          ctx.save();
          ctx.beginPath();
          if (layout && layout.rounded) {
            // The radius as a fraction of the WELL, so the bake, the screen
            // and the paper describe one circle. roundRect is not universal
            // (Safari < 16.4), so fall back to a plain rect rather than
            // throwing — a square-cornered bake is a wrong look; an exception
            // here loses the whole order.
            const wellMm = bakeWell(borderKey, captionsOn).w;
            // layout.rounded IS the radius in mm (roundedActive()), so the
            // bake, the screen and the paper still describe one circle.
            const radMm = typeof layout.rounded === 'number' ? layout.rounded : PHOTO_RADIUS_MM;
            const rad = Math.min(radMm / wellMm * cw, Math.min(cw, ch) / 2);
            if (typeof ctx.roundRect === 'function') ctx.roundRect(0, 0, cw, ch, rad);
            else ctx.rect(0, 0, cw, ch);
          } else {
            ctx.rect(0, 0, cw, ch);
          }
          ctx.clip();                       // the clip is the (unrotated) window
          // Same tilt as the screen: rotate around the window centre AFTER the
          // clip, so the picture turns inside a fixed window — imgStyle's
          // sandwich, in canvas. photoBox handed us b.deg and grew the cover.
          if (b.deg) {
            ctx.translate(cw / 2, ch / 2);
            ctx.rotate(b.deg * Math.PI / 180);
            ctx.translate(-cw / 2, -ch / 2);
          }
          if ('filter' in ctx) ctx.filter = EFFECT_FILTERS[photo.effect || 'original'] || 'none';
          ctx.drawImage(cut ? cutPixels(img, cut.mask) : img, dx, dy, dw, dh);
          if ('filter' in ctx) ctx.filter = 'none';
          ctx.restore();
          c.toBlob(function (b) { b ? resolve(b) : reject(new Error('bake_failed')); }, 'image/jpeg', 0.9);
        } catch (e) { reject(e); }
      };
      img.onerror = function () { reject(new Error('img_load_failed')); };
      img.src = photo.dataUrl;
    });
  }

  /* ⛔ A LOOK IS PIXELS ON SCREEN TOO, NOT A LIVE CSS FILTER (Yonatan, 16–17 Oct: on desktop Chrome the card flashed
     half grey the first time it opened with his photos; a fresh card with ?ppdeep=0, the default look off, did not).
     A `filter` on an <img> inside the preserve-3d scene re-rasters that photo through the filter at every new camera
     scale, and the default look put one on all four at once, the moment the card finished opening. A GPU near its tile
     limit then draws part of a picture and leaves the rest grey (2026-10-13-gpu-layers). So each photo's look is drawn
     once into its own image, the same filter print bakes with (bakePhoto), and the stage shows that image with no
     filter. Until it is ready, or where canvas has no `filter` (older Safari), the CSS filter stands in as before.
     Full-size copy of the import (no crop: imgStyle still frames it), JPEG on white so a transparent PNG stays white. */
  /* ⚠️ `'filter' in ctx` is not proof: an engine can carry the property and draw unfiltered, and a baked image with
     no look on it would silently strip the customer's look off the screen. So it is tested once on one pixel. */
  let canvasFilterOk = null;
  function canvasFilterWorks() {
    if (canvasFilterOk !== null) return canvasFilterOk;
    canvasFilterOk = false;
    try {
      const c = document.createElement('canvas');
      c.width = c.height = 1;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      if (ctx && 'filter' in ctx) {
        ctx.filter = 'grayscale(1)';
        ctx.fillStyle = '#f00';
        ctx.fillRect(0, 0, 1, 1);
        const d = ctx.getImageData(0, 0, 1, 1).data;
        canvasFilterOk = Math.abs(d[0] - d[1]) < 8;
      }
    } catch (e) { canvasFilterOk = false; }
    return canvasFilterOk;
  }

  function bakeLook(dataUrl, effect) {
    return new Promise(function (resolve) {
      const f = EFFECT_FILTERS[effect];
      if (!dataUrl || !f || f === 'none' || !canvasFilterWorks()) { resolve(null); return; }
      const img = new Image();
      img.onload = function () {
        try {
          const c = document.createElement('canvas');
          c.width = img.naturalWidth; c.height = img.naturalHeight;
          const ctx = c.getContext('2d');
          if (!ctx) { resolve(null); return; }
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, c.width, c.height);
          ctx.filter = f;
          ctx.drawImage(img, 0, 0);
          c.toBlob(function (b) { resolve(b ? URL.createObjectURL(b) : null); }, 'image/jpeg', 0.92);
        } catch (e) { resolve(null); }
      };
      img.onerror = function () { resolve(null); };
      img.src = dataUrl;
    });
  }

  // Rotate a photo's pixels 90° clockwise into a fresh JPEG data URL, swapping
  // its dimensions. ⛔ PRE-ROTATE, NOT A TRANSFORM: the rotated picture becomes
  // the picture, so the crop window, the on-screen <img>, and the print bake
  // all read it through the SAME unchanged code — display and print cannot
  // disagree, which is the one way this could go wrong silently. One re-encode
  // per press at q0.95; the crop is reset by the caller because a 90° turn
  // re-frames what fills the panel.
  function rotate90(dataUrl) {
    return new Promise(function (resolve, reject) {
      const img = new Image();
      img.onload = function () {
        try {
          const c = document.createElement('canvas');
          c.width = img.naturalHeight; c.height = img.naturalWidth;   // swapped
          const ctx = c.getContext('2d');
          ctx.translate(c.width / 2, c.height / 2);
          ctx.rotate(Math.PI / 2);
          ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
          resolve({ dataUrl: c.toDataURL('image/jpeg', 0.95), w: c.width, h: c.height });
        } catch (e) { reject(e); }
      };
      img.onerror = function () { reject(new Error('rotate_failed')); };
      img.src = dataUrl;
    });
  }

  // Unsigned upload of one baked Blob → its https delivery URL.
  async function cloudinaryUpload(blob, name) {
    const fd = new FormData();
    fd.append('upload_preset', PP_CLOUDINARY.preset);
    fd.append('file', blob, name || 'panel.jpg');
    const res = await fetch('https://api.cloudinary.com/v1_1/' + PP_CLOUDINARY.cloud + '/image/upload', {
      method: 'POST', body: fd,
    });
    if (!res.ok) throw new Error('cloudinary_' + res.status);
    const json = await res.json();
    if (!json.secure_url) throw new Error('cloudinary_no_url');
    return json.secure_url;
  }

  function tagLineFromDefaults(occasion) {
    const d = DEFAULTS[occasion] || DEFAULTS['just-because'];
    return d.tagline.map(function (t) { return { text: t }; });
  }

  function todayISO() {
    return new Date().toISOString().slice(0, 10);
  }

  function configuratorComponent(variantId, checkoutBaseUrl) {
    // The design picker is shared with the wizard — see assets/design-picker.js.
    // Mixed in FIRST so anything below can override it (nothing does today).
    // The finder ("describe the card you want") rides on the picker's screen —
    // assets/finder.js. Absent file, absent box: the guards are in the markup.
    const component = Object.assign(loadState(),
      window.PP.finderState ? window.PP.finderState() : { finder: null, finderQ: '', finderBusy: false, finderError: '', finderEnabled: false },
      window.PP.designPickerMethods, window.PP.finderMethods || {}, {
      variantId: variantId,
      checkoutBaseUrl: checkoutBaseUrl,
      colors: HIGHLIGHT_COLORS,

      // Set from ?claim= when someone arrives from an envelope insert's QR
      // code. DISPLAY ONLY — the discount itself lives in the Shopify session,
      // attached by the /discount/ link before they ever reached this page, and
      // Shopify is the only thing that decides what anyone pays.
      //
      // Declared here, AFTER loadState(), so it is null on every load and
      // cannot be restored from a saved draft. That is deliberate. A stale
      // "Free" shown to someone whose code was already redeemed sends them all
      // the way to a checkout that charges them, which reads as bait; a stale
      // paid price shown to someone who does still have a code costs nothing,
      // because checkout will tell them the truth in the better direction.
      claim: null,

      // Artist free-card mode (see ARTIST_TOKEN). null for every customer.
      //   state: loading | ready | signin | terms | locked | none | sent | error
      artist: ARTIST_TOKEN ? { want: ARTIST_TOKEN.design, state: 'loading', left: 0, order: '' } : null,

      // Org mode (see ORG_TOKEN). null for every customer.
      //   state: loading | ready | signin | error
      org: ORG_TOKEN ? { state: 'loading', label: '', orderName: '', back: '', error: '' } : null,

      // ── What the design catalogue asks of its host ───────────────────
      // design-picker.js is the catalogue and the chooser; the SCREEN it was
      // written for is gone (2026-08-13). These two are still how it finds
      // the occasion and the card to write onto.
      //
      // onDesignChosen() is deliberately absent: it advanced the step when a
      // customer picked a tile, and there are no tiles. design-picker.js
      // calls it only behind a `typeof === 'function'` guard, so leaving it
      // out is the way to say "choosing a design advances nothing".
      pickerOccasion() { return this.occasion || (this.card && this.card.occasion); },
      pickerCard() { return this.card; },

      init() {
        const self = this;

        // A photo this browser can't read is converted server-side; on a big
        // HEIC that is a few seconds in which the builder would otherwise look
        // frozen.
        window.addEventListener('pp:photo-converting', function (e) {
          self.converting = !!(e.detail && e.detail.active);
        });

        // ⚠️ SAVE ON THE WAY OUT, NOT JUST ON A TIMER. A phone that kills the
        // tab, a back-swipe, or the customer following a link all skip the
        // 400ms debounce. pagehide is the one event iOS Safari reliably fires
        // (unload does not, and beforeunload is unreliable there); the hidden
        // branch covers app-switching, which is how a phone usually leaves.
        window.addEventListener('pagehide', function () { self.saveNow(); });

        /* ⛔ A NEAR-MISS MUST NOT NAVIGATE AWAY FROM THE BUILDER.
           A file dropped anywhere a page does not handle is opened BY THE
           BROWSER — the tab becomes the JPEG, and a card somebody has been
           making is gone from the screen. With Y10 inviting people to drag
           photographs onto this page, a drop that lands two pixels outside the
           panel would do exactly that.

           So the window swallows file drops it did not ask for. The zone's own
           handler calls preventDefault first and stops propagation by doing
           its work; everything else lands here and is discarded, which reads
           as "nothing happened" — the correct outcome for a miss, and vastly
           better than losing the card.

           ⚠️ dragover TOO, not just drop: without preventDefault on dragover
           the browser never fires a drop event at all, and the default
           navigation happens regardless. */
        ['dragover', 'drop'].forEach(function (type) {
          window.addEventListener(type, function (e) {
            if (!self._dragHasFiles(e)) return;
            if (e.target && e.target.closest && e.target.closest('[data-pp-drop]')) return;
            e.preventDefault();
          });
        });

        /* ── BACK MEANS BACK ──────────────────────────────────────────────
           The entry steps are screens on ONE page, so until now they left no
           history at all: pressing back on the card grid abandoned the flow
           and returned to whatever preceded the product page -- the homepage,
           in Daniel's case, which is not where he came from.

           A history entry when the grid opens makes the browser's own back
           button the way to change your mind, which is why the screen no
           longer carries a "change occasion" chip: the chip was a second
           back button that only worked in one place.

           ⚠️ ONE ENTRY, NOT ONE PER STEP. Pushing on every step would make
           back walk the whole flow in reverse, and someone four screens deep
           pressing back four times to leave is worse than the problem. The
           grid is the only screen with somewhere obvious to go back TO. */
        self.$watch('entryStep', function (now, was) {
          /* In B neither 'occasion' nor 'design' is a screen, so no history entry for the grid either: wherever the
             builder would stand on one, it goes where a card is chosen (oldEntryAway). */
          if (self.noOldEntry()) { self.$nextTick(function () { self.oldEntryAway(); }); return; }
          // Back on the occasions is the end of a search: a tapped occasion
          // must open ITS designs, not the finder's (finder.js).
          if (now === 'occasion') {
            if (self.finder) self.finder = null;
            /* ...and the URL stops naming it, or a refresh here would ask the
               old question again and jump to its answer. */
            try {
              const u = new URL(window.location.href);
              if (u.searchParams.has('find')) {
                u.searchParams.delete('find');
                window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
              }
            } catch (e) { /* the URL keeps it; the page is right either way */ }
          }
          if (now !== 'design' || was !== 'occasion') return;
          /* Don't stack entries. Back then forward then back again would
             otherwise need one press per round trip made.
             ⚠️ BUT THE URL STILL MOVES. This used to return here, which left the
             entry naming the FIRST choice: Birthday, the in-page back arrow,
             then Christmas, and the address still said ?occasion=birthday, so a
             refresh opened birthday (found 2026-10-05 with the finder, whose
             ?find= made it obvious). The same entry, its URL replaced. */
          const at = (window.history.state || {}).ppStep;
          /* ⛔ THE URL NAMES THE OCCASION. A refresh has nothing else to go on:
             no draft is saved until there are photos or a note, so a customer
             who picked Christmas in-page and refreshed the grid landed back on
             the chooser (Daniel, 2026-09-02: "it goes back to the occasions
             page"). With ?occasion= in the entry, a refresh takes the same
             deep-link path a shared link does and re-opens the grid. Back pops
             to the original entry, whose URL is whatever it was. */
          let href = '';
          try {
            const u = new URL(window.location.href);
            /* A search names its sentence instead, so a refresh asks again. */
            if (self.finder) { u.searchParams.delete('occasion'); u.searchParams.set('find', self.finder.q); }
            else if (self.occasion) { u.searchParams.delete('find'); u.searchParams.set('occasion', self.occasion); }
            href = u.pathname + u.search + u.hash;
          } catch (e) { href = ''; }
          try {
            if (at === 'design') window.history.replaceState({ ppStep: 'design' }, '', href || undefined);
            else window.history.pushState({ ppStep: 'design' }, '', href || undefined);
          } catch (e) {}
        });

        window.addEventListener('popstate', function (e) {
          /* ⛔ READ THE STATE, DO NOT ASSUME A DIRECTION. popstate fires for
             BOTH back and forward, so a handler that only ever walks backward
             makes the forward button do nothing -- or worse, go back again.
             Daniel, 2026-09-02: "Forward doesn't work after I do back."

             The entry we pushed carries { ppStep: 'design' }; the page's
             original entry carries none. That is the whole direction signal. */
          const wants = (e.state || {}).ppStep;
          self.designSheet = null;      // never leave a dialog open across a step
          self.artistSheet = null;
          self.pdpZoom = false;
          if (wants === 'design' && self.entryStep === 'occasion') {
            self.entryStep = 'design';
            self.entryDir = 'fwd';
          } else if (!wants && self.entryStep === 'design') {
            self.entryStep = 'occasion';
            self.entryDir = 'back';
          }
        });
        document.addEventListener('visibilitychange', function () {
          if (document.hidden) self.saveNow();
        });

        /* ⛔ NO BARE BUILDER (Daniel, 12 Oct, "wtf", on /products/picturepop with nothing chosen): the old first
           question, "What's the occasion?", still drew its grid of blank tiles. Since 2026-10-09 a card starts from
           All cards, so the builder with no occasion, no design, no card in progress and no artist or org mode
           sends the visitor there, keeping their place in history clean (replace). */
        try {
          const q = new URLSearchParams(location.search);
          const chosen = ['occasion', 'design', 'artist', 'org', 'new', 'claim', 'code'].some(function (k) { return q.has(k); });
          if (!chosen && !self.artist && !self.org && self.stage === 'entry' && self.entryStep === 'occasion' && !self.hasDraftWork()) {
            /* The builder's preview switches ride on this address too (?ppenvfx=0 and friends): remember them before
               leaving, as the builder itself would have, so the link that turns one on still does (Daniel, 12 Oct). */
            rememberSwitches(q);
            location.replace('/pages/occasions?view=cards');
            return;
          }
        } catch (e) { /* stay */ }

        // Deep link — /products/picturepop?occasion=birthday. The occasion
        // landing pages link in this way, so arriving from one answers the
        // first question and starts at the design.
        try {
          const params = new URLSearchParams(location.search);
          const wanted = params.get('occasion');
          /* ⛔ AN EXPLICIT OCCASION USED TO BEAT THE DRAFT'S AND KEEP THE
             WORK — SUPERSEDED 2026-08-30. The reasoning was that honouring
             the click "costs them nothing", because applyOccasion swaps the
             cover and leaves the photos, captions and note alone.

             It costs them the card. Reproduced: an anniversary card with its
             note and captions intact, relabelled Halloween, still carrying
             anniversary cover art — "it still goes back to my card in
             progress. That shouldn't happen" (Daniel, 2026-08-30). Nobody
             clicking Halloween wants their anniversary card renamed.

             Neither reading is safe to guess, so it asks. switchAsk raises
             the dialog in builder-dock; nothing is applied until answered,
             and keepThisCard() strips the param so a refresh does not ask
             twice. ?new=1 — the secondary CTA — comes through the same door.

             ⚠️ THE ASK LIVES HERE, not with the other query handling further
             down, because whichever runs FIRST decides. The first version of
             this put it later, applyOccasion had already run by then, and the
             mismatch it was checking for could never be true. */
          const wantsNew = params.get('new') === '1';
          const isOcc = wanted && OCCASIONS.some((o) => o.slug === wanted);
          const mine = self.occasion || (self.card && self.card.occasion) || null;

          /* ⛔ A SET'S LINK NEVER REOPENS A CARD FROM OUTSIDE THE SET (Daniel, 18 Oct: the reviewers link in a Chrome
             with an old card on it opened that card, not one of the ten, and never asked). The site's own ask is on
             All cards, before the builder; a set's page has none, and a card in progress beats ?design= below. So,
             in a set: a card whose design is not in the set is let go and the page starts again on the design the
             link names (a reload, as startNewCard, so nothing derived from the old card survives); a card that is
             in the set but not the one named asks, as ?new=1 does. */
          const inSetNow = activeSet();
          const draftId = self.card && self.card.design && self.card.design.id;
          const linkId = params.get('design') || '';
          if (inSetNow && self.hasDraftWork() && draftId && !window.PPSet.has(draftId)) {
            track('pp_draft_discarded', { photos: self.photos.length, set: window.PPSet.name, reason: 'outside_set' });
            clearDraft();
            self._draftClosed = true;      // nothing may re-save on the way out
            location.replace(location.pathname + location.search + location.hash);
            return;
          }
          const setAsk = !!(inSetNow && self.hasDraftWork() && draftId && linkId && linkId !== draftId && window.PPSet.has(linkId));

          if (self.hasDraftWork() && (wantsNew || setAsk || (isOcc && wanted !== mine))) {
            self.switchTo = isOcc ? wanted : null;
            self.switchAsk = true;
          } else if (isOcc && wanted !== self.occasion) {
            self.applyOccasion(wanted);
          }
          /* The occasion the link's inside-top words are for (?for=, `any` for none: CARRIED_OCC), kept on a
             fresh card only, after its occasion is set above. A card in progress keeps its own words; the switch
             dialog asks about the rest, and startNewCard brings ?for= along to the fresh card it reloads into. */
          if (CARRIED_OCC && !self.hasDraftWork()) self.topWordsFrom(CARRIED_OCC);

          /* A search, refreshed or shared: ?find=a+birthday+card+for+my+dad
             (finder.js). Only where the occasions are asked: a card already in
             progress is never interrupted by a link. */
          const find = (params.get('find') || '').slice(0, 200);
          if (find && !isOcc && self.finderEnabled && !self.hasDraftWork() && typeof self.finderSearch === 'function') {
            self.finderQ = find;
            self.finderSearch();
          }

          // Free-card insert — /products/picturepop?claim=POP-XXXX-XXXX,
          // handed over by /pages/free. Shape-checked, not authenticated: this
          // only decides whether the price line reads "Free", and a well-formed
          // invention gets exactly as far as Shopify's checkout screen.
          const claimed = (params.get('claim') || '').toUpperCase();
          if (CLAIM_RE.test(claimed)) {
            self.claim = claimed;
            track('pp_claim_builder', { code: claimed });
            try { sessionStorage.setItem(CLAIM_KEY, claimed); } catch (e) { /* this load still says Free */ }
          } else {
            /* Back from All cards with the card they chose (12 Oct): the claim they arrived with, kept above, or
               by /pages/free itself (sections/free-card-claim.liquid). */
            try {
              const kept = (sessionStorage.getItem(CLAIM_KEY) || '').toUpperCase();
              if (CLAIM_RE.test(kept)) self.claim = kept;
            } catch (e) { /* the paid price shows; checkout still applies the discount */ }
          }
        } catch (e) { /* a malformed query string is not worth a broken page */ }

        // A returning visitor whose card is already past a question should not
        // be asked it again. Only ever moves FORWARD to the first unanswered
        // step, so it can't skip anyone past something they haven't done.
        // Forward to the PRODUCT step, not past it. A returning visitor who
        // already has an occasion has answered the picker, but has not
        // necessarily seen the card it sells — and the product page is now
        // where the card is actually shown.
        if (self.entryStep === 'occasion' && self.occasion) self.entryStep = 'design';
        /* ...but in B there is no 'design' screen to resume on (audit 1.3, 12 Oct): a card in progress that has its
           design reopens on Photos with the photos it has, from Continue card, the bare builder or any full link.
           One without a design is sent to choose one (oldEntryAway). The note below is about a fresh card. */
        if (self.noOldEntry() && self.stage === 'entry' && self.hasDraftWork() && self.designChosen()
            && (self.entryStep === 'occasion' || self.entryStep === 'design')) self.entryStep = 'photos';
        /* Every other way into 'occasion' or 'design' in B (?occasion= alone, ?new=1 with no card, an unknown
           occasion, ?find=, ?claim=) leaves now; a named design waits for the catalogue (designPending, below). The
           pending flag is not set yet here, so a ?design= that may still resolve is let through to that check. */
        try {
          const named = new URLSearchParams(location.search).get('design') || '';
          if (!(/^[a-z0-9-]{1,80}$/.test(named) && (!self.hasDraftWork() || !self.designChosen()))) self.oldEntryAway();
        } catch (e) { self.oldEntryAway(); }
        /* ⛔ NO FORWARD-SKIP OFF 'design'. The obvious line here --
           if designChosen() then advance -- is wrong, because applyOccasion()
           ALWAYS pre-chooses: it dresses the card the moment an occasion is
           known so the carousel and the stage have something to show. A skip
           on designChosen() therefore fires for every visitor, and
           ?occasion=halloween lands on 'name' having never seen the grid.
           Measured on Draft, 2026-09-02, before this comment existed. */

        // The pending pick is ephemeral, so a returning visitor arrives with
        // an empty one and their committed occasion would show as unselected.
        // Seeded from the answer they already gave.
        //
        // And FAILING THAT, seeded anyway. Since 2026-08-15 the deck's open
        // card is the chosen one, so "nothing chosen" is not a state the
        // screen can draw: it would have to part at a card and then claim
        // that card wasn't picked. Birthday is the opening hand — the deck
        // still starts cropped on the seasonal cards to its left.
        if (!self.occPick) self.occPick = self.occasion || DECK_OPENS_ON;

        // The occasion step's two ambient loops: the story inside the open
        // card, and the pill row's drift. Stopped on commit (applyOccasion)
        // and restarted by changeOccasion.
        self.occStoryStart();
        self.occPillGlide();

        // ── Card stage, rebuilt 2026-08-04 ──────────────────────────────
        // Geometry isn't reactive, so the camera has to be nudged. Debounced
        // with setTimeout, NOT requestAnimationFrame: rAF does not run while a
        // tab isn't painting, which leaves the camera stuck at scale 1 forever.
        let camTimer = null;
        const bumpCam = function () {
          clearTimeout(camTimer);
          camTimer = setTimeout(function () {
            // Resize is also when the layout scale can become wrong — a drag
            // onto the big monitor is exactly the case that showed up as
            // pixelated photos. Recalibrate first (hysteresis inside makes
            // the usual answer "no change"), and only a real change pays for
            // re-applying the frame. Never during the opening: openBox is
            // captured in the current scale's units and the camera is frozen.
            if (!self.opening && self.calibrateStage()) self.applyFrame(self.cardFrame);
            self.camNonce++;
          }, 0);
        };

        // The design catalogue, and the re-decode of a returning visitor's
        // saved design. Both live in design-picker.js.
        //
        // The then() is what covers a deep link: ?occasion=birthday is applied
        // above, synchronously, while this fetch is still in flight, so the
        // design could not be resolved at the time. Nothing else asks again —
        // there is no design step left to ask on.
        if (self.artist) {
          self.loadArtistDesigns();
        } else if (self.org) {
          self.loadOrgCard();
        } else {
          /* ⛔ NO GRID WHILE A NAMED DESIGN IS ON ITS WAY (Daniel, 12 Oct: after "Design a card" he saw "Just-because
             cards ... More designs coming soon!" for a few seconds). With ?design= the entry starts on the design
             step and the picker drew whatever had loaded (one theme design, then "coming soon") until the catalogue
             arrived and chooseDesign moved on. designPending hides the picker and shows "Opening your card…"
             (configurator-entry.liquid) until the catalogue answers, either way; a design it cannot find falls back
             to the grid, as before. */
          try {
            const named = new URLSearchParams(location.search).get('design') || '';
            if (/^[a-z0-9-]{1,80}$/.test(named) && (!self.hasDraftWork() || !self.designChosen())) self.designPending = true;
          } catch (e) { /* no pending: the grid shows, as before */ }
          /* In B, once the catalogue has answered, a step with no screen in B is left for where a card is chosen
             (oldEntryAway): a design that is not in the catalogue, for one. */
          const settled = function () { self.designPending = false; self.$nextTick(function () { self.oldEntryAway(); }); };
          /* A named artist design that the 3-second catalogue race missed: wait for the real catalogue (design-picker.js
             _artistFull), then try again, before anything sends the visitor away (18 Oct). */
          const artistLate = function () {
            try {
              const want = new URLSearchParams(location.search).get('design') || '';
              if (want.indexOf('artist-') !== 0 || self.catalogueComplete || !self._artistFull) return null;
              if (self.designs.some(function (x) { return x.id === want; })) return null;
              return self._artistFull;
            } catch (e) { return null; }
          };
          self.loadDesigns().then(function () { return artistLate(); }).then(function () {
            /* ?design=<id> (the redesign's design page, 2026-10-08: "Design a card" opens the builder with this
               design). A fresh card only: a card in progress keeps its own design, and the switch dialog above
               has already asked about the occasion. As the customer's choice, not a rehydrate: they chose it on
               the design's page, so the builder moves on past the grid of covers (onDesignChosen), as a click on
               it would. ensureDesignForOccasion() then keeps it. */
            try {
              const want = new URLSearchParams(location.search).get('design') || '';
              /* In a set, only the set's designs open; any other falls through to oldEntryAway, the set's page. */
              const inSet = !activeSet() || window.PPSet.has(want);
              const d = /^[a-z0-9-]{1,80}$/.test(want) && inSet && (!self.hasDraftWork() || !self.designChosen())
                && self.designs.filter(function (x) { return x.id === want; })[0];
              if (d) {
                /* ⛔ IN B A DESIGN ALWAYS BRINGS ITS OCCASION (audit 1.1, 12 Oct). A design's page linked ?design=
                   alone, so the builder had a design and no occasion and stayed on the old "What's the occasion?"
                   wall. With none in the address, the occasion is the design's own, as All cards and the design's
                   page name it (rd-design.js occasionFor, design-picker.js designOccasion): the first it was made
                   for, else the first its cover suits, else Just because. The inside top's words follow that
                   occasion, or none (`any`) for a generic card, unless the link named them (?for=). */
                if (self.noOldEntry() && !self.occasion) {
                  const made = Array.isArray(d.made) ? d.made : (d.occasions || []);
                  const known = function (h) { return h && OCCASIONS.some(function (o) { return o.slug === h; }); };
                  const words = made.filter(known)[0] || (d.suits || []).filter(known)[0] || '';
                  const occ = words || (known('just-because') ? 'just-because' : '');
                  if (occ) {
                    self.occPick = occ;
                    self.applyOccasion(occ, true);
                    self.topWordsFrom(CARRIED_OCC || words || 'any');
                  }
                }
                self.chooseDesign(want, false);
                // Straight to Photos: B has no design step to stand on.
                if (self.noOldEntry() && self.stage === 'entry') self.entryStep = 'photos';
              }
            } catch (e) { /* a malformed address keeps the occasion's own design */ }
            self.ensureDesignForOccasion();
            self.reconcileDesignToOccasion();
            settled();
          }, settled);
          setTimeout(settled, 15000);   // never a spinner for good
        }

        // Decide whether the draft we just restored is a finished purchase or
        // an abandoned checkout. Runs after loadState() has already put the
        // card on screen, so a returning customer never waits on the network
        // to see their own work.

        /* ── THE GEOMETRY, AND WHAT HAPPENS WHEN IT DOES NOT ARRIVE ──────
           card-geometry.json IS the card. Without it frameBoxes is never set,
           camReady() stays false, .pp-zoom stays at opacity 0 — and the
           customer gets a completely blank product page with NOTHING in the
           console, because the only handler logged and returned. Seen twice
           on 2026-08-29 against the local proxy; on the real CDN it is one
           dropped request on a bad connection, on the one page that takes the
           money. The section header has warned "if this page is ever blank"
           since 2026-08-11, which says it has bitten before.

           So: retry, and if it still will not come, SAY SO and offer the
           retry to the customer. A blank page that explains itself is a bad
           minute; a blank page that does not is a lost sale. */
        self.loadGeometry();

        const attachCamObserver = function () {
          const el = document.querySelector('[data-pp-stage]');
          if (!el) { bumpCam(); return; }
          if (window.ResizeObserver) {
            const ro = new ResizeObserver(bumpCam);
            ro.observe(el);
            // ⛔ AND EVERY FLOATING UI BAND (Daniel, 2026-09-03). The Border
            // popover left the card zoomed out because the only re-fit on
            // close raced toggleCardTool's synchronous camNonce++ — it won on
            // WebKit and lost on Chromium, so uiInset was still measuring the
            // open popover. A ResizeObserver on the [data-pp-ui] elements
            // themselves fires AFTER layout reflects the show/hide, so the
            // re-fit reads the settled height on every engine — no ordering to
            // get wrong. Covers the toolbar, both popovers, and anything that
            // grows the band later. ⛔ .pp-pop BY CLASS too, not only by
            // [data-pp-ui]: Border drops that attribute (it overlays the note
            // rather than reserving room), but the popover still needs
            // watching so the re-fit fires when it shows or hides — the
            // observer holds the element by reference, attribute or not.
            document.querySelectorAll('[data-pp-ui], .pp-pop').forEach(function (u) { ro.observe(u); });
          }
          bumpCam();
        };
        self.$nextTick(attachCamObserver);
        // The lock follows the stage — see applyScrollLock.
        self.applyScrollLock();
        self.$watch('stage', function () { self.applyScrollLock(); });
        window.addEventListener('resize', bumpCam, { passive: true });
        window.addEventListener('orientationchange', bumpCam, { passive: true });
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(bumpCam);
        /* ⛔ THE STUCK "ADDING…" ON BACK (Daniel, 2026-09-03). addToCart sets
           submitting=true and then navigates to /cart, and never clears it —
           there is nothing left to clear on a page that is leaving. But the
           back button restores this page from the bfcache with the whole JS
           heap intact, submitting still true, so the button reads "Adding…"
           forever. pageshow with persisted=true is the one signal that a
           restore happened; reset there. (A fresh load re-runs init, where
           submitting already starts false.) */
        window.addEventListener('pageshow', function (e) {
          if (e.persisted) { self.submitting = false; if (self.errors) self.errors._submit = ''; }
        }, { passive: true });
        /* ── NO PINCH ZOOM IN THE BUILDER (Daniel, 2026-09-04) ──────────
           A two-finger pinch on a trackpad is the browser's page zoom, and
           it re-lays the whole builder mid-edit — the 2026-09-03 user test
           had the stage scaled up with the toolbar half off screen. The
           card has its own zoom (a click in Preview, the crop slider), so
           the browser's is only ever an accident here.

           Chrome and Firefox deliver a trackpad pinch as a wheel event with
           ctrlKey set — that is the whole signal, and it is cancelable when
           the listener is not passive. Safari raises gesture* events for the
           same pinch and cancels on those. Keyboard zoom (⌘+ / ⌘−) is the
           browser's own and is deliberately left alone: it is an
           accessibility affordance, and the camera re-fits on the resize it
           causes (bumpCam above). Builder stage only — the marketing pages
           and the cart keep every browser default. */
        const pinchBlock = function (e) {
          if (self.stage !== 'builder') return;
          if (e.type === 'wheel' && !e.ctrlKey) return;
          e.preventDefault();
        };
        document.addEventListener('wheel', pinchBlock, { passive: false });
        ['gesturestart', 'gesturechange', 'gestureend'].forEach(function (t) {
          document.addEventListener(t, pinchBlock, { passive: false });
        });
        /* ── THE KEYBOARD, AND ONLY THE KEYBOARD ────────────────────────
           uiInset() measures the dock; the note is typed on the card now, so
           the thing most likely to be covering the paper is the on-screen
           keyboard, which is not in the DOM and fires no window resize on
           iOS. visualViewport is the only place it shows up.

           ⛔ BUT NOT ON 'scroll', AND NOT ON EVERY PIXEL OF 'resize'. Both
           listeners used to bump the camera directly, so every URL-bar
           collapse, every pinch and every focus-scroll re-framed the card
           mid-gesture — "if I scroll up and down, it changes the card"
           (Daniel, 2026-08-30). The 'scroll' listener is gone outright; a
           resize only reaches the camera when the keyboard's OPEN/CLOSED
           state actually flips, which is the only thing the framing cares
           about. Everything in between is browser chrome moving, and the
           card is not browser chrome. */
        /* ⛔ NO THRESHOLD. THE SHELL IS THE VISIBLE RECTANGLE, FULL STOP.
           This used to ask "is the keyboard open?" — covered > 120px — and
           only then hand the shell a height. Two things arrive in stages on
           iOS, the keyboard and then its autofill accessory bar, and whether
           that sum crossed the threshold in the right order decided whether
           the shell resized at all. It sometimes did and sometimes did not,
           on the same tap: "the keyboard once covers the toolbar and once
           doesn't" (Daniel, 2026-08-30), with the accessory bar sitting over
           the toolbar in the bad case and under it in the good one.

           A threshold is a thing to get wrong. visualViewport.height already
           IS the visible rectangle — keyboard, accessory bar, or nothing
           eating it — so take it as given and stop guessing what caused it.
           At rest it equals innerHeight, so this is a no-op until something
           actually covers the page. */
        let kbSettle = null;
        const kbWatch = function () {
          const vv = window.visualViewport;
          if (!vv) return;
          /* ⚠️ MEASURED AGAINST THE LAYOUT VIEWPORT, NOT window.innerHeight.
             innerHeight is not stable on iOS while a keyboard is up — it can
             come back already reduced, which makes "is something covering the
             page" answer NO with the keys plainly on screen, and the shell
             springs back to full height with the toolbar under them. That is
             the second half of "the keyboard once covers the toolbar and once
             doesn't" (Daniel, 2026-08-30), still reproducible after the
             threshold went. documentElement.clientHeight is the locked
             100svh box and nothing about a keyboard moves it. */
          const base = document.documentElement.clientHeight || window.innerHeight;
          const h = (vv.height < base - 2) ? Math.round(vv.height) : 0;
          if (h === self.kbBoxH) return;
          // The shell follows immediately — it is one style, and the toolbar
          // riding just above the keys is the whole point.
          self.kbBoxH = h;
          // The CAMERA waits for it to stop moving. A keyboard animates up in
          // a dozen steps and the accessory bar lands after it; re-framing the
          // card on each one is the jitter this whole mechanism exists to
          // avoid. One re-frame, once it has settled.
          clearTimeout(kbSettle);
          kbSettle = setTimeout(function () { bumpCam(); }, 160);
        };
        // Following the pan is a separate, cheaper thing: it moves one style
        // and must NEVER bump the camera, or the card re-frames per frame the
        // way it used to. Only while something is covering the page, and only
        // when it has actually moved.
        const kbPan = function () {
          const vv = window.visualViewport;
          if (!vv || !self.kbBoxH) { self.kbBoxTop = 0; return; }
          const top = Math.round(vv.offsetTop);
          if (Math.abs(top - self.kbBoxTop) > 2) self.kbBoxTop = top;
        };
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', function () { kbWatch(); kbPan(); });
          window.visualViewport.addEventListener('scroll', kbPan);
        }

        // ── The note's size follows the note ────────────────────────────
        // Every keystroke, because the size IS the feedback: the letters land
        // on the paper as they are typed, and a note that only resized when
        // you stopped would jump.
        //
        // The card's hand is a webfont. Measured in the fallback it fits at one
        // size and in the real face at another, so the first fit is provisional until
        // fonts.ready — which is also why this refits rather than assuming.
        //
        // Resize is in here even though the band and the type are both sized
        // in container units, and the ratio the fitter solves for is therefore
        // viewport-independent: it costs one measurement, and it is the thing
        // that would break silently if either ever became a px.
        self.$nextTick(function () { self.fitNote(); });
        self.$watch(function () { return self.card.bottomNote; },
                    function () { self.queueNoteFit(); });
        window.addEventListener('resize', function () { self.queueNoteFit(); }, { passive: true });
        window.addEventListener('orientationchange', function () { self.queueNoteFit(); }, { passive: true });
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(function () { self.queueNoteFit(); });
        }

        /* ⭐ AND START THE OPENING CLIP DOWNLOADING NOW, for the same reason
           the address library is warmed below: the moment it is wanted is far
           too late to begin. See prefetchOpenClip() for what this fixes and
           why it cannot wait for the shading canvas to be sized. */
        this.prefetchOpenClip();

        /* ⭐ WARM GOOGLE'S SCRIPT WHILE NOBODY IS WAITING FOR IT (Daniel,
           2026-09-10: "let's warm it up so it'll be immediate upon type").

           pp-address.js measures the library at ABOUT THREE SECONDS from first
           request to usable. Loading it when the address form opens therefore
           spends that three seconds with the customer already looking at the
           field — which is how it came to read as "the autocomplete doesn't
           find the addresses I give it" (Yonatan, 2026-09-10).

           ⛔ NOT IN openRecipients(). That is where the ZIP table is fetched
           and it is the right place for a 287 KB file nobody needs until then
           — but it fires at the same moment the field appears, so warming
           there buys nothing at all. Here it is minutes early.

           On idle, so it never competes with the card's first paint, with a
           timeout because idle may never come on a busy phone. A no-op when no
           Google key is configured, and idempotent, so calling it costs one
           script per session and nothing on repeat visits. */
        if (window.PPAddress && window.PPAddress.warm) {
          const warmAddresses = function () { try { window.PPAddress.warm(); } catch (e) {} };
          if (window.requestIdleCallback) window.requestIdleCallback(warmAddresses, { timeout: 4000 });
          else setTimeout(warmAddresses, 2500);
        }
        // The stage is hidden until the card is ready to be looked at, and a
        // hidden band measures as zero — a note restored from a previous visit
        // has to be fitted once there is something to fit it to.
        self.$watch('stage', function () { self.$nextTick(function () { self.queueNoteFit(); }); });
        // photoScroll is deliberately NOT here: cameraTarget() reads it
        // directly, so the camera already follows it, and nudging the nonce on
        // every wheel tick would only add work to the frame that must stay
        // cheapest.
        // ⛔ cardTool BELONGS HERE (Daniel, 2026-09-03: border zoom-out that
        // never came back). The Border/Font popovers carry data-pp-ui, so
        // opening one grows uiInset and the camera frames above it; closing it
        // must re-fit once the popover is gone. editSub (effect/crop) always
        // had this nextTick re-fit and restored cleanly; cardTool was missing
        // from the list, so toggleCardTool's own synchronous camNonce++ was
        // the only fit — and it measured uiInset while the popover was still
        // on screen, leaving the card stuck at the zoomed-out scale. The
        // nextTick fit runs after x-show has hidden it, so uiInset is honest.
        /* ⛔ noteSheetOpen BELONGS HERE for the same reason cardTool does
           (2026-09-16). The sheet carries data-pp-ui only while it is open, so
           uiInset() grows when it slides up and shrinks when it goes — but
           nothing re-framed the camera on that change, and an inset nobody
           measures again is an inset that does not exist. The nextTick fit runs
           after the attribute has been written, so uiInset is honest both ways. */
        ['stage', 'section', 'selectedPhoto', 'editSub', 'cardTool', 'noteSheetOpen'].forEach(function (k) {
          self.$watch(k, function () { self.$nextTick(bumpCam); });
        });

        // 2. Entering the builder reveals the stage and gives it a size.
        // The moment a card first exists to look at. CLAUDE.md sets a <90s
        // mobile target for this and measured it with a stopwatch.
        //
        // `stage` is a getter derived from photos.length, so $watch only fires
        // on a TRANSITION. A returning visitor whose photos rehydrate from
        // storage is already in the builder at init and never transitions —
        // they would have gone unrecorded. `restored` separates the two, since
        // time-since-landing means nothing on a resumed card.
        const markPreview = function (restored) {
          if (self._previewTracked) return;
          self._previewTracked = true;
          track('pp_first_preview', {
            ms_since_landing: msSinceLanding(),
            restored: !!restored,
          });
        };
        if (self.stage === 'builder') self.$nextTick(function () { markPreview(true); });
        self.$watch('stage', function (v) {
          if (v === 'builder') markPreview(false);
        });

        /* ── SAVING ──────────────────────────────────────────────────────
           ONE watcher over a cheap signature, not the eight this file used to
           carry. Those ran JSON.stringify over card{}, details{}, tagline{}
           and recipient{} on every keystroke, which is why they were removed
           along with the draft; the answer was never "don't save", it was
           "don't serialise the world to decide whether to".

           Debounced, because the note and the captions fire this per
           character and IndexedDB writes are not free. The photos are only
           re-written when their COUNT changes — their pixels are immutable
           once imported, so re-putting four data URLs on every caption
           keystroke would be pure waste. */
        self.$watch(function () {
          const c = self.card;
          return [
            self.occasion, self.photos.length,
            (self.photos || []).map(function (p) {
              return [p && p.cropScale, p && p.panX, p && p.panY, p && p.effect, p && p.tilt, p && p.rot].join(',');
            }).join('|'),
            (c.captions || []).join(''), c.bottomNote, c.border, c.noteFont, c.captionFont, c.fullSize, c.shadow, c.rounded,
            c.design && c.design.id,
            JSON.stringify(c.top), self.topPhoto ? (self.topPhoto.srcW + 'x' + self.topPhoto.srcH + ':' + (self.topPhoto.dataUrl || '').length) : '',
            self.name, self.email, JSON.stringify(self.recipient),
            /* The fan-out list. Serialised the way recipient{} is, and for the
               same reason — it is small, it changes per keystroke, and the
               debounce below is what makes that affordable. */
            JSON.stringify(self.recipients),
            // Direction B's Send: who it goes to, and From you (see emptyState).
            self.sendTo, JSON.stringify(self.sender),
          ].join('');
        }, function () { self.queueSave(); });
        /* A look on a photo is baked into its own image (see _bakeLooks). The signature is cheap: the effect and the
           data URL's length, never the data URL itself. */
        self.$watch(function () {
          return (self.photos || []).map(function (p) {
            return p ? (p.effect || 'original') + ':' + (p.dataUrl ? p.dataUrl.length : 0) : '';
          }).join('|');
        }, function () { self._bakeLooks(); });
        self._bakeLooks();
        /* ── ADDRESS SUGGESTIONS FOLLOW THE OPEN PERSON ─────────────────
           One attachment at a time, on the street field of whichever
           recipient is expanded. A watcher rather than a call in each of
           toggleRecipient/addRecipient/openRecipients, because those are three
           places to forget and the symptom — suggestions on the wrong row —
           looks like the API failing rather than like a missed call site.

           ⛔ THE PICK WRITES ALPINE STATE, NOT THE DOM. pp-address.js sets its
           own input and hands us the parts; we write them onto the recipient
           object and let x-model paint the fields. Writing the inputs directly
           would be the "don't write what a directive owns" trap — Alpine would
           overwrite them on its next pass and the customer would watch their
           address disappear.

           No-ops entirely when no Google key is set. */
        self._acOff = null;
        self.$watch('recipOpen', function (i) {
          if (self._acOff) { self._acOff(); self._acOff = null; }
          if (i < 0 || !window.PPAddress) return;
          self.$nextTick(function () {
            const row = document.querySelector('[data-pp-recip="' + i + '"]');
            /* data-pp-street, not the autocomplete attribute — pp-address.js
               rewrites that one to `off` when it attaches, so keying on it
               works once and silently never again. */
            const el = row && row.querySelector('[data-pp-street]');
            if (!el) return;
            window.PPAddress.attachAutocomplete(el, function (f) {
              const r = self.recipients[i];
              if (!r) return;
              if (f.line1) r.line1 = f.line1;
              /* ⚠️ line2 is only overwritten when Google actually returns a
                 subpremise. A customer who typed "Apt 4B" and then picked the
                 street from the list must not have it silently erased. */
              if (f.line2) r.line2 = f.line2;
              if (f.city) r.city = f.city;
              if (f.state) r.state = f.state;
              if (f.zip) r.zip = f.zip;
              if (f.country) r.country = f.country;
            }, {
              /* Read at query time, so changing the country re-aims the next
                 keystroke's suggestions (pp-address.js). */
              country: function () { const r = self.recipients[i]; return r && r.country; },
            }).then(function (off) {
              /* The row may have closed while Google's script was loading. */
              if (self.recipOpen === i) self._acOff = off; else off();
            });
          });
        });

        // The captions on the card are written by hand rather than by x-text —
        // see syncCaptions() for why — so they have to be pushed when the state
        // changes from anywhere else: the dock's field, a re-order, a restored
        // draft, or turning full size off.
        self.$watch(function () { return JSON.stringify(self.card.captions) + String(self.card.fullSize); },
                    function () { self.$nextTick(function () { self.syncCaptions(); }); });
        self.$nextTick(function () { self.syncCaptions(); });

        // Raise the compositor hint whenever the camera target actually
        // changes, and drop it once the move is over.
        //
        // Watching the computed camera rather than the things that move it:
        // sections, photo selection, paging, scrubbing and a window resize all
        // re-frame, and a watcher per cause would miss one. --pp-cam-* IS the
        // camera, so if it did not change there is nothing to promote for.
        //
        // The timer is --pp-cam-dur (520ms) plus a margin. transitionend would
        // be tighter but does not fire when a re-frame is interrupted by the
        // next one, which is exactly what paging through the strip does.
        self.$watch(function () { return JSON.stringify(self.backdropStyle()); }, function () {
          self.camMoving = true;
          clearTimeout(self._camMoveTimer);
          self._camMoveTimer = setTimeout(function () { self.camMoving = false; }, 700);
        });

        // Escape goes back to Full card (Daniel, 2026-08-11) — the same place
        // clicking off the card goes, and the same place the Full card pill
        // goes. Three ways out, one destination.
        //
        // On window, not on the stage: the key is pressed with focus wherever
        // the customer left it, which is usually the note textarea or a
        // caption input inside the dock — neither of which is in the stage's
        // subtree. leaveToFullCard() blurs it on the way past.
        //
        // A drag in flight gets cancelled instead, because Escape during a
        // gesture means "not that", not "leave".
        /* "Click to zoom" the moment a click would zoom, under a cursor that is
           already resting on the card: the card opens on its way into Preview,
           and the label is not wanted until it has. Waiting for the next move
           left it missing for anyone who held still (Daniel, 11 Oct: "it
           sometimes appears and sometimes doesn't. It's very fickle"). */
        this.$watch('previewTagWanted()', function (on) {
          if (on && self._tagOver) self.previewTagOn({ clientX: self._tagOver.x, clientY: self._tagOver.y });
          else if (!on && self.section === 'preview') self.previewTagOff();
        });
        window.addEventListener('keydown', function (e) {
          if (self.stage === 'builder' && self.previewKey(e)) return;
          if (e.key !== 'Escape' && e.key !== 'Esc') return;
          if (self.reordering()) { self._cancelPicturePress(); return; }
          if (self.stage !== 'builder') return;
          self.leaveToFullCard();
        });

        // The occasion / ?new=1 arrival is handled with the rest of the query
        // string above — see the note there for why it has to be first.

        // Rehydrate photo dataURLs from IndexedDB (localStorage only holds light refs now).
        (function rehydratePhotos() {
          if (!window.PP || !window.PP.idb) return;
          const photos = self.photos || [];
          if (!photos.some(function (p) { return p && !p.dataUrl && p.idbKey; })) return;
          Promise.all(photos.map(function (p) {
            if (p && p.dataUrl) return Promise.resolve(p.dataUrl);
            if (p && p.idbKey) {
              return window.PP.idb.get(p.idbKey).then(function (v) {
                if (!v) return null;
                return (typeof v === 'string') ? v : blobToDataUrl(v);
              }).catch(function () { return null; });
            }
            return Promise.resolve(null);
          })).then(function (urls) {
            const next = self.photos.slice();
            let changed = false;
            urls.forEach(function (u, i) {
              if (u && next[i] && !next[i].dataUrl) { next[i] = Object.assign({}, next[i], { dataUrl: u }); changed = true; }
            });
            if (changed) self.photos = next;
          });
        })();
        // The inside top's photo, the same way, then its preview.
        (function rehydrateTopPhoto() {
          const tp = self.topPhoto;
          if (!tp || tp.dataUrl || !window.PP || !window.PP.idb) return;
          window.PP.idb.get(TOP_PHOTO_KEY).then(function (v) {
            if (!v) { self.topPhoto = null; return null; }
            return (typeof v === 'string') ? v : blobToDataUrl(v);
          }).then(function (u) {
            if (u && self.topPhoto) { self.topPhoto = Object.assign({}, self.topPhoto, { dataUrl: u }); self.topRender(); }
          }).catch(function () {});
        })();

        // ───── The retired Three.js Card3D ──────────────────────────
        //
        // Removed 2026-08-11. `PP.Card3D` was the WebGL/.glb card; it was
        // replaced by card-stage.js, which builds the same artist geometry in
        // CSS 3D. The class went, but six $watch callbacks wired to it stayed
        // — and two of them called `_syncCard3DPhotos()` / `_syncCard3DDetails()`,
        // which went with the class. So every change to the photos threw
        // "is not a function", live, at exactly the moment the card opens.
        //
        // The other four were harmless only because they guarded on
        // `self.card3d`, which could never again be non-null.
      },

      // Stage (derived) lives below the Object.assign — defined via
      // defineProperty so it isn't invoked as a copy-target by Object.assign.

      // ───── Tabs (legacy Plan 8) — kept for any dormant references ───
      setTab(name) {
        if (['photos','words','note','preview'].indexOf(name) === -1) return;
        this.tab = name;
      },

      // ───── Sections (toolbar-driven) ─────────────────────────────────
      // Only three, because only three things are editable. Setting a section
      // moves the CAMERA over the card — nothing is swapped in or out.
      // CoverFront/CoverTop are still reachable by the camera (see
      // camTargetName) but are not customer-facing destinations.
      setSection(name) {
        if (SECTIONS.indexOf(name) === -1) return;
        // The card may not leave Preview folded — see _restoreOpenPose.
        // Leaving Preview to edit also un-latches "Already added to cart", so a
        // real re-add after a change works (and de-dupes by _pp_line).
        if (this.section === 'preview' && name !== 'preview') {
          this.previewZoom = false;
          if (this._pzRaf) { cancelAnimationFrame(this._pzRaf); this._pzRaf = 0; }
          this._restoreOpenPose();     // its _camRelease eases the camera back
          this.cartAdded = false;
        }
        // Leaving the note is when its final length is worth recording; firing
        // per keystroke would bury everything else.
        if (this.section === 'bottom' && name !== 'bottom') {
          const n = (this.card.bottomNote || '').length;
          if (n) track('pp_note_written', { length: n });
          // Leaving the note forgets that the paper was touched, so arriving
          // again by the toolbar offers the invitation again — see noteTouched.
          this.noteTouched = false;
        }
        // Arriving at Edit pictures always starts at the first picture. The
        // page is a position in a view you have just left; carrying it back in
        // lands the camera on picture 3 with no way to know why.
        if (this.section === 'inside' && name !== 'inside') {
          track('pp_inside_top_set', { mode: this.topMode(), template: this.topMode() === 'text' ? this.card.top.template : null });
        }
        if (name === 'photos' && this.section !== 'photos') this.photoScroll = 0;
        this.selectedPhoto = null;
        // Changing what you are looking at un-chooses whatever was open —
        // same rule as focusPhoto and closePhoto. A tool lit over a view it
        // does not belong to is the whole family of niggles this closes.
        this.clearTools();
        this.section = name;
        this.card.section = name;
        track('pp_section_viewed', { section: name });
        // The page (not a nested container) scrolls, and switching sections
        // never reset that scroll position — a taller panel (the Send address
        // form) stayed clipped under the fixed toolbar with no scroll hint,
        // hiding Add-to-cart entirely on some viewports.
        window.scrollTo(0, 0);
      },

      // Convenience — is the given section currently active?
      isSection(name) { return this.section === name; },

      /* THE CARD IS FINISHED AND WE ARE ON THE WAY OUT. Preview and the
         recipients list are both "past editing": the toolbar stands down, the
         stage frames the whole card, and the note's empty-state invitation is
         not offered any more. One predicate rather than three copies of the
         same `or`, because these two sections have to move together — the
         first version of this shipped with the toolbar reappearing over the
         address form on the way to the cart. */
      pastEditing() {
        return this.section === 'preview' || this.section === 'recipients';
      },

      // ───── Done — the way out, in the place the way in was ───────────
      // Daniel, 2026-08-16: with `Full card` deleted there was no visible way
      // back to the whole card at all. Rather than putting that pill back, each
      // mode pill becomes its own exit while its section is open.
      //
      // It deselects on the way, so the next visit to Edit photos starts on the
      // card-wide row rather than resuming a picture the customer has finished
      // with. And it drops the keyboard first: on a phone, leaving a caption or
      // the note with the field still focused holds the keyboard up over a card
      // nobody is editing any more.
      doneEditing() {
        const a = document.activeElement;
        if (a && typeof a.blur === 'function' && a !== document.body) a.blur();
        this.selectedPhoto = null;
        this.editSub = 'none';
        this.setSection('card');
      },

      // ───── Hover, drawn OUTSIDE the card ─────────────────────────────
      // "Hovering over a pic on desktop has to show a hover state that makes
      // it clear that it's clickable" (Daniel, 2026-08-16).
      //
      // ⛔ AND IT CANNOT BE PAINTED ON THE PANEL. That was tried, four times,
      // and the long note in stage.css is the post-mortem: any repaint inside
      // .pp-zoom drags the artist's 2000x2000 shading pass through the
      // compositor with it, and the whole UI — including the dock, which is
      // not even in the card — stutters as the cursor crosses a seam.
      //
      // So the outline is an SVG polygon in the UI layer, a sibling of the
      // card rather than a child: hovering repaints one small standalone
      // element and nothing inside the card at all. It can be exact rather
      // than approximate, too, because the geometry hands us the plane's four
      // PROJECTED CORNERS — the ring sits on the tilted panel instead of
      // around a bounding box that is visibly bigger than it.
      setHoverPhoto(i, e) {
        // Touch has no hover. A phone would otherwise light the panel up on
        // the way to a tap and leave it lit afterwards.
        if (e && e.pointerType && e.pointerType !== 'mouse') return;
        if (!this.panelInteractive()) return;
        this.hoverPhoto = i;
      },
      clearHoverPhoto(i) { if (this.hoverPhoto === i) this.hoverPhoto = null; },

      // The hovered panel's outline in stage pixels, or '' for nothing to
      // draw. The camera is a translate+scale about the stage's centre, so
      // this is the same transform .pp-zoom carries, applied by hand.
      hoverPoints() {
        const i = this.hoverPhoto;
        // ⛔ NOT OVER THE SELECTED PANEL. The selection ring is drawn on the
        // same corners in the same layer, and two rings on one panel is how
        // "hover looks more chosen than chosen" happened in the first place.
        if (i !== null && i === this.selectedPhoto) return '';
        return this._planePoints(i);
      },

      // ── The SELECTED panel's outline ─────────────────────────────────
      // Daniel, 2026-08-29: selection has to beat hover. It did not — the
      // selected panel was deliberately unmarked (only its neighbours faded to
      // 45%) while hover drew a full accent polygon, so the strongest thing on
      // screen followed the mouse.
      //
      // This respects the 2026-08-11 rule that made the panel unmarked in the
      // first place — "a treatment may leave the artwork alone, or act on
      // everything EXCEPT the selected panel, it may not act ON it". A polygon
      // in the UI layer sits on the panel's projected EDGE; it never touches
      // the photograph, and it costs no repaint inside .pp-zoom.
      selPoints() {
        if (this.selectedPhoto === null) return '';
        return this._planePoints(this.selectedPhoto);
      },

      // ⛔ THE RING IS A STRAIGHT QUAD, AND THAT IS THE DECISION.
      //
      // It briefly followed the outer panels' R5 corners, because a right
      // angle over rounded paper is visibly wrong. The curve was built in
      // SCREEN space — a quadratic through the projected corner — which is an
      // approximation of the projected arc, not the arc. Daniel looked at it:
      // "the rounded borders on the edges aren't good. If you can't match them
      // exactly to the 3D, then go back to having them at 90° angles."
      //
      // Matching exactly means sampling the rounded rectangle in the PLANE'S
      // OWN space and projecting each sample — the corner is an arc on a
      // tilted surface, so its projection is an ellipse arc, and no amount of
      // fiddling in screen space produces one. That is a real change to
      // _planePoints (it would have to return a polyline, not four corners)
      // and it is worth doing when the ring is the thing being worked on. It
      // is not worth an approximation that reads as wrong.
      //
      // CardStage.CORNERS stays exported for whoever does it.

      // A panel's four projected corners in stage pixels, or '' for nothing to
      // draw. The camera is a translate+scale about the stage's centre, so
      // this is the same transform .pp-zoom carries, applied by hand.
      _planePoints(i) {
        void this.camNonce;                 // recompute when the camera does
        if (i === null || i === undefined) return '';
        // Not during a move: the transform is animating over 520ms while this
        // reads the TARGET, so the ring would run ahead of the card.
        if (this.camMoving || this.scrubbing || this.opening) return '';
        const b = this.frameBoxes && this.frameBoxes[PANELS[i]];
        const stage = document.querySelector('[data-pp-stage]');
        if (!b || !b.quad || !stage) return '';
        const t = this.cameraTarget();
        const cx = stage.clientWidth / 2, cy = stage.clientHeight / 2;
        return b.quad.map(function (p) {
          return (cx + t.x + p[0] * t.scale).toFixed(1) + ',' +
                 (cy + t.y + p[1] * t.scale).toFixed(1);
        }).join(' ');
      },

      // Records that a tool was USED on a panel, once. The crop slider alone
      // would otherwise emit on every input event and drown the funnel.
      /* ── "SEE IT CLOSED", AT THE CARD'S BOTTOM-LEFT ──────────────────
         Daniel, 2026-09-03: a close control "next to the bottom left of the
         card and only in the zoomed out version." Zoomed out is the whole-card
         view (section 'card', no photo selected); the button folds the card in
         place — the same geometry stand-up as Preview, no clip, no section
         change — and becomes "Open" while it is shut, with the edit dock dimmed
         behind it (.pp-app--folded). It is a VIEW control, so it lives on the
         card, not in the dock. */
      showCardFold() {
        // The Open/Closed toggle lives in the whole-card, zoomed-out view AND
        // in Preview (Daniel, 2026-09-03: the nav toggle is the only open/close
        // control in Preview too). It stays put through open, the fold
        // animation AND shut — a segmented toggle has to be visible in both
        // states for the thumb to slide. Hidden only in the entry opening and
        // when zoomed into a panel/note.
        return this.stage === 'builder' &&
               (this.section === 'card' || this.section === 'preview') &&
               this.selectedPhoto === null &&
               !!this.geom && !this.opening && this.camReady();
      },

      // One tap anywhere flips the toggle. foldedInEditor is the intent flag
      // (set synchronously by foldCard/unfoldCard), so a tap mid-animation
      // reverses cleanly via _previewTo. The squash + confetti are cosmetic.
      toggleFold() {
        // Nothing to open onto while photos are still placeholders; the card
        // opens by itself once they land (openFromStanding).
        if (this.photosLoading()) return;
        this._foldAnimated = true;
        this.previewTagOff();            // the label must not sit on a folding card
        if (this.foldedInEditor) this.unfoldCard();
        else this.foldCard();
        clearTimeout(this._foldBurstT1);
        clearTimeout(this._foldBurstT2);
        this._foldBurst = [];
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const self = this;
        this._foldBurstT1 = setTimeout(function () {
          self._foldBurst = self._foldParticles;           // fire as the thumb settles
          self._foldBurstT2 = setTimeout(function () { self._foldBurst = []; }, 700);
        }, 330);
      },

      // The shut bar's width, matched to the toolbar captured at fold time.
      cardClosedDockStyle() {
        return this._dockW ? { minWidth: this._dockW + 'px' } : {};
      },

      // Anchored to the card's projected bottom-left corner — the empty
      // lower-left of the open cross, and the bottom-left of the closed card.
      // Same projection the selection ring uses (see _planePoints).
      cardFoldStyle() {
        void this.camNonce;
        // ⛔ AN OBJECT, NOT A STRING. Alpine merges an object per-property, so
        // x-show's own display:none survives; a :style STRING rewrites the
        // whole attribute and wipes it — the pill then showed while shut even
        // though showCardFold() was false (measured 2026-09-03). Same lesson
        // as backdropStyle.
        const stage = document.querySelector('[data-pp-stage]');
        if (!stage || !this.frameBoxes) return { left: '-9999px', top: '-9999px' };
        const boxes = Object.keys(this.frameBoxes).map((k) => this.frameBoxes[k]);
        const u = union(boxes);
        if (!u) return { left: '-9999px', top: '-9999px' };
        const t = this.cameraTarget();
        const blx = u.cx - u.w / 2, bly = u.cy + u.h / 2;
        const x = stage.clientWidth / 2 + t.x + blx * t.scale;
        const y = stage.clientHeight / 2 + t.y + bly * t.scale;
        return { left: x.toFixed(1) + 'px', top: y.toFixed(1) + 'px' };
      },

      _trackEdit(tool, panel) {
        this._edits = this._edits || {};
        const k = tool + ':' + panel;
        if (this._edits[k]) return;
        this._edits[k] = true;
        track('pp_edit_used', { tool: tool, panel: panel });
      },

      // ── Replace one picture ────────────────────────────────────────
      // handleFiles() takes four at once and replaces all four, which is right
      // for the entry flow and wrong here: someone who dislikes one of their
      // four should not have to start the card over.
      async replaceOne(fileList, i) {
        const files = Array.from(fileList || []);
        if (!files.length || i == null || !this.photos[i]) return;
        try {
          const im = await importPhoto(files[0]);
          // FRAMING RESETS, LOOK STAYS.
          //
          // Zoom used to survive a replace, on the reasoning that it was the
          // customer's stated choice (Daniel, 2026-08-03). Reversed 2026-08-11
          // by Daniel: zoom and pan together describe how a PARTICULAR picture
          // sits in the window, and neither means anything once that picture is
          // gone — a new photo arriving pre-magnified at someone else's setting
          // reads as a bug, not as a preserved preference.
          //
          // ⛔ AND THE EFFECT GOES WITH THEM — REVERSED 2026-09-10.
          //
          // This used to read "Effect and border still stay. Those are a look,
          // not a framing, and they carry over to any picture perfectly well."
          // Daniel overruled it the day the Magic filter shipped: "if there is
          // an effect and the picture is replaced, either by dragging or by
          // replacing through the toolbar, the effect should be removed."
          //
          // The old reasoning was about a look the customer had DELIBERATELY
          // picked off a tile. The wand changed what an effect is: it is now
          // something cast at a particular photograph, often without being
          // named, and a cyanotype chosen for a beach at sunset landing on a
          // portrait of somebody's grandmother is not a preserved preference.
          //
          // ⚠️ BORDER AND SHADOW STILL STAY (Daniel, 2026-09-10: "border and
          // shadow, not effect"). Both are CARD-WIDE — one setting every panel
          // reads, see setBorder() and shadowActive(), and they ride to print
          // as _Border and _Shadow — so they belong to the paper rather than
          // to any one picture and a replace has no business touching them.
          // This patch is per-photo, so it could not reach them even if it
          // tried; naming them here is to stop a future reader assuming the
          // omission was an oversight.
          //
          // The effect is the odd one out precisely because it IS per-photo.
          this._patchPhoto(i, {
            dataUrl: im.dataUrl, w: im.w, h: im.h,
            effect: 'original',
            // ⛔ srcW/srcH MUST be carried onto the photo. They are the true
            // dimensions before the import downscale, and the only honest
            // input to the resolution check — importPhoto() returning them is
            // useless if the object built from it drops them on the floor.
            srcW: im.srcW, srcH: im.srcH,
            name: files[0].name, size: files[0].size,
            cropScale: 1, panX: 50, panY: 50, tilt: 0,
            // A cut-out and a face pan belong to the picture that has gone.
            cutUrl: null, facePan: null,
          });
          this._faceCentre(i);
          this._trackEdit('replace', i);
          this.errors = {};
        } catch (e) {
          this.errors = { _photos: photoErrorMessage(e, files[0]) };
        }
      },

      // EVERY store keyed by picture index, in one place.
      //
      // `order` holds old indices in their new positions. This is the only
      // function that needs to know the full list, which is the point: a store
      // left out of a reorder silently mislabels every picture after it, and
      // the customer finds out on the printed card. swapPhotos() did exactly
      // that with captions before this existed.
      //
      // Per-photo look (effect, border, cropScale) needs no handling — it lives
      // on the photo object and travels with it.
      _applyPhotoOrder(order) {
        const self = this;
        const pick = function (arr) {
          if (!Array.isArray(arr)) return arr;
          const out = order.map(function (o) { return arr[o]; });
          // Anything past the reordered range keeps its place — captions is
          // fixed at four even when fewer pictures are in.
          for (let i = order.length; i < arr.length; i++) out.push(arr[i]);
          return out;
        };

        this.photos = pick(this.photos);
        this.card.captions = pick(this.card.captions);
        this.details = pick(this.details);

        // selectedPhoto is keyed by picture index too, so it belongs here with
        // the rest of them. It follows the PICTURE, not the slot: re-ordering
        // is about arrangement, and the picture the dock is editing should
        // still be the picture the dock is editing afterwards. Dragging the
        // selected one is the same rule, which is the point of writing it this
        // way — it lands wherever that picture landed.
        if (this.selectedPhoto !== null) {
          const moved = order.indexOf(this.selectedPhoto);
          if (moved !== -1) this.selectedPhoto = moved;
        }

        // Errors are keyed 'photo_<index>' and must follow too, or a message
        // ends up pointing at a picture that is now somewhere else.
        const next = {};
        Object.keys(this.errors || {}).forEach(function (k) {
          if (k.indexOf('photo_') !== 0) next[k] = self.errors[k];
        });
        order.forEach(function (o, n) {
          const v = self.errors && self.errors['photo_' + o];
          if (v) next['photo_' + n] = v;
        });
        this.errors = next;
      },

      _identityOrder() {
        const o = [];
        for (let i = 0; i < this.photos.length; i++) o.push(i);
        return o;
      },

      // Move a picture to another slot, taking everything about it along.
      movePicture(from, to) {
        const n = this.photos.length;
        if (from == null || to == null || from === to) return;
        if (from < 0 || to < 0 || from >= n || to >= n) return;
        const order = this._identityOrder();
        order.splice(to, 0, order.splice(from, 1)[0]);
        this._applyPhotoOrder(order);
        track('pp_edit_used', { tool: 'reorder', panel: to, from: from });
      },

      // Cover artwork for a face, from the design the customer chose.
      // Not editable — CoverFront/CoverTop/CoverBottom are artist assets
      // picked from a gallery (Daniel, 2026-08-03).
      // ── Card stage ────────────────────────────────────────────────────

      // Only `display` is bound; the box is written into the same string
      // because a :style STRING rewrites the whole attribute and would wipe
      // anything set imperatively. Evaluated once per opening — the framing is
      // frozen, so nothing here changes per frame.
      openVideoStyle() {
        void this.camNonce;
        /* ⛔ THE OPENING OWNS THIS CANVAS, AND ONLY THE OPENING. It used to
           stay up for the closed card, holding a captured frame of the shading
           — which is the artist's pass for the card at HIS camera angle, and
           Preview now stands the card square to a different one. Registered
           for one pose, painted over another, it was a smear across the cover.
           The closed card is lit by .pp-zoom's own drop-shadow instead. */
        if (!this.opening) return 'display:none;';
        const stage = document.querySelector('[data-pp-stage]');
        if (!stage) return 'display:none;';
        const w = stage.clientWidth, h = stage.clientHeight;
        if (!w || !h) return 'display:none;';
        /* ⛔ THE CAMERA THE CARD IS UNDER, NOT THE ONE IT WILL BE UNDER (2026-10-12). The opening can begin with the
           camera still held on .pp-zoom by hand (openFromStanding's tip, Builder B's glide), and that held value, not
           cameraTarget(), is what the card is drawn at. On a phone the two differed by 1.43x (cameraTarget 1.076, the
           card at 0.7545, measured in WebKit at 390x844): the shading of the OPEN card was painted 43% larger than the
           card unfolding under it, a big see-through silhouette opening beside the small solid card (Daniel's Pop
           List: "the doubled opacity silhouette"). _camLive() reads the value in force on the element; when nothing is
           held it IS cameraTarget(), so desktop, where they always agreed, is unchanged. */
        const t = this._camLive() || this.cameraTarget();
        // ⚠️ COMP UNITS, SCALED. The comp is 2000 (SPAN in
        // scripts/make-open-video.py) but the scene is laid out at
        // 1/LAYOUT_SCALE of geometry units, and t.scale is correspondingly
        // larger — so the comp square must be divided too or this canvas comes
        // out LAYOUT_SCALE times too big and the shading leaves the card.
        const size = compSpanCss() * t.scale;
        return `display:block; width:${size.toFixed(1)}px; height:${size.toFixed(1)}px;` +
               `left:${(w / 2 + t.x - size / 2).toFixed(1)}px;` +
               `top:${(h / 2 + t.y - size / 2).toFixed(1)}px;`;
      },

      // The union of every frame's extent, so one fixed framing can hold the
      // whole animation without ever cropping a panel mid-swing. Culled planes
      // are excluded — a plane that is not drawn must not size the frame.
      _openFraming() {
        const CS = window.PP && window.PP.CardStage;
        if (!CS || !this.geom) return null;
        let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
        for (let i = 0; i <= 100; i++) {
          const t = CS.frameTransforms(this.geom, i);
          Object.keys(t.planes).forEach(function (k) {
            if (!t.planes[k].facing) return;
            const b = t.planes[k].box;
            x0 = Math.min(x0, b.cx - b.w / 2); x1 = Math.max(x1, b.cx + b.w / 2);
            y0 = Math.min(y0, b.cy - b.h / 2); y1 = Math.max(y1, b.cy + b.h / 2);
          });
        }
        if (!isFinite(x0)) return null;
        return { cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, w: x1 - x0, h: y1 - y0 };
      },

      // ⚠️ EXACT LOCKSTEP, INTEGER FRAMES, 30fps. The artist: "the frame rate
      // must be identical between the motion the code creates and the motion
      // shown in the video — 30 FPS." The video presents artist frame N and
      // the geometry snaps to EXACTLY frame N, in the same rendering update.
      // Never interpolate between video frames: the paper then holds poses the
      // shading never rendered.
      startOpening() {
        const self = this;
        const vid = document.querySelector('[data-pp-shadevid]');
        // ⛔ EVERY PATH OUT OF HERE LANDS IN THE SAME PLACE (Daniel,
        // 2026-08-29: "no reason to have two states, and the opening
        // animation is the better state").
        //
        // decide() reveals the card without playing anything, and it is taken
        // on three routes: prefers-reduced-motion, no geometry, and no
        // framing. (A fourth — "already seen this session" — was removed on
        // 2026-09-03: the opening now plays every time the builder is
        // entered.) It used to stop there — only finishOpening() called
        // setSection('photos') — so those visitors were left in the
        // whole-card view while everyone else got Edit photos. Two entry
        // states, chosen by something the customer cannot see, and on a phone
        // the difference is 88px pictures against one that fills the screen.
        const decide = function () {
          self._openingDecided = true;
          self.camNonce++;
          self.landInBuilder();
        };
        const reduced = window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!vid || !this.geom) return decide();
        if (reduced) return decide();

        const framing = this._openFraming();
        if (!framing) return decide();

        // ⚠️ ONE COMMIT. The geometry and the shading PIXELS are set in the
        // same callback, so they reach the screen together. Letting the
        // <video> paint itself instead put the shading on the compositor's
        // schedule and the card on ours — two clocks, measured disagreeing on
        // 17.8% of display refreshes, always by exactly one pose. That is the
        // mismatch visible on "some frames". Drawing the frame ourselves
        // removes the second clock rather than trying to out-time it.
        //
        // rVFC, not rAF: it guarantees the frame corresponding to
        // meta.mediaTime is the one drawImage will get, so the pixels, the
        // pose and the timestamp are all the same instant.
        // The canvas and its edge fades live in _shadeDraw() since 2026-09-03,
        // shared with Preview's close.
        /* ⛔ SAFARI STOPS CALLING requestVideoFrameCallback PARTWAY THROUGH (18 Oct). Daniel's Safari 26.6, logged
           with an on-screen log (removed the same day): the clip played 0.6 to 3.2s untouched while the card sat at frame 68, then
           finishOpening jumped it to 0: "stuck, then it cuts straight to the open card". The callbacks stopped after a
           few frames (the shading's <video> is never painted itself, and the rate is slewed every frame; either may
           be what Safari drops it for). So a watchdog on rAF: when no video-frame callback has arrived for 100ms while
           the clip is playing, the card follows the clip's own clock (currentTime). Chrome's frame-exact path is
           untouched; Safari gets an unfold that can no longer freeze. */
        let lastStep = 0, rvfcPending = false;
        const watch = function () {
          if (!self.opening) return;
          if (!vid.paused && lastStep && performance.now() - lastStep > 100) {
            step(performance.now(), null);
          }
          requestAnimationFrame(watch);
        };
        const step = function (now, meta) {
          if (meta) rvfcPending = false;
          lastStep = performance.now();
          if (!self.opening) return;
          const t = meta ? meta.mediaTime : vid.currentTime;
          const idx = Math.min(100, Math.max(0, Math.round(t * 30)));
          const g = 100 - idx;
          if (g !== self.cardFrame) { self.cardFrame = g; self.applyFrame(g); }

          // ── PACING (Daniel, 2026-08-29) ──────────────────────────────
          // The card used to unfold at one flat speed for 3.4s, because the
          // clip plays at 1x and the geometry follows its timestamps. Against
          // the homepage — which is scrubbed, so it accelerates and settles
          // with the reader — that reads as mechanical.
          //
          // ⛔ THE EASING IS APPLIED TO THE VIDEO'S RATE, NOT TO THE FRAME
          // NUMBER. The geometry is driven by mediaTime, so slewing the rate
          // eases the paper and the artist's shading by the SAME amount, in
          // the same commit, for free. Easing the frame number instead would
          // put the card at a pose the shading never rendered — which is the
          // 17.8%-mismatch bug this whole rVFC arrangement exists to prevent.
          //
          // ⛔ AND NOT BY SEEKING. currentTime seeks measured 72ms median on
          // the 1080 encode (hero spec, 2026-08-18); a seek per frame is ~14
          // fps. Continuous playback at a varying rate keeps the decoder
          // streaming and never seeks at all.
          //
          // A half-sine on the speed: slow to lift, quickest through the
          // middle of the unfold, settling as the last panel lands. Mean rate
          // works out near 1, so the whole thing still takes ~3.4s.
          // (Nothing to gate on prefers-reduced-motion here — that preference
          // is handled at the top of startOpening, where it skips the opening
          // outright and lands on the same finished card.)
          const u = idx / 100;
          const rate = OPEN_RATE_MAX - (OPEN_RATE_MAX - OPEN_RATE_MIN) * u * u * u;
          // Only write when it actually moves: assigning playbackRate is a
          // pipeline change in some engines, and this runs 30 times a second.
          if (Math.abs(vid.playbackRate - rate) > 0.02) {
            try { vid.playbackRate = rate; } catch (e) { /* engine refused; 1x is fine */ }
          }
          self._shadeDraw(vid, idx);
          if (vid.requestVideoFrameCallback) {
            if (!rvfcPending) { rvfcPending = true; vid.requestVideoFrameCallback(step); }
          } else requestAnimationFrame(step);
        };

        // Commit only once playback is certain. Committing first and then
        // waiting would park a CLOSED card on screen for the length of the
        // wait and snap it open if the wait expired.
        const commit = function () {
          // Recomputed HERE, not reused from the gate above: between the gate
          // and canplaythrough a resize can recalibrate the layout scale, and
          // a framing captured in the old scale's units would misplace the
          // camera for the whole opening. This runs in the same tick as
          // `opening = true`, so nothing can move the scale under it.
          openBox = self._openFraming() || framing;
          self.opening = true;
          self.cardFrame = 100;
          self.applyFrame(100);              // start closed
          decide();                          // only now may the card be seen
          self.camNonce++;                   // the only camera write of the opening

          // Size the canvas' backing store to what the SCREEN actually needs.
          //
          // ⛔ THE CAP USED TO BE 2000 — "the source video's own resolution;
          // larger buys nothing". That is true of DETAIL and false of
          // SAMPLING, and the difference is what Daniel circled on
          // 2026-08-29. Measured on a 2x display at 1280x860: the card paints
          // the shading at 1610 CSS px, so the screen wants 3220 device px,
          // and a 2000px backing store was being stretched 1.61x — by the
          // COMPOSITOR, at whatever filter it feels like, over a layer whose
          // edges already carry the encoder's ringing. That is the blocky
          // stair-stepping at the fold seams.
          //
          // Matching the backing store to the device pixels turns a
          // composite-time stretch into one high-quality resample we control.
          // It adds no detail — the source is still 2000px — but it stops
          // magnifying the noise that is there.
          const cv = document.querySelector('[data-pp-shadecanvas]');
          if (cv) {
            const t = self.cameraTarget();
            const css = compSpanCss() * t.scale;
            // ⚠️ AND THE CAP IS A MEMORY CAP NOW, NOT A RESOLUTION ONE.
            // 3072² × 4 bytes ≈ 37MB, which is fine for ONE canvas on a
            // desktop and is never reached on a phone: at 375px the card
            // paints ~340 CSS px, so this asks for ~700 and allocates 2MB.
            // Do not raise it without checking iOS — the builder has been
            // killed by memory before (2026-08-13).
            const px = Math.min(SHADE_CANVAS_MAX, Math.round(css * (window.devicePixelRatio || 1)));
            /* ⚠️ ONLY IF IT CHANGED. Assigning width RESETS a canvas even when
               the number is identical, and at this size that is a fresh
               multi-megabyte buffer allocated on the animation's FIRST frame.
               openFromStanding sizes it during the hold instead, so this is
               usually a no-op — see _sizeShadeCanvas(). */
            if (cv.width !== px) {
            cv.width = px; cv.height = px;
            // ⛔ AFTER THE RESIZE, NEVER BEFORE. Assigning canvas.width RESETS
            // the 2D context to its defaults, so a smoothing hint set when the
            // context was created is silently wiped here — measured 2026-08-29,
            // it still read 'low' during the opening.
            //
            // It matters because the draw is now a real resample: a 2000px
            // video into a backing store the size of the screen. At 'low' a
            // ~2x downscale of a smooth gradient aliases, which is half of the
            // stair-stepping; the other half was the 1.61x upscale above.
            const ctx2 = cv.getContext('2d', { alpha: true });
            if (ctx2) ctx2.imageSmoothingQuality = 'high';
            }
          }

          /* ⏸ A BEAT WITH THE CARD SHUT — Daniel, 2026-09-01.
             decide() has just put a CLOSED card on screen. Hold it there before
             anything moves, so the customer sees the cover they chose, and then
             open it. The cover is the one part of the card they picked and
             never otherwise see up close.

             ⚠️ THIS IS NOT THE WAIT THE COMMENT ABOVE commit() WARNS ABOUT.
             That one was indefinite — parking a closed card while waiting for
             playback that might never become certain, and snapping it open if
             the wait expired. This runs AFTER playback is certain, and is a
             fixed pause we chose. Keep it short: half a second reads as "look
             at this", and much past that reads as broken.

             The stall guard starts INSIDE the hold, not before it. It trips
             after ~1s of a motionless video, and arming it first would spend
             half that budget watching a pause that is working correctly. */
          /* Named distinctly on purpose: there is already a module-level
             HOLD_MS (400ms, the photo drag-hold) and this shadows it. The
             minifier had to rename one of them to HOLD_MS2 to keep them
             apart, which is a fair warning about reading them apart. */
          /* ⚠️ ZERO WHEN THE CARD ARRIVED STANDING. This pause exists so the
             customer sees the cover they chose before it opens — and the
             stand-up now does exactly that, bigger and square, for the whole
             hold and tip-down. Keeping both put a 500ms dead stop in the
             middle of what is meant to be one motion (Daniel, 2026-09-03),
             and it was the single largest thing between the tip landing and
             the paper moving. Every other route in still gets its look. */
          const CLOSED_HOLD_MS = self._fromStanding ? 0 : 500;
          self._openHold = setTimeout(function () {
            self._openHold = null;
            if (!self.opening) return;   // torn down during the hold

            let last = -1, moved = Date.now();
            const guard = setInterval(function () {
              if (!self.opening) { clearInterval(guard); return; }
              if (vid.currentTime !== last) { last = vid.currentTime; moved = Date.now(); return; }
              if (Date.now() - moved > 1000 / Math.min(1, vid.playbackRate || 1)) {
                clearInterval(guard);
                self._giveUpOpening('stall');   // the decoder stalled: unfold anyway
              }
            }, 250);
            self._stallGuard = guard;

            vid.addEventListener('ended', function () { self.finishOpening(true); }, { once: true });
            try { vid.currentTime = 0; } catch (e) {}
            const p = vid.play();
            if (p && p.catch) p.catch(function () { self._giveUpOpening('play_refused'); });
            step();
            requestAnimationFrame(watch);
          }, CLOSED_HOLD_MS);
        };

        // ⚠️ Never play an unbuffered clip. canplaythrough IS the "buffered
        // enough to finish" signal; starting before it underruns mid-playback,
        // and because the card is driven from the video's presented frames the
        // card freezes with it and jumps on recovery. If it does not arrive,
        // skip the opening — the card is fully rendered either way, and a
        // stuttering first impression is worse than none.
        // ⚠️ Wait for the COVER ART too, not just the clip. CoverTop is only
        // revealed at frame 57; an undecoded cover pops in mid-unfold.
        const artPending = self.designChosen() && !self.designArtReady;
        /* A clip that is a blob is all on this device: HAVE_FUTURE_DATA (3) cannot underrun. Desktop Safari is slow
           to report 4 even then, and waiting for it is what tripped clip_late (18 Oct). */
        const clipReady = function () {
          return vid.readyState >= 4 || (vid.readyState >= 3 && /^blob:/.test(String(vid.currentSrc || vid.src || '')));
        };
        /* ⭐ PLAY THE DOWNLOADED CLIP, NEVER THE STREAMED ONE (18 Oct). Desktop Safari had the streamed <video> at
           readyState 4 already, so the gate committed on it, and partway through Safari stopped presenting its
           frames: the card froze (rVFC stopped) and, followed by currentTime, unfolded under a stale shading frame.
           The same opening on iPhone plays the blob prefetchOpenClip downloaded, and never stops (Daniel's two logs,
           an on-screen log). So when the blob is here, it is what plays: swapped in now, and the gate waits for it
           (a blob is ready at canplay, clipReady; ~50ms on the phone). */
        /* Safari's engine only (every iPhone browser, Safari on a Mac): Firefox given the blob here stalled at
           readyState 2 and never played (measured, 18 Oct), and Chrome and Firefox play the streamed clip correctly. */
        const blobU = self._openBlobUrl;
        if (IS_WEBKIT && blobU && String(vid.currentSrc || vid.src || '') !== blobU) {
          try { vid.src = blobU; vid.load(); } catch (e) { /* the streamed clip stands */ }
        }
        if (clipReady() && !artPending) commit();
        else {
          let settled = false;
          const tryCommit = function () {
            if (settled) return;
            if (!clipReady()) return;
            if (self.designChosen() && !self.designArtReady) return;
            settled = true; commit();
          };
          vid.addEventListener('canplaythrough', tryCommit, { once: true });
          vid.addEventListener('canplay', tryCommit, { once: true });     // enough for a blob (clipReady)
          const artWatch = self.$watch('designArtReady', tryCommit);
          void artWatch;
          // A re-opening from Preview finds the clip already decoded, and
          // canplaythrough will not fire again for it. Ask once, now.
          if (clipReady()) tryCommit();

          // ⚠️ FETCH THE CLIP OURSELVES, because iOS will not.
          //
          // The gate above is readyState 4 — "buffered enough to finish" —
          // and on a phone it never arrives. Safari does not honour
          // preload="auto"; it fetches metadata and then waits for a user
          // gesture, so the clip sits unbuffered, the two-second fallback
          // fires, and the opening is skipped every single time. Daniel, on
          // his iPhone: "it didn't show me the animation opening."
          //
          // Downloading the bytes ourselves and handing the element a blob
          // removes the race rather than working around it: once the data is
          // local, readyState 4 is immediate in every engine. It also makes
          // the artist's lockstep guarantee stronger, since the decoder can
          // never underrun mid-opening — which is the very thing the
          // canplaythrough gate was defending against.
          //
          // Costs nothing extra: this is the same file the element was
          // already told to fetch. On failure we simply leave the element's
          // own src alone and let the fallback do its job.
          /* ⭐ THE DOWNLOAD USUALLY STARTED LONG AGO — see prefetchOpenClip(),
             called at init. This is the same promise, and by the time the
             stand and the tip have played it has normally already resolved.
             Kept here as the cold path for any route that reaches the gate
             without init having run. */
          self.prefetchOpenClip().then(function (u) {
            if (!u || settled) return;
            if (vid.src !== u) { vid.src = u; vid.load(); }   // already handed over at prefetch: reloading resets it
            else tryCommit();
          });
          // ⚠️ THIS LINE CALLED giveUp(), WHICH WAS NEVER DEFINED ANYWHERE.
          // It is the whole reason the card was invisible on iPhone.
          //
          // The path: iOS does not preload video without a user gesture, so
          // readyState never reaches 4, so this fallback fires — and threw
          // ReferenceError instead of running. _openingDecided therefore
          // stayed false, camReady() stayed false, and .pp-zoom sat at
          // opacity: 0 with a perfectly built card behind it. Desktop never
          // saw it because the clip buffers long before two seconds are up.
          //
          // finishOpening(false) is what the comment above always intended:
          // skip the opening and show the card at rest. It is the same call
          // the stall guard and a refused play() already make, and it sets
          // _openingDecided itself — "however we got here, the card must be
          // visible".
          // ⚠️ THE CARD IS NOT VISIBLE WHILE THIS RUNS — camReady() is false
          // until something decides — so this timeout is a blank-stage window,
          // and every millisecond added here is a millisecond of nothing.
          // 3.5s buys the blob fetch above room to land on a slow connection
          // without making the wait feel broken; past that, no animation is
          // the better trade. Do not raise it further without moving the card
          // out from behind the gate first.
          /* ⛔ 3.5s IS THE ENTRY BUDGET, AND IT IS THE WRONG ONE ONCE THE CARD
             IS ALREADY ON SCREEN.

             The note above is about the ENTRY: camReady() is false, the stage
             is blank, and 3.5s buys the blob fetch room before giving up. That
             reasoning holds there and the number stays.

             It does not hold on the path from Preview. `_fromStanding` means
             the card has just been stood up and tipped — it is VISIBLE, at an
             angle, and the wait is not a blank stage but a frozen one. Yonatan,
             2026-09-07: it "gets stuck and doesn't open the card after the
             initial transition between standing and angle". Timed off his
             screen recording: the tip lands, then 3.5s of a motionless card,
             then it is simply open — no unfold at all, because this timeout is
             the thing that opened it.

             So the visitor paid three and a half seconds of frozen card for an
             animation that then did not play. A card that opens at once is
             better than that, and it is the same end state either way. 900ms
             still covers a clip that is nearly there; past that the honest
             answer is to show the card.

             ⚠️ The real fix is the one the note above asks for — move the card
             out from behind the gate so waiting costs nothing — and then this
             number stops mattering on both paths. */
          setTimeout(function () {
            if (!settled) { settled = true; self._giveUpOpening('clip_late'); }
          }, self._fromStanding ? 900 : 3500);
        }
      },

      /* ── PREVIEW: close and open the card (2026-09-03) ─────────────────
         Daniel: "add close button to editor to see the card in closed
         state." The opening plays the shading clip forward and maps its
         frames onto the geometry (idx 0 = closed, 100 = open). There is no
         reverse encode and Chrome does not honour a negative playbackRate,
         so the close SCRUBS: seek the clip back a few frames at a time and
         draw each frame as it lands. ~34 seeks on a 2000px clip is fine on a
         desktop; on a phone that is the thing to watch, and the stall guard
         below snaps the card shut rather than leaving it half-folded. */
      _shadeDraw(vid, idx) {
        const cvs = document.querySelector('[data-pp-shadecanvas]');
        if (!cvs || !vid || !vid.videoWidth) return;
        const cx = cvs.getContext('2d', { alpha: true });
        if (!cx) return;
        const W = cvs.width, H = cvs.height;
        if (!this._shadeEdges || this._shadeEdges.W !== W || this._shadeEdges.H !== H) {
          const B = Math.round(W * 72 / 2000);
          const mk = function (x0, y0, x1, y1) {
            const g = cx.createLinearGradient(x0, y0, x1, y1);
            g.addColorStop(0, 'rgba(0,0,0,1)'); g.addColorStop(1, 'rgba(0,0,0,0)');
            return g;
          };
          this._shadeEdges = { W: W, H: H, list: [
            { grad: mk(0, 0, B, 0), x: 0, y: 0, w: B, h: H },
            { grad: mk(W, 0, W - B, 0), x: W - B, y: 0, w: B, h: H },
            { grad: mk(0, 0, 0, B), x: 0, y: 0, w: W, h: B },
            { grad: mk(0, H, 0, H - B), x: 0, y: H - B, w: W, h: B },
          ] };
        }
        cx.globalCompositeOperation = 'source-over';
        cx.clearRect(0, 0, W, H);
        cx.drawImage(vid, 0, 0, W, H);
        cx.globalCompositeOperation = 'destination-out';
        this._shadeEdges.list.forEach(function (g) { cx.fillStyle = g.grad; cx.fillRect(g.x, g.y, g.w, g.h); });
        cx.globalCompositeOperation = 'source-over';
        this._drawnIdx = idx;
      },

      /* ── STANDING THE CLOSED CARD UP ───────────────────────────────────
         Daniel, 2026-09-03: "best would be for the closed big card to then
         'stand up' so the design isn't warped at all by the angle of the
         camera."

         The artist's camera looks at the card from below and to the front —
         at frame 100 its pitch is -69.4 while the cover's own is -91.8, so the
         closed cover is a keystone: measured, its top edge projects 17.9%
         wider than its bottom. Beautiful for a card in mid-air, wrong for
         looking at a design someone is about to buy.

         Square to the cover means one thing exactly: give the CAMERA the
         cover's pitch. The view matrix then cancels the plane's rotation and
         what is left is a flip in the plane itself — no tilt, no divide, a
         true rectangle. Measured after: top/bottom ratio 1.0000, and the box
         comes out at the panel's own 2760x3675 aspect.

         Pitch alone, and the camera ORBITS the cover to keep it centred: its
         offset from the cover is rotated by the same angle it turns through.
         Yaw and roll are left alone, so the card keeps the in-plane
         orientation it already had and nothing spins.

         Returns a shallow copy of the geometry carrying only a new camera —
         one keyframe, which is all frame 100 needs. `t` runs 0 (the artist's
         pose) to 1 (square). */
      _standGeom(t) {
        const g = this.geom;
        if (!g || !g.camera || !g.planes) return null;
        const F = '100';
        let cover = null;
        for (let i = 0; i < g.planes.length; i++) {
          if (g.planes[i].name === 'CoverFront') { cover = g.planes[i]; break; }
        }
        if (!cover) return null;
        const cardC = cover.position[F] || cover.position['0'];
        const planeO = cover.orientation[F] || cover.orientation['0'];
        const camP = g.camera.position[F], camO = g.camera.orientation[F], camZ = g.camera.zoom[F];
        if (!cardC || !planeO || !camP || !camO || !camZ) return null;

        const pitch = camO[0] + (planeO[0] - camO[0]) * t;
        const d = (pitch - camO[0]) * Math.PI / 180;
        const c = Math.cos(d), sn = Math.sin(d);
        const vx = camP[0] - cardC[0], vy = camP[1] - cardC[1], vz = camP[2] - cardC[2];
        // The orbit is about X, the only axis in play: the camera and the
        // cover differ in pitch and nothing else.
        const ny = c * vy - sn * vz;
        const nz = sn * vy + c * vz;
        return Object.assign({}, g, {
          camera: {
            position:    { 100: [cardC[0] + vx, cardC[1] + ny, cardC[2] + nz] },
            orientation: { 100: [pitch, camO[1], camO[2]] },
            zoom:        { 100: camZ },
          },
        });
      },

      // Where the camera would settle for a set of transforms, worked out
      // without moving the card to find out.
      _fitFor(t) {
        const boxes = [];
        Object.keys(t.planes).forEach(function (k) {
          if (t.planes[k].facing) boxes.push(t.planes[k].box);
        });
        return this._fit(boxes, FRAMING_PAD.card);
      },

      /* The camera, driven by hand for the length of the animation.

         Written ON .pp-zoom, which is the element the transform reads them
         from: a custom property set there shadows the one Alpine writes on
         .pp-viewport, so the two never fight and nothing has to be
         un-bound. The transition goes with it — 520ms of easing chasing a
         target that moves every frame is exactly the lag the scrub had. */
      // What the camera is showing RIGHT NOW, read off the element rather
      // than recomputed — the number in flight matters, because a section
      // change may still be gliding when the button is pressed.
      _camLive() {
        const z = document.querySelector('[data-pp-camera]');
        if (!z) return null;
        const cs = getComputedStyle(z);
        const x = parseFloat(cs.getPropertyValue('--pp-cam-x'));
        const y = parseFloat(cs.getPropertyValue('--pp-cam-y'));
        const scale = parseFloat(cs.getPropertyValue('--pp-cam-scale'));
        if (!isFinite(x) || !isFinite(y) || !isFinite(scale) || scale <= 0) return null;
        return { x: x, y: y, scale: scale };
      },

      _camDrive(p, smooth) {
        const z = document.querySelector('[data-pp-camera]');
        if (!z) return;
        // smooth=true lets the CSS transition on --pp-cam-* ease the move (the
        // preview zoom-IN); the default (and every fold frame) is instant.
        z.style.transition = smooth ? '' : 'none';
        z.style.setProperty('--pp-cam-x', p.x.toFixed(2) + 'px');
        z.style.setProperty('--pp-cam-y', p.y.toFixed(2) + 'px');
        z.style.setProperty('--pp-cam-scale', p.scale.toFixed(4));
      },

      /* And handed back. The value Alpine computes from the boxes now on
         screen IS the value sitting here — the animation ends on the fit it
         started from — so the handover is invisible. It still goes in three
         steps: bump, let Alpine write, then drop the local copies, and only
         then restore the transition. Dropping them first would expose one
         frame of the old inherited value. */
      _camRelease() {
        const z = document.querySelector('[data-pp-camera]');
        this.camNonce++;
        if (!z) return;
        requestAnimationFrame(function () {
          z.style.removeProperty('--pp-cam-x');
          z.style.removeProperty('--pp-cam-y');
          z.style.removeProperty('--pp-cam-scale');
          requestAnimationFrame(function () { z.style.removeProperty('transition'); });
        });
      },

      _previewAnim: null,

      /* ⛔ ONE LOOP, TWO BEATS, NO VIDEO. Closing: fold (geometry 0 -> 100)
         then stand (camera square to the cover, closing in). Opening: the
         same two, in the other order, at the same speed.

         Everything here is arithmetic on requestAnimationFrame. The first
         version scrubbed the shading clip backwards and waited on `seeked`
         events iOS never delivers, which is what left Daniel with "a few
         seconds of nothing, and then I see the card closed"; nothing in this
         path can block, and a frame the geometry refuses simply ends the
         animation at its destination rather than killing the loop. */
      _previewRun(dir, startEl) {
        const self = this;
        const CS = window.PP && window.PP.CardStage;
        const closing = dir === 'close';
        const base = startEl || 0;
        // Folding out of a look-close zoom: drop the zoom flag; the fold loop
        // owns the camera from here (its _camDrive overwrites the zoom vars).
        this.previewZoom = false;
        if (this._pzRaf) { cancelAnimationFrame(this._pzRaf); this._pzRaf = 0; }
        this._previewStop();

        const land = function () {
          self.closing = false;
          self.cardClosed = closing;
          self.cardFrame = closing ? 100 : 0;
          try { self.applyFrame(self.cardFrame, closing ? self._standGeom(1) : null); } catch (e) {}
          self._camRelease();
        };
        if (!this.geom || !CS) { land(); return; }

        // Both ends, before anything moves.
        let openFit, shutFit, standAt1;
        try {
          openFit = this._fitFor(CS.frameTransforms(this.geom, 0));
          standAt1 = this._standGeom(1);
          shutFit = this._fitFor(CS.frameTransforms(standAt1 || this.geom, 100));
        } catch (e) { land(); return; }
        if (!openFit || !shutFit) { land(); return; }

        /* ⛔ THE MOTION STARTS WHERE THE CAMERA IS, NOT WHERE IT OUGHT TO BE.
           Entering Preview re-frames on a 520ms CSS transition, and pressing
           Close inside that half-second used to snap the camera to the fit
           the fold was computed from — measured at 0.833 against a live
           0.862 on Chromium. Small, and exactly the class of thing Daniel
           means by a split-second apparition. */
        this.closing = true;
        this.cardClosed = false;       // mid-flight it is neither open nor closed
        const T = FOLD_MS + STAND_MS;
        const easeOut = function (u) { return 1 - Math.pow(1 - u, 3); };
        const easeInOut = function (u) {
          return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
        };
        let t0 = null;

        const step = function (now) {
          if (!self._previewAnim) return;
          // ⚠️ The clock starts on the FIRST FRAME. A rAF timestamp can precede
          // a performance.now() taken just before scheduling, and the negative
          // progress that produced asked the geometry for frame -1, which threw
          // and killed the loop — the iPhone's "nothing happens, then it is
          // shut" (2026-09-03).
          if (t0 === null) t0 = now;
          const el = Math.max(0, Math.min(T, base + (now - t0)));
          self._previewAnim.el = el;
          let frame, stand;
          if (closing) {
            if (el < FOLD_MS) { frame = Math.round(easeOut(el / FOLD_MS) * 100); stand = 0; }
            else { frame = 100; stand = easeInOut((el - FOLD_MS) / STAND_MS); }
          } else {
            if (el < STAND_MS) { frame = 100; stand = 1 - easeInOut(el / STAND_MS); }
            else { frame = Math.round(100 - easeOut((el - STAND_MS) / FOLD_MS) * 100); stand = 0; }
          }
          frame = Math.max(0, Math.min(100, frame));
          const g = stand > 0 ? (self._standGeom(stand) || self.geom) : self.geom;
          try {
            self.cardFrame = frame;
            // quiet while it runs; land() applies the resting frame for real.
            self.applyFrame(frame, stand > 0 ? g : null, el < T);
          } catch (e) { self._previewStop(); land(); return; }
          // `stand` runs 0->1 closing and 1->0 opening, so this is the
          // distance travelled in both directions.
          // Stand-based lerp: direction-agnostic, so a reversal is continuous.
          self._camDrive({
            x: openFit.x + (shutFit.x - openFit.x) * stand,
            y: openFit.y + (shutFit.y - openFit.y) * stand,
            scale: openFit.scale + (shutFit.scale - openFit.scale) * stand,
          });
          if (el >= T) { self._previewStop(); land(); return; }
          self._previewAnim.raf = requestAnimationFrame(step);
        };

        this._previewAnim = {
          raf: requestAnimationFrame(step),
          // A starved frame loop (a backgrounded tab) must still end somewhere.
          guard: setTimeout(function () { self._previewStop(); land(); }, (T - base) + 1500),
          dir: dir,
          el: base,
        };
      },

      _previewStop() {
        const a = this._previewAnim;
        this._previewAnim = null;
        if (!a) return;
        if (a.raf) cancelAnimationFrame(a.raf);
        if (a.guard) clearTimeout(a.guard);
      },

      /* ── ENTERING THE BUILDER: STAND, THEN OPEN ────────────────────────
         Daniel, 2026-09-03: "the big standing-to-open whole animation thing
         should happen when a user first enters the builder as well. At the
         moment it's small, 22.4 degrees to open, and I want it to be big,
         standing to fully open, like we did here for the preview."

         So the entry now begins where Preview ends: the closed card square to
         the camera and filling the screen. It holds for a beat, tips down
         into the artist's own closed pose — and hands over to HIS opening,
         shading clip and all. That handover is the whole reason this is a
         prefix rather than a copy of Preview's open: the clip is lit for his
         camera and can only play under it, and the opening is the one moment
         the card is a piece of paper rather than a picture of one.

         The camera is aimed at the OPENING's framing throughout the tip-down,
         which is where startOpening freezes it, so the two meet exactly and
         the inline camera stays put until finishOpening lets it go. */
      /* _primeOpenClip() lived here: it fetched the whole clip and swapped it
         in at the START of the entry, to spare the wait before playback. It
         is gone, and the reason is worth keeping.

         It never bought anything measurable — preload="auto" usually has the
         clip decoded already, so the gap before the paper moves was 20–150ms
         with or without it. What it DID buy was a stall: on a cold load the
         swap landed in the middle of the tip-down, and `vid.load()` on a
         2000x2000 clip blocked the main thread for ~180ms right at the end of
         the animation (measured in a real Chrome window, 2026-09-03).

         startOpening still fetches a blob when it needs one — as the fallback
         it always was, taken only if canplaythrough has not fired by the time
         the clip is wanted. Never eagerly, and never under an animation. */
      /* ⛔ EVERYTHING EXPENSIVE HAPPENS BEFORE THE CARD MOVES, NOT ON ITS
         FIRST FRAME.

         Measured under a 6x CPU throttle, the opening's first frame cost
         111–165ms in every run against a 13–18ms median — a freeze "always in
         the same spot, a little bit after the panels start opening" (Daniel,
         2026-09-03). Three things land on that one frame: a multi-megabyte
         canvas is allocated, the inside-top artwork is painted for the first
         time, and the four photographs are rastered as the folded stack is
         revealed. Two of them can simply be done earlier, during the hold and
         the tip-down, where nothing is moving and nobody can see the cost. */
      _sizeShadeCanvas(scale) {
        const cv = document.querySelector('[data-pp-shadecanvas]');
        if (!cv) return;
        const px = Math.min(SHADE_CANVAS_MAX,
          Math.round(compSpanCss() * scale * (window.devicePixelRatio || 1)));
        if (!px || cv.width === px) return;
        cv.width = px; cv.height = px;
        const c2 = cv.getContext('2d', { alpha: true });
        if (c2) c2.imageSmoothingQuality = 'high';
      },

      /* Decode every picture the card is about to show. An <img> inside a
         hidden plane has been FETCHED but not decoded, so the first frame that
         reveals it pays for that — and the inside-top artwork is revealed
         mid-unfold, at the same frame every time. decode() does the work off
         the critical path and says when it is finished; a failure is not worth
         blocking on. */
      _warmArt() {
        this.artWarm = false;
        const stage = document.querySelector('[data-pp-stage]');
        if (!stage) { this.artWarm = true; return; }
        const imgs = [].slice.call(stage.querySelectorAll('img'));
        const self = this;
        /* ⛔ EACH DECODE GETS 350ms, NO MORE (18 Oct). In Daniel's Safari some decode() never settled, so artWarm stayed
           false and the stand-up held the shut card for the whole 1.6 s cap every time: "a few seconds before the card
           starts opening", nothing moving (logged: smooth frames 88/3, pictures decoded false). Warming is a
           nicety; a picture that is slow here simply decodes when it is first shown. */
        const jobs = imgs.map(function (im) {
          if (!im.getAttribute('src')) return Promise.resolve();
          let d;
          try { d = im.decode ? im.decode().catch(function () {}) : Promise.resolve(); }
          catch (e) { d = Promise.resolve(); }
          return Promise.race([d, new Promise(function (r) { setTimeout(r, 350); })]);
        });
        Promise.all(jobs).then(function () { self.artWarm = true; },
                              function () { self.artWarm = true; });
      },

      /* And the video pipeline. The opening's first frame pays for the decoder
         starting AND for the first drawImage of a 2000x2000 clip into a
         canvas the size of the screen — a real resample, with the texture
         upload behind it. Both are done here against a card that is standing
         still: a play/pause primes the decoder, and one draw warms the
         resample path. The canvas is display:none until the opening, so none
         of it is visible, and commit() sets currentTime back to 0 before it
         plays for real. */
      /* WHICH ENCODE OF THE OPENING CLIP TO FETCH.
         Never fetch more pixels than we paint. The shading canvas has already
         been sized by _sizeShadeCanvas() before this runs, so the honest
         criterion is sitting on the element — no user-agent sniffing, and it
         follows a window resize or a DPR change for free.

         ⛔ THE THRESHOLD IS 1200, NOT 1000, AND THE DIFFERENCE IS EVERY MODERN
         iPHONE. At a 390pt viewport the card paints ~383 CSS px, which at
         devicePixelRatio 3 is a 1149px canvas — measured, not assumed. A 1000
         threshold sent exactly the phones this exists for back to the big
         file, which is the whole bug.

         The clip is 1000px and a 1149px canvas therefore upscales it by 15%.
         That costs nothing worth having: against the shipped clip at 1148px it
         scores SSIM 0.9986, where a 1200px encode scores 0.9987 for 32% more
         bytes. Measured 2026-09-11, both derived from the same pixels.

         ⚠️ Falls back to the big one whenever the canvas has not been sized
         yet (width 0), because guessing small and being wrong costs a desktop
         its sharpness, while guessing big only costs a phone the animation it
         was already missing. */
      _clipUrl(vid) {
        const big = vid.currentSrc || vid.src;
        const small = vid.getAttribute('data-pp-clip-sm');
        if (!small) return big;
        const cv = document.querySelector('[data-pp-shadecanvas]');
        const px = cv && (cv.width || +cv.dataset.px || 0);   // emptied after an opening: the size it had
        return (px && px <= 1200) ? small : big;
      },

      /* ⛔ FETCH THE OPENING CLIP AT PAGE LOAD, NOT AT THE OPENING.
         Yonatan, 2026-09-11, on his phone: the card jumps through three poses
         — shut and close, shut and far, open — instead of unfolding, and
         "only on the first time when entering". The second visit works
         because the clip is cached by then, which is the whole tell.

         Safari does not honour preload="auto", so on iOS NOTHING of this clip
         was downloaded until the gate in startOpening — the last possible
         moment, with 900ms allowed before the opening is abandoned. Measured
         on Chromium, which does honour it, the element asks at 664ms; iOS
         asked at roughly the moment the card had already stood up and tipped.

         So the fetch moves to init. Everything that happens between page load
         and the unfold — the geometry request, the stand, the tip — is time
         the bytes can be arriving in, and it is seconds rather than the 900ms
         the gate allows. Nothing else changes: the gate, the budget and the
         fallback all stay exactly as they were, and this simply means they
         are usually looking at a promise that has already resolved.

         ⚠️ THE SIZE IS CHOSEN FROM THE VIEWPORT HERE, NOT FROM THE CANVAS.
         _clipUrl() reads the shading canvas, which at init has not been sized
         yet and still holds a <canvas> default of 300 — that would have sent
         every desktop the phone clip. The bound used instead is the shorter
         viewport edge in device pixels: the card can never paint more than
         that, so it errs toward the big file and never toward a soft one.

         Idempotent: the first call owns the fetch, everyone after gets the
         same promise. */
      prefetchOpenClip() {
        if (this._openClipPromise) return this._openClipPromise;
        const self = this;
        const vid = document.querySelector('[data-pp-shadevid]');
        if (!vid || !window.fetch) return (this._openClipPromise = Promise.resolve(null));

        const small = vid.getAttribute('data-pp-clip-sm');
        const big = vid.getAttribute('src') || vid.currentSrc || vid.src;
        const bound = Math.min(window.innerWidth || 0, window.innerHeight || 0) *
                      (window.devicePixelRatio || 1);
        const url = (small && bound && bound <= 1200) ? small : big;
        if (!url) return (this._openClipPromise = Promise.resolve(null));

        this._openSrcFetched = true;    // the gate's old guard, kept honest
        this._openClipPromise = fetch(url)
          .then(function (r) { return r.ok ? r.blob() : null; })
          .then(function (b) {
            if (!b) return null;
            const u = URL.createObjectURL(b);
            self._openBlobUrl = u;
            /* ⛔ NOT HANDED TO THE ELEMENT HERE (tried 18 Oct, reverted the same day): a blob loaded early made
               Firefox drop the decoded clip (readyState 4 to 1) as the opening started, and the card unfolded
               without its shading. The handoff stays at the gate; the gate's clipReady is the Safari fix. */
            return u;
          })
          .catch(function () { return null; });   // the 900ms fallback still covers us
        return this._openClipPromise;
      },

      _warmClip() {
        const vid = document.querySelector('[data-pp-shadevid]');
        if (!vid || vid.readyState < 2) return;
        const self = this;
        try { self._shadeDraw(vid, 0); } catch (e) { /* not fatal */ }
        /* ⛔ THE PAUSE MUST CHECK WHO IS PLAYING. play() resolves on a later
           tick, and by then the opening may have started for real — pausing
           there would freeze the card mid-unfold, which is the exact bug this
           whole session is chasing. Only ever pause the warm-up's own play. */
        try {
          const stop = function () { if (!self.opening) { try { vid.pause(); } catch (e) {} } };
          const p = vid.play();
          if (p && p.then) p.then(stop, function () { /* autoplay refused; commit still tries */ });
          else stop();
        } catch (e) { /* not fatal */ }
      },

      openFromStanding(tries) {
        const CS = window.PP && window.PP.CardStage;
        const reduced = window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!this.geom || !CS || reduced) return this.startOpening();

        /* ⛔ NOTHING CAN BE FRAMED AGAINST A STAGE WITH NO SIZE. This runs off
           the geometry fetch, and the stage is x-show'd on `stage === builder`
           — Alpine applies that a tick later, so the first call here measured
           a 0x0 box and _fitU handed back the neutral camera. Everything then
           "worked" at scale 1: no stand, no growth, just the card sitting
           there (measured 2026-09-03, every trace row reading scale 1.000).
           Wait for a laid-out stage, briefly, then give up gracefully. */
        const st = document.querySelector('[data-pp-stage]');
        if (!st || !st.clientWidth || !st.clientHeight) {
          const n = (tries || 0) + 1;
          if (n > 40) return this.startOpening();
          const self0 = this;
          requestAnimationFrame(function () { self0.openFromStanding(n); });
          return;
        }

        const standGeom = this._standGeom(1);
        if (!standGeom) return this.startOpening();
        let shutFit, openTarget;
        try {
          shutFit = this._fitFor(CS.frameTransforms(standGeom, 100));
          const ob = this._openFraming();
          openTarget = ob ? this._fit([ob], FRAMING_PAD.card)
                          : this._fitFor(CS.frameTransforms(this.geom, 0));
        } catch (e) { return this.startOpening(); }
        if (!shutFit || !openTarget) return this.startOpening();

        const self = this;
        const go = function () {
          self._previewStop();
          self.closing = false;
          self._fromStanding = true;   // the opening skips its own closed hold
          self.startOpening();
        };

        // Stand it up, shut and big, and let it be seen.
        this.cardClosed = true;
        this.closing = true;           // holds --folded: no open-card shading still
        this.cardFrame = 100;
        try { this.applyFrame(100, standGeom); } catch (e) { return go(); }
        this._camDrive(shutFit);
        this._openingDecided = true;   // the card may be revealed now
        this.foldedInEditor = false;   // the ENTRY closing is not a user fold
        this.camNonce++;
        // The costs the opening's first frame used to carry.
        this._sizeShadeCanvas(openTarget.scale);
        this._warmArt();
        /* Safari: the downloaded clip goes in NOW, while the card stands shut, not at the opening's gate, where it had
           900ms to load (WebKit missed that once the stand-up stopped waiting 1.6s for decodes, 18 Oct). The gate
           still swaps it if the download lands later. Not Firefox: see startOpening. */
        try {
          const sv = document.querySelector('[data-pp-shadevid]');
          if (IS_WEBKIT && sv && this._openBlobUrl && String(sv.currentSrc || sv.src || '') !== this._openBlobUrl) {
            sv.src = this._openBlobUrl; sv.load();
          }
        } catch (e) { /* the gate swaps it */ }
        this._warmClip();
        /* ⛔ AND THE TOOLBAR IS BUILT NOW, NOT ON THE FIRST FRAME OF THE
           UNFOLD. decide() calls landInBuilder(), which moves the section to
           'photos' — a section change swaps whole x-if blocks in the dock, and
           Alpine renders all of it inside commit(), on the animation's very
           first frame. Done here it lands against a card that is standing
           still, and decide()'s own call then returns at its first guard
           because the section is no longer 'card'. */
        this.landInBuilder();

        /* ⛔ THE TIP MUST ARRIVE AT REST. IT IS A LANDING, NOT A HAND-OFF.

           This was `u²` for one round, on the theory that ending at full
           speed would flow into the unfold. It does the opposite: `u²` is at
           its QUICKEST at the very instant the rotation stops, so the card
           accelerated all the way down and then simply ceased turning —
           "it sort of snaps from one to the other" (Daniel, 2026-09-03).
           A stop reads as a collision; the unfold that follows is a different
           motion of a different part, and cannot absorb the tip's velocity.

           Smootherstep (6u⁵ - 15u⁴ + 10u³) is zero in BOTH the first and the
           second derivative at each end: it leaves the hold without a jolt
           and settles into the artist's pose without one either. A touch
           longer with it, because a deceleration has to be seen to read as a
           landing rather than as a stop.

           The unfold then starts at its own ceiling, immediately — the gap
           after the tip is 20–150ms — so the card settles and opens in one
           breath without the two motions fighting over the same instant. */
        /* ⛔ AND IT WILL NOT START UNTIL THE FRAMES ARE REAL.

           The tip begins in the busiest half-second the builder ever has: the
           customer's four photographs have just been handed over, and the
           browser is decoding them, writing them to IndexedDB and painting
           the stage for the first time. Measured in a real Chrome window,
           requestAnimationFrame was blocked for 663ms, 484ms, 217ms in that
           window — so a 430ms tip reached the screen as TWO camera writes,
           1.33 straight to 0.83, which is precisely the snap Daniel kept
           seeing while every headless measurement said the easing was fine.

           So the hold is a floor, not the whole wait: after it, the card
           stands there until three consecutive frames have arrived on time,
           and only then does it tip. A busy machine holds the closed card a
           little longer — which is no loss, since the card is SHUT and the
           photos are not on show yet — instead of dropping the animation.
           Capped, because a permanently busy thread must not park it. */
        const HOLD_MS = 130;           // long enough to read as an object
        const TIP_MS = 430;
        const FRAME_OK_MS = 32;        // two 60Hz frames: honest on a 30Hz panel too
        const HEALTHY_RUN = 3;
        const MAX_WAIT_MS = 1600;      // then tip regardless
        /* ⭐ AND NOT WHILE PHOTOS ARE STILL ARRIVING (Google Photos, 2026-09-29).
           The card stands shut with "Loading your pictures" in front until
           every placeholder has its real pixels — then the ordinary wait above
           runs, artWarm included, since _googleLanded re-warms the new images.
           Capped: a download that never lands must not park the card for good;
           the failure path takes the placeholders back out anyway. */
        const PHOTO_WAIT_MS = 30000;
        let tipStart = null, prevT = null, healthy = 0;
        const easeIn = function (u) { return u * u * u * (u * (u * 6 - 15) + 10); };
        let t0 = null;
        const step = function (now) {
          if (!self._previewAnim) return;
          if (t0 === null) t0 = now;
          const el = Math.max(0, now - t0);
          if (el < HOLD_MS) { self._previewAnim.raf = requestAnimationFrame(step); return; }
          if (tipStart === null && self.photosLoading() && el < PHOTO_WAIT_MS) {
            t0 = now - HOLD_MS;          // the frame-health wait starts when they land
            prevT = null; healthy = 0;
            self._previewAnim.raf = requestAnimationFrame(step);
            return;
          }
          if (tipStart === null) {
            if (prevT !== null && now - prevT <= FRAME_OK_MS) healthy++; else healthy = 0;
            prevT = now;
            if ((healthy < HEALTHY_RUN || !self.artWarm) && el < MAX_WAIT_MS) {
              self._previewAnim.raf = requestAnimationFrame(step);
              return;
            }
            tipStart = now;
          }
          const u = Math.min(1, (now - tipStart) / TIP_MS);
          const stand = 1 - easeIn(u);
          try {
            // quiet: the last frame publishes, and startOpening applies again
            self.applyFrame(100, stand > 0 ? (self._standGeom(stand) || self.geom) : null, u < 1);
          } catch (e) { go(); return; }
          self._camDrive({
            x: openTarget.x + (shutFit.x - openTarget.x) * stand,
            y: openTarget.y + (shutFit.y - openTarget.y) * stand,
            scale: openTarget.scale + (shutFit.scale - openTarget.scale) * stand,
          });
          if (u >= 1) { go(); return; }
          self._previewAnim.raf = requestAnimationFrame(step);
        };
        this._previewStop();
        /* The guard re-arms while photos are loading, or it would open the
           card on placeholders ~3.5s in, whatever step() is waiting for. */
        const armedAt = Date.now();
        const guard = function () {
          if (!self._previewAnim) return;
          if (self.photosLoading() && Date.now() - armedAt < PHOTO_WAIT_MS) {
            self._previewAnim.guard = setTimeout(guard, 500);
            return;
          }
          self._previewAnim.guard = setTimeout(go, HOLD_MS + MAX_WAIT_MS + TIP_MS + 1500);
        };
        this._previewAnim = {
          raf: requestAnimationFrame(step),
          guard: 0,
        };
        guard();
      },

      previewClose() { this._previewTo(true); },

      previewOpen() { this._previewTo(false); },

      /* ⛔ THE FIX FOR "OPEN LEAVES THE CARD STUCK CLOSED". Reverses an
         in-flight animation instead of ignoring the press: open(t) is
         close(T - t) (symmetric easings), so restarting the opposite
         direction at `T - currentElapsed` continues from the current pose
         with no jump — same animation, interruptible. */
      _previewTo(closed) {
        if (this.photosLoading()) return;   // see toggleFold
        const T = FOLD_MS + STAND_MS;
        const a = this._previewAnim;
        if (a) {
          if (a.dir === (closed ? 'close' : 'open')) return;
          this._previewRun(closed ? 'close' : 'open', T - (a.el || 0));
          return;
        }
        if (closed === this.cardClosed) return;
        this._previewRun(closed ? 'close' : 'open', 0);
      },

      /* The "See it closed" pill. Captures the open toolbar's width first — it
         is still on screen at this instant — so the shut bar can match it, and
         raises foldedInEditor so the dock swaps to "Open the card" at once. */
      foldCard() {
        if (this.opening) return;
        const tb = document.querySelector('.pp-tb');
        if (tb) this._dockW = Math.round(tb.clientWidth);
        // ⛔ CLOSE ANY OPEN TOOL FIRST (Daniel, 2026-09-03): folding with the
        // Border or Font popover open left it floating over the shut card.
        // The dock is x-show'd out while shut, but the state has to be cleared
        // too, or the popover springs back the moment the card reopens.
        this.cardTool = null;
        this.editSub = 'none';
        this.foldedInEditor = true;
        this.previewClose();
      },

      // The dock's "Open the card". Lowers the flag first, so the toolbar
      // returns as the card unfolds.
      unfoldCard() {
        if (this.opening) return;
        this.foldedInEditor = false;
        this.previewOpen();
      },

      /* Leaving Preview with the card shut used to leave it shut in the
         EDITOR, where nothing offers to open it again — "it goes back to the
         configurator with the closed card and no way to open" (Daniel,
         2026-09-03). Every road out of Preview comes through here, and it is
         instant on purpose: the customer asked for the editor, not for an
         animation on the way to it. */
      _restoreOpenPose() {
        this._previewStop();
        this.foldedInEditor = false;
        this.closing = false;
        /* ⛔ THE CAMERA IS PART OF THE POSE, AND IT IS RELEASED EITHER WAY.
           This used to sit after the early return below, so leaving Preview on
           an ALREADY-OPEN card — the ordinary case, and the only way this
           function is reached — cleared `previewZoom` and restored the toolbar
           while the inline --pp-cam-* variables stayed exactly where the zoom
           put them. The card stayed zoomed in with no way back: tapping zoom
           out did nothing (the flag was already false), and only closing and
           reopening the card fixed it, because the fold takes a different path
           that does release.

           Daniel, 2026-09-16, from a real iPhone: "I clicked Zoom Out, but it
           didn't zoom out… the only way for me to get rid of the bug was to
           close the card, then open it again."

           Reproduced 2026-09-16 on Draft: after leaving Preview the camera was
           still pinned at scale 1.9082, y -64.45px with previewZoom false. */
        this._camRelease();
        if (!this.cardClosed && this.cardFrame === 0) return;
        this.cardClosed = false;
        this.cardFrame = 0;
        try { this.applyFrame(0); } catch (e) {}
        this._camRelease();
      },

      /* ⛔ WHEN THE CLIP CANNOT DRIVE THE UNFOLD, THE GEOMETRY STILL CAN.
         Every route that gave up on the opening used to do the same thing —
         jump to the finished card. Clip arrived too late, play() refused, the
         decoder stalled: all of them, one frame, shut to open. That is the
         "three poses instead of an unfold" Yonatan reported on 2026-09-11, and
         chasing each cause in turn has cost this session four theories.

         So the failure mode changes instead. The card ALWAYS unfolds; what a
         failure costs is the artist's shading pass, not the animation. The
         geometry is ours and needs nothing from the network.

         ⚠️ WHAT IS LOST, HONESTLY: the shading canvas is the clip, so there is
         none — the card unfolds unlit, against .pp-zoom's own drop shadow. It
         is visibly cheaper than the real thing and it is incomparably better
         than a snap. Only the customers who are getting a snap today ever see
         it.

         ⛔ NOT FOR prefers-reduced-motion. That route goes through decide()
         and never reaches here, which is correct: somebody who asked for less
         motion is not being given some as a consolation prize.

         The camera needs no work — openFromStanding's tip has already put it
         at the open framing, and camNonce++ inside the opening is "the only
         camera write of the opening". This drives the paper and nothing else. */
      _unfoldWithoutClip() {
        if (this._fallbackUnfold) return true;       // already running
        const from = this.cardFrame;
        // Nothing meaningful left to travel: let the normal landing happen.
        if (!(from > 5)) return false;

        const self = this;
        this._fallbackUnfold = true;
        /* ⛔ THE UNFOLD RUNS ONLY WHILE `opening` IS UP (its step returns the moment it is not), and on the late-clip
           route it never went up: commit() is what raises it, and commit() is what did not happen. The card had just
           been stood up shut (openFromStanding: cardClosed, frame 100), the clip was not ready inside 900 ms, this was
           called, its first frame saw opening false and quietly stopped, and finishOpening never ran: the card stayed
           shut on Pictures for good, small and centred on its cover (Daniel, 11 Oct, a restored draft on desktop
           Chrome). Raise it here so the fallback actually unfolds and lands in finishOpening, like every other route. */
        if (!this.opening) {
          openBox = this._openFraming() || openBox;
          this.opening = true;
        }

        /* The canvas would otherwise hold whatever frame the clip last managed
           — a fragment of shading for one pose, smeared across every pose after
           it. Better nothing than a stale pass. */
        try {
          const cvs = document.querySelector('[data-pp-shadecanvas]');
          const cx = cvs && cvs.getContext('2d');
          if (cx) cx.clearRect(0, 0, cvs.width, cvs.height);
        } catch (e) { /* not fatal */ }

        /* Brisk on purpose. The real unfold is 3.37s because the artist's
           shading earns that time; an unlit one does not, and a long empty
           unfold reads as slow rather than as considered. */
        const T = 1200;
        const easeOut = function (u) { return 1 - Math.pow(1 - u, 3); };
        let t0 = null;

        const step = function (now) {
          if (!self.opening) { self._fallbackUnfold = false; return; }
          if (t0 === null) t0 = now;
          const u = Math.min(1, (now - t0) / T);
          const frame = Math.max(0, Math.round(from * (1 - easeOut(u))));
          if (frame !== self.cardFrame) {
            self.cardFrame = frame;
            try { self.applyFrame(frame, null, u < 1); } catch (e) {
              self._fallbackUnfold = false;
              self.finishOpening(false);
              return;
            }
          }
          if (u >= 1) {
            self._fallbackUnfold = false;
            self.finishOpening(false);
            return;
          }
          requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        return true;
      },

      /* One way out for every route that cannot use the clip, so a new one
         cannot be added that quietly snaps again.

         ⭐ AND IT REPORTS. Four theories about Yonatan's card died to guesswork
         this session because NOTHING measured the opening — there was no event
         for it at all, so how often the clip fails to drive the unfold was
         unknowable and stayed a matter of opinion. Now it is a number.

         Fires only when the clip could not drive the unfold, so at a healthy
         rate this event should be rare; against step 1 of the funnel it reads
         directly as "what fraction of builder entries lost the shading". */
      _giveUpOpening(reason) {
        const vid = document.querySelector('[data-pp-shadevid]');
        const src = String((vid && (vid.currentSrc || vid.src)) || '');
        track('pp_open_fallback', {
          reason: reason || 'unknown',
          ready: vid ? vid.readyState : -1,
          clip: /blob:/.test(src) ? 'blob' : (/-sm\./.test(src) ? 'small' : 'big'),
          frame: this.cardFrame
        });
        if (this._unfoldWithoutClip()) return;
        this.finishOpening(false);
      },

      /* ⭐ THE TOOLBAR'S ONE-TIME HINT (Daniel, 2026-09-20: "we see people
         missing it"). Three violet flashes on the bar and a "Start here"
         arrow above it, once per browser.

         ⛔ ARMED AFTER THE OPENING, NEVER DURING. The card unfolding is the
         thing we want looked at first, and two attention-grabs at once is
         neither. finishOpening() is the one place every route into an open
         card converges — a clean play, a refused play, and the stall guard —
         so arming there covers all three without a fourth flag.

         ⚠️ THE SEEN FLAG IS WRITTEN WHEN IT IS SHOWN, not when it is
         dismissed. Somebody who closes the tab mid-flash has had their one
         look; a hint that only retires on a tidy dismissal comes back forever
         for everyone who does not give it one. */
      _armToolbarHint() {
        if (this.tbHint) return;
        let seen = true;   /* fail CLOSED: a blocked store must not loop the hint */
        try { seen = localStorage.getItem(TB_HINT_KEY) === '1'; } catch (e) { seen = true; }
        if (seen) return;
        try { localStorage.setItem(TB_HINT_KEY, '1'); } catch (e) { /* private window */ }
        this.tbHint = true;
        const self = this;
        clearTimeout(this._tbHintT);
        this._tbHintT = setTimeout(function () { self.tbHint = false; }, TB_HINT_MS);
      },

      /* Any touch of the bar retires it early — they have found it, which is
         the whole point. Also the escape hatch if the timer never lands. */
      dismissToolbarHint() {
        if (!this.tbHint) return;
        clearTimeout(this._tbHintT);
        this.tbHint = false;
      },

      finishOpening(played) {
        // The hold may still be pending — finishing during it must not leave a
        // timer that starts playback on a card that is already open.
        if (this._openHold) { clearTimeout(this._openHold); this._openHold = null; }
        if (this._stallGuard) { clearInterval(this._stallGuard); this._stallGuard = null; }
        this.opening = false;
        // Reachable from the stall guard and from a refused play() as well as
        // from a clean end. However we got here, the card must be visible.
        this._openingDecided = true;
        // …and the card being visible is the moment the toolbar is worth
        // pointing at. Guarded internally, so calling it on all three routes
        // in is safe. See _armToolbarHint().
        try { this._armToolbarHint(); } catch (e) { /* a hint is never worth a throw */ }
        openBox = null;
        const vid = document.querySelector('[data-pp-shadevid]');
        if (vid) { try { vid.pause(); } catch (e) {} }
        /* ⛔ THE SHADING CANVAS GIVES BACK ITS BACKING STORE (13 Oct). openVideoStyle() hides it the moment the opening
           ends, and a hidden canvas still holds every pixel: up to SHADE_CANVAS_MAX squared, 37 MB of GPU memory, for as
           long as the builder is open, beside a card whose tiles already press the renderer's limit (the header and steps
           going unpainted, Daniel, 12-13 Oct; projects/pop-cards/output/2026-10-13-gpu-layers/REPORT.md). Two frames on,
           once the hide has painted, it is emptied; the next opening sizes it again during its hold (_sizeShadeCanvas),
           and _clipUrl still knows the size it had. */
        const self = this;
        requestAnimationFrame(function () { requestAnimationFrame(function () {
          const cv = document.querySelector('[data-pp-shadecanvas]');
          if (!cv || self.opening || !cv.width) return;
          cv.dataset.px = String(cv.width);
          cv.width = cv.height = 0;
          self._shadeEdges = null;
        }); });
        // Release the blob the opening fetched. The opening runs once per
        // builder entry, and startOpening fetches afresh each time, so
        // nothing will want these bytes again — and an unrevoked object URL
        // holds the whole clip in memory, which on the device this was all
        // fixed for is the last thing we want.
        /* The blob used to be revoked here. It is KEPT since 2026-09-03:
           Preview closes and re-opens the card from the same clip, and a
           revoked object URL cannot be seeked or replayed. It is ~550KB. */
        this.cardFrame = 0;
        this.cardClosed = false;
        // Calibration is skipped while the opening runs (openBox holds the
        // old scale's units), so a resize during those seconds lands here.
        this.calibrateStage();
        this.applyFrame(0);
        // `played` used to write sessionStorage `pp_opened_v2` so the opening
        // ran once per session. Nothing reads it now (2026-09-03 — the
        // opening plays on every entry); the parameter stays so the four
        // callers need not change.
        void played;
        // The rate was slewed across the opening for pacing; hand the element
        // back at 1x so nothing downstream inherits a half-sine.
        if (vid) { try { vid.playbackRate = 1; } catch (e) { /* not fatal */ } }
        // openFromStanding drove the camera by hand into this animation and
        // left it there; this is where Alpine takes it back. A no-op on every
        // other route in.
        this.cardClosed = false;
        this._fromStanding = false;
        this._camRelease();
        this.camNonce++;
        this.landInBuilder();
        this._foldNudgeArm();
      },

      /* THE TOGGLE ASKS FOR A LOOK, ONCE (Daniel, 2026-09-04: "making it more
         visible visually at the top — either bolder color or animation or
         both"). The 2026-09-03 tester never found Open/Closed. So after the
         card has finished opening and the customer has had a beat to look at
         it, the pill breathes a violet ring twice (stage.css). Once per
         builder entry, never under reduced motion, and never while the
         toggle is not on screen. */
      _foldNudgeArm() {
        if (this._foldNudged) return;
        this._foldNudged = true;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const self = this;
        setTimeout(function () {
          if (!self.showCardFold()) return;
          self._foldNudge = true;
          setTimeout(function () { self._foldNudge = false; }, 2700);
        }, 900);
      },

      // ⛔ ALWAYS ZOOMED OUT AFTER THE OPENING (Daniel, 2026-09-03: "it should
      // always be zoomed out after the opening animation"). This REVERSES the
      // 2026-08-16 call to open into Edit photos (and its 2026-08-29 restored-
      // card carve-out): the whole-card view is the hero now, the Open/Closed
      // toggle and the "Edit picture" hover labels make the next move obvious,
      // and dropping the customer straight into the strip of panels threw away
      // the moment they get to see the card they just watched fold open. So
      // landInBuilder no longer changes the section — the card stays on the
      // zoomed-out whole card, for new and returning customers alike.
      //
      // ⚠️ Kept as a method (called from finishOpening and the decide() skip
      // routes) and still guarded so a late geometry load or resize re-entry
      // cannot yank someone out of a note or a selected picture: it simply
      // does nothing now, which is exactly "leave the card as it is".
      landInBuilder() {
        if (this.stage !== 'builder') return;
        if (this.section !== 'card' || this.selectedPhoto !== null) return;
        // Stay put — the zoomed-out whole card is the landing.
      },


      /* ── THE KEYBOARD, AS ONE IDEA ───────────────────────────────────────
         Whenever anything covers part of the page, THE APP SHELL IS THE
         VISIBLE RECTANGLE. Not the layout viewport with corrections applied to it —
         the visible rectangle itself. Everything else falls out of that:

           · the toolbar's `bottom: 0` is the bottom of what you can see,
             because the shell's transform makes it the containing block for
             a position:fixed child (iOS otherwise fixes it to the LAYOUT
             viewport, which the keyboard covers — that is why Done used to
             disappear behind the keys);
           · the stage is shorter, so the camera frames the card into the
             space above the keyboard with no keyboard term in uiInset();
           · and iOS has no reason to pan, because the field it wants to
             reveal is already above the keys.

         kbBoxH is simply visualViewport.height whenever that is smaller than
         the layout viewport — no "is the keyboard open" test, because the
         keyboard and its accessory bar arrive separately and any threshold
         over their sum is a coin toss (see kbWatch). The shell follows it
         immediately; the CAMERA is debounced to one re-frame once it settles.
         kbBoxTop follows any pan iOS applies anyway, and deliberately does
         NOT bump the camera: it moves a transform, nothing more. */
      kbBoxH: 0,
      kbBoxTop: 0,

      /* ── THE BUILDER DOES NOT SCROLL ─────────────────────────────────────
         Daniel, 2026-08-30: "I don't want any scroll up and down on the page
         to affect the size, zoom in or out, etc., of the card or the toolbar
         at all in any way."

         The straight way to guarantee that is to leave nothing to scroll. The
         builder is one screen — card, toolbar, done — and every symptom in
         that report is downstream of the document being scrollable on iOS:
         the URL bar collapses, the layout viewport changes height under a
         position:fixed toolbar, and the visual viewport slides around
         underneath the whole thing.

         The ENTRY screens are a different animal — the occasion picker is
         1650px of tiles and has to scroll — so the lock is tied to the stage
         rather than to the page. .pp-builder-panels (the Send address form)
         scrolls itself; see builder-chrome.css. */
      applyScrollLock() {
        const on = this.stage === 'builder';
        document.documentElement.classList.toggle('pp-locked', on);
        if (on) window.scrollTo(0, 0);   // whatever the entry step left behind
        /* THE LOCK HOLDS (Daniel, 12 + 13 Oct, on Live and Draft: "the bar disappeared", the header and steps gone and the
           bar pushed below the window). overflow:hidden stops the wheel, not a script: a focus() or scrollIntoView
           anywhere in the builder could still scroll the page or its containers by the height of the header and steps,
           and nothing scrolled them back. While the builder is up, nothing at page level may scroll: any scroll of the
           page, body or the builder's containers goes straight back to 0. Not while a field is being typed in on a
           phone (iOS scrolls the page to show the caret; see the keyboard latch), and the Send page's panels scroll
           themselves, below this. */
        if (on && !window.__ppLockGuard) {
          window.__ppLockGuard = true;
          const typing = () => { const a = document.activeElement; return !!(a && /^(INPUT|TEXTAREA)$/.test(a.tagName) && matchMedia('(hover: none)').matches); };
          const PAGE = ['config-main'];
          document.addEventListener('scroll', (e) => {
            if (!document.documentElement.classList.contains('pp-locked') || typing()) return;
            const t = e.target;
            if (t === document || t === document.documentElement || t === document.body) {
              if (window.scrollY || window.scrollX) window.scrollTo(0, 0);
              if (document.body.scrollTop) document.body.scrollTop = 0;
            } else if (t && t.nodeType === 1 && (PAGE.includes(t.id) || t.classList.contains('pp-config-app__inner') || t.classList.contains('shopify-section'))) {
              if (t.scrollTop) t.scrollTop = 0;
              if (t.scrollLeft) t.scrollLeft = 0;
            }
          }, true);
          /* The card's own box (.pp-viewport) is scrolled by the browser to show a field typed in on a plane, while the
             guard above stands aside for the typing. When the field lets go, it goes back to 0 (19 Oct, an iPhone: the
             card stayed 96 px off centre). stage.css makes it overflow: clip where the browser has it; this is for the
             rest. */
          document.addEventListener('focusout', () => {
            setTimeout(() => {
              document.querySelectorAll('.pp-viewport').forEach((v) => { if (v.scrollLeft) v.scrollLeft = 0; if (v.scrollTop) v.scrollTop = 0; });
            }, 0);
          }, true);
        }
      },

      /* ── "You've got a card on the go" ───────────────────────────────
         Raised by init when the arrival says something other than resume —
         see the note there. switchTo is the occasion the new card would be,
         or null for a plain new card from the secondary CTA. */
      switchAsk: false,
      switchTo: null,

      /* What the customer would be giving up, in their words rather than
         ours. Named so the dialog can be specific — "4 pictures and a note"
         is a reason to think; "your work" is not. */
      draftSummary() {
        const n = this.photos.length;
        const bits = [];
        if (n) bits.push(n + (n === 1 ? ' picture' : ' pictures'));
        if ((this.card.bottomNote || '').trim()) bits.push('a note');
        const caps = (this.card.captions || []).filter(function (s) { return s && s.trim(); }).length;
        if (caps) bits.push(caps === 1 ? 'a caption' : caps + ' captions');
        if (!bits.length) return '';
        if (bits.length === 1) return bits[0];
        return bits.slice(0, -1).join(', ') + ' and ' + bits[bits.length - 1];
      },

      // The label for the card they would be starting, when the arrival named
      // one. occasionLabel() reads this.occasion, which is still the old card's.
      switchToLabel() {
        return this.switchTo ? this.occasionLabelOf(this.switchTo) : '';
      },

      /* Keep what is on the device. The params are stripped so a refresh does
         not ask the same question twice, and so a shared or bookmarked link
         stops carrying an intent that has already been answered. */
      keepThisCard() {
        this.switchAsk = false;
        this.switchTo = null;
        try {
          const u = new URL(window.location.href);
          u.searchParams.delete('occasion');
          u.searchParams.delete('new');
          u.searchParams.delete('design');   // the card kept is not the one the link named (a set's link asks on it)
          window.history.replaceState({}, '', u.pathname + u.search + u.hash);
        } catch (e) { /* history is best-effort */ }
        track('pp_draft_kept', {});
      },

      /* Let it go and start over. A RELOAD, not a reset in place: the builder
         holds a lot of derived state — camera, geometry frame, IndexedDB
         handles, the opening's session flag — and clearing a draft underneath
         all of it is a much wider change than starting from a clean document.
         The draft is gone before we navigate, so the new page finds nothing. */
      startNewCard() {
        track('pp_draft_discarded', { photos: this.photos.length });
        clearDraft();
        this._draftClosed = true;          // nothing may re-save on the way out
        const occ = this.switchTo;
        const base = window.location.pathname;
        /* The design and the occasion it was opened for come along: "Make this card" on All cards, or "Start a
           new card" on a design's page, names a design (?design=, ?for=), and dropping them opened the occasion's
           first design instead of the one the customer picked. ?view= too: the builder's direction B is the
           template ?view=builder-b, and a new card started there must not drop the tester back into today's. */
        const q = new URLSearchParams();
        if (occ) q.set('occasion', occ);
        try {
          const here = new URLSearchParams(window.location.search);
          ['design', 'for', 'view'].forEach(function (k) { if (here.get(k)) q.set(k, here.get(k)); });
        } catch (e) { /* the occasion alone, as before */ }
        const qs = q.toString();
        window.location.href = qs ? base + '?' + qs : base;
      },

      // Hidden until the geometry has loaded and the camera has framed once.
      camReady() {
        void this.camNonce; void this._openingDecided;
        return !!this.frameBoxes && this._openingDecided;
      },

      // Pick the layout scale for this stage and screen — see calibrate() in
      // card-stage.js for the two constraints it balances. Returns whether
      // the scale moved, so the caller knows the planes need re-applying.
      // The stage can be unmeasurable for a tick while hidden; the viewport
      // is the honest stand-in, and the resize path corrects any drift.
      // Attempts so far, and whether we have given up and told the customer.
      _geomTries: 0,
      geomFailed: false,
      geomRetrying: false,

      loadGeometry() {
        const self = this;
        if (!window.PP_GEOMETRY_URL) { this.geomFailed = true; return; }
        this.geomRetrying = true;
        this._geomTries++;
        fetch(window.PP_GEOMETRY_URL, { cache: 'reload' })
          .then(function (r) {
            // ⚠️ A 404 or a 502 resolves the promise — only the NETWORK layer
            // rejects. Without this an error page was parsed as JSON, threw,
            // and landed in the same silent catch.
            if (!r.ok) throw new Error('geometry_' + r.status);
            return r.json();
          })
          .then(function (g) {
            if (!g || !g.planes || !g.camera) throw new Error('geometry_malformed');
            self.geom = g;
            self.geomFailed = false;
            self.geomRetrying = false;
            self.$nextTick(function () {
              // Calibrate before the first apply, so the planes are never
              // rastered once at the default scale and again at the real one.
              self.calibrateStage();
              self.applyFrame(self.cardFrame);
              // ⛔ camNonce++, NOT bumpCam(). bumpCam is a local inside init()
              // and this is a METHOD — calling it here threw a ReferenceError
              // that took the REST of this callback with it, so applyFrame had
              // run (frameBoxes was set) but startOpening never did, and the
              // card stayed at opacity 0 after a successful retry. Caught
              // while testing the retry, 2026-08-29. The bump is all that was
              // wanted: bumpCam's extra work is for resize, not for this.
              self.camNonce++;
              if (self.stage === 'builder') self.openFromStanding();
              else self.$watch('stage', function (v) { if (v === 'builder') self.openFromStanding(); });
            });
          })
          .catch(function (e) {
            if (window.console) console.error('card geometry failed to load', e);
            track('pp_geometry_failed', { attempt: self._geomTries, reason: String(e && e.message || e) });
            // Two automatic goes, backing off — a dropped request usually
            // succeeds on the next one and the customer never knows. After
            // that it is not a blip and pretending otherwise just leaves them
            // looking at nothing.
            if (self._geomTries < 3) {
              setTimeout(function () { self.loadGeometry(); }, self._geomTries * 700);
              return;
            }
            self.geomRetrying = false;
            self.geomFailed = true;
          });
      },

      // The customer's own retry. Starts the count again, because they have
      // waited and may well be on a different connection by now.
      retryGeometry() {
        this._geomTries = 0;
        this.geomFailed = false;
        this.loadGeometry();
      },

      calibrateStage() {
        const CS = window.PP && window.PP.CardStage;
        if (!CS || !CS.calibrate || !this.geom) return false;
        const stage = document.querySelector('[data-pp-stage]');
        const w = (stage && stage.clientWidth) || window.innerWidth;
        const h = (stage && stage.clientHeight) || window.innerHeight;
        return CS.calibrate(this.geom, w, h, window.devicePixelRatio || 1);
      },

      // Place the seven planes for a frame of the artist's animation, and
      // record where each landed. card-stage.js also decides, per plane and
      // per frame, whether it faces the camera at all — a back-facing plane
      // gets display:none there, which is the artist's "if its back is to the
      // camera it is simply transparent".
      /* `geomOverride` is how Preview stands the card up: a shallow copy of
         the geometry carrying a different CAMERA, nothing else. frameTransforms
         reads the camera per frame, so a copy with one keyframe is a complete
         answer for that frame, and every consumer downstream — boxes, framing,
         culling — stays exactly as it is. See _standGeom(). */
      /* `quiet` is for ANIMATION FRAMES, and it is the difference between 60fps
         and 10 on Chrome.

         Publishing frameBoxes is a reactive write, and the effects behind it
         end in uiInset(), which calls getBoundingClientRect on the stage and
         on every [data-pp-ui] element. Doing that straight after writing the
         transforms of twenty-two planes forces a synchronous layout of the
         whole 3D scene, once per frame. WebKit shrugged it off; Chrome spent
         ~100ms a frame on it, so the 430ms tip-down reached the screen as
         FIVE steps and read as a snap — "it still snaps for me when I look on
         my desktop in Chrome" (Daniel, 2026-09-03), measured at 7 camera
         writes against WebKit's 27.

         Nothing reads frameBoxes during an animation that drives the camera
         itself, so the publish waits for the last frame. Never pass `quiet`
         for a frame that is a resting state. */
      applyFrame(frame, geomOverride, quiet) {
        const geom = geomOverride || this.geom;
        if (!geom || !window.PP || !window.PP.CardStage) return;
        const stage = document.querySelector('[data-pp-stage]');
        if (!stage) return;
        const t = window.PP.CardStage.apply(stage, geom, frame);
        const boxes = {};
        Object.keys(t.planes).forEach(function (k) {
          // Only planes that are actually drawn may influence the framing —
          // a culled plane is not on screen and must not pull the camera.
          if (t.planes[k].facing) boxes[k] = t.planes[k].box;
        });
        if (quiet) return;
        this.frameBoxes = boxes;
        // Not while Preview is animating: it drives the camera itself, frame
        // by frame, and a bump per frame is 60 reactive recomputes a second
        // whose only effect would be to write values this shadows anyway.
        if (!this.closing) this.camNonce++;
      },

      // ── The camera ────────────────────────────────────────────────────
      //
      // Four framings, one per thing the customer can be doing (Daniel,
      // 2026-08-05):
      //
      //   card    the WHOLE card, clear of the toolbar. The landing state.
      //   photos  the pictures brought up close — as many as fit, and a
      //           window onto them when they don't.
      //   photo   the one picture being edited.
      //   note    CoverBottom, which reads as the card sliding up off the top
      //           of the screen to reveal its bottom.
      //
      // Still a pure function of (geometry, frame, viewport, UI band). It
      // measures no element that the camera transform itself moves, so it
      // converges in one step and can be recomputed mid-transition.
      cameraTarget() {
        const stage = document.querySelector('[data-pp-stage]');
        if (!stage || !this.frameBoxes) return NEUTRAL_CAM;
        // ⚠️ FROZEN DURING THE OPENING. The camera must not follow the card as
        // it unfolds: framing from a running maximum of the card's extent
        // kinks wherever the two curves cross (measured once at 91x the median
        // jerk, at one specific frame), and the whole scene snaps. The artist's
        // own camera move is already baked into the geometry we project from,
        // so ours holds still and his is the only motion on screen.
        if ((this.opening || this.closing) && openBox) return this._fit([openBox], FRAMING_PAD.card);
        const mode = this.camMode();
        if (mode === 'photos') {
          const w = this.photoViewport();
          if (w) return this._fitU(w, FRAMING_PAD.photos);
        }
        const cam = this._fit(this.framingBoxes(mode), FRAMING_PAD[mode]);

        /* ── WRITING A CAPTION: SAME ZOOM, JUST LOOK LOWER ────────────────
           Framing the panel instead of the picture would bring the caption
           band into view by ZOOMING OUT, and on a phone that made the thing
           being typed smaller at the exact moment it needed to be bigger:
           "it shows the full photo and the text for the caption is very
           small ... let's keep the zoom the same and just raise the pictures
           up" (Daniel, 2026-08-29).

           So the scale is untouched and only the pan moves — the picture
           slides up by most of the caption band's own height, which puts the
           band where the picture's lower third was. */
        if (mode === 'photos' && this.selectedPhoto !== null &&
            this.editSub === 'caption' && this.captionsOn()) {
          const b = this.frameBoxes && this.frameBoxes[PANELS[this.selectedPhoto]];
          if (b) {
            // The band is a fixed share of the panel — --pp-caption-h, which
            // the border stop sets — so its height in projected units is that
            // share of the panel's box, times the live camera scale.
            const share = wellPct(this.borderStop(), this.captionsOn()).band / 100;
            return { x: cam.x, y: cam.y - b.h * share * cam.scale * CAPTION_LIFT, scale: cam.scale };
          }
        }
        return cam;
      },

      // What the camera is looking at right now.
      //
      // ⛔ SELECTING A PICTURE DOES NOT MOVE THE CAMERA WHEN ALL FOUR ARE
      // ALREADY ON SCREEN (Daniel, 2026-08-16). On a desktop the whole strip
      // is in view in Edit photos, so flying in to one panel takes away the
      // three the customer was comparing it against, and every click costs a
      // 520ms move to see something they could already see.
      //
      // The test is photosPerPage(), not a breakpoint: it derives from the
      // panels' own projected width, so a half-width window behaves like a
      // phone because it IS one, and the rule reads the same on both — move
      // only when there is something the customer cannot currently see.
      camMode() {
        // ⛔ THERE IS NO PER-PICTURE FRAMING ANY MORE (Daniel, 2026-08-29:
        // "it shouldn't be a swipe; it should be more like scrolling. The
        // motion should be fluid and not snappy between pictures").
        //
        // Selecting a picture used to switch the camera to a box around THAT
        // panel whenever fewer than four fitted — which is always, on a phone
        // — so moving between pictures could only ever be a jump from one box
        // to the next. The strip window is continuous by construction:
        // photoViewport() slides it along photoScroll, so the same drag that
        // scrolls with nothing selected scrolls with something selected.
        // On a phone the window is one picture wide, which looks identical
        // standing still and completely different moving.
        if (this.selectedPhoto !== null) return 'photos';
        if (this.section === 'photos') return 'photos';
        if (this.section === 'bottom') return 'note';
        if (this.section === 'inside') return 'top';
        return 'card';
      },

      // The planes a framing has to hold. Falls back to the whole card
      // whenever the named planes aren't drawn on this frame — a framing with
      // nothing in it would otherwise divide by zero and lose the card.
      framingBoxes(mode) {
        const b = this.frameBoxes || {};
        if (mode === 'note' && b.CoverBottom) return [b.CoverBottom];
        if (mode === 'top' && b.CoverTop) return [b.CoverTop];
        return Object.keys(b).map(function (k) { return b[k]; });
      },

      // Fit a set of projected boxes into the space actually visible, and
      // centre them IN THAT SPACE — not in the stage.
      //
      // The difference is the toolbar. It floats over the bottom of the stage,
      // so fitting the card to the stage's full height put its bottom edge
      // underneath the buttons; you could not see the whole card in the view
      // whose entire job is showing you the whole card.
      _fit(boxes, pad) {
        const stage = document.querySelector('[data-pp-stage]');
        if (!stage) return NEUTRAL_CAM;
        const sw = stage.clientWidth, sh = stage.clientHeight;
        if (!sw || !sh) return NEUTRAL_CAM;
        const u = union(boxes);
        if (!u) return NEUTRAL_CAM;
        return this._fitU(u, pad);
      },

      // The same fit, given the rect directly. Edit pictures needs this: its
      // window is not a set of planes but a slice of the strip that can sit
      // anywhere along it, including halfway across a fold.
      _fitU(u, pad) {
        const stage = document.querySelector('[data-pp-stage]');
        if (!stage || !u) return NEUTRAL_CAM;
        const sw = stage.clientWidth, sh = stage.clientHeight;
        if (!sw || !sh) return NEUTRAL_CAM;
        const inset = this.uiInset();
        const vh = Math.max(120, sh - inset);
        const scale = Math.min(sw / u.w, vh / u.h) * (pad || 0.88);
        // The visible box is the stage minus the UI band at its foot, so its
        // centre sits inset/2 above the stage's own centre — which is where
        // .pp-zoom's transform-origin is.
        return { x: -u.cx * scale, y: -u.cy * scale - inset / 2, scale: scale };
      },

      // Height of the band at the foot of the stage covered by floating UI —
      // the toolbar, and whichever drawer is open above it.
      //
      // Measured, not hardcoded: the drawers are different heights and the
      // toolbar's own height moves with the font. Safe to measure because none
      // of these elements is inside .pp-zoom, so nothing here is affected by
      // the transform this feeds — no feedback path.
      uiInset() {
        void this.camNonce; void this.section; void this.selectedPhoto; void this.editSub;
        const stage = document.querySelector('[data-pp-stage]');
        if (!stage) return 0;
        const s = stage.getBoundingClientRect();
        let inset = 0;
        const els = document.querySelectorAll('[data-pp-ui]');
        for (let i = 0; i < els.length; i++) {
          const r = els[i].getBoundingClientRect();
          if (!r.height) continue;          // x-show'd out: display:none, height 0
          inset = Math.max(inset, s.bottom - r.top);
        }
        // ⛔ AND *NOT* THE KEYBOARD. This measures the band at the foot of the
        // STAGE, and while the keyboard is open the stage is already only as
        // tall as the space above it — kbBoxStyle() shrinks the whole app to
        // the visible rectangle, so there is nothing left here to subtract.
        //
        // ⛔ TWICE WRONG BEFORE, IN OPPOSITE DIRECTIONS.
        //   · It read the visual viewport live, INCLUDING offsetTop — which is
        //     the visual viewport's SCROLL, so the framing was a function of
        //     scroll position. With a caption selected the scale ran 2.835 →
        //     1.717 on that alone (measured 2026-08-30).
        //   · Then it added a latched keyboard height instead — while the app
        //     was still full-height AND the toolbar was separately translated
        //     up AND iOS was panning the visual viewport on its own. Three
        //     compensations for one keyboard: the card was pushed off the top
        //     of the screen and the toolbar landed in the middle of it.
        //     "What is this abomination?" (Daniel, 2026-08-30). Fair.
        //
        // One mechanism now, and it is not this one.
        return Math.max(0, Math.min(inset, s.height * 0.65));
      },

      // ── Edit pictures: how many fit, and which ones are on screen ─────
      //
      // Not a breakpoint. The answer comes from the panels' own projected
      // width against the space actually visible, so it is right on a phone
      // held either way round and on a window someone is dragging.
      photosPerPage() {
        void this.camNonce;
        const b = this.frameBoxes;
        const stage = document.querySelector('[data-pp-stage]');
        if (!b || !stage) return PANELS.length;
        const sw = stage.clientWidth;
        const sh = Math.max(120, stage.clientHeight - this.uiInset());
        if (!sw) return PANELS.length;
        const counts = [4, 2, 1];
        for (let c = 0; c < counts.length; c++) {
          const n = counts[c];
          const group = [];
          for (let i = 0; i < n; i++) if (b[PANELS[i]]) group.push(b[PANELS[i]]);
          const u = union(group);
          if (!u) continue;
          const scale = Math.min(sw / u.w, sh / u.h) * FRAMING_PAD.photos;
          if (n === 1 || group[0].w * scale >= MIN_PICTURE_PX) return n;
        }
        return 1;
      },

      // ── The strip, and the window sliding along it ────────────────────
      //
      // The four pictures are ONE strip of paper, so scrolling them is not a
      // carousel of pages — it is a window of fixed size sliding along a fixed
      // strip. That is what makes a trackpad scroll continuous (Daniel,
      // 2026-08-05): every position between two pictures is a real position,
      // and the arrows are just the same window moved a whole width at once.
      //
      // The window's SIZE never changes as it slides, which is what keeps the
      // scale constant while scrolling — a window sized from whichever panels
      // happen to be under it would breathe in and out as you scrolled,
      // because the outer panels project wider than the inner ones.
      photoStrip() {
        const b = this.frameBoxes;
        if (!b) return null;
        const strip = union(PANELS.map(function (n) { return b[n]; }));
        if (!strip) return null;
        const per = this.photosPerPage();
        const win = union(PANELS.slice(0, per).map(function (n) { return b[n]; }));
        if (!win) return null;
        return { strip: strip, winW: win.w, travel: Math.max(0, strip.w - win.w) };
      },

      // Where the window is now, as a rect to frame.
      photoViewport() {
        const s = this.photoStrip();
        if (!s) return null;
        const t = Math.max(0, Math.min(1, this.photoScroll || 0));
        return {
          cx: s.strip.cx - s.travel / 2 + t * s.travel,
          cy: s.strip.cy,
          w: s.winW,
          h: s.strip.h,
        };
      },

      // Is there any strip left to scroll to on this side?
      canPagePhotos() {
        const s = this.photoStrip();
        return this.camMode() === 'photos' && !!s && s.travel > 1;
      },
      hasPrevPhotos() { return this.canPagePhotos() && this.photoScroll > 0.002; },
      hasNextPhotos() { return this.canPagePhotos() && this.photoScroll < 0.998; },

      /* ── THE SELECTION FOLLOWS THE SCROLL ────────────────────────────
         With a continuous strip there is no "step to the next picture" any
         more, so the selection cannot be moved by an arrow. It has to move
         by itself: whatever picture the window is looking at is the one the
         toolbar acts on.

         Only ever REASSIGNS a selection — it never creates one. Scrolling
         with nothing selected has to stay a plain look-around, or the
         picture toolbar would appear the moment the strip drifted.

         Nearest by projected centre, not by index: the four panels are not
         evenly spaced on screen. */
      syncSelectionToScroll() {
        if (this.selectedPhoto === null) return;
        const v = this.photoViewport();
        const b = this.frameBoxes;
        if (!v || !b) return;
        let best = this.selectedPhoto, bestD = Infinity;
        for (let i = 0; i < this.photos.length; i++) {
          const box = b[PANELS[i]];
          if (!box) continue;
          const d = Math.abs(box.cx - v.cx);
          if (d < bestD) { bestD = d; best = i; }
        }
        if (best === this.selectedPhoto) return;
        this.selectedPhoto = best;
        // The open tool belongs to the picture it was opened on. Carrying
        // Crop across to a picture the customer only scrolled past would
        // start editing something they never chose.
        if (this.editSub === 'crop') this.editSub = null;
      },

      // Move the window along the strip by dx SCREEN pixels. Screen pixels,
      // not fractions: a scroll should move the pictures exactly as far as the
      // fingers moved, at any zoom, which means dividing by the live scale.
      scrollPhotosBy(dx) {
        const s = this.photoStrip();
        if (!s || !s.travel) return;
        const scale = this.cameraTarget().scale || 1;
        const next = (this.photoScroll || 0) + (dx / scale) / s.travel;
        this.photoScroll = Math.max(0, Math.min(1, next));
        this.syncSelectionToScroll();
      },

      // The arrows: one window's width, and let the 520ms transition glide it.
      pagePhotos(dir) {
        const s = this.photoStrip();
        if (!s || !s.travel) return;
        const step = s.winW / s.travel;
        this.photoScroll = Math.max(0, Math.min(1, (this.photoScroll || 0) + dir * step));
        this.syncSelectionToScroll();
      },

      // Hold the camera transition off while something is actively driving it.
      // Released shortly after the last event rather than on any "end" event —
      // a wheel has no end, and trackpad momentum keeps delivering deltas long
      // after the fingers have lifted.
      _scrub() {
        const self = this;
        this.scrubbing = true;
        clearTimeout(this._scrubTimer);
        this._scrubTimer = setTimeout(function () { self.scrubbing = false; }, 140);
      },

      // ── Trackpad / wheel ─────────────────────────────────────────────
      // Two fingers sideways on a Mac trackpad, and the pictures slide with
      // them. preventDefault is not optional here: without it the same gesture
      // is a browser back-navigation, and the customer loses the card.
      //
      // Vertical is left alone — it belongs to the page, which still has to
      // scroll when the Send form is open.
      stageWheel(e) {
        if (this.previewWheel(e)) return;
        if (this.camMode() !== 'photos') return;
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        const s = this.photoStrip();
        if (!s || !s.travel) return;
        e.preventDefault();
        this._scrub();
        this.scrollPhotosBy(e.deltaX);
      },

      // ── Touch ────────────────────────────────────────────────────────
      // The same continuous scroll, driven by a finger on the strip itself, so
      // the pictures follow the drag instead of snapping when it ends. The
      // viewport is touch-action: pan-y, so the browser keeps vertical
      // scrolling and we only ever see the horizontal part.
      stageTouchStart(e) {
        /* ⭐ PREVIEW'S ROAM IS DRIVEN FROM HERE NOW (2026-09-16). It used to be
           pointer events only — previewPanDown/Move — and on a real iPhone the
           drag never happened: "drag to move still doesn't work for me on
           mobile", twice, the second time after a touch-action fix that
           verified in Chrome's device emulation. Emulated touch is not a
           thumb, and this file already had three passive touch listeners on the
           same element that work on the device every day. So the roam rides
           those instead of a second event model that has to be trusted.

           ⚠️ Passive, so it cannot preventDefault — it does not need to:
           .pp-viewport.is-roaming carries `touch-action: none` while zoomed,
           and the page itself is locked. */
        if (this.section === 'preview') {
          this._swipe = null;
          this._pzTouch = null;
          if (!this.previewZoom) return;
          const t0 = e.changedTouches && e.changedTouches[0];
          if (!t0 || (e.touches && e.touches.length > 1)) return;
          this._pzTouch = { fx: t0.clientX, fy: t0.clientY,
                            baseX: this._pzPanX || 0, baseY: this._pzPanY || 0 };
          this._pzMoved = false;
          return;
        }
        // Not while a picture is being panned in Crop, or the strip slides out
        // from under the finger that is trying to move the photo.
        if (this.panning() || this.editSub === 'crop') { this._swipe = null; return; }
        const t = e.changedTouches && e.changedTouches[0];
        // originX is the anchor the gesture is measured from; x/y track the
        // finger; t and vx are what tell a flick from a drag on release.
        const now = (window.performance && performance.now) ? performance.now() : Date.now();
        this._swipe = t
          ? { x: t.clientX, y: t.clientY, originX: t.clientX, moved: false, t: now, vx: 0 }
          : null;
      },
      stageTouchMove(e) {
        /* The zoomed Preview roam — see stageTouchStart. One rAF per frame,
           the same coalescing previewPanMove uses, because _previewApply
           writes the camera. */
        if (this._pzTouch) {
          const t0 = e.changedTouches && e.changedTouches[0];
          if (!t0) return;
          const dx = t0.clientX - this._pzTouch.fx, dy = t0.clientY - this._pzTouch.fy;
          if (!this._pzMoved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) this._pzMoved = true;
          if (!this._pzRaf) {
            const self = this;
            this._pzRaf = requestAnimationFrame(function () {
              self._pzRaf = 0;
              if (self._pzTouch) {
                self._previewApply(self._pzTouch.baseX + dx, self._pzTouch.baseY + dy, false);
              }
            });
          }
          return;
        }
        const s = this._swipe;
        // A picture being carried to a new slot must not also drag the strip
        // along under it. The hold wins because it committed first: it needed
        // the finger to stay still for 400ms, which no swipe ever does.
        if (this.reordering()) { this._swipe = null; return; }
        if (!s || this.panning()) return;
        if (this.camMode() !== 'photos') return;
        const t = e.changedTouches && e.changedTouches[0];
        if (!t) return;
        const dx = t.clientX - s.x, dy = t.clientY - s.y;
        // Until the drag is clearly horizontal it might still be a page
        // scroll, and stealing it would make the page feel stuck.
        if (!s.moved && (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy))) return;
        s.moved = true;

        // Keep a running velocity for the coast — px per ms, signed.
        const now = (window.performance && performance.now) ? performance.now() : Date.now();
        const dt = now - (s.t || now);
        if (dt > 0) s.vx = dx / dt;
        s.t = now;
        s.x = t.clientX; s.y = t.clientY;
        cancelAnimationFrame(this._glide);   // a new touch cancels the old throw
        this._scrub();
        this.scrollPhotosBy(-dx);       // drag right, the strip comes with you
      },
      /* The finger has lifted — hand its speed to the coast. */
      stageTouchEnd() {
        /* ⛔ _pzMoved SURVIVES THE LIFT. previewZoomToggle() reads it to tell a
           tap from the end of a roam; clearing it here would zoom out every
           time somebody finished dragging. It clears itself in that check. */
        if (this._pzTouch) { this._pzTouch = null; return; }
        const s = this._swipe;
        this._swipe = null;
        if (!s || !s.moved) return;
        if (this.camMode() === 'photos') this.glidePhotoScroll(s.vx || 0);
      },

      /* ── COASTING, NOT SNAPPING ─────────────────────────────────────────
         The strip used to settle onto whole pictures when the finger lifted.
         That is a carousel, and Daniel is right that it is the wrong object:
         "I don't like the snap from picture to picture ... the motion should
         be fluid and not snappy."

         So the strip keeps the speed it had and slows down, the way a scroll
         does — and it stops wherever it stops, including between two
         pictures, because that is what scrolling is.

         Velocity is in screen px/ms; scrollPhotosBy() already converts screen
         px to strip fraction at the live camera scale, so this only has to
         decay and hand it over. DECAY is per 16ms frame; the cutoff is where
         another frame would move less than a pixel. */
      glidePhotoScroll(vx) {
        cancelAnimationFrame(this._glide);
        let v = vx;
        if (Math.abs(v) < 0.02) return;
        const self = this;
        // The camera must not ease toward each frame's target while the
        // strip is being thrown, or the glide fights a 520ms transition.
        this.scrubbing = true;
        const step = function () {
          v *= GLIDE_DECAY;
          if (Math.abs(v) < 0.02) { self.scrubbing = false; return; }
          const before = self.photoScroll;
          self.scrollPhotosBy(-v * 16);
          // Ran into an end — stop rather than grinding against it.
          if (self.photoScroll === before) { self.scrubbing = false; return; }
          self._glide = requestAnimationFrame(step);
        };
        this._glide = requestAnimationFrame(step);
      },
      _glide: 0,

      // A picture is clickable WHEREVER you can see it — Full card included
      // (Daniel, 2026-08-11).
      //
      // This used to be `camMode() === 'photos'`, on the reasoning that in the
      // card view the card is there to be looked at. What that produced was a
      // card you could see your own photograph on and not touch, with the way
      // in hidden behind a toolbar button. Clicking the thing you want to
      // change is the whole interaction model; Edit photos is the map for
      // people who don't try clicking, not the toll gate.
      panelInteractive() {
        /* ⛔ AND NOT IN PREVIEW. Preview is the finished card being looked
           at, not edited — but the planes kept their hover states and their
           click handlers, so pointing at a photo lit it up and clicking one
           threw you back into the editor (Daniel, 2026-09-03: "I still get
           the hover states, and if I click, it goes straight back to edit.
           I don't want that"). This one predicate carries the cursor, the
           wash, the focus ring, the note's invitation and every click into
           the card, so it is the whole answer. */
        return this.stage === 'builder' && this.section !== 'preview';
      },

      // Returns an OBJECT, deliberately. Alpine merges an object into the
      // element's style per-property; a :style STRING rewrites the whole
      // attribute, which has silently eaten an inline background-image and an
      // x-show display:none in this file before. Never bind a string here.
      backdropStyle() {
        void this.camNonce; void this.section; void this.selectedPhoto;
        const p = this.cameraTarget();
        // The photo well rides along here rather than on each panel: one
        // write, four panels, and it inherits. See wellVars()/BORDER_STOPS.
        const w = wellPct(this.card.border, this.captionsOn());
        return {
          '--pp-cam-x': `${p.x.toFixed(2)}px`,
          '--pp-cam-y': `${p.y.toFixed(2)}px`,
          '--pp-cam-scale': p.scale.toFixed(4),
          '--pp-photo-w': `${w.w.toFixed(3)}%`,
          '--pp-photo-h': `${w.h.toFixed(3)}%`,
          '--pp-photo-top': `${w.top.toFixed(3)}%`,
          '--pp-caption-h': `${w.band.toFixed(3)}%`,
          /* cqw, not %, and the difference matters: a percentage border-radius
             resolves horizontally against the box's WIDTH and vertically
             against its HEIGHT, so on a 46x69 well it draws ELLIPSES. .pp-plane
             is the container (container-type: inline-size), so 1cqw is 1% of
             the panel — the same panel wellPct() states the well against. */
          '--pp-photo-radius': this.roundedActive()
            ? `${(this.roundedActive() / PANEL_W_MM * 100).toFixed(3)}cqw` : '0px',
        };
      },

      // Back to the occasion grid. This MUST clear the design too: the
      // occasion decides which designs are offered, and a design left behind
      // would satisfy designChosen() and skip the customer straight past the
      // picker to the photos on their way back through.
      changeOccasion() {
        this.designPeek = null;
        this.occStoryStart();          // the deck is on screen again
        this.occPillGlide();
        // Keep the old answer as the PENDING one, so coming back lands on the
        // list with the current choice ticked and its photos in the carousel
        // — "change" should show you what you are changing from.
        this.occPick = this.occasion || this.occPick || DECK_OPENS_ON;
        this.occasion = null;
        if (this.card) { this.card.occasion = null; this.card.design = null; }
        // The customer chooses the occasion now: the inside top's words follow their choice, not the link's.
        if (this.card && this.card.top) this.topWordsFrom(null);
        this.designArtReady = false;
        this.entryStep = 'occasion';
      },

      // changeDesign() lived here — "back to the design picker, keeping the
      // occasion". There is no picker to go back to, and nothing called it
      // once backEntry stopped needing it. In git at 6ccae7f with the screen
      // it belonged to.

      designArt(face) {
        // The inside top shows the customer's own when they have one (the composite topRender made).
        if (face === 'CoverTop' && this.topPreviewUrl && this.topActive()) return this.topPreviewUrl;
        return (this.card.design && this.card.design[face]) || '';
      },

      /* ═══ THE INSIDE TOP (2026-10-08) — see the constants block and assets/inside-top.js ═══ */

      /* Offered only on a design with a text-free top (the catalogue's CoverTopPlain), and never in
         the artist or organization modes, whose cards are made from their own designs. */
      hasTopChoice() {
        const d = this.card.design;
        return !!(d && d.CoverTopPlain && window.PPTop && !ARTIST_TOKEN && !ORG_TOKEN);
      },
      topMode() { return this.hasTopChoice() ? ((this.card.top && this.card.top.mode) || 'design') : 'design'; },
      // A layer to draw and to send: words, or a photo that has actually been chosen.
      topActive() {
        const m = this.topMode();
        return m === 'text' || (m === 'photo' && !!(this.topPhoto && this.topPhoto.dataUrl));
      },
      // Where the words may go, trim mm from the top (catalogue topBox; a blank panel by default).
      topBox() {
        const d = this.card.design;
        return d && Array.isArray(d.topBox) && d.topBox.length === 2 ? d.topBox : window.PPTop.BLANK_BOX;
      },
      topOneLine() { return !!(window.PPTop && window.PPTop.oneLine(this.topBox())); },
      // The drawing's options: inside-top.js optsFor, the same the lightbox draws its 3D Inside with.
      topOpts() {
        const o = window.PPTop.optsFor(this.topBox(), this._topImg);
        // ✂ The cut-out's mask, only for the photo it was made from (topCutOut).
        const m = this._topMask, tp = this.topPhoto;
        o.mask = (m && tp && tp.cutUrl && m.src === tp.dataUrl) ? m.img : null;
        return o;
      },
      /* ✂ REMOVE BACKGROUND ON THE INSIDE-TOP PHOTO (24 Oct; Yonatan's review, Daniel's go). The pictures' own cut-out
         (builder-b.js ppbCutOut), the same PPVision.person: Google's selfie segmenter run in the customer's browser,
         loaded only on this tap (pp-vision.js; the photo never leaves the device for it). The mask is kept in memory
         beside the photo, like the pictures' (a reload puts the background back); the cut is baked into `_Photo 5`
         as a transparent PNG, so print draws the person over the design's text-free top (`_Top Cut: yes`). */
      topCutOn() { return !!(this.topPhoto && this.topPhoto.cutUrl); },
      topCutBusy: false,
      topCutSaid: '',
      async topCutOut() {
        const tp = this.topPhoto;
        if (!tp || !tp.dataUrl || this.topCutBusy || !window.PPVision) return;
        this.topCutSaid = '';
        if (tp.cutUrl) {
          const old = tp.cutUrl;
          this.topPhoto = Object.assign({}, tp, { cutUrl: null });
          this._topMask = null;
          setTimeout(function () { try { URL.revokeObjectURL(old); } catch (e) { /* gone */ } }, 1000);
          this.topTouch(); this.topRender();
          track('pp_photo_cutout', { on: false, where: 'top' });
          return;
        }
        const src = tp.dataUrl, t0 = Date.now();
        this.topCutBusy = true;
        let blob = null;
        try { blob = await window.PPVision.person(src); } finally { this.topCutBusy = false; }
        if (!this.topPhoto || this.topPhoto.dataUrl !== src) return;     // replaced meanwhile
        if (!blob) {
          this.topCutSaid = 'We couldn’t find a person to cut out in this photo.';
          track('pp_photo_cutout', { on: true, found: false, where: 'top', ms: Date.now() - t0 });
          return;
        }
        const url = URL.createObjectURL(blob);
        const img = await new Promise(function (res) { const im = new Image(); im.onload = function () { res(im); }; im.onerror = function () { res(null); }; im.src = url; });
        if (!img || !this.topPhoto || this.topPhoto.dataUrl !== src) { URL.revokeObjectURL(url); return; }
        this._topMask = { src: src, img: img };
        this.topPhoto = Object.assign({}, this.topPhoto, { cutUrl: url });
        this.topTouch(); this.topRender();
        track('pp_photo_cutout', { on: true, found: true, where: 'top', ms: Date.now() - t0 });
      },
      topSpec(over) {
        const t = this.card.top || emptyTop();
        this._topAutoKick();
        return Object.assign({ mode: this.topMode(), template: t.template, title: t.title,
                               subtitle: this.topOneLine() ? '' : t.subtitle, font: t.font, color: t.color,
                               auto: this.topAuto, photo: t.photo }, over || {});
      },
      /* The cover's colour for the layouts (inside-top.js coverAccent; Daniel, 19 Oct). Read once per design; when it
         arrives the inside top and the layout thumbnails are drawn again. ?pptopauto=0 keeps each layout's own colour. */
      _topAutoKick() {
        const d = this.card && this.card.design, P = window.PPTop;
        if (!d || !P || !P.coverAccent || d.id === this._topAutoFor) return;
        this._topAutoFor = d.id;
        this.topAuto = null;
        if (TOP_AUTO_OFF) return;
        const self = this, id = d.id;
        P.coverAccent(d.CoverFront).then(function (hex) {
          if (!hex || !self.card.design || self.card.design.id !== id) return;
          self.topAuto = hex;
          self._topThumbsKey = null;
          try { if (self.topMode() === 'text') self.topRender(); } catch (e) { /* drawn next time */ }
          self.topThumbsSoon();
        });
      },
      /* The layouts offered (inside-top.js OFFERED, five since 19 Oct), and the card's own if it has another (a draft). */
      topTemplates() {
        const P = window.PPTop;
        if (!P) return [];
        if (!P.OFFERED) return P.TEMPLATES;
        const cur = this.card && this.card.top && this.card.top.template;
        return P.TEMPLATES.filter(function (t) { return P.OFFERED.indexOf(t.key) !== -1 || t.key === cur; });
      },
      topFonts() {
        if (!window.PPTop) return [];
        return window.PPTop.TITLE_FONTS.map(function (k) { return Object.assign({ key: k }, window.PPTop.FONTS[k]); });
      },
      topColours() { return window.PPTop ? window.PPTop.COLOURS : []; },
      topFontKey() { return window.PPTop ? window.PPTop.look(this.topSpec()).title.font : ''; },
      topColourKey() {
        const t = this.card.top || {};
        if (t.color) return t.color;
        const L = window.PPTop && window.PPTop.look(this.topSpec());
        return L ? L.t.accent : '';
      },
      topIsTemplate(k) { return this.topMode() === 'text' && this.card.top.template === k; },

      /* When a design is chosen (design-picker.js chooseDesign), and when the occasion changes under a design
         that stays (applyOccasion): until the customer has touched the inside top, it follows the occasion.
         The design's own greeting stays ONLY when the customer's occasion is one it was written for
         (topGreets); on any other occasion, and with no occasion at all, it starts as the occasion's words
         (or the no-occasion pair, "For a good friend / So glad you are in my life") on the text-free top, so a
         "Happy Birthday" card bought for a thank-you, or opened with no occasion from All cards or a link,
         does not say happy birthday and does not print it. Which occasion the words are for: topWordsOcc.

         ⛔ NO OCCASION IS NOT "AS DESIGNED" (Daniel, 2026-10-08: "There should only ever be an occasion set
         for a card if the cover or the cover-inside combination specifically have something to do with a
         specific occasion"). Until then a ?design= link with no occasion started on mode 'design', and the
         cart sent `_Top: design`: print printed the stripped greeting (measured on theme dev, "You quack me
         up", 8 Oct). The greeting decides which art shows, never the occasion.

         ⚠️ A REHYDRATE FOLLOWS THE SAME RULE, and that is safe for a restored draft: an untouched top's mode
         was always derived here, never chosen, and every choice the customer makes (a template, a font, a
         colour, a word, a photo, a mode) sets `touched`, which this never overrides. The old rule left the
         mode alone on a rehydrate, so the machine's own dressing of a fresh card (ensureDesignForOccasion:
         ?occasion=friendship dresses Koalaty, whose greeting was for a birthday) kept emptyTop()'s 'design'. */
      topOnDesign() {
        if (!this.card.top) this.card.top = emptyTop();
        this._topBase = null;
        this._topThumbsKey = '';
        if (!this.hasTopChoice()) { this.topSetPreview(''); return; }
        const t = this.card.top;
        if (!t.touched) {
          const occ = this.topWordsOcc();
          const w = window.PPTop.words(occ, OCCASION_HEADLINE[occ]);
          t.title = w.title;
          t.subtitle = w.subtitle;
          const greets = this.card.design.topGreets || [];
          /* A greeting that names a person narrower than its occasion ("Happy Birthday, Mom", catalogue topNames) starts
             on the occasion's words even on its own occasion; As designed stays in the Layout menu (2026-10-25). */
          t.mode = occ && greets.indexOf(occ) !== -1 && !this.card.design.topNames ? 'design' : 'text';
        }
        this.topRender();
      },

      /* THE OCCASION THE INSIDE TOP'S WORDS ARE FOR: the link's (?for=, kept on the card by topWordsFrom) while the
         builder's occasion is still the one the link came with, else the builder's own, else the link's for a
         builder that has none yet. `any`, or nothing at all, is no occasion: the no-occasion pair (inside-top.js
         words). The ORDER's occasion is this.occasion and is never changed here (CARRIED_OCC). */
      topWordsOcc() {
        const t = this.card.top || {}, occ = this.card.occasion || this.occasion || '';
        if (t.wordsFor) {
          if ((t.wordsWith || '') === occ) return t.wordsFor;
          t.wordsFor = null; t.wordsWith = null;      // another occasion now: the words follow it from here on
        }
        return occ || CARRIED_OCC || '';
      },
      /* Keep `occ` (an occasion, or `any` for none) as the occasion the inside top's words are for, while the
         builder's occasion stays the one it holds now. From a link (init, ?for=) and from the finder. */
      topWordsFrom(occ) {
        if (!this.card) return;
        if (!this.card.top) this.card.top = emptyTop();
        this.card.top.wordsFor = occ || null;
        this.card.top.wordsWith = occ ? (this.card.occasion || this.occasion || '') : null;
      },

      openInside() {
        if (!this.hasTopChoice()) return;
        this.setSection('inside');
        this.cardTool = 'topstyle';
        this.topEnter();
      },
      /* Every face before any measuring: the fitter that refuses a keystroke must measure the face
         that will be drawn, not its fallback (memory: measure text only after fonts.ready). */
      topEnter() {
        const self = this;
        if (this._topFontsOk) { this.topThumbsSoon(); return; }
        window.PPTop.ensureFonts(null).then(function () { self._topFontsOk = true; self.topRender(); self.topThumbsSoon(); });
      },
      topTool(name) {
        this.toggleCardTool(name);
        if (this.isCardTool('topstyle')) this.topThumbsSoon();
      },
      topFlash(msg) {
        const self = this;
        this.topMsg = msg;
        clearTimeout(this._topMsgT);
        this._topMsgT = setTimeout(function () { self.topMsg = ''; }, 3200);
      },
      topTouch() { this.card.top.touched = true; },

      setTopMode(m) {
        if (TOP_MODES.indexOf(m) < 0) return;
        this.topTouch();
        this.card.top.mode = m;
        if (m === 'photo' && !(this.topPhoto && this.topPhoto.dataUrl)) { this.cardTool = 'topphoto'; return; }
        this.topRender();
      },
      setTopTemplate(k) {
        const t = this.card.top;
        this._topFull = {};
        this.topTouch();
        t.template = k; t.font = null; t.color = null; t.mode = 'text';
        this.topRender();
      },
      setTopFont(k) { this._topFull = {}; this.topTouch(); this.card.top.font = k; this.card.top.mode = 'text'; this.topRender(); },
      setTopColor(k) { this.topTouch(); this.card.top.color = k; this.card.top.mode = 'text'; this.topRender(); },

      /* ⛔ A KEYSTROKE THAT WOULD NOT FIT IS REFUSED, the note's rule: the words shrink to fit and
         never spill into the safe margin or the art, and past the smallest legible size the field
         says so instead of printing them tiny. A shorter value is always taken, so a card made too
         long by a font change can always be edited back down. */
      topInput(field, e) {
        const t = this.card.top, el = e.target;
        const v = String(el.value || '').slice(0, field === 'title' ? 80 : 120);
        const was = String(t[field] || ''), shorter = v.length < was.length;
        if (!shorter && this._topFontsOk) {
          const next = this.topSpec({ mode: 'text' });
          next[field] = v;
          /* ⛔ AND ONCE THE WORDS ARE AT THEIR FLOOR, NOTHING LONGER GOES IN, SPACES INCLUDED (2026-10-09, the
             builder's direction B, "fixed in all three" 6). Asking only whether the new words fit let a narrow
             letter or a space through after a wide one was refused, which is how "happy returns" became "happy
             returs". The words shrink first; at the floor every lengthening keystroke is refused. */
          /* ⛔ AND A FIELD THAT HAS REFUSED A KEYSTROKE STAYS FULL until it is shorter than when it refused: a
             trailing space always "fits" (the drawing trims it) and a narrower letter can, which is the same bug
             wearing a space. _topFull keeps the length each field was refused at; a new look clears it. */
          const full = this._topFull || (this._topFull = {});
          const refused = v.length > was.length && full[field] != null && was.length >= full[field];
          const atFloor = v.length > was.length && window.PPTop.measure &&
            window.PPTop.measure(this.topSpec({ mode: 'text' }), this.topOpts()).atFloor;
          if (refused || atFloor || !window.PPTop.fits(next, this.topOpts())) {
            if (v.length > was.length) full[field] = was.length;
            /* ⛔ NEVER KEEP PART OF A WORD (2026-10-11, N2). The refusal used to keep everything typed before the
               refused letter, so a word that did not fit stayed on the card as its first letters -- "the best
               neighbour i", "the best nei" -- and the inside top is baked as it is drawn, so that is what would
               print. When the refused keystroke continues a word, that word did not fit: it goes, back to the
               last whole word. A refused space or punctuation mark leaves the words alone; they were whole.
               ⚠️ ONLY ON THE REFUSAL THAT FILLS THE FIELD, never on one refused because it is already full
               (`refused`): those are the rest of the same word arriving, and cutting again on each took the
               words back one at a time to nothing (measured on Draft, 2026-10-11). */
            const cut = refused ? null : topCutPartialWord(was, v);
            if (cut !== null) {
              /* And the field stays full at the cut, the rule above: the rest of the refused word is usually still
                 being typed, and taken it would glue onto the word before ("the best" + "ghbour") or skip to a
                 shorter next word. Nothing longer goes in until the words are made shorter. */
              full[field] = cut.length;
              t[field] = cut;
              this.topTouch();
              this.topRender();
              this.topThumbsSoon();
            }
            el.value = t[field];
            this.topFlash('That’s as much as fits here.');
            return;
          }
        }
        if (this._topFull && this._topFull[field] != null && v.length < this._topFull[field]) delete this._topFull[field];
        t[field] = v;
        this.topTouch();
        t.mode = 'text';
        this.topRender();
        this.topThumbsSoon();
      },

      async topPhotoPick(e) {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        try {
          const bad = rejectReason(file);
          if (bad) throw bad;
          const img = await decodeImageFile(file);
          const k = Math.min(1, TOP_IMPORT_MAX / Math.max(img.width, img.height));
          const w = Math.max(1, Math.round(img.width * k)), h = Math.max(1, Math.round(img.height * k));
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          c.getContext('2d').drawImage(img, 0, 0, w, h);
          this.topPhoto = { dataUrl: c.toDataURL('image/jpeg', 0.9), w: w, h: h, srcW: img.width, srcH: img.height };
          c.width = c.height = 0;
          this._topImg = null;
          this.topTouch();
          this.card.top.mode = 'photo';
          this.card.top.photo = { zoom: 1, px: 0, py: 0, opacity: 1 };
          this._topMask = null;
          this.topRender();
          this.queueSave();
          track('pp_inside_top_photo', { w: img.width, h: img.height });
        } catch (err) {
          this.topFlash(photoErrorMessage(err, file));
        }
      },
      setTopPhoto(k, v) {
        let n = Number(v);
        if (!isFinite(n)) return;
        const lim = { zoom: [TOP_ZOOM_MIN, 3], px: [-1, 1], py: [-1, 1], opacity: [TOP_OPACITY_MIN, 1] }[k];
        if (!lim) return;
        n = Math.min(lim[1], Math.max(lim[0], n));
        this.card.top.photo = Object.assign({}, this.card.top.photo, (function () { const o = {}; o[k] = n; return o; })());
        this.topTouch();
        this.topRender();
      },
      removeTopPhoto() {
        this.topPhoto = null;
        this._topMask = null;
        this._topImg = null;
        delete idbWritten[TOP_PHOTO_KEY];
        if (window.PP && window.PP.idb) window.PP.idb.delete(TOP_PHOTO_KEY).catch(function () {});
        this.card.top.mode = 'text';
        this.topRender();
      },

      // The design's text-free top as a decoded image, CORS-clean (the catalogue answers ACAO *),
      // so the canvas it is drawn into can still be read back.
      _topLoadBase() {
        const url = this.card.design && this.card.design.CoverTopPlain;
        if (!url) return Promise.resolve(null);
        if (this._topBase && this._topBase.url === url) return this._topBase.p;
        const p = new Promise(function (res) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = function () { res(img); };
          img.onerror = function () { res(null); };
          img.src = url;
        });
        this._topBase = { url: url, p: p };
        return p;
      },
      _topLoadPhoto() {
        const self = this, u = this.topPhoto && this.topPhoto.dataUrl;
        if (!u) return Promise.resolve(null);
        if (this._topImg && this._topImgSrc === u) return Promise.resolve(this._topImg);
        return new Promise(function (res) {
          const img = new Image();
          img.onload = function () { self._topImg = img; self._topImgSrc = u; res(img); };
          img.onerror = function () { res(null); };
          img.src = u;
        });
      },
      topSetPreview(url) {
        if (this.topPreviewUrl && this.topPreviewUrl.indexOf('blob:') === 0) URL.revokeObjectURL(this.topPreviewUrl);
        this.topPreviewUrl = url;
      },
      // Debounced: typing redraws at most every 60 ms, and only the last request paints.
      topRender() {
        const self = this;
        clearTimeout(this._topT);
        this._topT = setTimeout(function () { self._topRenderNow(); }, 60);
      },
      async _topRenderNow() {
        if (!window.PPTop || !this.hasTopChoice() || !this.topActive()) { this.topSetPreview(''); return; }
        const self = this, seq = (this._topSeq = (this._topSeq || 0) + 1);
        const base = await this._topLoadBase();
        if (this.topMode() === 'photo') await this._topLoadPhoto();
        else await window.PPTop.ensureFonts(this.topSpec());
        if (seq !== this._topSeq) return;
        const r = window.PPTop.preview(base, this.topSpec(), this.topOpts(), 1000);
        this.topSmall = r.small;
        r.canvas.toBlob(function (b) {
          r.canvas.width = r.canvas.height = 0;
          if (seq !== self._topSeq || !b) return;
          self.topSetPreview(URL.createObjectURL(b));
        }, 'image/jpeg', 0.9);
      },
      // The template tiles, in the customer's own words, each in its own font and colour.
      topThumbsSoon() {
        const self = this;
        clearTimeout(this._topThumbT);
        this._topThumbT = setTimeout(function () { self._topThumbsNow(); }, 250);
      },
      async _topThumbsNow() {
        if (!window.PPTop || !this.hasTopChoice() || !this.isCardTool('topstyle') || !this._topFontsOk) return;
        const t = this.card.top, key = [t.title, t.subtitle, this.card.design && this.card.design.id, this.topAuto].join('|');
        if (key === this._topThumbsKey) return;
        const base = await this._topLoadBase();
        const out = {}, self = this;
        this.topTemplates().forEach(function (tp) {
          const r = window.PPTop.preview(base, self.topSpec({ mode: 'text', template: tp.key, font: null, color: null }), self.topOpts(), 168);
          out[tp.key] = r.canvas.toDataURL('image/jpeg', 0.84);
          r.canvas.width = r.canvas.height = 0;
        });
        this.topThumbs = out;
        this._topThumbsKey = key;
      },

      /* The layer for print: baked on the master grid, uploaded beside the four photos, and handed
         to buildCartProperties. `_Src 5` is the crop in the ORIGINAL photo's pixels, as `_Src 1..4`. */
      async topBakeAndUpload() {
        const P = window.PPTop, spec = this.topSpec(), opts = this.topOpts();
        if (spec.mode === 'photo') { opts.photo = await this._topLoadPhoto(); if (!opts.photo) throw new Error('top_photo_missing'); }
        else await P.ensureFonts(spec);
        const out = await P.bake(spec, opts);
        const url = await cloudinaryUpload(out.blob, (spec.mode === 'photo' && !out.alpha) ? 'inside-top.jpg' : 'inside-top.png');
        let src = '', mmW = 98;
        if (spec.mode === 'photo' && this.topPhoto && this.topPhoto.srcW) {
          const c = P.photoCrop(opts.photo, spec.photo.zoom, spec.photo.px, spec.photo.py);
          const k = this.topPhoto.srcW / (opts.photo.naturalWidth || opts.photo.width);
          src = Math.round(c.w * k) + 'x' + Math.round(c.h * k) + ' of ' + this.topPhoto.srcW + 'x' + this.topPhoto.srcH;
          if (c.mmW) mmW = c.mmW;
        }
        const ph = spec.photo || {};
        return { url: url, src: src, mode: spec.mode, mmW: mmW,
                 opacity: spec.mode === 'photo' && ph.opacity != null && ph.opacity < 0.999 ? ph.opacity : 1,
                 cut: spec.mode === 'photo' && !!opts.mask };
      },

      /* THE PHOTO STRIP BORDER on one panel (Part B, 2026-09-19). The design's
         Strip is the whole strip, 218 x 78.9 mm — 214 x 74.9 plus 2 mm of bleed
         — so each 53.5 mm panel shows its own quarter of it, painted as the
         panel's BACKGROUND. A background is under everything the panel holds,
         so the photo always covers it and the rounded photo corners reveal it:
         what the border has there is what prints there (Daniel, 2026-09-19).

         background-size: the image is 218/53.5 panels wide, 78.9/74.9 tall.
         background-position in %, which aligns that point of the image with the
         same point of the box: x = (2 + 53.5 i) / (218 - 53.5), y = 50%.

         Returned as an OBJECT, never a string: Alpine applies an object one
         property at a time, and card-stage.js writes this element's width,
         height and transform inline — a string would clobber them.

         None at `none`: the photo runs over the whole panel there, and print
         does not print the border at all (print/card-art.mjs). */
      panelStrip(i) {
        const url = this.designArt('Strip');
        /* ✂ Except behind a cut-out (?ppbg=1): at `none` the picture fills the panel, so the art can only show where
           the person was cut out, and the bake draws it there for print (bakePhotoWith). Without it a cut-out at
           `none` stood on white paper (Daniel, 19 Oct: "it paints white behind the picture"). */
        const cut = this.photos[i] && this.photos[i].cutUrl;
        if (!url || (this.card.border === 'none' && !cut)) return { backgroundImage: '' };
        return {
          backgroundImage: 'url("' + url + '")',
          // 3:4 panel (SHOW_H_MM): 53.5 wide is 71.333 tall, so the art's 78.9 mm is cut top and bottom, centred.
          backgroundSize: (218 / 53.5 * 100).toFixed(3) + '% ' + (78.9 / (53.5 * 4 / 3) * 100).toFixed(3) + '%',
          backgroundPosition: ((2 + 53.5 * i) / 164.5 * 100).toFixed(3) + '% 50%',
          backgroundRepeat: 'no-repeat',
        };
      },

      // ───── Occasions ─────────────────────────────────────────────────
      // The picker renders from OCCASIONS, grouped. Liquid used to hold this
      // list inline; it now has one home, in JS, next to the filter that
      // reads it.
      occasionsIn(group) {
        return OCCASIONS.filter(function (o) { return o.group === group; });
      },

      /* ───── The design, resolved from the occasion ─────────────────────
       *
       * There is no design step any more (2026-08-13). This is the rule that
       * replaced it: the design made FOR this occasion, or the first that
       * suits anything, which is exactly the pair of bands the picker used to
       * show. One design per occasion at launch means those bands never held
       * more than one relevant card, and a screen that asks a question with
       * one answer is a screen worth deleting.
       *
       * ⚠️ IF A SECOND DESIGN PER OCCASION IS EVER ADDED, this silently picks
       * the first and the customer never sees the other. That is the moment
       * to bring the picker back — it is in git at 6ccae7f — not a reason to
       * keep an empty screen now.
       *
       * Returns undefined until designs.json resolves. Every caller has to
       * cope with that; see ensureDesignForOccasion for how.
       */
      designForOccasion(slug) {
        if (!slug) return undefined;
        /* The BEST fit, not the first in catalogue order (2026-10-08): since every generic card is on
           every occasion, "the first" would be the store's own Flower on Valentine's Day ahead of the
           cards made for it. Made for it, then suits it, then generic: design-picker.js designFit. */
        const fit = (window.PP && window.PP.designFit) || function (d, o) { return d.occasions && d.occasions.indexOf(o) !== -1 ? 1 : 0; };
        const matched = this.designs.filter(function (d) { return fit(d, slug) > 0; })
          .map(function (d, i) { return { d: d, i: i }; })
          .sort(function (a, b) { return (fit(b.d, slug) - fit(a.d, slug)) || (a.i - b.i); })
          .map(function (x) { return x.d; });
        if (slug === MY_DESIGNS) return matched[0];   // never a catalogue design
        return matched[0] || this.designs.filter(function (d) {
          return !d.occasions || !d.occasions.length;
        })[0];
      },

      /* Choose the occasion's design, if there is one to choose and nothing
       * is chosen yet.
       *
       * Called from two places because the catalogue is fetched: from
       * applyOccasion, which is the normal path and usually finds the
       * catalogue already loaded, and again when loadDesigns resolves, which
       * is the path a DEEP LINK takes — /products/picturepop?occasion=
       * birthday applies its occasion during init(), before the fetch has
       * come back, and without the second call that customer would reach the
       * builder with an undressed card and no screen left that could dress
       * it.
       *
       * Never overwrites a design already on the card: a returning visitor's
       * saved choice outranks this, and re-deriving it would throw away a
       * design chosen under an older catalogue.
       */
      ensureDesignForOccasion() {
        const card = this.pickerCard();
        if (!card || this.designChosen()) return;
        const slug = this.occasion || (card && card.occasion);
        const d = this.designForOccasion(slug);
        /* ⛔ AS A REHYDRATE, NOT AS A CHOICE. This is the machine dressing the
           card from the occasion, not the customer picking one, and
           chooseDesign()'s second argument is exactly that distinction: "same
           decode, but it must not advance a step or fire an event the customer
           didn't cause".

           Without it the grid was unreachable and the bug looked nothing like
           its cause. loadDesigns() re-runs this resolve when designs.json
           lands, by which time the step has advanced to 'design' -- so
           onDesignChosen() fired against the very screen it had just opened
           and moved straight on to 'name'. Measured on Draft, 2026-09-02. */
        if (d) this.chooseDesign(d.id, true);
      },

      // ⚠️ A RESTORED DRAFT CAN CARRY A COVER THAT NO LONGER MATCHES ITS
      // OCCASION. The occasion IS the design choice (see applyOccasion), so
      // the two can only disagree if the pairing was written by an older
      // build — or if the draft's occasion was overridden by ?occasion= after
      // the design was already stored. Left alone, a sympathy card goes out
      // with balloons on the front, which is how this was found (2026-08-29).
      //
      // ⛔ MUST RUN AFTER loadDesigns() RESOLVES. designForOccasion() reads
      // this.designs, and the catalogue arrives over fetch — called any
      // earlier it silently finds nothing to correct TO, and design-picker's
      // own _rehydrateDesign() then puts the stale cover back.
      //
      // Only ever corrects the DESIGN to the occasion, never the other way:
      // the occasion is the thing the customer actually chose.
      //
      // ⛔ ONLY A COVER THAT DOES NOT SERVE THE OCCASION IS CORRECTED. This
      // used to compare against designForOccasion(), the FIRST design for the
      // occasion — right while every occasion had one design, wrong since they
      // have several (2026-09-17, the platform's designs): a customer who chose
      // any other and came back to the draft was handed the first. Measured on
      // Live, 2026-10-05: a Birthday Leaves draft reopened as Grumpy Cat
      // (pp_design_reconciled from birthday-leaves to grumpy-cat).
      // A design missing from the catalogue is corrected only when the
      // catalogue arrived whole (it was withdrawn). The artist catalogue can
      // time out (design-picker.js, 3 s), and "not loaded" is not "wrong".
      reconcileDesignToOccasion() {
        if (!this._restored || !this.occasion || !this.designChosen()) return;
        const id = this.card.design.id;
        const mine = this.designs.filter(function (d) { return d.id === id; })[0];
        if (!mine && !this.catalogueComplete) return;
        if (mine && (!mine.occasions || !mine.occasions.length || mine.occasions.indexOf(this.occasion) !== -1)) return;
        const want = this.designForOccasion(this.occasion);
        if (!want || !want.id || this.card.design.id === want.id) return;
        track('pp_design_reconciled', { from: this.card.design.id, to: want.id });
        this.chooseDesign(want.id);
      },

      /* ───── The occasion step — split rail (handoff 2026-08-13) ────────
       *
       * One searchable list, not two bands. The bands were Daniel's grouping
       * of fourteen tiles into "this season" and "any time"; a search box
       * does that job better and the seasonal ones keep a SEASON tag, so the
       * grouping survives as a property of the row rather than as a heading
       * you have to scroll past.
       *
       * Case- and punctuation-insensitive, because "grandparents day" is what
       * someone types and "Grandparents' Day" is what the list holds — a
       * straight indexOf on the label finds nothing, and an empty result on a
       * correctly-spelled search reads as "we don't sell that".
       */
      occasionsFound() {
        const q = normalizeSearch(this.occQuery);
        if (!q) return OCCASIONS;
        return OCCASIONS.filter(function (o) {
          return normalizeSearch(o.label).indexOf(q) !== -1;
        });
      },

      pickOcc(slug) {
        if (this.occPick === slug) return;   // keeps the story running on a re-pick
        this.occPick = slug;
        this.occSlide = 0;                   // photo 1 of the new set, never mid-story
        this.occStoryStart();
      },

      occPicked(slug) { return this.occPick === slug; },

      occIsSeason(o) { return o.group === 'season'; },

      /* The carousel's photo set for the pending occasion: the three faces of
       * the design that occasion is dressed in — the closed front, the card
       * open, and the inside top.
       *
       * Read off the SAME catalogue the design picker uses, so the preview
       * here cannot show a card the next step doesn't offer, and so the
       * artist's real art replaces these placeholders in one place. When an
       * occasion has no design of its own it borrows the universal ones
       * (Confetti, today), which is exactly what the picker will offer it.
       *
       * Empty until designs.json resolves; the markup shows the frame with a
       * quiet ground rather than collapsing, so the rail does not jump.
       */
      /* Parameterised so the deck can ask for ANY occasion's photos, not just
       * the chosen one: every card in the strip shows its own cover, and the
       * card in full view plays the whole set. occSlides() is the same call
       * for the chosen occasion, kept because plenty of code already says it. */
      occSlides() { return this.occSlidesFor(this.occPick); },

      occSlidesFor(occ) {
        if (!occ) return [];
        const d = this.designForOccasion(occ);
        if (!d || !d.art) return [];
        const label = this.occasionLabelOf(occ);

        /* ── The full set, when the catalogue has one ──────────────────
         * Daniel, 2026-08-15: every card is to carry EIGHT photos. The three
         * below are all the placeholder catalogue holds today, so a design
         * may instead declare its own ordered `shots` and the deck plays
         * however many there are — the story bar draws one segment per photo
         * and never needs to know the number.
         *
         * Entries are either a filename or { file, label }. Adding the eight
         * shots is then a designs.json change and not a code one.
         */
        const set = (Array.isArray(d.shots) && d.shots.length) ? d.shots : PLACEHOLDER_SHOTS;
        if (set && set.length) {
          const self = this;
          return set.map(function (s, i) {
            const file = (typeof s === 'string') ? s : (s && s.file);
            const what = (s && s.label) || ('shot ' + (i + 1));
            // `name` is the shot alone ("closed", "front"); `label` keeps the
            // occasion for alt text and the lightbox. The grid captions every
            // tile, and repeating "Birthday ·" eight times down a column is
            // noise rather than information.
            return file ? { url: self.designUrl(file), name: what, label: label + ' · ' + what } : null;
          }).filter(Boolean);
        }

        const slides = [
          { url: this.designUrl(d.art.CoverFront), name: 'cover', label: label + ' · cover' },
        ];
        // The open shot is the only optional file in the catalogue — see the
        // readme in designs.json. Without it there is no "inside" to show, so
        // the set is two slides rather than one repeated.
        if (this.designHasOpenShot(d)) {
          slides.push({ url: this.designUrl(d.preview.open), name: 'inside', label: label + ' · inside' });
        }
        slides.push({ url: this.designUrl(d.art.CoverTop), name: 'pop-out', label: label + ' · pop-out' });
        return slides;
      },

      occSlideCount() { return this.occSlides().length; },

      /* ───── The deck (handoff 6a, replaces the 4a rail + carousel) ─────
       * The strip of overlapping cards IS the picker: whatever card is parted
       * into full view is the chosen occasion. There is no separate commit
       * tap and no carousel underneath — the photos play inside the open card.
       *
       * The strip is rendered from occasionsFound(), so a search filters the
       * deck itself rather than a list beside it. occPick is the only piece
       * of selection state; the parted index is derived from it, which means
       * the deck and every pill agree by construction.
       */
      occDeckIndex() {
        const list = this.occasionsFound();
        for (let i = 0; i < list.length; i++) if (list[i].slug === this.occPick) return i;
        return -1;
      },

      // Part the deck to a position in the CURRENT (possibly filtered) list.
      occDeckPart(i) {
        const list = this.occasionsFound();
        if (!list.length) return;
        this.pickOcc(list[Math.max(0, Math.min(list.length - 1, i))].slug);
      },

      // The still every buried card shows: its own cover, not the chosen
      // occasion's. Empty string until designs.json resolves.
      occCardStill(slug) {
        const s = this.occSlidesFor(slug);
        return s.length ? s[0].url : '';
      },

      /* How far the strip has to slide to bring the open card to the middle.
       *
       * ⚠️ Clamped at both ends, and that clamp is the design. The handoff
       * has the strip cropping at BOTH viewport edges; centring the open card
       * literally would walk the deck off the right edge after a few cards
       * and leave dead page on the left at the start. So it centres in the
       * middle of the deck and pins at the ends.
       */
      occDeckShift() {
        const deck = this.$refs.occdeck;
        if (!deck) return 0;
        const n = this.occasionsFound().length;
        const w = this.occCardW(), step = this.occStep();
        const i = Math.max(0, this.occDeckIndex());
        const raw = (deck.clientWidth / 2) - (w / 2) - (i * step);
        const min = deck.clientWidth - ((n - 1) * step + w);
        return Math.max(Math.min(raw, 0), Math.min(min, 0));
      },

      // Geometry lives in CSS custom properties so one breakpoint changes
      // both the layout and the arithmetic. Reading them back keeps the two
      // from drifting apart.
      occCardW() { return this.occMetric('--pp-deck-card-w', 260); },

      /* ⚠️ DERIVED FROM THE SLIVER, NEVER READ FROM --pp-deck-overlap.
       * That property is a calc(), and getComputedStyle hands custom
       * properties back UNEVALUATED — "calc(220px - 56px)", which parseFloat
       * turns into NaN. The fallback then applied silently: the phone laid
       * cards out on a 56px step while the arithmetic used the desktop 190,
       * and the deck parted to a card three along from the one it named.
       * Read only plain lengths back out of CSS.
       *
       * --pp-deck-sliver is also the one number JS may safely read after
       * occDeckStyle() has written --pp-deck-overlap inline: the spec value
       * stays untouched in the stylesheet, so the fit below cannot feed on
       * its own output and creep wider every frame. */
      occOverlap() { return this.occCardW() - this.occStep(); },

      /* The gap between one card's left edge and the next — the visible
       * sliver, and the unit everything else counts in.
       *
       * ⚠️ IT IS NOT ALWAYS THE SPEC 70. The handoff draws the deck in a
       * 1080px frame, where fourteen cards at a 70px sliver come to 1170px
       * and duly run off both edges. On a 1440px monitor the same strip is
       * 270px SHORT of the window and simply stops, which is what Daniel saw:
       * a deck that would not span the page. So the sliver opens up until the
       * strip overhangs both edges, and closes back to the spec as soon as
       * there are enough occasions to fill the width on their own — at 70px
       * that is twenty cards, which the catalogue will reach.
       *
       * Never smaller than the spec: a narrow window should crop the strip,
       * not shrink the slivers. */
      occStep() {
        const spec = this.occMetric('--pp-deck-sliver', 70);
        const deck = this.$refs.occdeck;
        const n = this.occasionsFound().length;
        if (!deck || n < 2) return spec;
        const need = (deck.clientWidth + spec - this.occCardW()) / (n - 1);
        return Math.max(spec, need);
      },
      occMetric(name, fallback) {
        const deck = this.$refs.occdeck;
        if (!deck) return fallback;
        const v = parseFloat(getComputedStyle(deck).getPropertyValue(name));
        return isFinite(v) && v > 0 ? v : fallback;
      },

      occDeckStyle() {
        // camNonce is READ, not used: the shift depends on the deck's pixel
        // width, which is not reactive, so a resize would otherwise leave the
        // strip centred for the old viewport until the next pick. camNonce is
        // already bumped (debounced) on resize, orientationchange and fonts
        // ready — exactly the moments this needs recomputing.
        void this.camNonce;
        const dx = this.occDrag && this.occDrag.axis === 'x' ? this.occDrag.dx : 0;
        // Publishes the fitted overlap for the CSS to lay the cards out with,
        // so the stylesheet and the arithmetic cannot disagree about the step.
        return '--pp-deck-overlap: ' + (this.occCardW() - this.occStep()) + 'px;' +
               'transform: translate3d(' + (this.occDeckShift() + dx) + 'px, 0, 0);' +
               (this.occDrag ? 'transition: none;' : '');
      },

      /* ───── The photos, as an Instagram story ─────────────────────────
       * Inside the open card only. Each shot holds for occStoryMs and then
       * CUTS — no slide, no fade — and the set loops. Tapping the left third
       * goes back, the right two-thirds forward.
       */
      occStoryMs: 2000,

      occStoryStart() {
        this.occStoryStop();
        if (this.occReduceMotion()) return;      // no unattended motion
        const self = this;
        // Armed even with nothing to show: designs.json resolves after this
        // runs, and a story that refused to start before the art existed
        // would never start at all. The tick idles until there are photos.
        this._occStoryT = setInterval(function () {
          const n = self.occSlides().length;
          if (n < 2) return;
          self.occSlide = (self.occSlide + 1) % n;
        }, this.occStoryMs);
      },

      occStoryStop() { clearInterval(this._occStoryT); this._occStoryT = null; },

      occReduceMotion() {
        return !!(window.matchMedia
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      },

      // Restarts the hold, so a photo you asked for gets its full time rather
      // than whatever was left of the previous one's.
      occStoryGo(d) {
        const n = this.occSlides().length;
        if (n < 2) return;
        this.occSlide = (this.occSlide + d + n) % n;
        this.occStoryStart();
      },

      /* ───── Browsing ──────────────────────────────────────────────────
       * Three ways in, one model. A tap on a buried card parts to it; a tap
       * on the open card runs the story; a drag or a sideways wheel scrubs
       * the whole deck.
       */
      occCardTap(i, e) {
        if (this.occDragMoved) return;           // the drag already answered
        if (i !== this.occDeckIndex()) { this.occDeckPart(i); return; }
        const box = e.currentTarget.getBoundingClientRect();
        this.occStoryGo((e.clientX - box.left) / (box.width || 1) < 0.3333 ? -1 : 1);
      },

      occDeckDown(e) {
        this.occDragMoved = false;
        this.occDrag = { id: e.pointerId, x0: e.clientX, y0: e.clientY,
                         dx: 0, axis: null, i0: Math.max(0, this.occDeckIndex()) };
      },

      occDeckMove(e) {
        const d = this.occDrag;
        if (!d || d.id !== e.pointerId) return;
        const dx = e.clientX - d.x0, dy = e.clientY - d.y0;

        // Decide the axis once. Until it is decided the gesture might be the
        // page scrolling past, and stealing it makes the step unscrollable
        // on a phone.
        if (!d.axis) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          d.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (d.axis === 'y') { this.occDrag = null; return; }
        }
        this.occDragMoved = true;
        d.dx = dx;
        const step = this.occStep();
        this.occDeckPart(d.i0 - Math.round(dx / step));
      },

      occDeckUp() {
        this.occDrag = null;                     // and the strip springs home
        const self = this;
        // Cleared after the click that follows this pointerup, not before, or
        // the tap handler fires on the card the drag happened to end over.
        setTimeout(function () { self.occDragMoved = false; }, 0);
      },

      /* Trackpad. Horizontal only: a vertical gesture is left entirely alone
       * so the page still scrolls over the deck, which is the whole reason
       * this is not a plain scroll container. */
      occDeckWheel(e) {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();
        const step = this.occStep();
        this.occWheel += e.deltaX;
        let i = Math.max(0, this.occDeckIndex());
        while (this.occWheel >= step) { this.occWheel -= step; i++; }
        while (this.occWheel <= -step) { this.occWheel += step; i--; }
        const n = this.occasionsFound().length;
        if (i < 0 || i > n - 1) this.occWheel = 0;
        this.occDeckPart(i);
      },

      /* ───── The pill row's idle glide ────────────────────────────────
       * One row that drifts, doubled so it loops seamlessly, and parks the
       * moment a pointer is on it. Driven by an interval rather than
       * requestAnimationFrame: the step is time-based, so a throttled tick
       * still travels the right distance instead of stalling.
       */
      occPillDrift: 14,        // px/sec; negative reverses, 0 parks it

      /* ───── Dragging the pill row ─────────────────────────────────────
       * The row scrolls natively and drifts on its own; this adds grabbing
       * it. Same shape as the deck's drag — the axis is decided once so a
       * vertical swipe still scrolls the page, and a gesture that moved is
       * not allowed to land as a click on whichever pill it finished over.
       */
      occPillDown(e) {
        const row = this.$refs.occpills;
        if (!row) return;
        this.occPillHeld = true;                 // parks the drift
        this.occPillMoved = false;
        this._occPillDrag = { id: e.pointerId, x0: e.clientX, y0: e.clientY,
                              left0: row.scrollLeft, axis: null };
      },

      occPillMove(e) {
        const d = this._occPillDrag, row = this.$refs.occpills;
        if (!d || !row || d.id !== e.pointerId) return;
        const dx = e.clientX - d.x0, dy = e.clientY - d.y0;
        if (!d.axis) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          d.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (d.axis === 'y') { this._occPillDrag = null; this.occPillHeld = false; return; }
          row.setPointerCapture && row.setPointerCapture(e.pointerId);
        }
        this.occPillMoved = true;
        // The row is doubled, so the scroll position wraps instead of ending.
        const set = row.firstElementChild && row.firstElementChild.nextElementSibling;
        const w = set ? set.offsetLeft : 0;
        let x = d.left0 - dx;
        if (w) { while (x >= w) x -= w; while (x < 0) x += w; }
        row.scrollLeft = x;
      },

      occPillUp() {
        this._occPillDrag = null;
        this.occPillHeld = false;
        const self = this;
        // Cleared after the click this pointerup is about to produce.
        setTimeout(function () { self.occPillMoved = false; }, 0);
      },

      // A pill only chooses when the row was not being dragged.
      occPillTap(slug) {
        if (this.occPillMoved) return;
        this.pickOcc(slug);
      },

      occPillGlide() {
        const self = this;
        clearInterval(this._occPillT);
        if (this.occReduceMotion()) return;
        let last = 0;
        this._occPillT = setInterval(function () {
          const row = self.$refs.occpills;
          if (!row || self.occPillHeld) { last = 0; return; }
          const set = row.firstElementChild && row.firstElementChild.nextElementSibling;
          const w = set ? set.offsetLeft : 0;
          if (!w || row.scrollWidth <= row.clientWidth) return;
          const now = Date.now();
          const dt = last ? Math.min(now - last, 250) : 0;
          last = now;
          let x = row.scrollLeft + self.occPillDrift * dt / 1000;
          while (x >= w) x -= w;
          while (x < 0) x += w;
          row.scrollLeft = x;
        }, 16);
      },

      /* ───── Search ────────────────────────────────────────────────────
       * Permanently visible under the pills on both viewports (Daniel,
       * 2026-08-15) — no ⌕ button, no collapse, and the placeholder always
       * says what the field is for.
       *
       * Filtering the deck can leave the chosen occasion off it, so the deck
       * parts to the best remaining match. Clearing the box keeps whatever
       * that landed on rather than jumping back.
       */
      occSearchInput() {
        const list = this.occasionsFound();
        if (!list.length) return;
        if (this.occDeckIndex() === -1) this.pickOcc(list[0].slug);
      },

      occClearSearch() {
        this.occQuery = '';
        if (this.$refs.occsearch) this.$refs.occsearch.focus();
      },

      // Continue. The commit point: everything before it was a preview.
      // applyOccasion() advances the step itself when it is called from here,
      // which is why this does not also call nextEntry().
      commitOccasion() {
        if (!this.occPick) return;
        this.applyOccasion(this.occPick);
      },

      // ── The gallery wall (handoff 8a) ────────────────────────────────
      // A tile NAVIGATES; it does not select. The handoff is explicit about
      // it — "no selected state on this screen (navigation, not selection)" —
      // and it is why the deck's pending-pick/Continue pair is gone from this
      // screen: with a product page behind every tile there is nothing left
      // to preview here, and a Continue button would ask the customer to
      // confirm a choice they can simply undo by going back.
      openOccasion(slug) {
        if (!slug) return;
        this.occPick = slug;
        this.applyOccasion(slug);      // resolves the design, advances the step
        this.entryStep = 'design';     // ...to the card grid, explicitly
        track('pp_occasion_opened', { occasion: slug });
      },

      // ── The product page (handoff 9d) ────────────────────────────────
      // The gallery is the chosen design's own faces, from the same catalogue
      // the card is built from — so the page cannot advertise a card the
      // builder will not produce. The handoff asks for six product photos
      // (cover, inside x2, pop-out, detail, envelope); none exist yet, so it
      // shows however many the catalogue holds and the thumb row sizes itself.
      // ── The expanded view (Daniel, 2026-09-02) ───────────────────────
      // A sheet over the card grid, opened by tapping a tile. It is the parked
      // product page's gallery, lightbox and buy column, re-skinned; the
      // methods below are what changed: the pictures come from the SHEET's
      // design rather than from the occasion's resolved one.
      openDesignSheet(id) {
        this.artistSheet = null;       // from an artist's profile: one dialog at a time
        this.designSheet = id;
        this.pdpIndex = 0;
        // Off its greeting's occasions, the sheet's Inside is the text-free top: drawn now, swapped in when ready.
        const d = this.sheetDesign();
        if (d && this.designTopIsPlain(d)) this.designTopShot(d);
        track('pp_design_sheet_opened', this.finder
          ? { design: id, from: 'finder', position: this.finderPosition(id) } : { design: id, from: 'occasion' });
      },

      closeDesignSheet() {
        this.designSheet = null;
        this.pdpZoom = false;
      },

      /* ── The artist's profile (Daniel, 2026-09-17) ───────────────────
         Opened by the artist's name under a tile. Everything it shows is in
         the artist catalogue already (credit: id, name, bio, instagram,
         photo), so it costs no request. Their designs are every design in
         the catalogue with the same credit id, whatever the occasion. */
      openArtistSheet(id) {
        if (!id) return;
        this.designSheet = null;
        this.pdpZoom = false;
        this.artistSheet = id;
        track('pp_artist_profile_opened', { artist: id });
      },

      closeArtistSheet() { this.artistSheet = null; },

      artistDesigns() {
        const id = this.artistSheet;
        if (!id) return [];
        return (this.designs || []).filter(function (d) { return d.credit && d.credit.id === id; });
      },

      artistCredit() {
        const d = this.artistDesigns()[0];
        return d ? d.credit : null;
      },

      artistInstagramUrl() {
        const c = this.artistCredit();
        return c && c.instagram ? 'https://www.instagram.com/' + encodeURIComponent(c.instagram) + '/' : '';
      },

      sheetDesign() {
        const id = this.designSheet;
        return (this.designs || []).filter(function (d) { return d.id === id; })[0] || null;
      },

      /* Three pictures, every one of them the card as an OBJECT: closed, the
         card open, and the inside top standing on its hinge. Daniel's order,
         2026-09-02 -- the face, then the whole card, then the detail. The flat faces used to
         follow these, and Daniel's verdict on them (2026-09-02) was that a
         face "just on a regular white background... not shaped like a card"
         is not a product picture. So the top face now comes shaped -- see
         preview.top in designs.json -- and the flat files are not shown at
         all; the closed shot already IS the front. A design without the
         shaped shot falls back to its flat CoverTop rather than to nothing.
         De-duplicated by URL so a fallback cannot list one file twice. */
      sheetShots() {
        const d = this.sheetDesign();
        if (!d) return [];
        const out = [], seen = {};
        const self = this;
        const push = function (file, label, alt) {
          const url = self.designUrl(file);
          if (!url || seen[url]) return;
          seen[url] = true;
          out.push({ url: url, label: label, alt: alt || '' });
        };
        /* Alt text from the catalogue when the design has it: what Claude read off the card
           (artists.picturepops.com catalogue.json `alt`, reviewed on the platform, 2026-10-05).
           Without it the picture is named by its pose, as before. */
        const p = d.preview || {}, a = d.art || {}, alt = d.alt || {};
        push(p.closed || a.CoverFront, 'Closed', alt.front);
        push(p.open, 'Open');
        // The inside top as this occasion gets it: the stripped greeting only where it was written for (designTopUrl).
        push(this.designTopUrl(d), 'Inside', alt.inside);
        return out;
      },

      /* Per design when the catalogue carries one; the general paragraph
         otherwise. Since 2026-10-05 the artist platform's designs carry one:
         written by Claude from the card itself, reviewed by Daniel and Yonatan
         (catalogue.json `description`). The store's own designs.json has none. */
      sheetDescription() {
        const d = this.sheetDesign();
        if (d && d.description) return d.description;
        return 'A pop-up card that opens into your own photos. You pick the ' +
               'pictures, we print them into the pop-out \u2014 one card, four of ' +
               'your favourite moments, ready to mail.';
      },

      /* The sheet's button and the one under the tile do the same thing;
         this one also closes the sheet first so the next screen is not
         behind a dialog. */
      chooseDesignAndGo(id) {
        this.closeDesignSheet();
        this.chooseDesign(id);
      },

      // The gallery and the lightbox read from here. They used to read the
      // occasion's resolved design; now they read the sheet's.
      pdpShots() { return this.sheetShots(); },

      pdpShot() {
        const s = this.pdpShots();
        if (!s.length) return null;
        return s[Math.min(this.pdpIndex, s.length - 1)] || s[0];
      },

      pdpPick(i) { this.pdpIndex = i; },

      // ── The lightbox ────────────────────────────────────────────────
      // Wrapping, not clamping: someone stepping through six pictures with an
      // arrow key should not hit an invisible wall at either end.
      pdpOpenZoom(i) {
        if (!this.pdpShots().length) return;
        if (typeof i === 'number') this.pdpIndex = i;
        this.pdpMag = false;
        this.pdpZoom = true;
      },
      pdpCloseZoom() { this.pdpZoom = false; this.pdpMag = false; },

      pdpZoomStep(d) {
        const n = this.pdpShots().length;
        if (!n) return;
        this.pdpMag = false;
        this.pdpIndex = ((this.pdpIndex + d) % n + n) % n;
      },

      // ── The magnifier inside the lightbox ───────────────────────────
      // Daniel, 2026-09-02: the open card is small even enlarged, so every
      // lightbox picture gets a second zoom -- "on click there's a big zoom
      // and I can move around the picture by moving the cursor". Click
      // toggles it; while it is on, the picture is scaled up around wherever
      // the cursor is, so moving the cursor pans it. The same handler takes a
      // finger: pointer events cover both, and the stage turns off
      // touch-action while magnified so a drag pans instead of scrolling.
      pdpToggleMag(ev) {
        this.pdpMag = !this.pdpMag;
        if (this.pdpMag && ev) this.pdpMagMove(ev);
      },
      pdpMagMove(ev) {
        if (!this.pdpMag || !ev || !ev.currentTarget) return;
        const r = ev.currentTarget.getBoundingClientRect();
        if (!r.width || !r.height) return;
        const clamp = function (v) { return Math.max(0, Math.min(100, v)); };
        this.pdpMagX = clamp((ev.clientX - r.left) / r.width * 100);
        this.pdpMagY = clamp((ev.clientY - r.top) / r.height * 100);
      },
      // transform-origin at the cursor is the whole trick: scaling about the
      // point under the cursor keeps that point still, so as the cursor moves
      // the origin moves with it and the picture slides the other way.
      pdpMagStyle() {
        if (!this.pdpMag) return '';
        return 'transform-origin: ' + this.pdpMagX.toFixed(2) + '% ' +
               this.pdpMagY.toFixed(2) + '%; transform: scale(' + PDP_MAG_SCALE + ')';
      },

      // "A birthday wish" — the occasion's own headline, which is already the
      // line printed on the card, so the page and the product agree.
      pdpTitle() {
        const o = this.occasion || this.occPick;
        return (o && OCCASION_HEADLINE[o]) || 'Your card';
      },

      pdpOccasionLabel() {
        /* In a search the sheet's design takes the occasion the finder would give it (finder.js finderOccasionFor),
           not the deck's pending pick, which is Birthday for anyone who never touched the deck. */
        const o = this.finder && typeof this.finderOccasionFor === 'function'
          ? this.finderOccasionFor(this.sheetDesign()) : (this.occasion || this.occPick);
        return o ? this.occasionLabelOf(o) : '';
      },

      // One tile's cover art — the first face of the design that occasion is
      // dressed in. Same catalogue as everything else, so a tile can never
      // advertise a card the builder does not offer.
      occCoverFor(slug) {
        // ⚠️ `url`, not `src`. occSlidesFor() returns { url, label } — reading
        // .src silently yielded '' and every tile rendered as a bare tint.
        const s = this.occSlidesFor(slug);
        return (s && s.length && s[0] && s[0].url) || '';
      },

      // The faint tint behind a tile's art. The handoff gives six (pink, teal,
      // iris, peach, leaf, ink) and no mapping, so they are dealt out by the
      // occasion's position — deterministic, so a tile keeps its colour
      // between visits, and evenly spread without anyone maintaining a table.
      occTint(slug) {
        const tints = ['pink', 'teal', 'iris', 'peach', 'leaf', 'ink'];
        let i = 0;
        for (let n = 0; n < OCCASIONS.length; n++) if (OCCASIONS[n].slug === slug) { i = n; break; }
        return tints[i % tints.length];
      },

      // Label shown on the empty cover until a design is chosen.
      occasionLabel() {
        return this.card.occasion ? this.occasionLabelOf(this.card.occasion) : 'Your design';
      },

      // ───── Saving ────────────────────────────────────────────────────
      //
      // ⛔ ONCE THE CARD IS IN THE CART, NOTHING MAY WRITE THE DRAFT AGAIN.
      // addToCart() clears it and then navigates to checkout — and that
      // navigation fires `pagehide`, whose handler is saveNow(). Without this
      // latch the draft was cleared and instantly written back, so every
      // customer who bought a card would find "Continue card" waiting for
      // them on the way home. Found while testing the clear, 2026-08-29.
      _draftClosed: false,

      // Coalesced: the note and the captions call this per keystroke.
      queueSave() {
        if (this._draftClosed) return;
        const self = this;
        clearTimeout(this._saveTimer);
        this._saveTimer = setTimeout(function () { saveDraft(self); }, 400);
      },

      // Called where a delay would lose the work: leaving the page.
      saveNow() {
        clearTimeout(this._saveTimer);
        if (this._draftClosed) return;
        saveDraft(this);
      },

      hasDraftWork() {
        const c = this.card || {};
        return this.photos.length > 0
          || (c.bottomNote && c.bottomNote.length > 0)
          || (c.captions || []).some(s => s && s.length > 0);
      },

      // ───── Close builder ────────────────────────────────────────────
      //
      // ⛔ NO window.confirm (Daniel, 2026-08-29). Two things were wrong with
      // it: it is the browser's chrome dropped into the middle of a card the
      // customer has been making, and what it SAID — "Your work is saved
      // here" — was false for ten days. Both are fixed here: the draft is
      // real now, and the asking is ours (.pp-leave, builder-dock).
      leaveOpen: false,

      closeBuilder() {
        if (!this.hasDraftWork()) { window.location.href = '/'; return; }
        this.saveNow();
        this.leaveOpen = true;
      },

      confirmLeave() {
        this.saveNow();
        track('pp_builder_left', { photos: this.photos.length });
        window.location.href = '/';
      },

      cancelLeave() { this.leaveOpen = false; },

      // greetingLine() was here. It rendered "Hi Daniel — your card · $17.99"
      // in the top bar, where the price read as part of a greeting. The Paper
      // & Ink top bar states the price on its own (2026-08-11), so there is
      // nothing left to greet with.

      // The photo currently being edited, or ''. The effect and border swatches
      // bound straight to `photos[selectedPhoto].dataUrl`, and x-show does NOT
      // stop Alpine evaluating a binding on a hidden element — so with no photo
      // selected (the normal state) that threw on every render pass, eight
      // times a page load, burying anything real in the console.
      editingPhotoUrl() {
        const p = this.selectedPhoto !== null && this.photos[this.selectedPhoto];
        return (p && p.dataUrl) || '';
      },

      // ───── The entry flow: occasion → design → name → email → photos ──
      //
      // `stage` still decides entry-vs-builder off photos + design, so these
      // only ever move the customer around inside the entry. The photos step
      // has no "next": adding the fourth photo flips `stage` on its own.
      isEntryStep(slug) { return this.stage === 'entry' && this.entryStep === slug; },

      entryIndex() { return ENTRY_STEPS.indexOf(this.entryStep); },

      // Position in the pip row. The card grid is still "step 1" territory —
      // the occasion narrowed the choice and this is picking within it, not a
      // new question — so it shares the first mark. Photos is the last dot.
      // (Name and email used to sit on the second mark; gone 2026-09-03.)
      entryProgress() {
        const i = this.entryIndex();
        if (i <= 0 || this.entryStep === 'design') return 1;
        return this.entryStep === 'photos' ? 3 : 2;
      },

      nextEntry() {
        const i = this.entryIndex();
        if (i === -1 || i === ENTRY_STEPS.length - 1) return;
        this.entryStep = ENTRY_STEPS[i + 1];
      },

      backEntry() {
        const i = this.entryIndex();
        if (i <= 0) return;
        // Going back TO the occasion means un-choosing it, and the design it
        // resolved with it — otherwise init's forward-skip sees an occasion
        // already set and bounces straight past the step being returned to.
        const prev = ENTRY_STEPS[i - 1];
        if (prev === 'occasion') { this.changeOccasion(); return; }
        this.entryStep = prev;
      },

      /* ── B: THE OLD ENTRY NEVER SHOWS (12 Oct, audit section 1; builderB() above) ──────────────────────────────
         True on B's page for a customer: the classic entry's 'occasion' and 'design' steps are never a screen.
         Artist mode (?artist=1) runs B since 14 Oct: the artist chooses a card where customers do, with the artist mode
         carried (ARTIST_CARDS_PARAM). Org mode (?org=1) runs B since the same day (audit 1.7): its card is the order's
         own design, so it never stands on the old entry at all (loadOrgCard goes to Photos); see oldEntryAway. */
      noOldEntry() { return builderB(); },

      /* Whenever B would stand on 'occasion' or 'design', go where a card is chosen instead: the occasion's own page
         when the builder knows a real one, else All cards, keeping ?for= and ?find= (and the claim, the preview
         switches). Not while a named design is still loading (designPending: B's wait shows, and this runs again
         when it settles), and never for a card in progress that has its design: that resumes on Photos. Called
         from init, when the catalogue settles, and on every change of entryStep. Returns whether it left. */
      oldEntryAway() {
        if (!this.noOldEntry() || this._oldEntryLeft) return false;
        /* ⭐ ORG MODE never leaves: the business app names the one design (loadOrgCard), and its screens (builder-org.liquid)
           stand here until it has. There is nowhere else to choose a card from. */
        if (this.org) return false;
        if (this.stage !== 'entry' || (this.entryStep !== 'occasion' && this.entryStep !== 'design')) return false;
        if (this.designPending) return false;
        /* ⭐ ARTIST MODE: not before the artist platform has answered (loadArtistDesigns). Signed out, terms, no cards
           left: those screens stand here (builder-artist.liquid). Ready with no design: All cards in artist mode, where
           the artist's own designs come first (pp-artist-mode.js; Daniel, 2026-09-17: "the artist's own designs lead
           every grid"), and every card opens back here with ?artist=1. */
        if (this.artist) {
          if (this.artist.state !== 'ready') return false;
          this._oldEntryLeft = true;
          location.replace('/pages/occasions?view=cards&' + ARTIST_CARDS_PARAM + '=1');
          return true;
        }
        if (this.hasDraftWork() && this.designChosen()) { this.entryStep = 'photos'; return false; }
        /* ⭐ A SET: where its cards are chosen is the set's page. */
        const set = activeSet();
        if (set) { this._oldEntryLeft = true; location.replace(set.home); return true; }
        let q;
        try { q = new URLSearchParams(location.search); } catch (e) { q = new URLSearchParams(); }
        rememberSwitches(q);
        try {
          const claimed = (q.get('claim') || '').toUpperCase();
          if (CLAIM_RE.test(claimed)) sessionStorage.setItem(CLAIM_KEY, claimed);
        } catch (e) { /* the claim's discount is in the Shopify session either way */ }
        const occ = this.occasion || q.get('occasion') || '';
        const real = occ && occ !== MY_DESIGNS && OCCASIONS.some(function (o) { return o.slug === occ; });
        const keep = new URLSearchParams();
        ['for', 'find'].forEach(function (k) { const v = (q.get(k) || '').slice(0, 200); if (v) keep.set(k, v); });
        let to = real ? '/pages/occasion/' + encodeURIComponent(occ) : '/pages/occasions?view=cards';
        const ks = keep.toString();
        if (ks) to += (to.indexOf('?') === -1 ? '?' : '&') + ks;
        this._oldEntryLeft = true;
        location.replace(to);
        return true;
      },

      /* "Change" on B's Photos page before the card exists (audit 1.2): back to where the visitor chose the design,
         never the old picker. The browser's Back when they came from one of our pages that chooses cards (All cards
         and its lightbox, an occasion's, a design's, a series' or an artist's page), else All cards, opened at this
         card's occasion. */
      ppbChangeDesign() {
        this.saveNow();
        /* ⭐ A SET (assets/pp-set.js; reviewers.com, 18 Oct): back to the set's page, never All cards. */
        const set = activeSet();
        if (set) { window.location.href = set.home; return; }
        let back = false;
        try {
          const r = document.referrer ? new URL(document.referrer) : null;
          back = !!r && r.origin === location.origin && window.history.length > 1
            && /^\/pages\/(occasions|occasion\/|cards|series|artists)/.test(r.pathname);
        } catch (e) { back = false; }
        if (back) { window.history.back(); return; }
        const occ = this.occasion && this.occasion !== MY_DESIGNS ? this.occasion : '';
        window.location.href = '/pages/occasions?view=cards' + (occ ? '&from=' + encodeURIComponent(occ) : '')
          + (this.artist ? '&' + ARTIST_CARDS_PARAM + '=1' : '');
      },

      // ───── Occasion ──────────────────────────────────────────────────
      applyOccasion(name, chosen) {
        // ⚠️ A CHANGE OF OCCASION IS A CHANGE OF COVER. The cover art is the
        // occasion — designForOccasion() resolves one from the other — so a
        // design chosen for the PREVIOUS occasion has to be let go, or
        // ensureDesignForOccasion() below sees designChosen() and keeps it.
        //
        // That is how a restored draft plus ?occasion=sympathy produced a
        // sympathy card with balloons on the front (2026-08-29). The photos,
        // captions and note are untouched: only the artwork that was picked
        // FOR the old occasion goes.
        const changing = this.occasion && this.occasion !== name;
        this.occasion = name;
        this.designPeek = null;
        this.occStoryStop();           // the deck is behind us now
        if (this.card) this.card.occasion = name;
        if (changing && this.card) this.card.design = null;
        if (DEFAULTS[name]) {   // dormant 3D fields — harmless
          const d = DEFAULTS[name];
          this.tagline = tagLineFromDefaults(name);
          this.highlight = Object.assign({}, d.highlight);
          this.message = d.message;
        }
        // Prefill the headline (app parity) unless the user already typed one.
        if (this.card && !this.card.topSentence && OCCASION_HEADLINE[name]) {
          this.card.topSentence = OCCASION_HEADLINE[name];
        }
        // `chosen`: the finder, where the customer picked the design FIRST and
        // the occasion follows from it (finder.js finderBeforeChoose). Nothing
        // to resolve, and the step is the picker's to advance.
        if (chosen) return;
        /* A design that STAYS (no occasion before this one: a ?design= link, All cards) still has its inside top
           dressed for no occasion. Its words, and whether its greeting shows, follow the occasion now, until the
           customer touches the top (topOnDesign). A changed occasion let the design go above, and the new one is
           dressed by chooseDesign below. */
        if (!changing && this.designChosen() && typeof this.topOnDesign === 'function') this.topOnDesign();
        // The occasion IS the design choice now. Before the step advances, so
        // that whatever comes next already has a dressed card.
        this.ensureDesignForOccasion();
        if (this.entryStep === 'occasion') this.nextEntry();
      },

      // ───── Photos ────────────────────────────────────────────────────
      // ───── Photo selection counting ──────────────────────────────────
      // The Accordion is four panels, so four photos, and the entry step now
      // says so in counts rather than in a paragraph. Previously anything
      // under four was rejected outright and the selection thrown away; a
      // partial pick is kept and topped up instead.
      photosMissing() { return Math.max(0, 4 - this.photos.length); },

      /* THE HEADLINE CARRIES THE COUNT (Daniel, 2026-09-04). With two photos
         in, the title used to stay "Pick 4 photos" and a small pill under it
         said "Add 2 photos"; in the 2026-09-03 user test the pill went
         unseen and the tester thought the pick had done nothing. */
      photoTitle() {
        /* ⭐ THE HEADLINE SAYS THE REFUSAL (2026-09-16). A refused pick adds
           nothing, so every other branch of this function returns the same
           "Pick 4 photos" it returned before: the customer taps Upload, picks
           five, the sheet closes, and the biggest words on the screen have not
           moved. That is the 2026-09-03 finding wearing a different hat — the
           tester "read the same 'Pick 4 photos' and thought nothing had
           happened". Daniel's 2026-09-04 answer was exactly this pair, the
           headline plus a tinted pill, and it was only ever applied to the ASK
           pill; the refusal kept the old silence. Measured on a 375px phone
           before the fix: the error sat 20px from the Upload button, fully on
           screen, in the page's own background colour, under an unchanged
           30px headline. */
        /* ⚠️ THE MARKER LIVES INSIDE `errors`, AND THAT IS THE WHOLE TRICK.
           Only ONE of the six `_photos` messages may take the headline — a
           HEIC that would not decode, a drop that replaced a picture, Google
           Photos not being connected are all wrong answers to "how many". A
           separate boolean would have to be cleared at every one of those
           sites (there are three that never touch acceptPhotos or handleFiles,
           including the single-photo replace path), and the first one anybody
           forgot would leave this headline shouting "That's more than 4" over
           an unrelated message. `errors` is replaced wholesale everywhere, so
           a key inside it dies with the message it belongs to, by construction
           and without a single reset. Sniffing the message text would also
           work today and break the first time somebody rewords it. */
        if (this.errors && this.errors._photoLimit) {
          /* ⚠️ TWO DIFFERENT REFUSALS, TWO DIFFERENT SENTENCES. "That's more
             than 4" is true when the PICK was too big. It is nonsense when the
             card is already full and they added one — Daniel, 2026-09-16:
             "even when I upload one photo, it says that's more than four." */
          return this.photos.length >= 4 ? 'Your card already has 4' : 'That’s more than 4';
        }
        const have = this.photos.length;
        if (have <= 0 || have >= 4) return 'Pick 4 photos';
        const n = 4 - have;
        return n === 1 ? 'Add one more photo' : 'Add ' + n + ' more photos';
      },

      /* The pill's nudge, re-armed. An identical second refusal leaves the
         message string untouched, and Alpine rightly does nothing with a value
         that has not changed — so the class must come off and go back on for
         the animation to restart. Two frames, not one: within a single frame
         the removal and the re-add coalesce into one style recalculation and
         the browser never observes the class absent. */
      _bumpPhotoErr() {
        const self = this;
        this.photoBump = false;
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { self.photoBump = true; });
        });
      },

      /* The refusal in the customer's own terms. A card that is already full
         and a pick that is simply too big are different problems, and only one
         of them is answered by "pick fewer" — the other needs a picture taken
         off the card first. Says how much room there actually is, because the
         next question after "no" is always "well, how many then". */
      _photoLimitMsg() {
        const have = this.photos.length;
        if (have >= 4) return 'Your card already has 4 — remove one first';
        const room = 4 - have;
        return 'There is room for ' + (room === 1 ? 'one more photo' : room + ' more photos');
      },
      // Said when a pick was bigger than the room and only the first ones were kept (handleFiles, _takeGooglePhotos).
      _trimNote(n) {
        return 'A card holds 4 photos, so we used the first ' + (n === 1 ? 'one' : n) +
          ' you picked. You can replace any of them.';
      },
      photoPrompt() {
        // Under the headline: where the count stands, in the same breath.
        return this.photos.length + ' of 4 picked';
      },

      /**
       * The one door photos come through, whatever the source.
       *
       * Returns false and sets the error when the pick would take the card
       * past four. Both the device picker and Google Photos call this, so the
       * counting rule is stated once.
       */
      acceptPhotos(items) {
        if (!items || items.length === 0) return false;
        if (this.photos.length + items.length > 4) {
          track('pp_photos_rejected', {
            count: items.length, have: this.photos.length, reason: 'more_than_four',
          });
          // _photoLimit rides with the message — see photoTitle().
          this.errors = { _photos: this._photoLimitMsg(), _photoLimit: true };
          this._bumpPhotoErr();
          return false;
        }
        /* ⛔ A PICK THAT LANDED RETIRES THE LAST REFUSAL. Without this the
           headline goes on shouting about a pick two picks ago: card holds 3,
           a pick of 2 is refused, a pick of 1 then succeeds — and the card,
           now legitimately full, still carries "Your card already has 4" as a
           complaint rather than a fact. Measured on Draft, 2026-09-16.

           ⚠️ IT BELONGS HERE, not only in handleFiles. This is the one door
           every source comes through — Google Photos calls it directly — and
           clearing it upstream only covers the file picker. The earlier clear
           in handleFiles stays: it fires BEFORE the import, so the headline
           does not sit stale for the second or two a phone takes to decode. */
        if (this.errors && this.errors._photoLimit) this.errors = {};

        const first = this.photos.length;
        this.photos = this.photos.concat(items);
        this.details = this.details.concat(items.map(function () {
          return { date: todayISO(), caption: '' };
        }));
        /* ✨ THE PICTUREPOPS LOOK BY DEFAULT (Daniel's Pop List, 12 Oct: "Turn on the Deep colour filter by default,
           show a sparkly 'beautifying your photos' moment as the photos appear, and let people switch it off"; then,
           the same day, gentler than Deep: the 'pop' look in EFFECT_FILTERS).
           Only photos new to this card: a restored draft's carry their own effect (they arrive with the key), and a
           Google Photos placeholder has no picture yet to sweep. The sweep waits for the card to open (_magicSoon),
           and the look lands under it, as the wand's does. ?ppdeep=0 turns it off in this browser, ?ppdeep=1 back. */
        if (this._deepByDefault()) {
          const fresh = [];
          items.forEach(function (it, k) { if (!('effect' in it) && it.dataUrl) fresh.push(first + k); });
          if (fresh.length) { this._magicIdx = (this._magicIdx || []).concat(fresh); this._magicSoon(); }
        }
        // Faces into the window, once the pictures are already on the card (see _faceCentre).
        const self = this;
        items.forEach(function (it, k) { if (it.dataUrl && !it._loading) self._faceCentre(first + k); });
        return true;
      },
      /* ── FACES IN THE WINDOW (Daniel and Yonatan's 19 Oct sync: "auto-centre uploaded photos on faces so faces
         aren't cropped") ─────────────────────────────────────────────────────────────────────────────────────────
         PPVision (assets/pp-vision.js) finds the faces in the customer's own browser, and the pan moves only as far as
         it takes to bring them, with room for hair and chin, inside the window: a picture whose faces already show is
         left exactly as it was. Faces wider than the window are centred.
         ⛔ NEVER IN THE WAY. Asked after the picture is on the card; null (could not look) and [] (no face) leave the
         crop alone; and the answer is dropped if the customer has moved, zoomed or tilted the picture meanwhile, or
         it has been replaced. The picture is found again by its pixels, since a swap can move it to another window.
         The pan it chose is kept as facePan, so Reset goes back to it ("back to how it arrived"). */
      _faceCentre(i) {
        const p = this.photos[i];
        if (!p || !p.dataUrl || p._loading || !window.PPVision) return;
        const self = this, src = p.dataUrl, t0 = Date.now();
        window.PPVision.faces(src).then(function (found) {
          if (!found || !found.length) return;
          const at = self.photos.findIndex(function (q) { return q && q.dataUrl === src; });
          const q = self.photos[at];
          if (!q || (q.cropScale || 1) > 1.001 || (q.tilt || 0) !== 0 ||
              (q.panX != null && q.panX !== 50) || (q.panY != null && q.panY !== 50)) return;
          const pan = facePan(photoBox(q, self.boxAspect()), found);
          if (!pan) return;
          self._patchPhoto(at, { panX: pan.x, panY: pan.y, facePan: pan });
          self.queueSave();
          track('pp_photo_facecentre', { faces: found.length, ms: Date.now() - t0 });
        });
      },
      _deepByDefault() {
        try {
          const m = /[?&]ppdeep=([01])(?:&|$)/.exec(location.search);
          if (m) { if (m[1] === '0') localStorage.setItem('pp_ppdeep', '0'); else localStorage.removeItem('pp_ppdeep'); }
          return localStorage.getItem('pp_ppdeep') !== '0';
        } catch (e) { return true; }
      },
      _magicSoon() {
        if (this._magicWait) return;
        const self = this, t0 = Date.now();
        const tick = function () {
          if (!self._magicIdx || !self._magicIdx.length) { self._magicWait = null; return; }
          const ready = self.stage === 'builder' && self._openingDecided && !self.opening && !self.closing && !self.casting && !self._castPrep;
          if (!ready && Date.now() - t0 < 20000) { self._magicWait = setTimeout(tick, 200); return; }
          self._magicWait = null;
          self._magicCast();
        };
        this._magicWait = setTimeout(tick, 400);
      },
      async _magicCast() {
        const self = this, idx = (this._magicIdx || []).filter(function (i) { return self.photos[i]; });
        if (!idx.length) { this._magicIdx = []; return; }
        const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.magicNote = 'casting';
        this._castPrep = true;
        /* The look is baked first, one photo at a time, under the "Beautifying" note, so mid-sweep each picture swaps to
           a finished image instead of four live filters landing in the 3D scene at once (bakeLook). Capped: a slow
           machine gets the CSS filter for a moment rather than a stalled card. */
        await Promise.race([
          (async function () { for (const i of idx) await self._prebake(i, 'pop'); })(),
          new Promise(function (r) { setTimeout(r, 2500); }),
        ]);
        this._castPrep = false;
        this.casting = true;
        this.castPhoto = 'all';
        setTimeout(function () {
          idx.forEach(function (i) {
            const p = self.photos[i];
            if (p && (!p.effect || p.effect === 'original')) self._patchPhoto(i, { effect: 'pop' });
          });
          self._magicDone = idx;
          track('pp_deep_default', { photos: idx.length });
        }, reduced ? 0 : CAST_APPLY_MS + 280);
        setTimeout(function () {
          self.casting = false; self.castPhoto = null; self._magicIdx = [];
          self.magicNote = 'done';
          clearTimeout(self._magicNoteT);
          self._magicNoteT = setTimeout(function () { if (self.magicNote === 'done') self.magicNote = null; }, 7000);
        }, reduced ? 0 : CAST_MS + 700);
      },
      // "Use the originals": the photos the look was put on go back to no filter.
      magicUndo() {
        const self = this;
        (this._magicDone || []).forEach(function (i) {
          const p = self.photos[i];
          if (p && p.effect === 'pop') self._patchPhoto(i, { effect: 'original' });
        });
        this._magicDone = [];
        this.magicNote = null;
        track('pp_deep_default_undo', {});
      },

      /* `source` reaches the analytics at the bottom: 'device' for the file
         picker, 'drop' for a drag onto the step. Same event either way — the
         funnel counts photo picks, and how they arrived is a property of the
         pick, not a different pick. */
      async handleFiles(fileList, source) {
        const files = Array.from(fileList);
        if (files.length === 0) return;

        // Checked BEFORE decoding: importing five photos and then refusing
        // them wastes the slowest part of the step on a pick we will reject.
        if (this.photos.length >= 4) {
          track('pp_photos_rejected', {
            count: files.length, have: this.photos.length, reason: 'more_than_four',
          });
          // _photoLimit rides with the message — see photoTitle().
          this.errors = { _photos: this._photoLimitMsg(), _photoLimit: true };
          this._bumpPhotoErr();
          return;
        }
        /* ⭐ A PICK BIGGER THAN THE ROOM KEEPS THE FIRST ONES THAT FIT (Daniel, 2026-10-25). Refusing the whole pick
           lost ad visitors: in PostHog replays of 18-24 Oct, people on phones picked 5+ photos, read "That's more
           than 4" over "There is room for 4 more photos", got nothing on the card, and left. Interim, until the
           photo step takes any number and lets them choose (projects/pop-cards/docs/plans/2026-10-25-part-b-photo-count-plan.md). */
        const room = 4 - this.photos.length;
        const dropped = Math.max(0, files.length - room);
        if (dropped) files.length = room;

        /* ⛔ THE LAST REFUSAL IS ABOUT THE LAST PICK, AND THIS IS A NEW ONE.
           Cleared HERE rather than after the import below, because importing a
           photograph on a phone takes a second or two and the headline would
           go on shouting "That's more than 4" over a pick that was perfectly
           fine — which is half of what Daniel saw. */
        if (this.errors && this.errors._photoLimit) this.errors = {};

        const next = [];
        const newErrors = {};
        for (let i = 0; i < files.length; i++) {
          try {
            const im = await importPhoto(files[i]);
            next.push({ dataUrl: im.dataUrl, w: im.w, h: im.h,
                        // See the note in replacePhoto — these two must ride
                        // along or the resolution check has nothing to read.
                        srcW: im.srcW, srcH: im.srcH,
                        name: files[i].name, size: files[i].size,
                        cropScale: 1, panX: 50, panY: 50, tilt: 0 });
          } catch (e) {
            newErrors['photo_' + i] = photoErrorMessage(e, files[i]);
          }
        }
        /* ⛔ A REFUSED FILE MUST BE SAID WHERE IT CAN BE READ. The per-file
           messages above live under photo_N, and since the builder's photo
           screens were rebuilt nothing renders photo_N: only errors._photos
           has a line on screen (configurator-entry, builder-b-photos). Found
           2026-10-11 with a 14 MB HEIC: the import refused it and the page
           said nothing at all, still "Pick 4 photos". photo_N stays for the
           reorder bookkeeping; the sentences ride on _photos. */
        const failed = Object.keys(newErrors).length;   // files, before _photos joins them
        const said = Object.keys(newErrors).map(function (k) { return newErrors[k]; })
          .filter(function (m, j, a) { return a.indexOf(m) === j; });
        if (dropped && next.length) said.unshift(this._trimNote(next.length));
        if (said.length) newErrors._photos = said.join(' ');
        this.errors = newErrors;
        this.acceptPhotos(next);
        // The other way in: not zoomed at all, just a small photograph. Said
        // once, after the set lands, rather than per file — four toasts in a
        // row is a dialog by another name.
        this.$nextTick(() => {
          for (let i = 0; i < this.photos.length; i++) {
            const q = this.photoQuality(i);
            if (q && q.band !== 'ok') { this.showSoftNotice(q.band); break; }
          }
        });
        track('pp_photos_selected', {
          count: next.length, total: this.photos.length, source: source || 'device',
          failed: failed, dropped: dropped,
        });
      },

      /* ───── Y10 — DROP THE FILE INSTEAD OF HUNTING FOR IT ────────────────
         Yonatan, 2026-09-10: "עוד רעיון שיהיה מאוד כיפי כשעושים במחשב - לעשות
         דרופ של הקובץ במקום לחפש בתיקייה" — drag and drop the file rather than
         going through a folder.

         ⭐ THE DESKTOP HALF OF Y4. On a phone the interaction is already
         direct — two fingers on the picture (see pinchAllowed). On a desktop
         it is a file dialog, and this is the same complaint from the other
         side of the screen.

         Everything lands in handleFiles(), which already owns the count guard,
         the per-file errors, the HEIC path and the resolution notice. Nothing
         about importing is re-implemented here; this only decides what counts
         as a droppable file and where a drop is accepted. */
      dropping: false,
      _dropDepth: 0,
      /* The panel under the cursor during a drag, or null. A drop on a picture
         REPLACES that picture; a drop anywhere else adds to the set. */
      dropPanel: null,

      /* Only a drag carrying FILES. Dragging selected TEXT across the page
         also fires dragenter, and lighting the panel up for it would be a
         promise we cannot keep. */
      _dragHasFiles(e) {
        const t = e && e.dataTransfer && e.dataTransfer.types;
        if (!t) return false;
        for (let i = 0; i < t.length; i++) if (t[i] === 'Files') return true;
        return false;
      },

      /* ⚠️ TYPE OR EXTENSION, NEVER TYPE ALONE. A HEIC dropped from Finder
         very often arrives with an EMPTY `type` — the browser does not know
         the format — and `type.startsWith('image/')` would throw away exactly
         the format an iPhone produces by default. Yonatan's own screenshot
         shows .HEIC files in the picker, so this is the common case, not the
         edge. The `accept` attribute on the file input names them for the same
         reason. */
      _isImageFile(f) {
        if (!f) return false;
        if (f.type && /^image\//i.test(f.type)) return true;
        return /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp|tiff?)$/i.test(f.name || '');
      },

      /* Which picture the pointer is over, or null. Uses the panel's existing
         data-pp-panel rather than a second attribute — the planes already take
         clicks (focusPhoto), so they are hit-tested during a drag too. */
      _panelUnder(e) {
        const el = e && e.target;
        if (!el || !el.closest) return null;
        const panel = el.closest('[data-pp-panel]');
        if (!panel) return null;
        const i = parseInt(panel.getAttribute('data-pp-panel'), 10);
        return isNaN(i) ? null : i;
      },

      photoDragOver(e) {
        if (!this._dragHasFiles(e)) return;
        /* Both preventDefault AND dropEffect: without the first the drop never
           fires, without the second the cursor says "no entry" over a target
           that will in fact accept it. */
        e.preventDefault();
        try { e.dataTransfer.dropEffect = 'copy'; } catch (err) {}
        this.dropping = true;
        const i = this._panelUnder(e);
        this.dropPanel = (i !== null && this.photos[i]) ? i : null;
      },

      /* What the veil says. The three cases are genuinely different and one
         wording for all of them would be wrong in two of them. */
      dropHint() {
        if (this.dropPanel !== null) return 'Drop to replace this photo';
        if (this.photos.length >= 4) return 'Card is full — drop onto a photo to replace it';
        const left = 4 - this.photos.length;
        return this.photos.length
          ? ('Drop to add — room for ' + left + (left === 1 ? ' more' : ' more'))
          : 'Drop your photos';
      },

      /* ⛔ COUNTED, NOT TOGGLED. dragenter/dragleave fire for every child the
         pointer crosses, so a plain "leave means off" flickers the whole panel
         as the cursor passes over the button, the icon, the text. Depth
         counting is the standard answer and the only one that survives nested
         children. */
      photoDragEnter(e) {
        if (!this._dragHasFiles(e)) return;
        this._dropDepth++;
        this.dropping = true;
      },

      photoDragLeave(e) {
        if (!this._dragHasFiles(e)) return;
        this._dropDepth = Math.max(0, this._dropDepth - 1);
        if (this._dropDepth === 0) { this.dropping = false; this.dropPanel = null; }
      },

      photoDrop(e) {
        if (!this._dragHasFiles(e)) return;
        e.preventDefault();
        this._dropDepth = 0;
        this.dropping = false;
        const onPanel = this._panelUnder(e);
        this.dropPanel = null;

        const all = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
        const imgs = all.filter((f) => this._isImageFile(f));

        if (!imgs.length) {
          /* Say which of the two it was. "That is not a photo" over an empty
             drop is confusing; over a PDF it is the answer. */
          this.errors = {
            _photos: all.length
              ? (all.length === 1 ? 'That file isn’t a photo' : 'Those files aren’t photos')
              : 'Nothing was dropped — try again',
          };
          track('pp_photos_rejected', { count: all.length, have: this.photos.length,
                                        reason: 'not_an_image' });
          return;
        }

        /* ⭐ A DROP ONTO A PICTURE REPLACES THAT PICTURE. It is the obvious
           meaning of the gesture, and it is the only thing that works once the
           card is full — which is most of the time somebody is looking at it. */
        if (onPanel !== null && this.photos[onPanel]) {
          this.replaceOne([imgs[0]], onPanel);
          if (imgs.length > 1) {
            this.errors = { _photos: 'Dropped ' + imgs.length + ' — replaced this one with the first' };
          }
          track('pp_photos_selected', { count: 1, total: this.photos.length,
                                        source: 'drop_replace', failed: 0 });
          return;
        }

        /* ⛔ FULL, AND NOT AIMED AT A PICTURE. handleFiles would answer "Pick
           only 4 photos", which is true and useless: it does not say what to do
           instead. Say the thing that works. */
        if (this.photos.length >= 4) {
          this.errors = { _photos: 'Your card already has 4 photos — drop onto one to replace it' };
          track('pp_photos_rejected', { count: imgs.length, have: this.photos.length,
                                        reason: 'full_not_on_panel' });
          return;
        }

        /* ⚠️ A mixed drop takes the pictures and says nothing about the rest.
           Refusing the whole drop because a .txt rode along would be pedantry;
           handleFiles still refuses if the PICTURES do not fit. */
        this.handleFiles(imgs, 'drop');
        /* A drop can arrive from any step, including the occasion picker — so
           send the customer where their photographs just went. */
        if (this.stage === 'entry') this.entryStep = 'photos';
        this.setTab('photos');
      },

      // ───── Per-photo edit mode (Effect · Border · Crop · Caption) ────
      focusPhoto(i) {
        if (i < 0 || i >= this.photos.length) return;
        // Already editing this one: do nothing. The panel's own @click fires at
        // the end of a pan drag too, and this used to reset editSub — so the
        // Crop drawer closed the moment the customer let go of the picture.
        if (this.selectedPhoto === i && this.editSub !== 'none') return;
        // A drag that has just finished re-ordering the pictures ends with a
        // click on whatever is under the finger. Opening that picture's editor
        // is not what the customer asked for — they asked to move one.
        if (this._suppressPanelClick) return;
        // ⛔ EVERY ARRIVAL AT A PICTURE STARTS WITH NO TOOL SELECTED.
        //
        // This used to open on Effect, and to KEEP the open tool when moving
        // between pictures — the reasoning was that comparing two photographs
        // under one tool should not cost three clicks. What it actually
        // produced is a tool that is lit without having been chosen: leave a
        // picture with Caption open, come back, and Effect is lit again;
        // open Crop on picture 1, click picture 2, and Crop is still on.
        // Daniel, 2026-08-29: "I don't want the crop to still be selected.
        // That's the same thing for all of the options."
        //
        // So the rule is now the simple one — nothing is selected unless the
        // customer selected it, this time. clearTools() is the single place
        // that says so; every way IN and every way OUT goes through it.
        this.clearTools();
        this.selectedPhoto = i;
        this.section = 'photos';
        // Centre the strip on this picture, or Done hands back a view that
        // jumps sideways to somewhere the customer never scrolled to.
        const s = this.photoStrip();
        const b = this.frameBoxes && this.frameBoxes[PANELS[i]];
        if (s && s.travel && b) {
          const t = (b.cx - (s.strip.cx - s.travel / 2)) / s.travel;
          this.photoScroll = Math.max(0, Math.min(1, t));
        }
      },
      // The one place that puts the toolbar back to "nothing chosen". Both the
      // card-wide popover and the picture's own tool, because a customer who
      // has just left a picture is not still holding either — and Done used to
      // leave Font lit over the card behind it (Daniel, 2026-08-29).
      clearTools() {
        this.cardTool = null;
        this.editSub = 'none';
      },

      closePhoto() {
        this.selectedPhoto = null;
        this.clearTools();
      },

      // ───── The two ways out, and they are the same way ───────────────
      //
      // Click off the card, or press Escape, and you are back at Full card
      // (Daniel, 2026-08-11). Not "close the editor" — all the way out, to the
      // whole card. There is no Done anywhere in the dock, so these two plus
      // the Full card pill are the entire vocabulary for leaving a state, and
      // they had better all land in the same place.
      //
      // setSection('card') already nulls selectedPhoto, so this covers editing
      // a picture and writing the note in one move.
      leaveToFullCard() {
        if (this.stage !== 'builder') return;
        if (this.section === 'card' && this.selectedPhoto === null) return;
        // A field inside the dock may still hold focus — the note, a caption.
        // Leaving the keyboard caret behind in a box that is about to collapse
        // strands it, and on a phone the keyboard stays up over the card.
        const a = document.activeElement;
        if (a && typeof a.blur === 'function' && a !== document.body) a.blur();
        // ⛔ FROM A SELECTED PICTURE THIS ONLY DESELECTS. It used to go all the
        // way out to the whole-card view, which was right while `Full card`
        // was a pill you could come back on. It isn't one any more
        // (2026-08-16), so leaving Edit photos on a stray click would drop the
        // customer into a view with no tool showing and no lit pill to explain
        // how they got there. Deselecting lands them on the card-wide row,
        // which is where the card opened.
        if (this.section === 'photos' && this.selectedPhoto !== null) {
          this.closePhoto();
          return;
        }
        this.setSection('card');
      },

      // A click on the stage that did not land on the card itself.
      //
      // NOT @click.self: .pp-zoom and .pp-scene fill the viewport, so a click
      // on empty cream is almost never targeted at the viewport element. What
      // actually distinguishes "off the card" is that the click did not come
      // from inside a plane — ask that instead.
      stageBackgroundClick(e) {
        // In Preview a click on the card (or the stage) toggles the look-close
        // zoom — the only click action on this screen.
        if (this.section === 'preview') { this.previewZoomToggle(e); return; }
        if (this._suppressPanelClick) return;
        if (this.panning() || this.reordering()) return;
        if (e.target && e.target.closest && e.target.closest('.pp-plane')) return;
        this.leaveToFullCard();
      },

      /* ── PREVIEW: LOOK CLOSE ──────────────────────────────────────────────
         Click the card to zoom in and roam it with the cursor — a loupe over
         the finished card, so the customer can check their photos at high
         fidelity before buying. Click again to zoom out. The label on the
         cursor (below) flips between the two.

         The zoom is driven straight onto the camera vars with _camDrive, the
         same channel the fold uses — no reactive write per pointermove (that
         has drawn this builder blank before). Zoom-in eases; the roam is
         instant so the card tracks the cursor; zoom-out hands the camera back
         to cameraTarget, which glides to the fit on its own transition. */
      previewZoomText() { return this.previewZoom ? 'Click to zoom out' : 'Click to zoom'; },
      // The touch equivalent of the cursor label — a static hint, since a
      // phone has no hover to carry "Click to zoom". On a desktop it shows
      // only while zoomed, to say how to move around (stage.css .is-roaming).
      previewZoomHint() {
        return this.previewZoom ? 'Drag to move · tap to zoom out' : 'Tap the card to zoom in';
      },
      // Touch only. A desktop had "Drag or scroll to look around" while zoomed, for an afternoon: Daniel, 11 Oct, "on
      // product zoom we don't need the drag or scroll". The cursor label still says "Click to zoom out".
      previewHintOn() {
        return this.coarsePointer() && this.previewCanZoom();
      },
      /* ⭐ THE ROAM (2026-10-11). Desktop panning was the cursor's position
         mapped to the pan, run from the stage's pointermove — and that handler
         had not run since 2026-09-10, when the note's balloon added a SECOND
         @pointermove to the same element and the HTML parser kept only the
         first. Zoomed, the card could not be moved at all, and the label froze
         where the cursor came in (Daniel, 11 Oct). Now it is a drag, a wheel or
         trackpad scroll and the arrow keys, shared with the design lightbox
         (assets/cursor-tag.js, PP.Roam). */
      _pzRoamer() {
        if (this._pzRoamObj) return this._pzRoamObj;
        if (!window.PP || !window.PP.Roam) return null;
        const self = this;
        this._pzRoamObj = window.PP.Roam.create({
          geom: function () { return self._previewGeom(); },
          write: function (x, y, smooth) { self._previewApply(x, y, smooth); },
        });
        return this._pzRoamObj;
      },
      previewCanZoom() {
        return this.section === 'preview' && !this.cardClosed &&
               !this.opening && !this.closing && this.camReady();
      },
      previewZoomToggle(e) {
        // A tap that ended a pan drag is not a toggle — see previewPanMove.
        if (this._pzMoved) { this._pzMoved = false; return; }
        const r0 = this._pzRoamObj;
        if (r0 && r0.tapAfterDrag()) return;
        if (this.previewZoom) { this._previewZoomExit(); return; }
        if (!this.previewCanZoom()) return;
        this.previewZoom = true;
        this._pzPanX = 0; this._pzPanY = 0;
        if (this.coarsePointer() || !e) {
          // Touch: zoom in CENTRED, then a finger drag roams.
          this._previewApply(0, 0, true);
        } else {
          // Desktop: toward the click, so what was clicked is what comes
          // close; from there a drag, the wheel or the arrows move it.
          this._pzMX = e.clientX; this._pzMY = e.clientY;
          this._previewPanFromCursor(true);
        }
        const r = this._pzRoamer();
        if (r) r.reset(this._pzPanX, this._pzPanY);
      },
      _previewZoomExit() {
        if (!this.previewZoom) return;
        this.previewZoom = false;
        this._pzDrag = null;
        if (this._pzRaf) { cancelAnimationFrame(this._pzRaf); this._pzRaf = 0; }
        this._camRelease();          // eases back to the fit
      },

      // The zoom-in geometry: where the card centres at ZOOM, and how far it
      // can be roamed in each axis (its own projected overflow past the visible
      // area, so every edge is reachable). Shared by the cursor and drag paths.
      _previewGeom() {
        const stage = document.querySelector('[data-pp-stage]');
        if (!stage) return null;
        const r = stage.getBoundingClientRect();
        const sw = r.width, sh = r.height;
        if (!sw || !sh) return null;
        const base = this.cameraTarget();
        const inset = this.uiInset();
        const ZOOM = 2.3;            // how close the loupe gets
        const scaleZ = base.scale * ZOOM;
        const cx = base.x * ZOOM;
        const cy = (base.y + inset / 2) * ZOOM - inset / 2;
        const u = union(this.framingBoxes('card'));
        const halfW = u ? (u.w * scaleZ) / 2 : sw / 2;
        const halfH = u ? (u.h * scaleZ) / 2 : sh / 2;
        const clearH = Math.max(1, sh - inset);
        return {
          r: r, cx: cx, cy: cy, scaleZ: scaleZ, clearH: clearH,
          rangeX: Math.max(0, halfW - sw / 2),
          rangeY: Math.max(0, halfH - clearH / 2),
        };
      },
      // Apply a pan (px) around the centred zoom, clamped to the card's reach.
      _previewApply(panX, panY, smooth) {
        const g = this._previewGeom();
        if (!g) return;
        const px = Math.max(-g.rangeX, Math.min(g.rangeX, panX));
        const py = Math.max(-g.rangeY, Math.min(g.rangeY, panY));
        this._pzPanX = px; this._pzPanY = py;
        this._camDrive({ x: g.cx + px, y: g.cy + py, scale: g.scaleZ }, smooth);
      },
      // Desktop: the cursor's position over the stage maps to the pan — cursor
      // toward an edge reveals that edge (normalised over the clear area).
      _previewPanFromCursor(smooth) {
        const g = this._previewGeom();
        if (!g) return;
        let tx = (this._pzMX - g.r.left) / g.r.width;
        let ty = (this._pzMY - g.r.top) / g.clearH;
        if (tx < 0) tx = 0; else if (tx > 1) tx = 1;
        if (ty < 0) ty = 0; else if (ty > 1) ty = 1;
        this._previewApply(g.rangeX * (1 - 2 * tx), g.rangeY * (1 - 2 * ty), smooth);
      },

      // ── Touch: grab and drag the zoomed card ─────────────────────────────
      // Relative, not the desktop cursor-follow: the card moves WITH the
      // finger (drag left reveals the right). A drag also cancels the tap, so
      // it never zooms out mid-roam (the _pzMoved guard in previewZoomToggle).
      previewPanDown(e) {
        /* ⛔ TOUCH IS THE TOUCH HANDLERS' NOW (2026-09-16) — see stageTouchStart.
           Both models fire for one finger on iOS, so leaving this path live
           applied every drag twice and the card roamed at double speed. This
           stays for any pointer device that is coarse but not touch (a pen, a
           kiosk panel), which is why it is not deleted outright. */
        if (e && e.pointerType === 'touch') return;
        if (this.section !== 'preview' || !this.previewZoom) return;
        /* A mouse or a pen: the shared roam. Captured on the stage, so the drag
           keeps going over the bars and outside the window. */
        const r = this._pzRoamer();
        if (r && r.down(e)) {
          this._pzDrag = { roam: true };
          const vp = document.querySelector('[data-pp-stage]');
          try { if (vp && vp.setPointerCapture) vp.setPointerCapture(e.pointerId); } catch (err) {}
          if (vp) vp.classList.add('is-grabbing');
        }
      },
      previewPanMove(e) {
        if (!this._pzDrag) return;
        if (this._pzDrag.roam) { const r = this._pzRoamer(); if (r) r.move(e); return; }
        const dx = e.clientX - this._pzDrag.fx, dy = e.clientY - this._pzDrag.fy;
        if (!this._pzMoved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) this._pzMoved = true;
        if (!this._pzRaf) {
          const self = this;
          this._pzRaf = requestAnimationFrame(function () {
            self._pzRaf = 0;
            if (self._pzDrag) self._previewApply(self._pzDrag.baseX + dx, self._pzDrag.baseY + dy, false);
          });
        }
      },
      previewPanUp() {
        if (this._pzDrag && this._pzDrag.roam) {
          const r = this._pzRoamer();
          if (r) { r.up(); this._pzPanX = r.x; this._pzPanY = r.y; }
          const vp = document.querySelector('[data-pp-stage]');
          if (vp) vp.classList.remove('is-grabbing');
        }
        this._pzDrag = null;
      },
      // Wheel or two-finger trackpad scroll while zoomed: the card moves the
      // way a page scrolls. A trackpad pinch (ctrlKey) zooms in and out.
      previewWheel(e) {
        if (this.section !== 'preview') return false;
        if (e.ctrlKey) {
          e.preventDefault();
          const now = Date.now();
          if (now - (this._pzPinchAt || 0) < 450) return true;
          if (e.deltaY < -1 && !this.previewZoom && this.previewCanZoom()) { this._pzPinchAt = now; this.previewZoomToggle(e); }
          else if (e.deltaY > 1 && this.previewZoom) { this._pzPinchAt = now; this._previewZoomExit(); }
          return true;
        }
        if (!this.previewZoom) return false;
        const r = this._pzRoamer();
        if (!r) return false;
        r.wheel(e);
        this._pzPanX = r.x; this._pzPanY = r.y;
        return true;
      },
      previewKey(e) {
        if (this.section !== 'preview' || !this.previewZoom) return false;
        const t = e.target;
        if (t && t.closest && t.closest('input, textarea, select, [contenteditable="true"]')) return false;
        if (e.key === 'Escape' || e.key === 'Esc') { this._previewZoomExit(); return true; }
        const r = this._pzRoamer();
        if (!r || !r.key(e)) return false;
        this._pzPanX = r.x; this._pzPanY = r.y;
        return true;
      },

      // ── The "Click to zoom" label, on the cursor (Preview only) ──────────
      // Reuses the note hover-tag's spring loop and module vars; the two never
      // run at once (different sections). Direct style writes, never Alpine.
      /* Only where a click WILL zoom. Over the shut card (and while it folds)
         previewCanZoom() is false and a click does nothing, yet the label
         still said "Click to zoom" — seen in the 2026-09-03 user test and
         still on Live 2026-09-04 (bd03a58 hid only the touch hint). While
         zoomed the label reads "Zoom out" and stays, whatever is under it. */
      previewTagWanted() {
        return this.section === 'preview' && !this.coarsePointer() &&
               (this.previewZoom || this.previewCanZoom());
      },
      previewTagOn(e) {
        if (!this.previewTagWanted()) return;
        const el = document.querySelector('[data-pp-zoomtag]');
        const vp = document.querySelector('[data-pp-stage]');
        if (!el || !vp) return;
        TAG.rect = vp.getBoundingClientRect();
        TAG.el = el;
        TAG.tx = TAG.px = e.clientX - TAG.rect.left;
        TAG.ty = TAG.py = e.clientY - TAG.rect.top;
        TAG.angle = 0; TAG.seen = false;
        el.style.transform = 'translate3d(' + TAG.px.toFixed(1) + 'px,' + TAG.py.toFixed(1) + 'px,0)';
        el.classList.add('is-on');
        TAG.loop();
      },
      previewTagMove(e) {
        // Where the cursor is over the stage, kept on every step (a plain field, never reactive) so the label can
        // appear under a cursor that is not moving (the previewTagWanted watcher in init).
        this._tagOver = { x: e.clientX, y: e.clientY };
        if (this.section !== 'preview') return;
        if (!this.previewTagWanted()) { this.previewTagOff(); return; }
        // Lazy start: entering Preview with the cursor already over the stage
        // fires no pointerenter, so the label is set up on the first move.
        const el = document.querySelector('[data-pp-zoomtag]');
        if (TAG.el !== el) {
          this.previewTagOn(e);
        } else if (TAG.rect) {
          TAG.tx = e.clientX - TAG.rect.left;
          TAG.ty = e.clientY - TAG.rect.top;
          TAG.seen = false;
          if (!TAG.raf) TAG.loop();
        }
        // The cursor no longer roams the zoom: a drag, the wheel and the
        // arrows do (_pzRoamer, 2026-10-11).
      },
      previewTagOff(e) {
        if (e && e.type === 'pointerleave') this._tagOver = null;
        if (this.section !== 'preview') return;
        if (TAG.el) TAG.el.classList.remove('is-on');
        TAG.el = null; TAG.rect = null;
      },

      /* ── "Edit picture", on the cursor over a photo ───────────────────────
         Daniel, 2026-09-03: the same violet cursor pill over the pictures, in
         Edit pictures and in the zoomed-out whole card. Since 2026-09-04 it
         rides the WHOLE panel — border, picture and caption band — and the
         handlers hang off the panel root (Daniel: "keep the label visible
         whenever a user hovers over any of the panels"). A click anywhere on
         the panel opens the picture, so the label is honest everywhere on it.

         Between two panels the pointer leaves one and enters the next within
         a frame or two. pictureTagOff therefore only SCHEDULES the removal
         (TAG_OFF_MS); an On that lands first cancels it and just re-aims the
         spring, so the pill glides across the join instead of blinking out
         and re-entering with a jump. Reuses .pp-hovertag and the note's
         spring loop; only one tag runs at a time. Not while a picture is
         already selected for editing. */
      pictureTagText() { return 'Edit picture'; },
      pictureTagWanted() {
        return !this.coarsePointer() && this.panelInteractive() &&
               this.selectedPhoto === null && !this.reordering() &&
               (this.section === 'photos' || this.section === 'card');
      },
      pictureTagOn(e) {
        if (!this.pictureTagWanted()) return;
        const el = document.querySelector('[data-pp-pictag]');
        const vp = document.querySelector('[data-pp-stage]');
        if (!el || !vp) return;
        // A removal pending from the panel just left: cancel it, and if the
        // pill is still up simply keep chasing the cursor from where it is.
        if (this._picTagOffT) { clearTimeout(this._picTagOffT); this._picTagOffT = 0; }
        if (TAG.el === el && el.classList.contains('is-on')) {
          TAG.rect = vp.getBoundingClientRect();
          TAG.tx = e.clientX - TAG.rect.left;
          TAG.ty = e.clientY - TAG.rect.top;
          TAG.seen = false;
          if (!TAG.raf) TAG.loop();
          return;
        }
        TAG.rect = vp.getBoundingClientRect();
        TAG.el = el;
        TAG.tx = TAG.px = e.clientX - TAG.rect.left;
        TAG.ty = TAG.py = e.clientY - TAG.rect.top;
        TAG.angle = 0; TAG.seen = false;
        el.style.transform = 'translate3d(' + TAG.px.toFixed(1) + 'px,' + TAG.py.toFixed(1) + 'px,0)';
        el.classList.add('is-on');
        TAG.loop();
      },
      pictureTagMove(e) {
        const el = document.querySelector('[data-pp-pictag]');
        if (TAG.el !== el) {
          if (this.pictureTagWanted()) this.pictureTagOn(e);
          return;
        }
        // Selecting a picture to edit takes the label away on the next move.
        if (!this.pictureTagWanted()) { this.pictureTagOff(); return; }
        if (!TAG.rect) return;
        TAG.tx = e.clientX - TAG.rect.left;
        TAG.ty = e.clientY - TAG.rect.top;
        TAG.seen = false;
        if (!TAG.raf) TAG.loop();
      },
      pictureTagOff() {
        const self = this;
        if (this._picTagOffT) clearTimeout(this._picTagOffT);
        this._picTagOffT = setTimeout(function () {
          self._picTagOffT = 0;
          const el = document.querySelector('[data-pp-pictag]');
          // Always take the pill down; only forget the shared loop state if
          // it is still ours (the note's label may have taken over meanwhile).
          if (el) el.classList.remove('is-on');
          if (TAG.el === el) { TAG.el = null; TAG.rect = null; }
        }, TAG_OFF_MS);
      },

      // Choose which control the contextual block is showing. NOT a toggle any
      // more: clicking the active chip used to set 'none' and close the
      // drawer, which was the way out when the drawer was a separate floating
      // sheet. In the dock the chips and the control share one surface, so
      // toggling off would leave the dock open at full height around a hole.
      // The way out of editing is Full card, or a click on empty stage.
      setEditSub(name) {
        if (EDIT_SUBS.indexOf(name) === -1) return;
        this.editSub = name;
        this.cardTool = null;   // the two popovers are mutually exclusive
      },

      // ── The card-wide popovers (v4) ──────────────────────────────────
      // Clicking the open one closes it: on a toolbar the button IS the way
      // out, exactly as each mode pill was before it.
      toggleCardTool(name) {
        const opening = this.cardTool !== name;
        this.cardTool = opening ? name : null;
        // One popover body at a time — opening a card-wide tool closes
        // whichever picture tool was showing. focusPhoto() does the reverse.
        //
        // Closing one puts NOTHING back: "nothing is selected unless you
        // selected it" is the whole rule now, and a picture with no tool open
        // is a legitimate resting state rather than a hole to fill.
        if (opening) this.editSub = 'none';
        this.camNonce++;
      },
      isCardTool(name) { return this.cardTool === name; },

      // Shut whichever card-wide popover is open, whatever it is.
      //
      // ⛔ NOT THE SAME DONE AS THE BAR'S. Daniel, 2026-09-20: "if I'm on
      // Crop, I only have a Done for the whole toolbar, which exits the per
      // picture options, rather than having a Done just for Crop." The bar's
      // Done is closePhoto() and means "stop editing this picture"; this
      // means "shut this panel" and leaves everything else where it was.
      // Crop and Effect have their own equivalent — setEditSub('none').
      //
      // Toggling the tool's own button off would do the same thing, and that
      // is still true; this exists so the Done buttons say what they do
      // rather than relying on a toggle's second press.
      closeCardTool() {
        if (this.cardTool === null) return;
        this.cardTool = null;
        this.camNonce++;
      },

      // Captions on/off — the card-wide switch that used to be "Full size".
      // Same state, said the way the customer thinks about it: the well is
      // chosen by the BORDER stop now, and this only decides whether a band
      // is reserved at the foot of each panel.
      /* ⛔ THE ONE PLACE THAT ANSWERS "ARE THERE CAPTIONS?". Everything —
         the well geometry, the bake, the _Layout property, every control that
         is gated on it — reads this rather than card.fullSize, so switching
         captions off is one flag and cannot leave two halves of the card
         disagreeing.
         ⚠️ AND IT OVERRIDES A RESTORED DRAFT. A card saved while captions were
         still offered carries fullSize: false; without the flag first, opening
         it would bring the captions back on a build that no longer offers a
         way to turn them off. */
      captionsOn() { return CAPTIONS_OFFERED && !this.card.fullSize; },
      toggleCaptions() {
        this.setFullSize(!this.card.fullSize);
        // A caption tool open over a card that no longer prints captions is a
        // control that would silently do nothing.
        if (this.card.fullSize && this.editSub === 'caption') this.editSub = 'effect';
      },

      // ───── What the dock needs to know about itself ──────────────────
      // Whether the contextual block is open at all — a photo is selected, or
      // the note is being written. Drives the dock's width AND, via
      // uiInset(), how much room the camera leaves at the foot of the stage.
      dockOpen() {
        // Edit photos is included whether or not a picture is selected: the
        // card-wide row lives there, so the dock is open the whole time — and
        // holding ONE width across both states is half of what keeps the card
        // still when a picture is picked. The other half is the shared height;
        // see is-bulk in builder-chrome.css.
        return this.selectedPhoto !== null || this.isSection('bottom') || this.isSection('photos');
      },

      // The zoom slider's fill, as a percentage of its own 1..4 range.
      // `accent-color` can colour a track but cannot give it a 22px paper
      // thumb with an ink outline, so the fill is drawn as a gradient and
      // needs the number.
      cropPct() {
        const s = this.photoCropScale(this.selectedPhoto);
        return (((s - 1) / 3) * 100).toFixed(1);
      },

      // ───── The note sizes itself to the paper ────────────────────────
      // The customer types free text into a fixed band. A fixed font size is
      // wrong at both ends of that: eight words float in a sea of paper, and a
      // full note runs up the panel and under the accordion. So the size is
      // measured, not chosen — the largest multiple of the base size at which
      // the text still fits the band, found by bisection.
      //
      // Bounds are NOTE_FIT_MIN / NOTE_FIT_MAX at the top of this file.
      //
      // ⛔ THIS IS ONE HALF OF A PAIR. print/make-print-file.mjs runs the same
      // rules over the same box (48.75 x 72mm), from the same 5.0mm base, with
      // the same bounds and the same 1.35 leading — so what the customer sizes
      // by eye here is what the printer produces. Daniel chose print's box as
      // the one that wins, 2026-08-16, because it is the one with a physical
      // constraint behind it. Change the rules here and change them there, or
      // the preview goes back to promising room the paper does not have.
      //
      // Bisection assumes bigger never fits where smaller didn't, which holds
      // for text in a fixed box. Nine steps land inside ~0.3% of the true
      // maximum — far below what the eye can see at this size.
      //
      // Cheap enough to run per keystroke: it touches one small subtree, and
      // queueNoteFit() collapses a burst of typing into one run.
      //
      // ── THIS METHOD OWNS THE NOTE ELEMENT'S TEXT AND ITS `display` ──────
      // No x-text, no x-show on [data-pp-note-text] — deliberately. What is
      // printed is not the raw string (see the blank-line rules below), and
      // the fitter has to swap the text to measure it, so a directive writing
      // the same properties would be a second author on one element. That is
      // exactly what broke it before: x-show and the fitter both wrote inline
      // `display` and left the note hidden with 140 characters in it. The
      // PLACEHOLDER beside it is Alpine's, through `noteEmpty`, which this
      // method sets — one owner each.
      // ⛔ AND IT STOPS OWNING THE TEXT WHILE THE TEXT IS BEING TYPED INTO.
      // The note is editable ON THE CARD now (Daniel, 2026-08-16: on a phone
      // "you can just tap the note ... and the text just comes up on the card.
      // I don't really need a box for that"), and rewriting an element's
      // textContent while the caret is in it drops the caret back to position
      // 0 — so typing would come out reversed. While it is focused the size is
      // measured against a HIDDEN COPY and the live element is left alone; the
      // one exception is trailing blank lines, which are cut with the caret put
      // back at the end, because otherwise pressing Enter lifts the note off
      // centre — the exact motion that got fixed this morning.
      fitNote() {
        /* ⛔ THE FITTER DOES NOT RUN WHILE THE PHONE NOTE SHEET IS OPEN.
           Aimed at "hardly wrote anythig and already got this" (Daniel,
           2026-09-10 — around fifty characters in the box and the note already
           declared full).

           ⚠️ HONEST STATUS: I could NOT reproduce that report off a device, so
           this is not a fix traced to a measured root cause. What it does is
           remove the only machinery that can produce the symptom, on grounds
           that stand on their own:

           The rawOverflow test below measures `[data-pp-note-field]` — the
           textarea ON THE CARD. While the sheet is up that is NOT the element
           being typed into (`[data-pp-note-sheetfield]` is; verified in the
           browser 2026-09-10, `sameElement: false`). So every keystroke in the
           sheet was judged against a different textarea — one sized by
           --pp-note-fit and the 3D scene, sitting behind the sheet with a
           keyboard over it. When that judgement says no, `noteFieldOpen` is
           true, so it lands on _rejectNoteEdit(): the keystroke is rolled back
           and the full-note message goes up. Measuring the wrong element, in a
           layout the customer cannot see, to refuse a key they just pressed,
           is not a thing to tune — it is a thing to not do.

           ⭐ IT IS ALSO THE SCREEN DANIEL ASKED FOR: "write in a box and tap
           done, THEN see it on the note." So the note is fitted once, on Done,
           when the keyboard has gone and the card is measurable again.
           NOTE_MAX still applies while typing; that limit is a character count
           and needs nothing measured.

           ⛔ IF THIS REPORT COMES BACK, the cause was upstream of here and the
           next thing to instrument is availH on the device itself — do not
           assume this guard settled it.

           ── ⭐ AMENDED 2026-09-16: IT SIZES, IT STILL NEVER REFUSES ────────
           Our first outside tester, on a phone: "when I type it and go away and
           tap it again… is my text, like, not getting registered?" She was
           right to doubt it. The card's own textarea is visible behind the
           sheet and x-model keeps its VALUE live, but its HEIGHT and
           --pp-note-fit are written here — so with the fitter fully stood down
           they stayed at whatever the note was before the sheet opened, and a
           textarea with `overflow: hidden` at a stale height shows the old
           number of lines. The words were in the element and could not be seen.
           Daniel, 2026-09-16: "update card live behind sheet."

           So the sheet no longer stops the fitter; it stops the three things
           that made standing down necessary, each guarded by `sheet` below:
             1. the trailing-blank-line trim, which writes into the CARD's
                field and its caret — not the element being typed into;
             2. every path that calls _rejectNoteEdit(), because a rollback
                while the caret is in another element is a keystroke vanishing
                from under somebody's thumb;
             3. the rawOverflow test, which measures the card's textarea — the
                wrong element, in a layout behind a keyboard, which is the
                measurement this whole comment exists about.
           What is left is measuring a hidden <p> twin and writing a size, an
           alignment and a height. A note too long to fit says so — the sheet
           carries its own "Note is full" — and Done still judges it properly
           with the keyboard gone, which is where the refusal belongs. */
        const sheet = !!this.noteSheetOpen;

        const box = document.querySelector('[data-pp-note-box]');
        const txt = box && box.querySelector('[data-pp-note-text]');
        if (!box || !txt) return;
        // While the field is open it is what the customer can see, so the <p>
        // is hidden and must not be rewritten — and the SIZE still has to be
        // solved, for the field. Everything below therefore measures a hidden
        // twin and sizes the box; only the <p>'s own text waits.
        const field = box.querySelector('[data-pp-note-field]');
        const editing = !!this.noteFieldOpen;

        // Runaway trailing blank lines, cut live in the field.
        //
        // ⚠️ AN ALLOWANCE, NOT A PURGE, AND THE DIFFERENCE MATTERS. Cutting
        // every trailing blank line the moment it was typed destroyed the gap
        // someone was in the middle of making: Enter, Enter, "Daniel" came out
        // as "…Mum.Daniel" on one line, because both newlines were gone before
        // the D arrived. Two are kept — a paragraph break and one spare, which
        // is every real note — and only a customer leaning on the key gets
        // trimmed. Caught on the draft, 2026-08-16.
        //
        // Only when the caret is at the very end: anywhere else there is
        // nothing trailing to cut and moving it would be its own bug.
        /* ⛔ NOT WHILE THE SHEET IS UP: `field` is the card's textarea and the
           caret is in the sheet's, so this would cut text the customer is not
           looking at and move a selection that is not theirs. */
        if (editing && !sheet && field && field.selectionStart === field.value.length) {
          const raw = field.value;
          const m = raw.match(/(?:[ \t]*\n)+[ \t]*$/);
          if (m && (m[0].split('\n').length - 1) > NOTE_TRAIL_ALLOW) {
            const cut = raw.slice(0, m.index) + '\n'.repeat(NOTE_TRAIL_ALLOW);
            field.value = cut;
            this.card.bottomNote = cut;
            field.setSelectionRange(cut.length, cut.length);
          }
        }

        // ── Which lines are real ────────────────────────────────────────
        // Once the field is closed, leading and trailing blank lines are
        // dropped. On a centred note they cannot be seen; all they can do is
        // shove the words off centre and shrink them, so pressing Enter at
        // the end of a finished note made the whole note smaller for no
        // visible reason (Daniel, 2026-08-16). Blank lines BETWEEN words are
        // kept: those are the gap between a message and a signature, and
        // they are visible.
        //
        // ⚠️ WHILE THE FIELD IS OPEN THEY ARE KEPT AND PAID FOR. The caret is
        // on them: a trailing blank line that was not sized for sat below a
        // full band, the raw-field check below refused it, and the second
        // Enter before a signature never landed (2026-09-03). Now the text
        // shrinks for it exactly as it will once the signature is typed, and
        // grows back on blur if nothing follows. The trailing cut above keeps
        // the count at NOTE_GAP_MAX, so this cannot run away.
        const lines = (this.card.bottomNote || '').split('\n');
        if (!editing) {
          while (lines.length && !lines[0].trim()) lines.shift();
          while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
        }

        // ⛔ A FOURTH BLANK LINE IN ONE GAP IS REFUSED, LIKE A CHARACTER PAST
        // maxlength. It used to be accepted into the state and then budgeted
        // out of the rendering, so the field showed a gap the card did not —
        // the very confusion Daniel described on 2026-09-03. Only an
        // insertion is refused (measured by length against the last good
        // text): a draft restored with a longer gap can still be edited.
        if (editing) {
          let run = 0, over = false;
          for (let i = 0; i < lines.length; i++) {
            if (lines[i].trim()) run = 0;
            else if (++run > NOTE_GAP_MAX) { over = true; break; }
          }
          if (over && (this.card.bottomNote || '').length > (this._noteLastGood || '').length) {
            /* In the sheet the gap is simply not paid for — `sizing` below caps
               it at NOTE_GAP_MAX either way — and the message says why. Nothing
               is rolled back: see the sheet note at the top. */
            if (sheet) this._noteSayFull();
            else { this._rejectNoteEdit(); return; }
          }
        }

        // Nothing but blank lines is nothing. The placeholder comes back,
        // rather than the panel going quietly empty. (Asked of the words,
        // since while editing the blank lines are still in `lines`.)
        if (!lines.some(function (l) { return l.trim(); })) {
          // An empty note that is being typed into must NOT be display:none —
          // hiding the element the caret is in ends the edit and dismisses the
          // keyboard, so the first keystroke of a new note would close it.
          if (!editing) {
            txt.textContent = '';
            txt.style.display = 'none';
          }
          txt.style.removeProperty('max-height');
          box.style.setProperty('--pp-note-fit', '1');
          box.style.setProperty('--pp-note-align', 'center');
          this.noteEmpty = true;
          this.noteOverflow = false;
          return;
        }

        this.noteEmpty = false;
        // ⛔ THE PRINTED TEXT IS HIDDEN WHILE THE FIELD IS OPEN. They sit in the
        // same grid cell, so leaving both visible put the same words on the card
        // twice, one behind the other, half a pixel apart.
        if (editing) txt.style.display = 'none';
        else txt.style.removeProperty('display');
        // Off before measuring. The stylesheet's own `max-height: 100%` stays —
        // that one is the band, and it is what holds if this never runs.
        txt.style.removeProperty('max-height');
        // Correct text first, so every bail below leaves the right words on the
        // card even when the sizing can't be worked out yet.
        if (!editing) txt.textContent = lines.join('\n');

        // ⛔ NEVER MEASURE SOMETHING THAT ISN'T LAID OUT. A hidden box is zero
        // by zero, every size "fits", and the fitter would leave a full note at
        // its largest. It happens for as long as the stage itself is hidden,
        // which is the whole entry step for a note restored from a previous
        // visit.
        //
        // ⚠️ ASKED OF THE BOX, NOT THE TEXT. It used to test the <p>, which
        // broke the moment the field arrived: with the field open the <p> is
        // deliberately empty, an empty grid item is zero wide, and the fitter
        // read that as "not laid out" and bailed on every keystroke — so the
        // note typed at the base size and never fitted at all.
        //
        // Try again a couple of times in case it is a tick of layout lag, then
        // stop: a hidden stage is a state, not a race, and the $watch on
        // `stage` is what recovers from it. Retrying forever would leave a
        // timer chasing an element that is not coming back until then.
        if (box.offsetParent === null || box.clientWidth === 0) {
          if (this._noteFitRetries < 3) { this._noteFitRetries++; this.queueNoteFit(); }
          return;
        }
        this._noteFitRetries = 0;

        // The band, inside its padding. Layout px, unaffected by the camera's
        // transform — scrollHeight and clientHeight are pre-transform, which
        // is the whole reason this can be measured while the card is tilted.
        const cs = getComputedStyle(box);
        const availH = box.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
        const availW = box.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
        // Nothing to measure against yet — the stage hasn't been given a size.
        // Leave the last good value alone; same bounded retry as above.
        if (!(availH > 0) || !(availW > 0)) {
          if (this._noteFitRetries < 3) { this._noteFitRetries++; this.queueNoteFit(); }
          return;
        }

        // ⛔ WHAT THE SIZE IS MEASURED AGAINST: the words, plus every blank
        // line up to NOTE_GAP_MAX per gap — which, for a note typed here, is
        // every blank line there is, since a fourth is refused above.
        //
        // A gap the customer put between a message and a signature IS
        // printed, so it is paid for the same way a line of words is: the
        // handwriting shrinks to make room, down to the floor, and past the
        // floor the Enter is refused (best == null below). What is rendered
        // is then always exactly what is in the field.
        //
        // For one afternoon on 2026-09-03 this sized on the words alone and
        // let blank lines fill only what was left, so a gap never shrank the
        // text. Daniel: "something with the enters doesn't make sense. I
        // want the user to be able to move down a line once, or even twice,
        // or even three times." So gaps are paid for again — the fix for
        // "the previous line breaks get pushed up" is the refusal above and
        // the raw-field check below, not a size that ignores them. Removing
        // blank lines cannot change how the other lines wrap, so the size
        // measured here is the size they render at.
        const sizing = [];
        let run = 0;
        for (let i = 0; i < lines.length; i++) {
          if (lines[i].trim()) { run = 0; sizing.push(lines[i]); }
          else if (++run <= NOTE_GAP_MAX) { sizing.push(lines[i]); }
        }
        const words = sizing.join('\n');
        // The thing that gets measured. While the note is being typed into it
        // is a hidden copy sitting in the same box — same width, same font,
        // same container units, so it wraps identically — and the live element
        // is never touched. Otherwise it is the live element itself.
        const probe = editing ? this._noteProbe(box, txt) : txt;
        // ⚠️ A <p> DOES NOT DRAW A LINE FOR A TRAILING NEWLINE; A TEXTAREA
        // DOES (the caret is on it). So while editing, a note ending in
        // Enter measured one row short of the field it was sizing for: the
        // size stayed put, the raw-field check below saw the extra row, and
        // the second Enter before a signature was refused (2026-09-03). A
        // zero-width space after a trailing newline makes the probe count
        // the row the caret is on. Never in the <p> itself: closed, trailing
        // blank lines are stripped before this point.
        probe.textContent = (editing && /\n$/.test(words)) ? words + '\u200b' : words;

        const fits = function (m) {
          box.style.setProperty('--pp-note-fit', String(m));
          // +0.5 absorbs the sub-pixel rounding in scrollHeight, which is an
          // integer: without it the fitter refuses a size that fits exactly.
          /* ⛔ A HANGING SPACE IS NOT OVERFLOW. In pre-wrap the space a line
             breaks at hangs past the line's end, and Firefox counts it in
             scrollWidth (Chrome, WebKit and print's Chromium do not). So a line
             whose last word reached the edge read about 0.17em too wide at
             every size, the fitter found no size at all, and the note was
             "full" at 220 characters with the band half empty (2026-10-06,
             measured in Firefox: scrollWidth 875 against 865 at 1.0, 870 at the
             floor). overflow-wrap: break-word already breaks a word longer than
             the line, so the width test only has to catch a real overflow, and
             that is never a space: 0.3em of it is allowed. */
          const hang = 0.3 * (parseFloat(getComputedStyle(probe).fontSize) || 0);
          return probe.scrollHeight <= availH + 0.5 && probe.scrollWidth <= availW + 0.5 + hang;
        };

        const FIT_MIN = window.PP_NOTE_FIT_MIN || NOTE_FIT_MIN;
        const FIT_MAX = window.PP_NOTE_FIT_MAX || NOTE_FIT_MAX;
        let lo = FIT_MIN, hi = FIT_MAX, best = null;
        if (fits(FIT_MAX)) {
          best = FIT_MAX;
        } else {
          for (let i = 0; i < 9; i++) {
            const mid = (lo + hi) / 2;
            if (fits(mid)) { best = mid; lo = mid; } else { hi = mid; }
          }
          /* ⛔ THE FLOOR IS A CANDIDATE SIZE, NOT JUST A FALLBACK. The
             bisection only ever tests points STRICTLY ABOVE FIT_MIN — the
             first midpoint is (FIT_MIN + FIT_MAX)/2 and they close on lo from
             above — so a note that fits at the floor and nowhere above it came
             out with best == null, which reads as "does not fit". The size
             written was FIT_MIN either way, so it LOOKED right on the card;
             what broke was everything keyed off best == null: noteOverflow
             went true, and mid-edit _rejectNoteEdit() rolled the keystroke
             back. The customer could not type a note the card had room for.

             Latent since the fitter was written and harmless while the floor
             sat far below anything real. It bit the moment the floor moved to
             3.0mm on 2026-09-07, because the note that move was made for fits
             at 0.6003 against a floor of 0.60 — nine midpoints all miss by
             three ten-thousandths.

             print/card-art.mjs has the same bisection and the same fix. */
          if (best == null && fits(FIT_MIN)) best = FIT_MIN;
        }

        /* ⛔ ROUNDED DOWN, NEVER TO THE NEAREST. `best` is the largest size the
           bisection TESTED, and it lands where the last word only just fits at
           the end of its line. toFixed(3) rounds half the time UP, to a size
           nobody tested, and a hair larger is enough to push that word onto a
           new row: 1.44375 gave 4 rows and fitted, the 1.444 written gave 5 and
           ran over the band. While typing, the raw-field check below then saw
           the field overflow and rolled the keystroke back with "Note is full",
           with half the paper empty (Yonatan, 2026-10-04: "a word that's on
           the very end of the line. It would not let you go down one line
           automatically"). Measured on 40 typed notes: 113 keystrokes refused
           across 18 of them, and 783 states where the card's own note ran over
           its band. Rounded down: none. print/card-art.mjs writes its tested
           size unrounded, so the paper never had this. */
        box.style.setProperty('--pp-note-fit', String(best == null ? FIT_MIN : (Math.floor(best * 1000) / 1000).toFixed(3)));

        /* ── The alignment, at the size we just settled on ────────────────
           Measured AFTER the size is written, because the row count is a
           property of the rendered note and not of the text: the same words
           are 5 rows at one size and 9 at another, and it is the rendered
           shape the reader sees. Reading scrollHeight flushes layout, so this
           is the count at the final size and not the last one the bisection
           happened to try.

           The probe is the live <p> when the note is closed and a hidden twin
           in the same box when it is being typed into — either way it is the
           thing that wraps the way the card does, which is why the count is
           taken from it rather than from the textarea. */
        const noteLh = parseFloat(getComputedStyle(probe).lineHeight);
        const noteRows = noteLh > 0 ? Math.round(probe.scrollHeight / noteLh) : 1;
        /* ⛔ `start`, NEVER `left`. `left` is a PHYSICAL value: it flushes to
           the visual left whatever the writing direction is, so a Hebrew note
           long enough to trip this threshold came out flush-left and ragged
           on the RIGHT — backwards (Yonatan, 2026-09-10: "the Hebrew doesn't
           centre"). `start` is the logical value and resolves per element
           against its own direction. Both elements that read this carry
           dir="auto" — .pp-cardnote__text and .pp-cardnote__field in
           builder-stage.liquid — and both are block-level, so each resolves it
           for itself: right for Hebrew, left for English.
           print/card-art.mjs counts the same rows at the same threshold and
           was fixed in the same change; the two must not drift. */
        box.style.setProperty('--pp-note-align',
          noteRows > NOTE_ALIGN_ROWS ? 'start' : 'center');
        // ⛔ A NOTE THAT DOES NOT FIT IS NOT ACCEPTED (Daniel, 2026-08-29:
        // "I don't want the red text ... and I also don't want a situation
        // where someone thinks that something is going to print, and it
        // doesn't print").
        //
        // There used to be a warning here and the text stayed in the box,
        // clipped. That is the exact failure he means: the card on screen and
        // the card in the envelope disagree, and the only thing standing
        // between the customer and that is a line of red they may not read.
        //
        // So the last state that FITTED is the truth, and anything that does
        // not fit is rolled back to it — the keystroke simply does not take,
        // the way maxlength behaves. There is then no overflow state to warn
        // about, which is why the warning could go.
        if (best == null && !editing) {
          // Not while typing: the rollback below owns that case and needs the
          // caret. Outside typing (a restored draft, a font change, a border
          // change) all we can do is keep it legible.
          this.noteOverflow = true;
        } else {
          this.noteOverflow = false;
        }
        if (best == null && editing) {
          /* The sheet says so and lets them keep typing; Done re-fits with the
             keyboard gone and holds the sheet open if it still does not fit
             (_noteSheetLand). A refusal belongs where the card is measurable. */
          if (sheet) this._noteSayFull();
          else { this._rejectNoteEdit(true); return; }
        }

        // ⛔ THE RAW FIELD MUST FIT THE BAND TOO. For a note typed here it
        // is the same text the size was chosen for, so this is a backstop;
        // it matters for a draft restored with gaps longer than NOTE_GAP_MAX,
        // which the size does not pay for and the textarea still shows. Nine
        // Enters between the message and the signature once ran the field
        // 366px into a 146px band with "Love, Daniel" and the caret below
        // the paper's edge (Daniel, 2026-09-03: "text is cut off when lots
        // of Enters").
        //
        // Only an INSERTION is refused, measured by length against the last
        // good text. A deletion is always accepted, so a draft restored with
        // more gaps than card (sized on the budgeted copy, so it looked fine
        // closed) can be edited out of that state instead of rejecting every
        // keystroke.
        //
        // Height 0 before reading, not `auto`: at `auto` a textarea is never
        // shorter than its `rows`, so the reading could not go below two
        // lines. See the field sizing below for the same reset.
        let rawOverflow = false;
        /* ⛔ AND NOT WHILE THE SHEET IS UP. This measures the CARD's textarea,
           which is not the element being typed into then — judging a keystroke
           by a different box, behind a keyboard, is the fault the sheet note at
           the top of this method is about. */
        if (editing && !sheet && field) {
          field.style.height = '0px';
          /* ⛔ THE DESCENDER PADDING IS NOT CONTENT, AND scrollHeight COUNTS IT.
             REGRESSION, 2026-09-10, mine: b930a47 gave this textarea
             `padding-bottom: 0.06em` so Indie Flower's tails stop being clipped
             (N1). box-sizing is border-box, so from that moment scrollHeight
             included the padding — while this test still measured it against a
             budget that does not.

             A note filling the band exactly used to read
                 N*lh  >  N*lh + 0.5   ->  fits
             and afterwards read
                 N*lh + 2.4px  >  N*lh + 0.5   ->  OVERFLOW, always.

             So the last line became unreachable and every keystroke at that
             boundary was rolled back by _rejectNoteEdit — which is exactly
             what Daniel reported: "lost letters, a much shorter ch count than
             before, skipping", in Hebrew and English alike, because the cause
             is geometric and has nothing to do with the script.

             ⚠️ The allowance belongs on BOTH sides or neither. The field is
             deliberately rendered `min(availH, wrapped*lh) + descPad` tall (see
             below) and .pp-cardnote's clip-path lets that ink show, so descPad
             of overflow is a state the card is designed to hold. The probe path
             above needs no such term: the probe is a <p> and carries no
             padding. */
          const rawPad = NOTE_DESC_PAD_EM * (parseFloat(getComputedStyle(field).fontSize) || 0);
          /* ⛔ 1.5px OF SLACK, NOT 0.5. scrollHeight is a whole number and the
             budget here is not: in WebKit six rows of 97.58px plus the 4.34px
             pad (589.82px) reads as 590 against a budget of 589.84, so a field
             holding exactly the rows the probe fitted was "over" and every
             keystroke on that row was rolled back. Measured 2026-10-06 on the
             anniversary note typed at a human pace: Safari's engine refused 94
             of 246 characters even after the round-down above, Chrome and
             Firefox none. A real overflow is a whole extra row, 60-120px, so
             1.5px cannot let one through. */
          rawOverflow = field.scrollHeight > availH + rawPad + 1.5;
          /* ⛔ THE FIELD CAN WRAP WHERE THE PROBE DID NOT, AND THAT IS NOT A FULL NOTE (2026-10-11, N6). In Firefox
             the textarea breaks a line a word earlier than the hidden <p> it was sized on, so at the size just
             chosen the field ran a row over the band and this check rolled the keystroke back: "neighbour" came
             out "neghbour", 3 runs in 3 at 390 (measured: field 401 px against a 362 px band at fit 1.978, the
             probe fitting). The letter was never too much for the paper, only for that size. So the size steps
             down further, measured on the field itself, the largest that holds it (the floor tested first, rounded
             down: memory "bisection never tests the floor", "a bisected size rounded up overflows"). Only while
             typing: closed, the <p> is the card and is sized on itself again, which is what print matches. A field
             that does not fit even at the floor is full, the paper's own refusal (_rejectNoteEdit(true)). */
          if (rawOverflow && best != null) {
            const fieldFits = function (m) {
              box.style.setProperty('--pp-note-fit', String(m));
              field.style.height = '0px';
              const pad = NOTE_DESC_PAD_EM * (parseFloat(getComputedStyle(field).fontSize) || 0);
              return field.scrollHeight <= availH + pad + 1.5;
            };
            const was = box.style.getPropertyValue('--pp-note-fit');
            let ok = null;
            if (fieldFits(FIT_MIN)) {
              ok = FIT_MIN;
              let lo2 = FIT_MIN, hi2 = Math.max(FIT_MIN, parseFloat(was) || FIT_MIN);
              for (let i = 0; i < 9; i++) {
                const mid = (lo2 + hi2) / 2;
                if (fieldFits(mid)) { ok = mid; lo2 = mid; } else { hi2 = mid; }
              }
            }
            if (ok != null) {
              box.style.setProperty('--pp-note-fit', (Math.floor(ok * 1000) / 1000).toFixed(3));
              rawOverflow = false;
            } else {
              box.style.setProperty('--pp-note-fit', was);
            }
          }
          if (rawOverflow) {
            const good = this._noteLastGood || '';
            if ((this.card.bottomNote || '').length > good.length) { this._rejectNoteEdit(best != null); return; }
          }
        }
        if (!editing || !rawOverflow) {
          this._noteLastGood = this.card.bottomNote || '';
          /* Everything up to here fits, so there is nothing left to undo. The
             stack only ever holds keystrokes since the last good state — see
             _noteUndo. */
          if (this._noteUndo.length) this._noteUndo.length = 0;
          this._notePrevValue = this._noteLastGood;
          this._noteRejecting = false;
          /* ⛔ ROOM AGAIN ONLY COUNTS IF THE NOTE ACTUALLY GOT SHORTER.
             This used to clear on any successful fit, and at the limit that is
             every single keystroke: the refusal restores the previous text and
             that text fits, so the message was hidden a frame after it
             appeared. Requiring a real deletion is what makes it stay up while
             somebody keeps typing and go the moment they make room. */
          if (this.noteFull && (this.card.bottomNote || '').length < this._noteFullAt) {
            clearTimeout(this._noteFullT);
            this._balloonStop();
          }
        }

        // ── The blank lines go back, down to the bottom of the card ──────
        // "New rows only until the bottom of the card" (Daniel, 2026-08-16).
        // The words have taken however many lines they take; whatever is left
        // of the band is the budget for empty rows, and Enter fills it one row
        // at a time until it is spent. After that the key does nothing to the
        // card — which is the honest answer, since there is no more card.
        //
        // Earlier gaps are kept first, so a paragraph break near the top
        // survives and it is the run at the bottom that stops growing.
        const lh = parseFloat(getComputedStyle(txt).lineHeight) || 1;
        // ⚠️ THE SAME +0.5 THE FIT USES. Without it the two disagree by one
        // line at the boundary: fits() accepts a note that overruns the band by
        // half a pixel, then this floors that line away and the budget drops the
        // gap the size had just been chosen to accommodate — a note asking for
        // one blank line between the message and the signature printed with
        // none. Round the same way in both places or they will differ exactly
        // when it matters.
        const maxLines = Math.max(1, Math.floor((availH + 0.5) / lh));
        // ⚠️ MEASURED AGAINST THE WORDS ALONE, which is NOT what the size was
        // fitted to. The fit above pays for up to two blank lines per gap; if
        // the budget is taken from that same measurement it believes those
        // lines are words, finds no room left, and drops the very gap it just
        // sized for — a note came back as "Mum." and "Daniel" on consecutive
        // lines after asking for a space between them. Two measurements, two
        // questions: how big can this be, and how much room is then left.
        probe.textContent = lines.filter(function (l) { return l.trim(); }).join('\n');
        let budget = Math.max(0, maxLines - Math.round(probe.scrollHeight / lh));
        const shown = [];
        for (let i = 0; i < lines.length; i++) {
          if (lines[i].trim()) shown.push(lines[i]);
          else if (budget > 0) { shown.push(lines[i]); budget--; }
        }
        // Budgeting the blank lines means REMOVING some, which is a rewrite —
        // so it waits until the customer has finished typing. noteBlur() runs
        // the fitter again with the field closed, and that is where a note with
        // more gaps than card settles into what will actually print.
        if (!editing) txt.textContent = shown.join('\n');
        if (editing) {
          this._noteProbeEl.textContent = '';
          // The field is centred in the band by being exactly as tall as its
          // own text — a textarea cannot centre its content vertically, and a
          // full-height one would put the first line at the top of the band
          // while the <p> it replaces sits in the middle. Capped at the band,
          // after which it scrolls rather than growing past the card.
          if (field) {
            // ⚠️ RESET BEFORE MEASURING. A textarea's scrollHeight is never less
            // than its own height, so measuring while an earlier height is
            // still applied can only ever grow it — the field ratcheted up a
            // line per keystroke and the note drifted down the panel with it.
            //
            // ⚠️ TO ZERO, NOT `auto`. At `auto` a textarea is as tall as its
            // `rows` — two by default — so a one-line note measured as two,
            // the field opened at twice the <p>'s height, and place-items
            // centred the taller box: the words sat half a line higher the
            // moment the paper was tapped ("jumping text upon click on/off",
            // Daniel, 2026-09-03). The markup now says rows="1" as well, but
            // the reading must not depend on it.
            field.style.height = '0px';
            const rows = Math.max(1, field.value.split('\n').length);
            const wrapped = Math.max(rows, Math.round(field.scrollHeight / lh) || rows);
            /* + the descender allowance the field's padding-bottom holds.
               box-sizing is border-box, so padding comes OUT of the height
               unless it is added back — without this the extra room would be
               taken from the last line instead of given to its tails. The
               negative margin-bottom in stage.css keeps the layout box the
               size it was. See .pp-cardnote__field. */
            const descPad = NOTE_DESC_PAD_EM * (parseFloat(getComputedStyle(field).fontSize) || 0);
            field.style.height = (Math.min(availH, wrapped * lh) + descPad) + 'px';
          }
        } else if (field) {
          // ⚠️ AND GIVE THE HEIGHT BACK WHEN IT IS NOT BEING TYPED IN. The
          // field used to be display:none here, so a stale inline height cost
          // nothing; it is always rendered now and shares a grid cell with the
          // <p>, so a leftover editing height would size the row for text
          // nobody can see.
          field.style.removeProperty('height');
        }

        // Past the floor the WORDS are longer than the card — nothing to do
        // with blank lines, which can no longer cause this — and something has
        // to give. It gives at a LINE boundary, not mid-letter: clamped to
        // whole lines the note reads as unfinished, which is true, instead of
        // looking like a rendering fault. The warning in the dock says so in
        // words — this is never a silent cut.
        if (best == null) {
          // min() keeps the band as the hard cap: if this number is ever stale
          // — a resize between two fits — the 100% still wins and nothing can
          // reach the accordion. See the note in stage.css for why this is a
          // max-height and not a line-clamp.
          txt.style.maxHeight = 'min(100%, ' + (maxLines * lh).toFixed(2) + 'px)';
        }
      },

      // One fit per turn of the event loop, however many keystrokes landed in
      // it. setTimeout rather than rAF for the same reason bumpCam uses it: a
      // background tab never paints, and the note would stay at the size it
      // had when the tab lost focus.
      // The last note text that actually fitted the panel. Everything typed is
      // measured against it, and anything that does not fit is rolled back to
      // it — see fitNote.
      _noteLastGood: '',

      /* ⛔ ONE KEYSTROKE PER REJECTION, WHICH IS WHAT WAS ALWAYS INTENDED.
         _rejectNoteEdit() used to restore _noteLastGood — the last text that
         FITTED — and _noteLastGood only advances when a fit succeeds, while
         fitNote() itself is debounced behind queueNoteFit()'s setTimeout(0).
         So every character typed while a fit was pending was discarded
         together, and the faster you typed the more you lost.

         Measured 2026-09-10, same 350 characters into the same card:
           120 ms/char (human)  22 rollbacks, 1 character each   — correct
            10 ms/char (burst)   1 rollback,  12 characters      — the bug
         Yonatan and Daniel both hit it as words losing a letter mid-note:
         "cannot" printing as "annot", "will" as "wll".

         So keep a stack of the value BEFORE each input event, and pop exactly
         one. _rejectNoteEdit() re-queues a fit, so it converges: pop, re-fit,
         pop again, until it fits — landing on the longest prefix the card can
         hold instead of throwing away the whole burst.

         Cleared on every successful fit: the stack only ever needs the
         keystrokes typed since the last state that fitted. */
      _noteUndo: [],
      _notePrevValue: null,
      _noteFullLen: null,      // the note's length when it was refused; nothing longer goes in until shorter (N3)
      _noteRejecting: false,

      /* ⭐ SAY SO WHEN THE NOTE IS FULL (Daniel, 2026-09-10: "instead of just
         flashing the whole text at the end with no indication that you ran out
         of characters, stop accepting keystrokes and surface a little message
         that says Note is full").

         The refusal already worked — a keystroke past the paper is rolled back
         and never prints, which was the 2026-08-29 decision. What it never did
         was SAY anything, so from the customer's seat a letter simply failed to
         appear and the note flickered. Silence is the bug, not the refusal.

         Held for a beat after the last refusal rather than cleared on the next
         keystroke: at the limit every key is refused, and a message that
         blinked once per keystroke would be its own kind of noise. */
      noteFull: false,
      noteFullGoing: false,
      _noteFullT: 0,
      /* The length the note was at when it filled up. The message clears early
         only when the text gets SHORTER than this — see the fit path. */
      _noteFullAt: 0,
      /* ── THE BALLOON ────────────────────────────────────────────────────
         Daniel, 2026-09-10: the message sits under the note, is hollow rather
         than a solid fill, "sticks around for a few seconds before it fades
         away", and "if I bumped with my cursor… sort of like a balloon, it
         bounces around the screen gently, not aggressively or too quickly".

         So it is a light thing on a spring: it rests where it is put, a
         passing cursor shoves it, and it drifts back. Position lives here and
         is written to two custom properties; every pixel of motion is a
         transform, which is the only thing on this stage that is cheap.

         ⛔ THE DRIFT IS NOT WHAT MAKES IT VISIBLE. Three times today I have
         made something the customer needs depend on an animation frame, so:
         the balloon is fully opaque and correctly placed from the moment the
         flag goes up. If not one frame ever runs, it simply sits still and
         still says what it says. */
      _bx: 0, _by: 0, _bvx: 0, _bvy: 0,
      /* Rotation and angular velocity. ⛔ WITHOUT THESE IT CANNOT FEEL TOUCHED
         (Daniel, 2026-09-10: "if I knock it from bottom left, the left side
         floats up… as if the cursor is physically touching the message"). A
         thing that only ever slides is being pushed by a field, not hit by an
         object. */
      _brot: 0, _bva: 0,
      _bRaf: 0,
      /* The pointer's own velocity, which is where the torque comes from —
         see the note in noteBalloonBump. */
      _bPX: null, _bPY: null, _bPT: 0,

      /* Undo the keystroke that pushed the note past the paper.
         The textarea is the field, so the value AND the caret both have to go
         back — otherwise the caret jumps to the end and the next character
         lands in the wrong place. Restoring to the same offset works because
         the rejected edit is at most a few characters (a keystroke, or a
         paste, which lands whole and is rejected whole). */
      /* `atLimit`: the refusal is the paper's own (no size down to the floor fits: fitNote's best == null), not a
         backstop. Only that one cuts back to a whole word and marks the note full (N3): the raw-field backstop can
         refuse a keystroke for a frame while the size is still settling (Firefox at 390, measured 2026-10-11), and
         a lock or a cut on that would stop a note at two lines or take away a word nobody asked to lose. */
      _rejectNoteEdit(atLimit) {
        const el = this._noteFieldEl();
        /* ONE STEP BACK, not all the way. The stack holds the value before each
           keystroke since the last successful fit; _noteLastGood is the floor
           it converges to when the stack runs out. See _noteUndo above. */
        let target = this._noteUndo.length
          ? this._noteUndo.pop()
          : (this._noteLastGood || '');
        /* ⛔ NEVER BACK ONTO PART OF A WORD (2026-10-11, N3). Stepping back one keystroke at a time stopped at the
           first state that fitted, and that was often mid-word -- "used to d" -- which is what the card showed and
           print would have printed. When the refused text only carried on the word the step-back ends in, that
           word did not fit: the note goes back to the last whole word (the inside top's rule, topCutPartialWord),
           and the undo steps longer than that are dropped so the convergence carries on from there. */
        const cut = atLimit ? noteCutPartialWord(target, this.card.bottomNote || '') : null;
        if (cut !== null) {
          target = cut;
          this._noteUndo = this._noteUndo.filter(function (u) { return u.length < cut.length && cut.indexOf(u) === 0; });
          if ((this._noteLastGood || '').length > cut.length) this._noteLastGood = cut;
        }
        /* And it STAYS full at that length until it is shorter (noteInput): a space or a narrow letter can still
           fit after a wide one was refused, and in Firefox trailing spaces never wrap, so they kept going in,
           invisible, after "Note is full". */
        if (atLimit) this._noteFullLen = target.length;
        if (this.card.bottomNote === target) return;
        const caret = el ? el.selectionStart : null;
        this.card.bottomNote = target;
        this._notePrevValue = target;
        if (el) {
          el.value = target;
          const at = Math.min(caret == null ? target.length : caret, target.length);
          try { el.setSelectionRange(at, at); } catch (e) { /* best-effort */ }
        }
        /* ⚠️ ONCE PER REJECTION, NOT ONCE PER POP. A burst can pop a dozen
           times converging on a length that fits; that is one refusal from the
           customer's point of view, and twelve events would misreport it as
           twelve. Cleared by the next successful fit. */
        if (!this._noteRejecting) {
          this._noteRejecting = true;
          track('pp_note_rejected', { length: target.length });
        }
        /* The message rides every refusal, not just the first: the timer is
           restarted so it stays up while somebody keeps typing into a full
           note, and goes 1.6s after they stop. */
        this._noteSayFull();
        // Re-fit against the restored text: it may still not fit, in which
        // case this pops again. That loop is the convergence.
        this.queueNoteFit();
      },

      // The first character is engagement too, whichever door you came in by.
      noteInput() {
        this.noteTouched = true;
        /* ⛔ NOTE_MAX IS ENFORCED HERE, NOT BY `maxlength`. The attribute did
           the job silently: the browser refuses the key, fires no input event,
           and nothing downstream can announce it — so somebody who had simply
           run out of characters saw a key do nothing and got no explanation.
           Done here, the truncation is ours and can say so. */
        const el = this._noteFieldEl();
        if (el && el.value.length > NOTE_MAX) {
          const caret = el.selectionStart;
          const typed = el.value;
          el.value = typed.slice(0, NOTE_MAX);
          // Not part of a word at the character limit either (N3): the word that ran past it goes whole.
          const cut = noteCutPartialWord(el.value, typed);
          if (cut !== null) el.value = cut;
          this.card.bottomNote = el.value;
          this._noteFullLen = el.value.length;     // and nothing longer until it is shorter, as at the paper's limit
          const at = Math.min(caret == null ? el.value.length : caret, el.value.length);
          try { el.setSelectionRange(at, at); } catch (e) { /* best-effort */ }
          this._noteSayFull();
        }
        /* ⛔ A FULL NOTE TAKES NOTHING LONGER (2026-10-11, N3) until it has been made shorter than where it filled
           (_rejectNoteEdit sets _noteFullLen). Spaces included: they hang at the end of a line, so they always
           "fit", and Firefox let them in after the note said it was full. Not in the sheet, which never refuses. */
        const prevNote = this._notePrevValue;
        if (el && this._noteFullLen != null && !this.noteSheetOpen) {
          if (el.value.length < this._noteFullLen) this._noteFullLen = null;
          else if (prevNote != null && el.value.length > prevNote.length && prevNote.length >= this._noteFullLen) {
            const caret = el.selectionStart, grew = el.value.length - prevNote.length;
            el.value = prevNote;
            this.card.bottomNote = prevNote;
            const at = Math.max(0, Math.min(caret == null ? prevNote.length : caret - grew, prevNote.length));
            try { el.setSelectionRange(at, at); } catch (e) { /* best-effort */ }
            this._noteSayFull();
            return;
          }
        }
        this._notePushUndo();
        /* In the sheet the fit SIZES the card and never refuses a key — see the
           sheet note at the top of fitNote(). */
        /* ⛔ FITTED NOW, IN THE KEYSTROKE, NOT QUEUED (2026-10-18). queueNoteFit() is a setTimeout, and a frame can
           be painted between the input event and it. On the keystroke that wraps the note, that frame showed the
           field still one row tall holding two, scrolled to the caret's row: only "t" on the card, the first line
           gone, for one frame (Live, Chrome at 390, recording the Meta ad: field 119 px tall, 233 px of text,
           scrollTop 109). The fit is what grows the field, so it has to land before the next paint. x-model is
           registered before @input (Alpine's directive order), so card.bottomNote already holds the keystroke.
           One input event is one fit, which the method is cheap enough for; the queue stays for everything else. */
        this._noteFitQueued = false;
        this.fitNote();
      },

      /* One place raises the message, so the paper limit and the character
         limit say the same thing in the same way. */
      /* ⛔ ONE MESSAGE PER SPELL OF BEING FULL, NOT ONE PER KEYSTROKE.
         Daniel, 2026-09-10: "it just flashes very quickly on the screen, and
         it flashes."

         Both halves were mine. The flashing was a fight between this and the
         eager clear in the fit path: at the limit each keystroke is refused,
         the refusal restores the previous text, and THAT text fits — so the
         very next fit declared there was room again and hid the message. True,
         false, true, false, once per key.

         So the early clear now needs the note to have got genuinely SHORTER
         than the length it filled at, which only a deletion does. A rejection's
         own re-fit cannot clear it, because a rollback does not make the note
         shorter than the point it stopped at.

         And the timer is only ever EXTENDED while somebody keeps typing into a
         full note — never restarted from false — so the message is continuous
         rather than re-mounting and replaying its arrival every key. */
      _noteSayFull() {
        const fresh = !this.noteFull;
        if (fresh) {
          this._noteFullAt = (this.card.bottomNote || '').length;
          this._bx = this._by = this._bvx = this._bvy = 0;
          this._brot = this._bva = 0;
        }
        this.noteFull = true;
        this.noteFullGoing = false;
        if (fresh) this.$nextTick(() => { this._balloonPlace(); this._balloonRun(); });

        /* ⭐ FADES, NEVER VANISHES. The flag drops in two steps: `going` starts
           a CSS opacity transition, and the element is only removed once that
           has had time to finish. Daniel asked for it to fade away rather than
           just disappear, and a thing that pops out of existence next to a
           card somebody is writing on reads as a glitch. */
        clearTimeout(this._noteFullT);
        const self = this;
        this._noteFullT = setTimeout(function () {
          self.noteFullGoing = true;
          self._noteFullT = setTimeout(function () { self._balloonStop(); }, 700);
        }, 3200);
      },

      /* Under the note, centred on it — where Daniel drew the ring. Read from
         the note box's own screen rect rather than guessed at, because the card
         is on a tilted plane at a camera scale that changes with the window.
         Clamped so a small window cannot put it off the bottom of the stage. */
      _balloonPlace() {
        const stage = document.querySelector('[data-pp-stage]');
        const note = document.querySelector('[data-pp-note-box]');
        const el = document.querySelector('.pp-notefull');
        if (!stage || !note || !el) return;
        const s = stage.getBoundingClientRect();
        const n = note.getBoundingClientRect();
        if (!s.height || !n.height) return;
        const x = (n.left + n.width / 2) - s.left;
        const y = Math.min((n.bottom - s.top) + 14, s.height - 56);
        el.style.left = Math.round(x) + 'px';
        el.style.top = Math.round(Math.max(8, y)) + 'px';
      },

      _balloonStop() {
        this.noteFull = false;
        this.noteFullGoing = false;
        if (this._bRaf) { cancelAnimationFrame(this._bRaf); this._bRaf = 0; }
      },

      /* A spring with a lot of damping: shoved, it swings back and settles
         rather than oscillating. `k` and the friction are the whole of the
         personality — gentle was the brief, so the numbers are small. */
      _balloonRun() {
        if (this._bRaf) return;
        const el = document.querySelector('.pp-notefull');
        if (!el) return;
        const self = this;
        const step = function () {
          self._bRaf = 0;
          if (!self.noteFull) return;
          self._bvx = (self._bvx - self._bx * 0.012) * 0.94;
          self._bvy = (self._bvy - self._by * 0.012) * 0.94;
          self._bx += self._bvx;
          self._by += self._bvy;

          /* The same spring on the angle, a little slacker so the tilt lingers
             a moment after the slide has settled — which is what a light thing
             on a string does. Capped: past about 12 degrees a pill stops
             reading as nudged and starts reading as broken. */
          self._bva = (self._bva - self._brot * 0.010) * 0.93;
          self._brot += self._bva;
          if (self._brot > 12) { self._brot = 12; self._bva *= 0.4; }
          if (self._brot < -12) { self._brot = -12; self._bva *= 0.4; }

          el.style.setProperty('--bx', self._bx.toFixed(2) + 'px');
          el.style.setProperty('--by', self._by.toFixed(2) + 'px');
          el.style.setProperty('--brot', self._brot.toFixed(2) + 'deg');

          /* Stop the loop once it has come to rest — a rAF running behind a
             motionless balloon is a battery cost with nothing to show. */
          if (Math.abs(self._bvx) > 0.04 || Math.abs(self._bvy) > 0.04 ||
              Math.abs(self._bx) > 0.4 || Math.abs(self._by) > 0.4 ||
              Math.abs(self._bva) > 0.03 || Math.abs(self._brot) > 0.2) {
            self._bRaf = requestAnimationFrame(step);
          }
        };
        this._bRaf = requestAnimationFrame(step);
      },

      /* ⚠️ THE CURSOR NEVER TOUCHES IT — the balloon is pointer-events: none,
         so it can never swallow a click meant for the card underneath. The
         "bump" is proximity measured from the stage's own pointermove: near
         enough, and it gets a shove directly away from the pointer, scaled by
         how close and capped so a fast sweep cannot fling it. */
      noteBalloonBump(e) {
        /* The pointer's own speed, kept whether or not the balloon is up: a
           bump has to know how fast the hand was already moving when it
           arrived, and sampling only once inside the balloon would read zero. */
        const now = e.timeStamp || performance.now();
        const pdt = now - (this._bPT || now);
        let cvx = 0, cvy = 0;
        if (this._bPX !== null && pdt > 0 && pdt < 120) {
          cvx = (e.clientX - this._bPX) / pdt * 16.7;   // px per frame
          cvy = (e.clientY - this._bPY) / pdt * 16.7;
        }
        this._bPX = e.clientX; this._bPY = e.clientY; this._bPT = now;

        if (!this.noteFull) return;
        const el = document.querySelector('.pp-notefull');
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (!r.width) return;
        const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
        const dx = cx - e.clientX, dy = cy - e.clientY;
        const d = Math.hypot(dx, dy);
        const R = Math.max(70, r.width * 0.72);
        if (d > R || d < 0.001) return;
        const near = 1 - d / R;

        /* ⛔ A RADIAL PUSH CANNOT SPIN ANYTHING, and that is why the first
           version only ever slid. A force aimed at the centre of mass has no
           lever: torque is r x F, and when F is parallel to r the cross
           product is zero, always. So the shove is the CURSOR'S OWN MOTION
           applied at the point it touches, with a small radial part so a slow
           cursor still displaces it.

           torque = r x v, with r measured from the balloon's centre to the
           contact point — which is what makes a knock at the bottom-left send
           that corner up and drop the other, exactly as Daniel described. The
           sign falls out of the geometry rather than being chosen. */
        const rx = e.clientX - cx, ry = e.clientY - cy;
        const push = near * 2.0;
        this._bvx += (dx / d) * push + cvx * near * 0.35;
        this._bvy += (dy / d) * push + cvy * near * 0.35;

        /* Divided by the half-width so a long pill does not spin faster than a
           short one for the same knock — that is the moment of inertia, near
           enough for a balloon. */
        const arm = Math.max(24, r.width / 2);
        this._bva += ((rx * cvy - ry * cvx) / arm) * near * 0.30;

        const cap = 7, acap = 1.6;
        this._bvx = Math.max(-cap, Math.min(cap, this._bvx));
        this._bvy = Math.max(-cap, Math.min(cap, this._bvy));
        this._bva = Math.max(-acap, Math.min(acap, this._bva));
        this._balloonRun();
      },

      /* Runs on the input event, which fires AFTER the value changed — so what
         gets pushed is the value remembered from last time, which is the value
         before this keystroke. Capped: a stack that grows with the note is a
         second copy of it, and nothing needs to undo further back than the
         last state that fitted. */
      _notePushUndo() {
        const el = this._noteFieldEl();
        if (!el) return;
        const prev = this._notePrevValue;
        if (prev != null && prev !== el.value) {
          this._noteUndo.push(prev);
          if (this._noteUndo.length > NOTE_UNDO_MAX) this._noteUndo.shift();
        }
        this._notePrevValue = el.value;
      },

      queueNoteFit() {
        if (this._noteFitQueued) return;
        this._noteFitQueued = true;
        const self = this;
        setTimeout(function () { self._noteFitQueued = false; self.fitNote(); }, 0);
      },
      _noteFitQueued: false,
      _noteFitRetries: 0,
      // The hidden twin used to measure the note while it is being typed into.
      // Ephemeral; never stored.
      _noteProbeEl: null,

      // ───── The note, written on the card ─────────────────────────────
      // Daniel, 2026-08-16: on a phone there is no reason for a text box —
      // "you can just tap the note or tap Write Notes, and then your mobile
      // keyboard pops up and you write. The text just comes up on the card."
      //
      // So the note ON THE CARD is the field. The dock's textarea is still
      // there on a desktop, where there is room for both and a keyboard that
      // costs nothing; on a phone it is gone (builder-chrome.css) and this is
      // the only way in.
      /* Has the customer engaged with the PAPER, as opposed to the field
         merely having been focused for them?

         The invitation used to clear on focus, which is right when the tap
         landed on the paper — the words are where the finger went, and
         leaving them under the caret is what Daniel called out first. It is
         wrong when the caret was put there BY THE TOOLBAR: tapping Note took
         you to a big blank panel with the invitation gone and nothing saying
         where to write. "When I first tapped on note, the text that says 'Tap
         to write your note' wasn't there" (2026-08-29).

         So the invitation stands until the customer touches the paper or
         types. Both complaints are the same rule seen from two doors. */
      noteTouched: false,

      /* ───── The design's own message, and who owns the panel ───────────
       *
       * Some designs arrive with words already on the inside lower panel —
       * Grumpy Cat says "hooray, or whatever". That panel is also where the
       * customer writes, so exactly one of the two can be showing.
       *
       * The design's message is the DEFAULT, and the customer replaces it
       * deliberately. That way a card is never blank where the artist put
       * something good, and nobody loses words they typed. */
      /* Advances only FROM the design step. applyOccasion() dresses the card
         the moment an occasion is picked, so the grid opens with the matching
         design already chosen -- and without this guard that pre-dressing
         would skip straight past the screen it is meant to populate. */
      onDesignChosen() { if (this.entryStep === 'design') this.nextEntry(); },

      /* Back to the grid, keeping the occasion. Counterpart of
         changeOccasion(); it is what the tile's "change" chip calls. */
      changeDesign() {
        this.entryStep = 'design';
        this.entryDir = 'back';
      },

      hasDesignMessage() { return !!this.designArt('CoverBottom'); },
      showDesignMessage() { return this.hasDesignMessage() && !this.card.noteOwn; },

      // Asked before replacing, so the design's line is never wiped by a
      // stray tap on the paper.
      noteReplaceAsk: false,

      /* ⛔ FOCUS SYNCHRONOUSLY, IN THE CLICK THAT ASKED FOR IT — the same iOS
         rule noteEdit() is built around. This runs from the confirm button's
         own @click, so the keyboard comes up; routing it through a watcher or
         a $nextTick would give a caret and no keys. */
      noteUseOwn() {
        this.card.noteOwn = true;
        this.noteReplaceAsk = false;
        this.noteTouched = true;
        const field = document.querySelector('[data-pp-note-field]');
        if (field) {
          try { field.focus(); field.setSelectionRange(field.value.length, field.value.length); }
          catch (e) { /* selection APIs are best-effort */ }
          this._noteUndo.length = 0;
          this._notePrevValue = field.value;
        }
        if (!this.isSection('bottom')) this.setSection('bottom');
        this.noteFieldOpen = true;
        // Re-solve the size once the DOM has settled. Not synchronous: fitNote
        // measures, and measuring in the same tick as the class change reads
        // the old state.
        const self0 = this;
        requestAnimationFrame(function () { self0.fitNote && self0.fitNote(); });
        track('pp_note_replaced_design', { design: this.card.design && this.card.design.id });
      },

      /* The way back. Clears the note as well as the flag: leaving the text
         behind would bring it silently back the next time they tapped Note,
         which is the opposite of "put it back how it was". */
      noteUseDesign() {
        this.card.bottomNote = '';
        this.card.noteOwn = false;
        this.noteReplaceAsk = false;
        this.noteFieldOpen = false;
        this.noteTouched = false;
        const field = document.querySelector('[data-pp-note-field]');
        if (field) field.blur();
        const self1 = this;
        requestAnimationFrame(function () { self1.fitNote && self1.fitNote(); });
        track('pp_note_restored_design', { design: this.card.design && this.card.design.id });
      },

      /* ── N8 — ON A PHONE YOU WRITE IN A BOX ──────────────────────────────
         ⛔ THIS REVERSES THE 2026-08-16 DECISION, deliberately and at Daniel's
         call (2026-09-10): "let's tackle the phone UI. I think we should write
         in a box and tap done, then see it on the note."

         What it reverses: "on a phone there is no reason for a text box — you
         can just tap the note or tap Write Notes, and then your mobile keyboard
         pops up and you write. The text just comes up on the card." That was a
         good idea and it does not survive contact with a keyboard: the keyboard
         covers the half of the screen the card is on, the note re-fits under the
         thumb as the fitter re-solves it, and the only way out is a Done button
         somewhere behind the keyboard. Daniel, on using it: "it's a bit jarring."

         ⚠️ DESKTOP IS UNTOUCHED. There the note IS the field and always has
         been — there is no keyboard eating the screen, and writing straight onto
         the paper is the whole charm of it. Only the coarse-pointer path
         changes. */
      noteSheetOpen: false,

      /* Whichever field the customer is actually typing into. The CARD's field
         stays the measuring surface for the fitter — its box is the paper — but
         the caret, the undo stack and the character clamp belong to the one with
         focus. Both carry x-model, so the text itself is never copied by hand. */
      _noteFieldEl() {
        return (this.noteSheetOpen
          ? document.querySelector('[data-pp-note-sheetfield]')
          : null) || document.querySelector('[data-pp-note-field]');
      },

      openNoteSheet() {
        this.noteSheetOpen = true;
        this.noteFieldOpen = true;   /* the fitter's `editing` flag */
        const el = document.querySelector('[data-pp-note-sheetfield]');
        if (el) {
          /* ⛔ SYNCHRONOUS FOCUS, in the tap that asked for it. The iOS rule
             that noteEdit is built around applies here identically: a focus
             that waits for a reveal gets a caret and no keyboard. The sheet is
             therefore always rendered and only its transform changes. */
          try {
            el.focus();
            const n = el.value.length;
            el.setSelectionRange(n, n);
          } catch (e) { /* selection APIs are best-effort */ }
          this._noteUndo.length = 0;
          this._notePrevValue = el.value;
        }
        /* The card behind the sheet is sized by the fitter, and a note restored
           from a draft has never been through it in this layout — so ask once
           on the way in, rather than leaving the first keystroke to do it. */
        this.queueNoteFit();
        track('pp_note_sheet_opened', { chars: (this.card.bottomNote || '').length });
      },

      /* Done. Puts the keyboard away, waits for the viewport to come back, and
         only then asks whether the words fit the paper.

         ⚠️ THE DELAY IS NOT A FLOURISH. The fitter measures the card, and the
         card's box is not trustworthy until the keyboard has actually gone and
         the visual viewport has settled — measuring in the same tick as the
         blur reads the squeezed layout and can refuse a note that fits
         perfectly well. Two frames' worth of grace, then measure. */
      closeNoteSheet() {
        const el = document.querySelector('[data-pp-note-sheetfield]');
        if (el) { try { el.blur(); } catch (e) {} }
        const self = this;
        setTimeout(function () { self._noteSheetLand(); }, 260);
      },

      /* ⛔ IF IT DOES NOT FIT, THE BOX STAYS OPEN. The alternative is closing
         on a note the card cannot print, which is the one thing this whole
         surface exists to prevent (2026-08-29: never text on screen that will
         not be on the card). Keeping them in the box with the words intact and
         the message up is the only outcome that is both honest and fixable —
         they can cut a line and press Done again. */
      _noteSheetLand() {
        /* Both flags down BEFORE the fit: `noteSheetOpen` is what makes
           fitNote() stand down, and `noteFieldOpen` is what makes it judge
           rather than roll back a keystroke. They go back up together below if
           the words turn out not to fit. */
        this.noteSheetOpen = false;
        this.noteFieldOpen = false;
        try { this.fitNote(); } catch (e) { /* never trap somebody in the box */ }

        if (this.noteOverflow) {
          this.noteSheetOpen = true;
          this.noteFieldOpen = true;
          this._noteSayFull();
          /* ⛔ DO NOT FOCUS THE FIELD BACK. We are a timeout deep from the tap
             on Done, and iOS only raises the keyboard for a focus that is
             synchronous with a gesture — refocusing here would put a caret in
             the box with no keyboard under it and no way to type, which is
             worse than leaving it alone. The sheet stays up with the words and
             the message; tapping the field is the gesture that brings the
             keyboard back. */
          return;
        }

        /* noteBlur settles the note into what will print — the blank lines are
           budgeted and the <p> is rewritten. Same landing as the desktop path. */
        this.noteBlur();
      },

      /* ⛔ THERE IS NO SECOND KEYBOARD WATCHER, AND THERE MUST NOT BE ONE.
         (Removed 2026-09-16. It was added the same day and shipped a worse bug
         than the one it fixed, so it is recorded here rather than deleted
         silently.)

         It wrote two custom properties from visualViewport — --pp-kb, the
         keyboard's height, and --pp-kb-top, the pan — and the note sheet
         positioned itself from both. Both were already answered, one floor up:
         kbWatch/kbPan near the top of this file set kbBoxH and kbBoxTop, and
         kbBoxStyle() puts them on the app shell as `height` and `translateY`.
         The shell is therefore the visible rectangle above the keys AND, being
         transformed, the containing block for every position:fixed child in it.
         A child that compensates again is compensating twice.

         Twice is not a rounding error here. The height term stranded the sheet
         a whole keyboard above the keyboard; the pan term made it travel at 2×
         under a finger — "the text box goes up and down outside of view instead
         of being fixed."

         ⚠️ THE STANDING RULE ON THIS SCREEN: exactly one thing answers the
         keyboard, and it is the shell. Anything fixed inside it uses plain
         `bottom`. The toolbar has said so since 2026-08-30 (builder-dock.liquid,
         "NOTHING HERE COMPENSATES FOR THE KEYBOARD, AND THAT IS THE FIX") —
         that comment was in the repo while this was being written, and reading
         it would have saved the round trip.

         ⚠️ AND IT CANNOT BE CHECKED HERE. Desktop emulation raises no keyboard,
         so every one of these numbers reads 0 on this machine and a double
         correction looks perfect. Only a real iPhone can tell. */

      // Whether the note is typed on the card on a phone too. Today's builder writes in the sheet there; B overrides.
      noteOnCard() { return false; },

      noteEdit(fromPaper) {
        if (this.stage !== 'builder') return;
        // pointerleave does not fire when a dialog or a caret takes the
        // surface out from under the cursor, so the label is dismissed here.
        this.noteTagOff();

        /* The design's words are showing, so ASK before taking the panel.
           Returns before the focus below: there is nothing to type into yet,
           and raising the keyboard over a question is not an answer. */
        if (this.showDesignMessage()) {
          if (!this.isSection('bottom')) this.setSection('bottom');
          this.cardTool = null;
          this.noteReplaceAsk = true;
          return;
        }
        if (fromPaper) this.noteTouched = true;

        /* ⛔ FOCUS FIRST, IN THIS CALL STACK, BEFORE ANYTHING REACTIVE.
           iOS raises the keyboard only for a focus() that runs synchronously
           inside the tap that asked for it. Everything below — setSection,
           noteFieldOpen, the fit — goes through Alpine and lands on a later
           tick, so focusing after any of it gave the note a caret and no keys:
           "note doesnt have keyboard oniphone" (Daniel, 2026-08-30).

           This can be first now because the field is no longer x-show'd: it is
           always rendered and always focusable, and `is-open` only changes
           opacity and pointer-events. See the notes in builder-stage.liquid
           and stage.css. */
        /* And it closes whatever was open over the card. Tapping the note
           with the Font popover up left the font row sitting over the paper
           you had just asked to write on — "when i did tap the note again
           when font was previously selected, the font options stayed visible
           instead of just the keyboard" (Daniel, 2026-08-30). */
        this.cardTool = null;

        /* The phone writes in a box. Same call stack, so the keyboard still
           comes up on the tap that asked for it. Not in the builder's direction B, which types on the card
           itself with the camera on the note alone (noteOnCard, assets/builder-b.js). */
        if (this.coarsePointer() && !this.noteOnCard()) {
          if (!this.isSection('bottom')) this.setSection('bottom');
          this.openNoteSheet();
          return;
        }

        const field = document.querySelector('[data-pp-note-field]');
        if (field && document.activeElement !== field) {
          try {
            field.focus();
            const n = field.value.length;
            field.setSelectionRange(n, n);
          } catch (e) { /* selection APIs are best-effort */ }
        }
        // Start the undo stack from what is actually in the field, so the
        // first keystroke of this edit cannot push a value left over from the
        // last one. See _noteUndo.
        if (field) { this._noteUndo.length = 0; this._notePrevValue = field.value; }

        if (!this.isSection('bottom')) this.setSection('bottom');
        this.noteFieldOpen = true;
        const self = this;
        // A BACKSTOP, no longer the main path. The focus above is what makes
        // the keyboard appear; this only catches the cases where the field was
        // not in the DOM yet at tap time (a note reached from another section,
        // where setSection has to render the plane first). It also runs the
        // fit, which does need the revealed field to measure against.
        //
        // Kept bounded: a field that never appears is a broken card, and
        // looping forever hides that.
        let tries = 0;
        const land = function () {
          const el = document.querySelector('[data-pp-note-field]');
          // offsetParent is null for a display:none element — the exact state
          // that makes focus() a no-op.
          if (!el || el.offsetParent === null) {
            if (++tries < 6) requestAnimationFrame(land);
            return;
          }
          if (document.activeElement !== el) {
            el.focus();
            const n = el.value.length;
            try { el.setSelectionRange(n, n); } catch (e) { /* best-effort */ }
          }
          self.fitNote();
        };
        this.$nextTick(land);
      },

      // Kept for the settle on the way out, NOT as the source of truth about
      // whether the note is being edited — see fitNote, which asks the DOM.
      noteBlur() {
        this.noteFieldOpen = false;
        this.noteTouched = false;
        clearTimeout(this._noteFullT);
        this.noteFull = false;
        // With the field closed the fitter may rewrite the <p>: leading and
        // trailing blank lines go, and the gaps in the middle are budgeted down
        // to what the card can hold. This is the moment the note settles into
        // what will print.
        this.fitNote();
      },

      // The card's note -> state, on every keystroke. Raw, including the blank
      // lines: the STATE is what the customer typed and the CARD is what fits,
      // and keeping those two separate is what lets the fitter normalise
      // without ever editing the customer's own text.
      setNoteFromCard(e) {
        const el = e && e.target;
        if (!el) return;
        let v = (el.textContent || '').replace(/ /g, ' ');
        if (v.length > NOTE_MAX) {
          v = v.slice(0, NOTE_MAX);
          el.textContent = v;
          this._caretToEnd(el);
        }
        this.card.bottomNote = v;
        this.queueNoteFit();
      },

      // A hidden twin of the note, in the same box so it inherits the width,
      // the font and the container units — which is what makes it wrap
      // identically. Built once, then reused.
      _noteProbe(box, txt) {
        if (this._noteProbeEl && this._noteProbeEl.isConnected) return this._noteProbeEl;
        const el = document.createElement('p');
        el.className = txt.className;
        el.setAttribute('aria-hidden', 'true');
        el.style.cssText = 'position:absolute;left:0;right:0;top:0;visibility:hidden;' +
                           'pointer-events:none;max-height:none;margin:0;';
        box.appendChild(el);
        this._noteProbeEl = el;
        return el;
      },

      // The rendered text of a contenteditable, breaks included. innerText is
      // the only property that reports a <br> as a newline; textContent skips
      // it entirely, and every keyed Enter with it.
      _plainFrom(el) {
        const t = el.innerText != null ? el.innerText : (el.textContent || '');
        // innerText reports a trailing break for the <br> browsers park at the
        // end of an editable block. That one is furniture, not a line the
        // customer typed, and left in it grows the note by a row per keystroke.
        return t.replace(/\n$/, '');
      },

      _caretToEnd(el) {
        try {
          const r = document.createRange();
          r.selectNodeContents(el);
          r.collapse(false);
          const s = window.getSelection();
          s.removeAllRanges();
          s.addRange(r);
        } catch (e) { /* selection APIs are best-effort */ }
      },

      // ───── Captions, written on the card ─────────────────────────────
      // Daniel, 2026-08-16: "I want to be able to write the caption directly
      // on the card." The dock's field stays — it is the same caption, and on a
      // phone with the keyboard up the dock is where you can still see it.
      //
      // The state is the truth and the DOM is a view of it, EXCEPT in the one
      // element currently being typed into: rewriting that element's text puts
      // the caret back at position 0, which turns typing into a reversal. So
      // the flow is one-way per element — read from the focused one, write to
      // all the others.
      captionsEditable() {
        return this.stage === 'builder' && this.captionsOn();
      },

      // "Click" on a mouse, "Tap" on a touchscreen. Being told to click on a
      // phone reads as a bug in the copy.
      captionPlaceholder() {
        return this.coarsePointer() ? 'Tap to add a caption' : 'Click to add a caption';
      },

      // The note's placeholder is now the ONLY thing telling the customer the
      // paper can be written on — the popover that used to hold a textarea is
      // gone (Daniel, 2026-08-29). So it asks, in the same words the caption
      // band uses, rather than just naming the surface.
      /* ── The label on the cursor, following the note's THREE states ────
         Daniel, 2026-09-03: "we need to change the hover state based on what
         is on the bottom part of the card."

           · the design's own baked message  ->  "Write your own note"
           · blank paper                     ->  "Write a note"
           · the customer's own note         ->  nothing at all

         The third is the interesting one. A label over words someone has
         already written has nothing to tell them — they can see the note, and
         they know they wrote it. Clicking still opens it; the label just
         stops narrating.

         Desktop only either way: a phone has no hover, and the invitation
         printed on the paper says the same thing there. */
      noteTagText() {
        return this.showDesignMessage() ? 'Write your own note' : 'Write a note';
      },

      noteTagWanted() {
        if (this.showDesignMessage()) return true;          // offer to replace it
        return !(this.card.bottomNote || '').trim();        // blank invites; written does not
      },

      noteTagOn(e) {
        /* ⛔ RETIRED (Daniel, 2026-10-11: "I don't want the edit note dragging label anymore"). The note is still
           opened by a click or a tap on the paper, and its empty-state line ("Click to write your note") still says
           so; only the label hanging off the cursor is gone. The handlers stay wired (noteTagMove and noteTagOff
           no-op without it), so turning it back on is deleting this line. */
        if (NOTE_TAG_OFF) return;
        if (this.coarsePointer() || !this.panelInteractive() || this.noteFieldOpen) return;
        // Not while a picture is selected for editing (Daniel, 2026-09-03) —
        // a "write your note" label over the photo you are editing distracts.
        // It stays for the whole-card and Edit-pictures views (nothing selected).
        if (this.selectedPhoto !== null) return;
        if (!this.noteTagWanted()) return;
        const el = document.querySelector('[data-pp-hovertag]');
        const vp = document.querySelector('[data-pp-stage]');
        if (!el || !vp) return;
        // ONE rect read, on enter. Reading it per move is a forced layout in
        // the middle of a pointer stream.
        TAG.rect = vp.getBoundingClientRect();
        TAG.el = el;
        // Start the label AT the cursor, settled — so it drops in rather than
        // flying across the stage from wherever it was left.
        TAG.tx = TAG.px = e.clientX - TAG.rect.left;
        TAG.ty = TAG.py = e.clientY - TAG.rect.top;
        TAG.angle = 0; TAG.seen = false;
        el.style.transform = 'translate3d(' + TAG.px.toFixed(1) + 'px,' + TAG.py.toFixed(1) + 'px,0)';
        el.classList.add('is-on');
        TAG.loop();
      },

      noteTagMove(e) {
        if (!TAG.el || !TAG.rect) return;
        TAG.tx = e.clientX - TAG.rect.left;
        TAG.ty = e.clientY - TAG.rect.top;
        TAG.seen = false;
        if (!TAG.raf) TAG.loop();
      },

      /* One rАF loop for the whole hover. The label chases the cursor on a
         spring (SPRING), and hangs off it by the lag: the horizontal distance
         it is behind becomes a rotation, so a quick flick makes it swing out
         and settle back — the pegman's dangle. It keeps running a few frames
         after the cursor stops so the swing has somewhere to die down. */
      _tagLoop() { TAG.loop(); },   // the spring is assets/cursor-tag.js (shared with the design lightbox)

      noteTagOff() {
        const el = document.querySelector('[data-pp-hovertag]');
        if (el) el.classList.remove('is-on');
        /* ⛔ ONLY THE NOTE'S OWN LABEL. Leaving the note used to take down whichever label was up, and in Preview
           that is "Click to zoom": crossing the note panel blinked it out (one cause of "sometimes appears and
           sometimes doesn't", Daniel, 11 Oct). */
        if (TAG.el && TAG.el !== el) return;
        if (TAG.raf) { cancelAnimationFrame(TAG.raf); TAG.raf = 0; }
        TAG.el = null; TAG.rect = null;
      },

      notePlaceholder() {
        return this.coarsePointer() ? 'Tap to write your note' : 'Click to write your note';
      },

      coarsePointer() {
        return !!(window.matchMedia && window.matchMedia('(hover: none)').matches);
      },

      captionClick(i) {
        if (!this.panelInteractive()) return;
        if (!this.captionsEditable()) return;
        // Clicking the caption means "edit THIS caption", so it selects the
        // picture and opens the matching tool — otherwise the dock would sit on
        // Effect while the customer types into the card.
        this.focusPhoto(i);
        this.setEditSub('caption');
        const el = document.querySelector('[data-pp-caption="' + i + '"]');
        if (el && document.activeElement !== el) {
          el.focus();
          // Caret to the END, not the start: a click on a caption that already
          // says something is almost always "add to this", and a caret at 0
          // makes the first keystroke rewrite the beginning of the line.
          try {
            const r = document.createRange();
            r.selectNodeContents(el);
            r.collapse(false);
            const s = window.getSelection();
            s.removeAllRanges();
            s.addRange(r);
          } catch (e) { /* selection APIs are best-effort */ }
        }
      },

      /* Normalise on the way out: a contenteditable collects newlines and
         non-breaking spaces that a one-line printed caption cannot use.

         ⛔ READ BEFORE WRITING. This used to assign straight from state —
         `el.textContent = this.card.captions[i]` — on the assumption that
         every change had already arrived as an `input`. Not everything does.
         iOS commits an autocorrection, a prediction or a dictation result in
         ways that can land after the last input we processed, and blur then
         wrote the OLDER state back over the newer text: characters silently
         disappearing from a caption the customer had already typed.

         Suspected cause of "I love you" turning into "I loyou" on the draft
         (2026-08-30) — suspected, not proven: one screenshot, no repro. But
         committing what is actually in the element before rendering it back
         is correct on its own terms and costs nothing, so it does not need
         to wait for a reproduction. */
      captionBlur(i, e) {
        const el = e && e.target;
        if (!el) return;
        this.setCaptionFromCard(i, e);
        el.textContent = this.card.captions[i] || '';
      },

      setCaptionFromCard(i, e) {
        const el = e && e.target;
        if (!el) return;
        //   is what a contenteditable inserts for a run of spaces. It
        // prints as a space but sorts, measures and breaks differently, so it
        // is normalised at the door rather than carried into the order.
        let v = (el.textContent || '').replace(/ /g, ' ').replace(/[\r\n]+/g, ' ');
        if (v.length > CAPTION_MAX) {
          v = v.slice(0, CAPTION_MAX);
          // Only rewrite the element when we actually cut something — see the
          // note above about the caret.
          el.textContent = v;
          try {
            const r = document.createRange();
            r.selectNodeContents(el); r.collapse(false);
            const s = window.getSelection(); s.removeAllRanges(); s.addRange(r);
          } catch (err) { /* best-effort */ }
        }
        const next = (this.card.captions || []).slice();
        next[i] = v;
        this.card.captions = next;
        this._trackEdit('caption', i);
      },

      captionKeydown(i, e) {
        // A caption is one line. Enter would insert a <br> that print has
        // nowhere to put, so it commits instead — the same thing Enter does in
        // the dock's single-line field.
        if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
        if (e.key === 'Escape') { e.preventDefault(); e.target.blur(); }
      },

      // Push state -> DOM for every caption EXCEPT the one being typed into.
      // Runs when a caption changes anywhere (the dock's field, a photo
      // re-order, a restored draft), which is what keeps the two fields in
      // step without either of them fighting the caret.
      syncCaptions() {
        const caps = this.card.captions || [];
        for (let i = 0; i < 4; i++) {
          const el = document.querySelector('[data-pp-caption="' + i + '"]');
          if (!el) continue;
          // Never the one with the caret in it — see fitNote for what that
          // costs. Asked of the DOM rather than tracked in a flag, for the same
          // reason: a flag can miss an event, activeElement cannot.
          if (document.activeElement === el) continue;
          if (el.textContent !== (caps[i] || '')) el.textContent = caps[i] || '';
        }
      },

      // Characters left in the caption, for "N left · printed under the photo".
      captionLeft() {
        const c = (this.card.captions && this.card.captions[this.selectedPhoto]) || '';
        return Math.max(0, CAPTION_MAX - c.length);
      },

      // The top bar's autosave line, or '' for no line at all.
      //
      // ONLY when work is actually being remembered. The "Remember a
      // customer's work between visits" setting is off by default while the
      // flow is being built (layout/configurator.liquid), and a permanent
      // "Saved just now" over a builder that saves nothing is a lie the
      // customer only finds out about after they close the tab.

      // Patch a photo immutably so the `photos` $watch fires (persists to localStorage).
      _patchPhoto(i, patch) {
        if (i == null || !this.photos[i]) return;
        const next = this.photos.slice();
        next[i] = Object.assign({}, next[i], patch);
        this.photos = next;
      },
      effectsOffered() { return EFFECTS_OFFERED; },

      setEffect(e) {
        if (!EFFECT_FILTERS[e]) return;
        this._patchPhoto(this.selectedPhoto, { effect: e });
        this._trackEdit('effect', this.selectedPhoto);
      },

      /* ───── Y5 — THE WAND ────────────────────────────────────────────────
         One tap moves this picture to the next look in the curated cycle. Tap
         again for another; it wraps, so it can be walked round and round.

         ⛔ ONE PHOTO, NOT THE CARD (Daniel, 2026-09-10 — I recommended
         card-level and was overruled). A card can therefore end up with four
         different looks. That is an accepted outcome, recorded rather than
         argued: it also makes the control simpler, because "try again" plainly
         means this picture.

         A look that is not in the cycle — `original`, or one of the retired
         keys off an old draft — starts at the beginning rather than being
         hunted for. */
      /* ───── THE CAST ─────────────────────────────────────────────────────
         Daniel, 2026-09-10: "upon press, there should be a cute animation with
         the stars coming out of the wand, a quick sheen or light effect on the
         panel in question, and then the filter applied… the idea is for users
         to feel like something magical is happening — it needs to take a
         second."

         Three beats in one second: the wand throws stars, a sheen sweeps the
         picture, and the new look is UNDER the sheen when it passes.

         ⛔ THE STATE LIVES HERE, THE MOTION LIVES IN CSS. Alpine sets two
         flags; every keyframe is in builder-chrome.css and stage.css. Driving
         a sweep from JS would mean writing style on the panel every frame,
         which this builder has been burned by before (see the note on
         .pp-plane and transitions).

         ⚠️ A SECOND TAP DURING A CAST IS NOT IGNORED. Ignoring it makes an
         eager customer think the button is broken. It restarts the cast and
         advances again, so tapping four times quickly walks four looks — the
         last one wins and the animation simply re-runs. */
      casting: false,
      castPhoto: null,
      designPending: false,     // ?design= named a design that is still loading (12 Oct): no grid meanwhile
      magicNote: null,          // 'casting' | 'done' while the default look is put on new photos (12 Oct)
      _castT1: 0,
      _castT2: 0,

      /* Baked looks (bakeLook): looks[i] = { src, effect, url }. Valid only while src is still that photo's data URL
         and effect its effect, so a reorder, a removal or a new look falls back to the CSS filter until the next bake. */
      looks: {},
      _lookBaking: false,
      lookUrl(i) {
        const p = this.photos[i], r = this.looks[i];
        return (p && r && r.src === p.dataUrl && r.effect === this.photoEffect(i)) ? r.url : null;
      },
      lookSrc(i) { const p = this.photos[i]; return this.lookUrl(i) || (p && p.dataUrl); },
      /* One photo at a time, so a first entry with four new photos never decodes and filters four at once. Loops until
         nothing is missing, because a look can change while one is baking. */
      async _bakeLooks() {
        if (this._lookBaking) { this._lookAgain = true; return; }
        this._lookBaking = true;
        try {
          do {
            this._lookAgain = false;
            for (let i = 0; i < this.photos.length; i++) {
              const p = this.photos[i], eff = this.photoEffect(i);
              if (!p || !p.dataUrl || eff === 'original' || this.lookUrl(i)) continue;
              await this._prebake(i, eff);
            }
          } while (this._lookAgain);
        } finally { this._lookBaking = false; }
      },
      /* Bake photo i in `eff` ahead of the look landing on it (the cast and the wand), so the swap at mid-sweep is to a
         finished image. True when looks[i] holds it. */
      async _prebake(i, eff) {
        const p = this.photos[i];
        if (!p || !p.dataUrl) return false;
        const have = this.looks[i];
        if (have && have.src === p.dataUrl && have.effect === eff) return true;
        const src = p.dataUrl;
        const url = await bakeLook(src, eff);
        if (!url) return false;
        const now = this.photos[i];
        if (!now || now.dataUrl !== src) { URL.revokeObjectURL(url); this._lookAgain = true; return false; }
        const old = this.looks[i];
        const next = Object.assign({}, this.looks);
        next[i] = { src: src, effect: eff, url: url };
        this.looks = next;
        // Late, so an <img> still showing the old object URL has its next frame first.
        if (old && old.url) setTimeout(function () { URL.revokeObjectURL(old.url); }, 4000);
        return true;
      },

      isCasting(i) { return this.casting && (this.castPhoto === i || (this.castPhoto === 'all' && (this._magicIdx || []).indexOf(i) >= 0)); },

      wandNext(i) {
        const idx = (i === null || i === undefined) ? this.selectedPhoto : i;
        if (idx === null || idx === undefined || !this.photos[idx]) return;

        const now = this.photoEffect(idx);
        const at = WAND_CYCLE.indexOf(now);
        const step = (at + 1) % WAND_CYCLE.length;
        const next = WAND_CYCLE[step];

        clearTimeout(this._castT1);
        clearTimeout(this._castT2);

        /* ⛔ THE FILTER MUST NOT DEPEND ON THE ANIMATION.
           This hung the whole cast — the setTimeout that applies the look
           included — off a requestAnimationFrame, so in a background or
           throttled tab the wand did NOTHING AT ALL: no sparks, no sweep, and
           no filter. Measured with rAF stalled: the effect was still
           'original' a full second after the tap.

           The state changes synchronously now and the beats are timers, which
           a throttled tab still runs (late, but it runs). The animation is
           decoration on top; if it is skipped the customer still gets the look
           they asked for. */
        const self = this;
        this._prebake(idx, next);   // usually done inside the 420 ms; if not, the CSS filter stands in until it is
        this.castPhoto = idx;
        this.casting = false;

        /* A CSS animation does not replay while its class is already on, so a
           rapid second tap needs the class off and on with a REFLOW between.
           $nextTick is a microtask — unlike rAF it runs in a background tab —
           and reading offsetWidth is what forces the flush that makes the
           re-add count. Cosmetic only: if any of it is skipped, the timers
           below still land the filter. */
        this.$nextTick(function () {
          const el = document.querySelector('.pp-tbtn--magic');
          if (el) void el.offsetWidth;
          self.casting = true;
        });

        /* The look lands mid-sweep, hidden by the light. */
        this._castT1 = setTimeout(function () {
          self._patchPhoto(idx, { effect: next });
          self._trackEdit('effect', idx);
        }, CAST_APPLY_MS);
        this._castT2 = setTimeout(function () {
          self.casting = false;
          self.castPhoto = null;
        }, CAST_MS);

        track('pp_wand_tap', { effect: next, step: step, photo: idx });
      },

      /* What the wand button says it will do. Named so the label can say
         "Try another" once it has been used — the first tap is a promise, the
         rest are a dial. */
      wandUsed(i) {
        const idx = (i === null || i === undefined) ? this.selectedPhoto : i;
        return WAND_CYCLE.indexOf(this.photoEffect(idx)) !== -1;
      },

      /* "Magic filter" until a look is on, then "Try another".
         ⚠️ THIS WENT BOTH WAYS ON 2026-09-10 and the second call is the one
         that stands. First: keep the label fixed and put "again" on its own
         round button. Then, having seen it: "the refresh button should go and
         the text should actually change to Try another." The row is quieter
         with one button that renames itself than with two that do not. */
      wandLabel(i) { return this.wandUsed(i) ? 'Try another' : 'Magic filter'; },

      /* ⭐ BACK TO THE PHOTOGRAPH AS IT CAME (Daniel: "also need a way to
         revert to original — at the moment you can't"). He is right, and it
         was a hole I made: the wand cycles through the six looks and never
         returns to `original`, and the Effect tiles that used to hold Original
         are gone from the toolbar. So there was no way back at all. */
      wandReset(i) {
        const idx = (i === null || i === undefined) ? this.selectedPhoto : i;
        if (idx === null || idx === undefined || !this.photos[idx]) return;
        if (this.photoEffect(idx) === 'original') return;
        clearTimeout(this._castT1);
        clearTimeout(this._castT2);
        this.casting = false;
        this.castPhoto = null;
        this._patchPhoto(idx, { effect: 'original' });
        this._trackEdit('effect', idx);
        track('pp_wand_tap', { effect: 'original', step: -1, photo: idx });
      },
      // ── The two card-wide picture settings ──────────────────────────
      // Not _patchPhoto: these belong to the card, so they are one write that
      // every panel reads.
      // ── ⭐ ONE SETTING DOES NOT TOUCH ANOTHER (Daniel, 2026-09-10) ─────
      //
      // Both of these change the shape of the printed window. Until today both
      // answered that by resetting cropScale/panX/panY/tilt on ALL FOUR
      // photos, on the stated reasoning that "any crop framed against the old
      // window is no longer the picture the customer approved".
      //
      // ⛔ THE REASONING WAS WRONG, AND IT COST REAL WORK. Yonatan, 2026-09-10,
      // twice: arranged four photos, tapped a control, and got them back at
      // the state he started from ("captions on ... it reset everything";
      // "changing the border also resets the design and the crop").
      //
      // The crop does not need re-framing, because it was never framed in the
      // window's units in the first place. All four values are ALREADY
      // NORMALISED against whatever well is current — see photoBox():
      //
      //   cropScale  is relative to COVER. s = 1 exactly fills the window
      //              whatever its aspect; it is clamped to [1, 4], so a gap
      //              cannot appear at any stop.
      //   panX/panY  are fractions OF THE OVERFLOW — x = -(w - 100) * panX/100.
      //              50 is centred in any window; 0 and 100 are its edges.
      //   tilt       is an angle, and K is recomputed from the new box aspect.
      //
      // So photoBox() re-fits the picture to the new well on the very next
      // render, from the same four numbers. Keeping them IS the re-fit.
      //
      // ⚠️ What does change is the visible CONTENT, because the wells differ in
      // aspect as well as size (classic 46.25x68.75 = 0.673, super
      // 34.25x56.75 = 0.604). A narrower window shows less width at the same
      // zoom. That is unavoidable and it is the MINIMUM possible change:
      // holding the visible width would need s < 1, which is precisely the
      // gap the clamp exists to prevent. Resetting threw away the zoom AND the
      // position AND did it to photos the customer had not touched.

      // into a box it was never framed for.
      setFullSize(on) {
        const next = !!on;
        if (next === !!this.card.fullSize) return;
        this.card.fullSize = next;
        // The crop is kept. See the block above setFullSize.
        this.queueNoteFit();
        track('pp_bulk_fullsize', { on: next });
      },

      // Same contract as setFullSize: the crop rides across untouched.
      setBorder(key) {
        if (!BORDER_STOPS.some(function (b) { return b.key === key; })) return;
        if (key === this.card.border) return;
        // ⛔ NO BORDER MEANS NO SHADOW (Daniel, 2026-08-29). The shadow is cast
        // by the photo onto the paper around it, and at this stop the picture
        // runs to the panel edge — there is no paper for it to fall on. The
        // toolbar hides the switch here; this is what stops the ORDER carrying
        // `shadow: true` into a layout that cannot print it.
        /* ⛔ THE SHADOW IS NOT CLEARED HERE EITHER, as of 2026-09-10. It used
           to be, for a good reason badly executed: at the `none` stop the
           picture runs to the panel edge and there is no paper for a shadow to
           fall on, so the order must never carry one. Clearing the flag did
           achieve that — by discarding a choice the customer had made, so a
           trip through `none` and back came out with the shadow off and no
           explanation. Same fix as rounded: keep the intent, derive the
           effect. See shadowActive(). */
        /* ⛔ ROUNDED IS *NOT* CLEARED HERE, unlike the shadow above (Daniel,
           2026-09-10: "if I do no border and then I turn on a border again,
           and I turned on rounded corners before that, it doesn't turn it off
           once I switch back"). `card.rounded` is the customer's INTENT and
           survives a trip through `none`; roundedActive() is what anything
           downstream reads, and that is false at `none` whatever the intent.
           Clearing the flag would have been the easy way to keep the order
           honest, and it would have thrown away a choice they had made. */
        this.card.border = key;
        // The crop is kept. See the block above setFullSize.
        this.queueNoteFit();
        track('pp_bulk_border', { stop: key });
      },

      setShadow(on) {
        this.card.shadow = !!on;
        track('pp_bulk_shadow', { on: !!on });
      },
      /* Card-wide like the border and the shadow, not per photo: four pictures
         with three square corners and one round would be an accident, never a
         choice. Same reason the row above says so for the shadow. */
      /* Takes millimetres. 0 is off, and is how the slider expresses off —
         there is no separate switch to keep in step with it any more. */
      setRounded(mm) {
        const n = Math.max(0, Math.min(PHOTO_RADIUS_MAX_MM, parseFloat(mm) || 0));
        this.card.rounded = n;
        track('pp_bulk_rounded', { on: n > 0, mm: n });
      },
      roundedMax() { return PHOTO_RADIUS_MAX_MM; },
      roundedStep() { return PHOTO_RADIUS_STEP_MM; },
      /* "6.5 mm" / "Square". The slider needs to say what it is set to: a
         number with no unit beside a track reads as a percentage. */
      roundedLabel() {
        const n = this.roundedMm();
        return n > 0 ? n.toFixed(1).replace(/\.0$/, '') + ' mm' : 'Square';
      },
      /* The shadow's half of the intent/effect split — see roundedActive()
         below, which carries the full reasoning. At the `none` stop the
         picture runs to the panel edge and there is no paper for a shadow to
         fall on, so it never applies there whatever the customer asked for. */
      // "2,4": the slots (1-based, as `_Photo N`) whose picture is cut out, or ''.
      cutSlots() {
        const out = [];
        (this.photos || []).forEach(function (p, i) { if (p && p.cutUrl) out.push(i + 1); });
        return out.join(',');
      },
      shadowActive() {
        return !!this.card.shadow && (this.card.border || BORDER_DEFAULT) !== 'none';
      },
      /* ⭐ INTENT vs EFFECT, and everything downstream must read the EFFECT.
         `card.rounded` is what the customer asked for and it persists through
         a trip to the `none` stop. This is whether it actually applies — and
         at `none` it never does: the picture runs to the trim, print lays that
         stop down OVERSIZE to be trimmed, and a radius would be cut straight
         through. Read by backdropStyle(), the bake layout and the cart
         property, so an order can never carry a radius that cannot print. */
      /* ⚠️ RETURNS MILLIMETRES NOW, NOT A BOOLEAN — 0 when rounding does not
         apply. Every existing caller used it in a boolean position and still
         works, because 0 is falsy and any real radius is not; the ones that
         need the number (the bake, the screen, the cart) read it directly. */
      roundedActive() {
        if ((this.card.border || BORDER_DEFAULT) === 'none') return 0;
        return this.roundedMm();
      },

      /* The stored intent, coerced. ⛔ A DRAFT SAVED BEFORE 2026-09-11 HOLDS A
         BOOLEAN here, and localStorage keeps it forever — `true` means the
         2.0 mm this used to be, `false` means none. Without this, an old draft
         restored as `true` would reach arithmetic and produce NaN corners. */
      roundedMm() {
        const v = this.card.rounded;
        if (v === true) return PHOTO_RADIUS_MM;
        if (!v) return 0;
        const n = parseFloat(v);
        if (!isFinite(n) || n <= 0) return 0;
        return Math.min(PHOTO_RADIUS_MAX_MM, n);
      },
      /* ⛔ THE INDEX IS AN ARGUMENT NOW, NOT `selectedPhoto`.
         Since 2026-09-10 a pinch works on ANY picture on a phone, with nothing
         selected and no crop mode — so the two-finger path has a photo in mind
         that is not the selected one, and writing to `selectedPhoto` would
         have zoomed a different picture than the one under the fingers. The
         no-index wrappers below keep every existing caller (the slider, the
         dock, the one-finger pan) exactly as it was. */
      setCropScaleAt(i, v) {
        if (i === null || i === undefined || !this.photos[i]) return;
        const before = this.photoQuality(i);
        const s = Math.max(1, Math.min(4, parseFloat(v) || 1));
        this._patchPhoto(i, { cropScale: s });
        this._trackEdit('crop', i);
        // ⚠️ ONLY ON A CROSSING, never on every frame. A pinch calls this
        // dozens of times a second; announcing the same thing on each one is
        // the pop-up-you-have-to-swat that this feature exists to avoid.
        const after = this.photoQuality(i);
        // B1 says it under the Zoom row instead (builder-b-tray), and a toast over the stage would cover the photo
        // being framed.
        if (after && after.band !== 'ok' && (!before || before.band !== after.band) && !this.frameB1Live(i)) {
          this.showSoftNotice(after.band);
        }
      },

      setCropScale(v) { this.setCropScaleAt(this.selectedPhoto, v); },

      // ── Rotate the selected picture 90° (Daniel, 2026-09-03) ──────────────
      // Re-encodes the pixels turned (rotate90) rather than carrying an angle,
      // so display and the print bake read one already-turned picture — no
      // WYSIWYG gap. A 90° turn re-frames the panel, so the crop (and any
      // tilt) reset to fill. srcW/srcH swap too, so the resolution check stays
      // honest. Guarded against a double-tap mid-encode.
      async rotatePhoto(i) {
        const idx = (i == null) ? this.selectedPhoto : i;
        const p = this.photos[idx];
        if (!p || !p.dataUrl || this._rotating) return;
        this._rotating = true;
        try {
          const r = await rotate90(p.dataUrl);
          this._patchPhoto(idx, {
            dataUrl: r.dataUrl, w: r.w, h: r.h,
            srcW: p.srcH, srcH: p.srcW,
            rot: (((p.rot || 0) + 90) % 360),
            cropScale: 1, panX: 50, panY: 50, tilt: 0,
            // The cut-out's mask was drawn for the unturned pixels; the face pan for the unturned frame.
            cutUrl: null, facePan: null,
          });
          this._trackEdit('rotate', idx);
          this.camNonce++;
          this.queueSave();
        } catch (e) { /* non-fatal — the picture is unchanged */ }
        finally { this._rotating = false; }
      },

      // ── Tilt the selected picture ±45° (Daniel, 2026-09-03) ───────────────
      // A fine angle on top of the crop: photoBox grows the cover so the
      // corners stay filled, imgStyle and bakePhoto turn by the same degrees
      // around the window centre. Combines with zoom and pan; reset by a rotate.
      photoTilt(i) { const p = this.photos[i]; return (p && p.tilt) || 0; },
      tiltPct() {
        // slider fill 0-100 for -45..45, like cropPct for the zoom slider
        return ((this.photoTilt(this.selectedPhoto) + 45) / 90) * 100;
      },
      setTilt(v) {
        const t = Math.max(-45, Math.min(45, parseFloat(v) || 0));
        this._patchPhoto(this.selectedPhoto, { tilt: t });
        this._trackEdit('tilt', this.selectedPhoto);
      },
      resetTilt() {
        this._patchPhoto(this.selectedPhoto, { tilt: 0 });
        this._trackEdit('tilt', this.selectedPhoto);
      },

      /* ── THE SOFT WARNING ────────────────────────────────────────────
         Modelled on Mixtiles, measured 2026-08-30 (docs/2026-08-30-low-res-
         warning-ux.md). It is deliberately NOT a dialog:

           - nothing to dismiss, nothing to click through, no blocked button
           - it names the fix rather than only the problem
           - no numbers. "214 PPI" means nothing to someone choosing a photo
             of their grandmother; those live on the order and in summary.txt
           - it is quieter than the CTA. A gifting customer is already anxious
             about getting it right, and alarm red says "you have done
             something wrong" when the truth is "you might want to know"

         The badge on the panel is the persistent half — this toast only
         announces the moment the state changes. */
      softNotice: null,
      _softTimer: null,
      showSoftNotice(band) {
        this.softNotice = band === 'low'
          ? { msg: 'This photo is a little small for print.', cta: 'Zoom out a touch' }
          : { msg: 'This photo will print slightly soft.',    cta: 'Zoom out to sharpen it' };
        clearTimeout(this._softTimer);
        // Long enough to read twice, short enough that it leaves on its own.
        this._softTimer = setTimeout(() => { this.softNotice = null; }, 7000);
      },
      dismissSoftNotice() {
        clearTimeout(this._softTimer);
        this.softNotice = null;
      },

      // ───── Pinch to zoom the picture ─────────────────────────────────
      //
      // The slider is the desktop control and the accessible one; on a phone
      // it was the ONLY one, which is not how anyone zooms a photograph
      // (Daniel, 2026-08-29). Two fingers on the picture do the same thing to
      // the same value, so the slider tracks the pinch and vice versa.
      //
      // Touch events rather than pointer events on purpose: e.touches gives
      // both fingers in one object, where pointer events would need a map of
      // live pointers rebuilt on every down/up. .pp-plane__photo carries
      // touch-action: none while cropping (see .is-panning in stage.css), so
      // Safari's own page-zoom never gets the gesture.
      _pinch: null,

      /* ⭐ TWO FINGERS ON ANY PICTURE, ON A PHONE — no Crop, no selection.
         Yonatan, 2026-09-10, on the commonest action in photo editing:
         "instead of select → crop → move, you should be able to just grab any
         photo and move it within its frame." Daniel: two fingers, and MOBILE
         ONLY.

         Desktop is untouched and still needs crop mode: two fingers on a
         trackpad is the page zoom this builder deliberately blocks, so there
         is nothing to ungate there — and the slider is the accessible control.

         ⛔ NOT IN PREVIEW OR THE RECIPIENTS LIST. Both are past editing (the
         toolbar stands down in exactly those two, see `pastEditing`), and
         Preview's own two-finger behaviour is the look-close roam. A pinch
         that silently re-cropped a photograph from the review screen would be
         the worst possible place to do it.

         ⚠️ NOTHING IS SELECTED BY THIS. Selecting would open the dock and dim
         the other three panels mid-gesture; the pinch addresses its photo by
         index instead — see setCropScaleAt/setPanAt. */
      pinchAllowed(i) {
        if (this.coarsePointer()) {
          return this.section === 'card' || this.section === 'photos';
        }
        return this.frameLive(i);
      },

      pinchStart(e, i) {
        if (!this.pinchAllowed(i)) return;
        if (!e.touches || e.touches.length !== 2) return;
        // A pinch is not a one-finger pan. Drop any in-flight drag so the
        // picture does not also slide while the fingers are spreading.
        this._pan = null;
        // B1: two fingers are never a pick-up to swap windows.
        if (this.frameB1Live(i)) { this._cancelPicturePress(); this._frameStart(i); }
        const el = e.currentTarget;
        const r = el ? el.getBoundingClientRect() : null;
        const c = touchMid(e.touches);
        this._pinch = {
          d0: touchGap(e.touches), s0: this.photoCropScale(i),
          // ── TWO-FINGER MOVE (Daniel, 2026-08-29: "if possible I'd like to
          // be able to move the picture with two fingers not just with one
          // finger"). The MIDPOINT between the fingers is the handle: it is
          // where the gesture is centred, so tracking it means a pinch that
          // also drifts moves the picture, which is what two fingers on a
          // photograph do everywhere else.
          cx: c.x, cy: c.y,
          panX: this.photoPanX(i), panY: this.photoPanY(i),
          w: r ? r.width : 0, h: r ? r.height * (1 + 2 * showExt(this.card.border, this.captionsOn())) : 0, i: i,
        };
      },

      pinchMove(e) {
        const p = this._pinch;
        if (!p || !e.touches || e.touches.length !== 2) return;
        const d = touchGap(e.touches);
        if (!p.d0 || !d) return;
        e.preventDefault();
        this.setCropScaleAt(p.i, p.s0 * (d / p.d0));
        // ...and the midpoint's travel moves it, in the same units the
        // one-finger drag uses — see movePan().
        const c = touchMid(e.touches);
        const b = photoBox(this.photos[p.i], this.boxAspect());
        const overX = b.w - 100, overY = b.h - 100;
        let panX = p.panX, panY = p.panY;
        if (overX > 0.01 && p.w) panX = p.panX - ((c.x - p.cx) / p.w * 100) * 100 / overX;
        if (overY > 0.01 && p.h) panY = p.panY - ((c.y - p.cy) / p.h * 100) * 100 / overY;
        this.setPanAt(p.i, panX, panY);
      },

      // Ends on the FIRST finger up: with one finger left the gesture is a pan
      // again, and carrying a stale d0 into it would make the next pinch jump.
      pinchEnd(e) {
        if (this._pinch && (!e.touches || e.touches.length < 2)) {
          const i = this._pinch.i;
          this._pinch = null;
          if (this.frameB1Live(i)) this._frameEnd(i, 'zoom');
        }
      },

      pinching() { return !!this._pinch; },

      // In-flight pan drag, or null. Ephemeral (nothing is persisted at all
      // builds its object field by field, so it cannot pick this up).
      _pan: null,

      // ───── Pan: drag the picture inside its window ───────────────────
      //
      // photoBox() has always honoured panX/panY, and photoPanX/photoPanY have
      // always read them — but nothing ever WROTE them. Zoom had a slider and
      // pan had nothing, so a customer could enlarge a picture and then not
      // choose which part of it to keep (Daniel, 2026-08-11).
      //
      // Screen pixels are converted with the photo box's own rendered rect.
      // That is an approximation on a plane the camera holds at an angle, but
      // focusPhoto() frames the panel near-square-on, so over a drag it is
      // within a pixel or two — and being exactly proportional matters far
      // less than the picture tracking the finger.
      setPanAt(i, x, y) {
        if (i === null || i === undefined || !this.photos[i]) return;
        const c = (v) => Math.max(0, Math.min(100, v));
        this._patchPhoto(i, { panX: c(x), panY: c(y) });
      },

      setPan(x, y) { this.setPanAt(this.selectedPhoto, x, y); },

      // Is there anywhere for the selected picture to be dragged TO? The
      // window is filled exactly at zoom 1, so the answer is no until the
      // customer zooms in — which is what startPan() has always tested, and
      // what the Crop hint now says out loud instead of promising a gesture
      // that no-ops (Daniel, 2026-08-29).
      canPanPhoto() {
        const p = this.selectedPhoto !== null && this.photos[this.selectedPhoto];
        if (!p) return false;
        const b = photoBox(p, this.boxAspect());
        return !(b.w - 100 < 0.01 && b.h - 100 < 0.01);
      },

      startPan(e, i) {
        if (!this.panelInteractive()) return;
        if (!this.frameLive(i)) return;
        const p = this.photos[i];
        if (!p) return;
        const b1 = this.frameB1Live(i);
        // B1: the window takes the keyboard from the press that grabs it (its arrows move the photo).
        if (b1 && e.pointerType === 'mouse') {
          const panel = e.currentTarget.closest && e.currentTarget.closest('[data-pp-panel]');
          if (panel && document.activeElement !== panel) { try { panel.focus({ preventScroll: true }); } catch (err) { /* not fatal */ } }
        }
        const b = photoBox(p, this.boxAspect());
        // Nothing to pan when the picture exactly fills its window.
        if (b.w - 100 < 0.01 && b.h - 100 < 0.01) return;
        const r = e.currentTarget.getBoundingClientRect();
        if (!r.width || !r.height) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        e.preventDefault();
        /* B1 on touch: the press also reaches the panel, whose hold (HOLD_MS, still) picks the picture up to swap
           windows; a finger that moves first is a move of the photo, and the hold cancels itself (DRAG_SLOP). With a
           mouse the photo is only ever moved: a swap is by its paper edge, or Swap places. */
        if (!(b1 && e.pointerType !== 'mouse')) e.stopPropagation();
        if (b1) this._frameStart(i);
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* not fatal */ }
        this._pan = {
          id: e.pointerId, i: i,
          x: e.clientX, y: e.clientY,
          panX: this.photoPanX(i), panY: this.photoPanY(i),
          // The bake box's height, which is the window's and the 3:4 cut's (showExt).
          w: r.width, h: r.height * (1 + 2 * showExt(this.card.border, this.captionsOn())),
        };
      },

      movePan(e) {
        const d = this._pan;
        if (!d || e.pointerId !== d.id) return;
        // Two fingers are a pinch, not a drag — see pinchStart.
        if (this._pinch) return;
        // A hold that has picked the picture up owns the finger now (B1 on touch).
        if (this.reordering()) return;
        e.preventDefault();
        const b = photoBox(this.photos[d.i], this.boxAspect());
        const overX = b.w - 100, overY = b.h - 100;
        let panX = d.panX, panY = d.panY;
        // Dragging right reveals more of the picture's left edge, so panX falls.
        if (overX > 0.01) panX = d.panX - ((e.clientX - d.x) / d.w * 100) * 100 / overX;
        if (overY > 0.01) panY = d.panY - ((e.clientY - d.y) / d.h * 100) * 100 / overY;
        this.setPan(panX, panY);
      },

      endPan(e) {
        const d = this._pan;
        if (!d || (e && e.pointerId !== d.id)) return;
        this._pan = null;
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (err) { /* not fatal */ }
        // One event per drag, not one per pointermove.
        this._trackEdit('crop', d.i);
        if (this.frameB1Live(d.i)) this._frameEnd(d.i, 'move');
      },

      // True while a pan drag is in flight — the stage's swipe-to-scrub must
      // not also run, or the strip slides out from under the finger.
      panning() { return !!this._pan; },

      // ───── Photo framing, B1: the picture just moves (Brief 21 part B; FRAME_B1) ─────────────────────────────
      //
      // Claude Design's "Builder Photo Framing", direction B1, chosen by Daniel on 13 Oct. Select a picture on the
      // Pictures step and it is live: drag it in its window, scroll over it (a computer) or pinch it (touch) to zoom,
      // double-click or double-tap to go to 2x at that point and back, arrows / + / - / 0 on the keyboard. No tool
      // first: Crop goes. Every change goes through the crop model above (cropScale, panX, panY, tilt, rot), the same
      // four numbers photoBox() and the print bake read, so nothing new reaches the order.
      frameB1() { return FRAME_B1; },
      /* Is picture i the one the pointer moves? Old Crop (editSub 'crop') still answers yes, so the switch off is
         today's builder exactly. */
      frameLive(i) {
        if (i === null || i === undefined || this.selectedPhoto !== i) return false;
        if (this.editSub === 'crop') return true;
        return FRAME_B1 && this.ppbStep === 'pictures' && this.panelInteractive() && !this.ppbKb;
      },
      frameB1Live(i) { return FRAME_B1 && this.editSub !== 'crop' && this.frameLive(i); },
      /* The rest of the photo, faded beyond the window: in old Crop whenever it is open (as before); in B1 only while
         the photo is being changed, and FRAME_SPILL_MS after, so at rest the card looks like the print. */
      frameSpillOn(i) {
        if (this.editSub === 'crop' && this.selectedPhoto === i) return true;
        return this.frameSpill > 0 && this.frameB1Live(i);
      },
      frameLabel(i) {
        return this.frameB1Live(i)
          ? 'Picture ' + (i + 1) + ' of ' + this.photos.length + '. Arrow keys move it, plus and minus zoom.'
          : 'Edit picture ' + (i + 1);
      },
      frameHintOn(i) { return this.frameHint && this.frameB1Live(i); },
      frameHintText() { return this.coarsePointer() ? 'Drag to move · Pinch to zoom' : 'Drag to move it · Scroll to zoom'; },
      /* The hint, "Drag to move · Pinch to zoom": EVERY time a picture is selected, until the customer has moved or
         zoomed a picture in this visit (Daniel, 19 Oct: "I'm not sure where the hints are that you can pinch to resize
         and move around, but let's make sure that they're there"). Until then it showed once per browser, ever
         (FRAME_HINT_KEY), so anyone who had seen it once never saw it again. Called on every selection. */
      frameHintSelect(i) {
        if (!FRAME_B1) return;
        if (i === null || i === undefined) { this.frameHint = false; return; }
        if (this.frameHint) { if (this._frameHintFor !== i) this.frameHint = false; else return; }
        if (this.ppbStep !== 'pictures' || this._frameUsed) return;
        this._frameHintShown = true;
        this._frameHintFor = i;
        this.frameHint = true;
      },
      // Small arrows at the edges where the photo carries on, at rest (not over the hint, not while it is changing).
      frameCue(i, side) {
        if (!this.frameB1Live(i) || this.frameSpill || this.frameHint || !this.photos[i]) return false;
        const b = photoBox(this.photos[i], this.boxAspect());
        const ox = b.w - 100, oy = b.h - 100, x = this.photoPanX(i), y = this.photoPanY(i);
        if (side === 'up') return oy > 0.5 && y > 0.5;
        if (side === 'down') return oy > 0.5 && y < 99.5;
        if (side === 'left') return ox > 0.5 && x > 0.5;
        return ox > 0.5 && x < 99.5;
      },
      frameSoftText(i) {
        const q = this.photoQuality(i);
        return q && q.band === 'low' ? 'A little small for print at this zoom.' : 'Slightly soft at this zoom.';
      },
      _frameStart(i) {
        clearTimeout(this._frameSpillT); clearTimeout(this._frameFadeT);
        if (this.frameSpill !== 1) this.frameSpill = 1;
        if (this.frameHint) this.frameHint = false;
        this._frameUsed = true;           // moved or zoomed by hand: the hint has done its job for this visit
        if (!this._frameFrom || this._frameFrom.i !== i) {
          this._frameFrom = { i: i, panX: this.photoPanX(i), panY: this.photoPanY(i), s: this.photoCropScale(i) };
        }
      },
      /* A change has stopped. The spill fades FRAME_SPILL_MS later (at once under reduced motion: no fade). The
         announcement goes `wait` ms later, once: on letting go (0), or after the last key or wheel tick. */
      _frameEnd(i, what, wait) {
        const self = this;
        clearTimeout(this._frameSpillT); clearTimeout(this._frameFadeT); clearTimeout(this._frameSayT);
        let still = false;
        try { still = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { /* motion it is */ }
        this._frameSpillT = setTimeout(function () {
          if (still) { self.frameSpill = 0; return; }
          self.frameSpill = 2;
          self._frameFadeT = setTimeout(function () { self.frameSpill = 0; }, 250);
        }, FRAME_SPILL_MS);
        if (!what) { this._frameFrom = null; return; }
        this._frameSayT = setTimeout(function () { self._frameAnnounce(i); }, wait || 0);
      },
      _frameAnnounce(i) {
        const f = this._frameFrom;
        this._frameFrom = null;
        if (!f || f.i !== i || !this.photos[i]) return;
        const s = this.photoCropScale(i);
        if (Math.abs(s - f.s) > 0.001) {
          this._frameSay('Zoom ' + s.toFixed(1) + ' times.' + (this.photoIsSoft(i) ? ' ' + this.frameSoftText(i) : ''));
          return;
        }
        const dx = this.photoPanX(i) - f.panX, dy = this.photoPanY(i) - f.panY, w = [];
        // panY up = more of the bottom shows = the photo went up; panX up = the photo went left.
        if (Math.abs(dy) > 0.01) w.push(dy > 0 ? 'up' : 'down');
        if (Math.abs(dx) > 0.01) w.push(dx > 0 ? 'left' : 'right');
        if (w.length) this._frameSay('Picture ' + (i + 1) + ', moved ' + w.join(' and ') + '.');
        else if (this._frameTried) this._frameSay('Picture ' + (i + 1) + ' can’t move further ' + this._frameTried + '.');
        this._frameTried = null;
      },
      // The polite live region in the tray (builder-b-tray). Emptied first, so the same words twice are said twice.
      _frameSay(msg) {
        const self = this;
        this.frameSaid = '';
        setTimeout(function () { self.frameSaid = msg; }, 40);
      },
      /* Zoom to s with the point (fx, fy) of the window (0-1 each) staying where it is: the same photoBox() the print
         reads, solved for the pan that keeps that point of the picture under the pointer. Tilt makes it approximate. */
      frameZoomAt(i, s, fx, fy) {
        const p = this.photos[i];
        if (!p) return;
        const a = this.boxAspect();
        const s1 = Math.max(1, Math.min(4, s));
        const b0 = photoBox(p, a);
        const ix = (fx * 100 - b0.x) / b0.w, iy = (fy * 100 - b0.y) / b0.h;
        const b1 = photoBox(Object.assign({}, p, { cropScale: s1 }), a);
        const ox = b1.w - 100, oy = b1.h - 100;
        this.setCropScaleAt(i, s1);
        this.setPanAt(i, ox > 0.01 ? -(fx * 100 - ix * b1.w) * 100 / ox : this.photoPanX(i),
                         oy > 0.01 ? -(fy * 100 - iy * b1.h) * 100 / oy : this.photoPanY(i));
      },
      // + / − (buttons and keys): 0.1x a step, centred where it is. Announced after the last one.
      frameZoomStep(i, d) {
        if (!this.photos[i]) return;
        this._frameStart(i);
        const s = Math.round((this.photoCropScale(i) + d) * 10) / 10;
        this.setCropScaleAt(i, s);
        this._frameEnd(i, 'zoom', FRAME_SAY_MS);
      },
      /* Move the photo by (dx, dy) PER CENT OF ITS WINDOW (+ is right / down), in the pan's own units: panX/panY are
         fractions of the overflow, so a window-% step is dx * 100 / overflow. Keys: 1, Shift 10; Move with buttons: 5. */
      frameNudge(i, dx, dy) {
        const p = this.photos[i];
        if (!p) return;
        this._frameStart(i);
        const b = photoBox(p, this.boxAspect());
        const ox = b.w - 100, oy = b.h - 100;
        this.setPanAt(i, ox > 0.01 ? this.photoPanX(i) - dx * 100 / ox : this.photoPanX(i),
                         oy > 0.01 ? this.photoPanY(i) - dy * 100 / oy : this.photoPanY(i));
        this._frameTried = dy < 0 ? 'up' : dy > 0 ? 'down' : dx < 0 ? 'left' : 'right';
        this._trackEdit('crop', i);
        this._frameEnd(i, 'move', FRAME_SAY_MS);
      },
      /* Reset: back to how it arrived. 1x, centred, no tilt, not turned (the turns are undone one quarter at a time,
         through rotatePhoto, which re-encodes as it always has). The filter stays. */
      async frameReset(i) {
        if (i === null || i === undefined || !this.photos[i]) return;
        this.frameHint = false;
        let n = 0;
        while (this.photos[i] && ((this.photos[i].rot || 0) % 360) !== 0 && n++ < 3) await this.rotatePhoto(i);
        if (!this.photos[i]) return;
        const fp = this.photos[i].facePan;   // how it arrived: on its faces, when _faceCentre moved it
        this._patchPhoto(i, { cropScale: 1, panX: fp ? fp.x : 50, panY: fp ? fp.y : 50, tilt: 0 });
        this._trackEdit('crop', i);
        this._frameFrom = null;
        this._frameSay('Picture ' + (i + 1) + ', reset.');
      },
      // The keyboard, on the selected picture's window (one focus stop, the panel).
      frameKey(e, i) {
        if (e.target !== e.currentTarget || !this.frameB1Live(i)) return;
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        const step = e.shiftKey ? 10 : 1, k = e.key;
        if (k === 'ArrowUp') this.frameNudge(i, 0, -step);
        else if (k === 'ArrowDown') this.frameNudge(i, 0, step);
        else if (k === 'ArrowLeft') this.frameNudge(i, -step, 0);
        else if (k === 'ArrowRight') this.frameNudge(i, step, 0);
        else if (k === '+' || k === '=') this.frameZoomStep(i, 0.1);
        else if (k === '-' || k === '_') this.frameZoomStep(i, -0.1);
        else if (k === '0') this.frameReset(i);
        else return;
        e.preventDefault();
        e.stopPropagation();
      },
      /* A computer: a plain scroll over the selected photo zooms it, around the pointer (the design's pick, and its
         question 1 for Daniel). Ctrl + scroll is how a trackpad pinch arrives: since 25 Oct it zooms too (frameWheel). */
      /* A height share of the 3:4 window as a share of the bake box behind it (showExt). */
      showFy(fy) {
        const e = showExt(this.card.border, this.captionsOn());
        return (fy + e) / (1 + 2 * e);
      },
      /* A trackpad pinch over the selected photo zooms it too (Daniel, 25 Oct), around the pointer. Chrome and Firefox
         send it as a wheel with ctrlKey and small deltas, so it gets a stronger rate than a scroll; Safari sends gesture*
         events instead (frameGesture*). Anywhere else a pinch still goes to pinchBlock, which swallows it. */
      frameWheel(e, i) {
        if (!this.frameB1Live(i) || e.metaKey || this.coarsePointer()) return;
        const pinch = e.ctrlKey;
        if (pinch && this._frameGesture) { e.preventDefault(); e.stopPropagation(); return; }   // Safari's gesture has it
        const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;
        if (!dy) { if (pinch) e.preventDefault(); return; }
        e.preventDefault();
        e.stopPropagation();
        const r = e.currentTarget.getBoundingClientRect();
        if (!r.width || !r.height) return;
        this._frameStart(i);
        this.frameZoomAt(i, this.photoCropScale(i) * Math.exp(-Math.max(-120, Math.min(120, dy)) * (pinch ? 0.01 : 0.0025)),
                         (e.clientX - r.left) / r.width, this.showFy((e.clientY - r.top) / r.height));
        this._trackEdit('crop', i);
        this._frameEnd(i, 'zoom', FRAME_SAY_MS);
      },
      /* Safari's trackpad pinch: e.scale runs from 1 for the whole gesture, so zoom = the scale at its start x e.scale.
         Not on touch screens: an iPad raises the same events for a finger pinch, which pinchStart already drives. */
      frameGestureStart(e, i) {
        if (!this.frameB1Live(i) || this.coarsePointer() || this._pinch) return;
        e.preventDefault();
        e.stopPropagation();
        this._frameGesture = { i: i, s0: this.photoCropScale(i) };
        this._frameStart(i);
      },
      frameGestureChange(e, i) {
        const g = this._frameGesture;
        if (!g || g.i !== i) return;
        e.preventDefault();
        e.stopPropagation();
        const r = e.currentTarget.getBoundingClientRect();
        if (!r.width || !r.height || !e.scale) return;
        const fx = e.clientX != null ? (e.clientX - r.left) / r.width : 0.5;
        const fy = e.clientY != null ? (e.clientY - r.top) / r.height : 0.5;
        this.frameZoomAt(i, g.s0 * e.scale, Math.max(0, Math.min(1, fx)), this.showFy(Math.max(0, Math.min(1, fy))));
        this._trackEdit('crop', i);
      },
      frameGestureEnd(e, i) {
        if (!this._frameGesture) return;
        e.preventDefault();
        e.stopPropagation();
        this._frameGesture = null;
        this._frameEnd(i, 'zoom', FRAME_SAY_MS);
      },
      /* Double-click (a computer) and double-tap (touch): to 2x around that point, then back to 1x. Read from the
         photo's own pointerdown/up, because the browser's dblclick is not dependable on touch and touch-action:none
         already keeps the page from zooming on a double tap. */
      frameDown(e, i) {
        this._frameTapDown = this.frameB1Live(i) ? { x: e.clientX, y: e.clientY, t: Date.now(), id: e.pointerId } : null;
      },
      frameUp(e, i) {
        const d = this._frameTapDown;
        this._frameTapDown = null;
        if (!d || d.id !== e.pointerId || !this.frameB1Live(i) || this.reordering() || this._pinch) return;
        const now = Date.now();
        if (Math.abs(e.clientX - d.x) > DRAG_SLOP || Math.abs(e.clientY - d.y) > DRAG_SLOP || now - d.t > 350) { this._frameTap = null; return; }
        const last = this._frameTap;
        if (last && last.i === i && now - last.t < 400 && Math.abs(e.clientX - last.x) < 30 && Math.abs(e.clientY - last.y) < 30) {
          this._frameTap = null;
          const r = e.currentTarget.getBoundingClientRect();
          if (!r.width || !r.height) return;
          this._frameStart(i);
          if (this.photoCropScale(i) > 1.01) this.setCropScaleAt(i, 1);
          else this.frameZoomAt(i, 2, (e.clientX - r.left) / r.width, this.showFy((e.clientY - r.top) / r.height));
          this._trackEdit('crop', i);
          this._frameEnd(i, 'zoom', 0);
        } else {
          this._frameTap = { i: i, t: now, x: e.clientX, y: e.clientY };
        }
      },

      // ───── Click and hold to re-order the pictures ───────────────────
      //
      // Hold a picture still for HOLD_MS, then drag it over another one and
      // let go. movePicture() does the actual move — it already carries the
      // caption, the effect, the border and the crop along, and it is the only
      // function that knows every store keyed by picture index.
      //
      // WHY A HOLD AND NOT A PLAIN DRAG. Two other gestures start life as a
      // drag on a picture: panning it inside its window in Crop, and swiping
      // the strip sideways. A press that has to stay still first cannot be
      // confused with either, and it is the phone convention for "pick this
      // up" besides.
      _drag: null,
      _suppressPanelClick: false,

      reordering() { return !!(this._drag && this._drag.active); },
      isDragging(i) { return !!(this._drag && this._drag.active && this._drag.from === i); },
      isDropTarget(i) { return !!(this._drag && this._drag.active && this._drag.over === i && this._drag.over !== this._drag.from); },

      startPicturePress(e, i) {
        if (!this.panelInteractive()) return;   // Preview: look, don't drag
        // Crop owns the drag on the selected picture — that is the pan.
        if (this.editSub === 'crop' && this.selectedPhoto === i) return;
        // B1: a mouse on the selected photo moves it; only its paper edge picks it up (Builder Photo Framing, B1).
        if (this.frameB1Live(i) && e.pointerType === 'mouse' && e.target && e.target.closest && e.target.closest('.pp-plane__photo')) return;
        if (!this.photos[i] || this.photos.length < 2) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        const self = this;
        const el = e.currentTarget;
        this._drag = {
          id: e.pointerId, from: i, over: i, active: false,
          x: e.clientX, y: e.clientY, el: el,
          timer: setTimeout(function () {
            const d = self._drag;
            if (!d || d.active) return;
            d.active = true;
            /* B1 on touch: the same finger began a move of the photo. Holding still is what makes it a pick-up, so
               the move is undone (it is at most DRAG_SLOP of wobble) and the swap takes the finger. */
            const pan = self._pan;
            if (pan && pan.id === d.id) {
              self._pan = null;
              self.setPanAt(pan.i, pan.panX, pan.panY);
              self._frameEnd(pan.i, null);
            }
            // Capture only once the hold has actually won. Capturing at
            // pointerdown would steal the very gestures the hold exists to
            // stay out of the way of.
            try { el.setPointerCapture(d.id); } catch (err) { /* not fatal */ }
            if (navigator.vibrate) { try { navigator.vibrate(12); } catch (err) { /* ignore */ } }
          }, HOLD_MS),
        };
      },

      movePicturePress(e) {
        const d = this._drag;
        if (!d || e.pointerId !== d.id) return;
        if (!d.active) {
          // Moved before the hold landed: this was a swipe or a stray wobble,
          // never a pick-up. SLOP is generous because a finger on glass is.
          if (Math.abs(e.clientX - d.x) > DRAG_SLOP || Math.abs(e.clientY - d.y) > DRAG_SLOP) {
            this._cancelPicturePress();
          }
          return;
        }
        e.preventDefault();
        // Which picture is under the pointer. elementFromPoint hit-tests
        // through the 3D transform, so this is right on planes the camera is
        // holding at an angle — which is all of them.
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const panel = el && el.closest && el.closest('[data-pp-panel]');
        const over = panel ? Number(panel.getAttribute('data-pp-panel')) : null;
        d.over = (over != null && !isNaN(over) && this.photos[over]) ? over : d.from;
      },

      endPicturePress(e) {
        const d = this._drag;
        if (!d || (e && e.pointerId !== d.id)) return;
        clearTimeout(d.timer);
        try { if (e) d.el.releasePointerCapture(d.id); } catch (err) { /* not fatal */ }
        const moved = d.active && d.over !== d.from;
        this._drag = null;
        if (!moved) return;
        // The selection is remapped inside _applyPhotoOrder, along with every
        // other store keyed by picture index. Setting it to d.over here — as
        // this first did — moved the editor onto the dragged picture even when
        // the customer was editing a different one, so re-ordering silently
        // changed what the dock's controls acted on.
        this.movePicture(d.from, d.over);
        this._swallowNextClick();
      },

      // A pointerup at the end of a drag is followed by a click on whatever
      // happens to be under the finger, and that click would open the editor
      // for a picture the customer was only moving.
      //
      // A capture-phase listener that removes itself, NOT a flag cleared on a
      // timer: the order of "click" against a setTimeout(0) is not something
      // the spec promises, and a flag that clears too early swallows nothing
      // while a flag that clears too late swallows the customer's next real
      // tap. This catches exactly one click and then gets out of the way. The
      // timer is only a fallback for the case where no click follows at all —
      // dragging onto a panel that was removed, say.
      _swallowNextClick() {
        const self = this;
        this._suppressPanelClick = true;
        let done = false;
        const off = function () {
          if (done) return;
          done = true;
          window.removeEventListener('click', swallow, true);
          self._suppressPanelClick = false;
        };
        const swallow = function (e) {
          e.stopPropagation();
          e.preventDefault();
          off();
        };
        window.addEventListener('click', swallow, true);
        setTimeout(off, 400);
      },

      _cancelPicturePress() {
        const d = this._drag;
        if (!d) return;
        clearTimeout(d.timer);
        if (d.active) { try { d.el.releasePointerCapture(d.id); } catch (err) { /* not fatal */ } }
        this._drag = null;
      },

      photoPanX(i) { const p = this.photos[i]; return p && p.panX != null ? p.panX : 50; },
      photoPanY(i) { const p = this.photos[i]; return p && p.panY != null ? p.panY : 50; },

      /* ── HOW SHARPLY THIS PICTURE WILL PRINT ─────────────────────────
         Effective resolution, measured on the pixels inside the customer's
         crop at the size that crop is printed. NOT the pixel size of anything
         we upload — bakePhoto() scales the crop up to the bake size, so the
         uploaded file is the same dimensions however few real pixels went in.

         Bands from the print spec, section 03:
             >= 300   fine, say nothing
             180-300  prints soft — warn, never block
             < 180    a face on matte stock reads as mush

         Returns null when unmeasurable — a draft rehydrated from a session
         older than srcW/srcH. Absent must never render as "fine". */
      photoQuality(i) {
        const cp = cropPixels(this.photos[i], this.boxAspect());
        if (!cp) return null;
        const w = bakeWell(this.card.border, this.captionsOn());
        const ppi = Math.min(cp.w / (w.w / 25.4), cp.h / (w.h / 25.4));
        return { ppi: Math.round(ppi), band: ppi >= 300 ? 'ok' : ppi >= 180 ? 'soft' : 'low' };
      },
      /* Used by the badge. Only the two bands worth showing, so the template
         reads `photoIsSoft(i)` rather than repeating the band comparison. */
      photoIsSoft(i) {
        const q = this.photoQuality(i);
        return !!q && q.band !== 'ok';
      },

      photoEffect(i) { return (this.photos[i] && this.photos[i].effect) || 'original'; },
      photoCropScale(i) { return (this.photos[i] && this.photos[i].cropScale) || 1; },
      // Where the photo sits inside its 4:5 window, as percentages of that
      // window. The print bake runs the SAME function (photoBox) — if the two
      // ever computed it separately they would drift, and the customer would
      // receive a different crop from the one they set.
      // The aspect of the window a picture is being shown in — 4:5 with
      // captions, 50.5:73.5 at full size. Everything that crops reads it from
      // here so the screen, the pan maths and the print bake stay one answer.
      // The crop window's aspect. v4: derived from the current stop + caption
      // state, so every stop crops into the box it will actually print in.
      // classic+captions still returns its historical 4:5, and
      // slim+no-captions returns FULL_ASPECT.
      boxAspect() {
        const w = bakeWell(this.card.border, this.captionsOn());
        return w.w / w.h;
      },

      // ── BORDER STOPS — the card-wide picture margin ──────────────────
      // ── The card's two hands ─────────────────────────────────────────
      // The note and the captions are set independently. Which one a Font
      // button drives is decided by WHERE it is: the note's own row sets the
      // note, every other row sets the captions.
      cardFonts() { return CARD_FONTS.filter(function (f) { return CARD_FONTS_OFFERED.indexOf(f.key) !== -1; }); },

      fontSurface() { return this.isSection('bottom') ? 'note' : 'caption'; },

      cardFontKey(surface) {
        const s = surface || this.fontSurface();
        const k = s === 'note' ? this.card.noteFont : this.card.captionFont;
        return k || CARD_FONT_DEFAULT;
      },
      isCardFont(k, surface) { return this.cardFontKey(surface) === k; },

      setCardFont(key, surface) {
        if (!CARD_FONTS.some(function (f) { return f.key === key; })) return;
        const s = surface || this.fontSurface();
        if (s === 'note') {
          if (key === this.card.noteFont) return;
          this.card.noteFont = key;
          this._noteFullLen = null;      // a new face has its own room (N3: the full mark is per look)
          // The note is fitted to the panel by measurement and a new face
          // wraps differently, so the size it settled on is wrong the instant
          // this changes.
          this.queueNoteFit();
        } else {
          if (key === this.card.captionFont) return;
          this.card.captionFont = key;
        }
        track('pp_card_font', { font: key, surface: s });
      },

      // Both hands, as custom properties on the app root. The note reads the
      // --pp-note-* set and the captions read --pp-cap-*; they used to share
      // one --pp-font-hand, which is what made them move together.
      /* The shell's own style. Folded in with the fonts because ONE binding
         has to own the style attribute — a second writer and they clobber
         each other. `flex: none` matters: the shell is a flex item of the
         locked page and would otherwise be stretched back to full height. */
      kbBoxStyle() {
        if (!this.kbBoxH) return '';
        return 'height:' + this.kbBoxH + 'px;' +
               'flex:none;' +
               'transform:translateY(' + this.kbBoxTop + 'px);';
      },

      cardFontStyle() {
        const n = cardFont(this.cardFontKey('note'));
        const c = cardFont(this.cardFontKey('caption'));
        return this.kbBoxStyle() +
               '--pp-note-font:' + n.family + ';' +
               '--pp-note-weight:' + n.weight + ';' +
               '--pp-note-scale:' + n.scale + ';' +
               '--pp-cap-font:' + c.family + ';' +
               '--pp-cap-weight:' + c.weight + ';' +
               '--pp-cap-scale:' + c.scale + ';';
      },

      borderStops() { return BORDER_STOPS; },
      borderStop() { return this.card.border || BORDER_DEFAULT; },

      // The swatch in the Border popover, drawn TO SCALE from the stop's own
      // well — so the row shows what the card will actually do, caption band
      // included, rather than a decorative approximation.
      //
      // The first pass inset the picture by `side * 1.6px` on a 26x32 swatch,
      // which at the wide stop (8mm) left 0.4px of picture: the swatch read as
      // empty. Only looking at it caught that. The panel is 57.5 x 80.5, so
      // the honest scales are 26/57.5 and 32/80.5.
      stopInset(stop) {
        const SW = 26, SH = 32;
        const kx = SW / PANEL_W_MM, ky = SH / SHOW_H_MM;   // the 3:4 panel on screen (SHOW_H_MM)
        const w = this.captionsOn() ? stop.cap : stop.full;
        const top = w.top * ky;
        const bottom = (PANEL_H_MM - w.top - w.h) * ky;   // the paper below is the same mm on either panel
        const side = stop.side * kx;
        return top.toFixed(2) + 'px ' + side.toFixed(2) + 'px '
             + bottom.toFixed(2) + 'px ' + side.toFixed(2) + 'px';
      },


      // ⚠️ A TRANSFORM, NOT left/top/width/height.
      //
      // The geometry is photoBox()'s and is unchanged — this is the same
      // rectangle expressed a different way. width/height/left/top in percent
      // are LAYOUT, so every pointermove of a crop drag relaid out the panel
      // subtree; a trace of one drag recorded 90 layout shifts, essentially
      // all of them the crop ghost, and the real picture underneath was doing
      // the same thing before the ghost existed. Daniel, 2026-08-29: "after
      // cropping and dragging around a picture and then clicking somewhere
      // else, it all became a little bit laggy and slow."
      //
      // translate+scale off a 100%x100% box is exactly equivalent: with
      // transform-origin at 0 0, translate's percentages resolve against the
      // element's own border box, which IS the parent's size here, and the
      // scale then multiplies it. Composited instead of laid out.
      //
      // ⛔ The BAKE is untouched and must stay so — bakePhoto() reads
      // photoBox() numbers directly and never looks at the DOM, which is why
      // the two cannot drift. Do not "simplify" this to object-fit: that
      // crops inside the element and the bake cannot reproduce it.
      // `live`: the ghost shows the raw data URL, so it keeps the CSS filter; the picture itself shows lookSrc(),
      // which carries the look in its pixels once baked (see bakeLook) and then needs no filter.
      imgStyle(i, live) {
        const b = photoBox(this.photos[i], this.boxAspect());
        // The tilt sandwich rotates the cover+panned picture around the WINDOW
        // centre — translate(-50%) moves the well centre to the origin, rotate,
        // translate(50%) back. 50% is of the img's own box, which fills the
        // window, so it IS the window centre. bakePhoto does the same in canvas.
        const rot = b.deg ? `translate(50%,50%) rotate(${b.deg}deg) translate(-50%,-50%) ` : '';
        /* The 3:4 window (showExt): the img's box is the 5:7 bake box, centred on the window and cut by it, so every
           percentage below still means what the bake means by it. */
        const ex = showExt(this.card.border, this.captionsOn()) * 100;
        return `position:absolute;left:0;top:${(-ex).toFixed(3)}%;width:100%;height:${(100 + 2 * ex).toFixed(3)}%;` +
               'transform-origin:0 0;' +
               `transform:${rot}translate(${b.x.toFixed(3)}%,${b.y.toFixed(3)}%) ` +
               `scale(${(b.w / 100).toFixed(5)},${(b.h / 100).toFixed(5)});` +
               'filter:' + ((!live && this.lookUrl(i)) ? 'none' : (EFFECT_FILTERS[this.photoEffect(i)] || 'none')) + ';' +
               ((!live && this.photos[i] && this.photos[i].cutUrl) ? cutMaskCss(this.photos[i].cutUrl) : '');
      },
      // The crop ghost: the SAME geometry as the real picture, carrying the
      // picture as a background rather than as a second <img> (see
      // builder-stage). Sharing imgStyle() is the point — the two layers have
      // to be placed identically or the ghost stops being the same photograph
      // seen past the window and becomes a smear behind it.
      ghostStyle(i) {
        const p = this.photos[i];
        if (!p || !p.dataUrl) return 'display:none;';
        return this.imgStyle(i, true);
      },

      effectOptions() { return EFFECT_ORDER.map(function (k) { return { key: k, label: EFFECT_LABELS[k], filter: EFFECT_FILTERS[k] }; }); },

      // ── Full-spread prototype (?pano=1) ───────────────────────────────
      panoActive() { return this.pano && this.panoOn && !!this.panoUrl; },
      // Panel i shows its quarter of one image: the img is 400% of the
      // panel's width and slides left one panel-width per index. The spread
      // is generated at 2.86:1 — four 57.5x80.5mm panels exactly — so a
      // 400% x 100% box holds it without meaningful distortion.
      panoStyle(i) {
        return `position:absolute;width:400%;height:100%;left:${-100 * i}%;top:0;`;
      },
      // Live fill from picture 1's crop. With PP_SPREAD_WORKER set (the
      // pp-spread Cloudflare Worker), the fill is FLUX.2 Pro Outpaint —
      // the chosen production model. Without it, Cloudinary gen_fill is
      // the client-reachable stand-in; there the first fetch of a new
      // transformation URL is the generation itself (~10-30s), so the
      // Image() preload below IS the wait, not decoration.
      async panoGenerate() {
        const p = this.photos[0];
        if (!p || !p.dataUrl || this.panoBusy) return;
        this.panoBusy = true;
        this.panoError = '';
        try {
          const blob = await (await fetch(p.dataUrl)).blob();
          const url = await cloudinaryUpload(blob);
          let spread;
          const worker = window.PP_SPREAD_WORKER;
          if (worker) {
            // Dimensions ride along because the Worker never decodes pixels.
            const dims = await new Promise(function (resolve, reject) {
              const img = new Image();
              img.onload = function () { resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
              img.onerror = function () { reject(new Error('probe_failed')); };
              img.src = p.dataUrl;
            });
            const res = await fetch(worker, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ url: url, w: dims.w, h: dims.h }),
            });
            if (!res.ok) throw new Error('worker_' + res.status);
            spread = (await res.json()).url;
            if (!spread) throw new Error('worker_no_url');
          } else {
            spread = url.replace('/image/upload/', '/image/upload/ar_2.86,b_gen_fill,c_pad/');
          }
          await new Promise(function (resolve, reject) {
            const img = new Image();
            img.onload = resolve;
            img.onerror = function () { reject(new Error('spread_load_failed')); };
            img.src = spread;
          });
          this.panoUrl = spread;
          this.panoOn = true;
        } catch (e) {
          this.panoError = 'Fill failed — try again.';
        } finally {
          this.panoBusy = false;
        }
      },

      /* ───── GOOGLE PHOTOS — one window, one click, one door ───────────────
         Rewritten 2026-09-29 with assets/gphotos.js; its header says how the
         single window works and why nothing can watch it.

         ⚠️ The button is NOT disabled while the picker is open. We cannot see
         that window, so a disabled button would stay dead behind one the
         customer shut. A second click starts a fresh pick; the old one drops.

         ⛔ THE PHOTOS ARRIVE THROUGH importPhoto(), like every other photo.
         The July path cropped each one to 720 x 900 itself and skipped the
         size, crop and resolution fields every photo carries. srcW/srcH come
         from Google's metadata — the ORIGINAL's size — because we download a
         1600px rendition, and the resolution check and the _Src order line
         must describe the photograph, not our download of it.

         ⭐ A PICK THAT COMPLETES THE CARD GOES STRAIGHT TO THE CARD (Daniel,
         2026-09-29). Downloads take a moment after Done, so the four photos
         land first as placeholders — right size, right aspect, no pixels —
         which puts the customer on the stage at once, card standing SHUT and
         large, with "Loading your pictures" in front. openFromStanding()
         holds the unfold until the real pixels have replaced every one. A
         placeholder is never saved (saveDraft) and never reaches the cart
         (canSubmit). A pick that leaves the card short takes the plain road:
         download, then the entry step, with a spinner line meanwhile. */
      photosLoading() {
        return (this.photos || []).some(function (p) { return p && p._loading; });
      },

      // ⚠️ NOT async, and nothing may be awaited before gp.pickPhotos(): it
      // opens the window, and only the click's own synchronous turn may.
      pickGooglePhotos() {
        const gp = window.PP && window.PP.gphotos;
        if (!gp || !gp.isConfigured()) return;   // the button is not rendered then
        const self = this;
        this.errors = {};
        gp.pickPhotos({ maxCount: this.photosMissing() || 4 })
          .then(function (res) { return self._takeGooglePhotos(gp, res); })
          .catch(function (e) { self._googleFailed(e); });
      },

      _googleFailed(e) {
        const code = e && e.code;
        const say = (msg) => { this.errors = { _photos: msg }; };
        // They closed it, clicked again, or walked away: nothing to report.
        if (code === 'cancelled' || code === 'superseded' || code === 'timed_out') return;
        if (code === 'popup_blocked') return say('Your browser blocked the Google Photos window. Tap the button again.');
        if (code === 'not_granted') return say('Google Photos wasn’t connected. Allow access to choose photos, or use “Upload photos”.');
        if (code === 'reconnect') return say('Google Photos needs you to sign in again. Tap “Choose from Google Photos”.');
        say('Google Photos didn’t work just now. Try again, or use “Upload photos”.');
      },

      _takeGooglePhotos(gp, res) {
        const self = this;
        let items = res.items || [];
        // More picked than the card has room for: keep the first ones that fit, as handleFiles does (2026-10-25).
        const room = 4 - this.photos.length;
        const trimmed = room > 0 && items.length > room;
        if (trimmed) items = items.slice(0, room);
        const videos = function () {
          if (res.skipped) self.errors = { _photos: items.length
            ? 'Videos can’t go on a card, so we left those out.'
            : 'Videos can’t go on a card. Choose photos instead.' };
          else if (trimmed) self.errors = { _photos: self._trimNote(items.length) };
        };
        if (!items.length) { res.done(); videos(); return; }

        // Orientation-safe: metadata is the original's size, the rendition is
        // what we decoded. Keep the original's magnitude in the decoded shape.
        const srcOf = function (im, it) {
          if (!it.width || !it.height) return { w: im.srcW, h: im.srcH };
          const flip = (im.w > im.h) !== (it.width > it.height);
          return flip ? { w: it.height, h: it.width } : { w: it.width, h: it.height };
        };
        const fetchOne = function (it) {
          return gp.download(it, IMPORT_MAX).then(importPhoto).then(function (im) {
            const s = srcOf(im, it);
            return { im: im, srcW: s.w, srcH: s.h };
          });
        };

        const completes = this.photos.length + items.length === 4 &&
          items.every(function (it) { return it.width && it.height; });

        if (!completes) {
          // The plain road: the card stays short, so there is no stage to show.
          this.gphotos.loading = true;
          return Promise.all(items.map(fetchOne)).then(function (got) {
            const next = got.map(function (g, i) {
              return { dataUrl: g.im.dataUrl, w: g.im.w, h: g.im.h, srcW: g.srcW, srcH: g.srcH,
                       name: items[i].name, size: 0, cropScale: 1, panX: 50, panY: 50, tilt: 0 };
            });
            if (self.acceptPhotos(next)) self._googleLanded(next.length);
            videos();
          }).catch(function (e) { self._googleFailed(e); })
            .finally(function () { self.gphotos.loading = false; res.done(); });
        }

        const holders = items.map(function (it) {
          const k = Math.min(1, IMPORT_MAX / Math.max(it.width, it.height));
          const w = Math.max(1, Math.round(it.width * k));
          const h = Math.max(1, Math.round(it.height * k));
          return { dataUrl: placeholderDataUrl(w, h), w: w, h: h, srcW: it.width, srcH: it.height,
                   name: it.name, size: 0, cropScale: 1, panX: 50, panY: 50, tilt: 0,
                   _loading: true, _gid: it.id };
        });
        if (!this.acceptPhotos(holders)) { res.done(); return; }
        videos();

        return Promise.all(items.map(function (it) {
          return fetchOne(it).then(function (g) {
            const i = self.photos.findIndex(function (p) { return p && p._gid === it.id && p._loading; });
            if (i < 0) return;
            // A new object, so every binding sees new pixels. Crop and effect
            // are carried over: the customer may have touched them already.
            const p = self.photos[i];
            const real = Object.assign({}, p, { dataUrl: g.im.dataUrl, w: g.im.w, h: g.im.h,
                                               srcW: g.srcW, srcH: g.srcH, _loading: false });
            delete real._gid;
            self.photos.splice(i, 1, real);
            self._faceCentre(i);
          });
        })).then(function () {
          self._googleLanded(items.length);
          // The unfold reveals these mid-way; decode them before it starts.
          self.$nextTick(function () { self._warmArt(); });
        }).catch(function (e) {
          // Take the placeholders back out; the entry step asks again. Tail
          // entries, so photos and details stay in step.
          for (let i = self.photos.length - 1; i >= 0; i--) {
            if (self.photos[i] && self.photos[i]._loading) {
              self.photos.splice(i, 1);
              self.details.splice(i, 1);
            }
          }
          self.errors = { _photos: 'Some of your Google photos didn’t load. Try again, or use “Upload photos”.' };
        }).finally(function () { res.done(); });
      },

      // Once real pixels are in: the same analytics and resolution notice as a
      // device pick (handleFiles), and the save a placeholder was kept out of.
      _googleLanded(count) {
        this.queueSave();
        this.$nextTick(() => {
          for (let i = 0; i < this.photos.length; i++) {
            const q = this.photoQuality(i);
            if (q && q.band !== 'ok') { this.showSoftNotice(q.band); break; }
          }
        });
        track('pp_photos_selected', {
          count: count, total: this.photos.length, source: 'google_photos', failed: 0,
        });
      },

      // Swapped photos and details by hand and forgot captions, which meant
      // every swap mislabelled two pictures. Goes through _applyPhotoOrder now
      // so it cannot drift from movePicture again.
      swapPhotos(i, j) {
        if (i === j || !this.photos[i] || !this.photos[j]) return;
        const order = this._identityOrder();
        order[i] = j; order[j] = i;
        this._applyPhotoOrder(order);
      },

      updateDetail(idx, field, value) {
        const next = this.details.slice();
        next[idx] = Object.assign({}, next[idx] || {}, { [field]: value });
        this.details = next;
      },

      // ───── Tagline + Highlight ───────────────────────────────────────
      wordsOf(lineIndex) {
        const line = this.tagline[lineIndex];
        if (!line || !line.text) return [];
        return line.text.split(/\s+/);
      },

      updateTagline(idx, text) {
        const next = this.tagline.slice();
        while (next.length <= idx) next.push({ text: '' });
        next[idx] = { text: (text || '').toUpperCase() };
        this.tagline = next;
      },

      setHighlight(lineIndex, wordIndex) {
        this.highlight = { lineIndex: lineIndex, wordIndex: wordIndex, color: this.highlight.color };
      },

      setHighlightColor(color) {
        if (!HIGHLIGHT_COLORS[color]) return;
        this.highlight = Object.assign({}, this.highlight, { color: color });
      },

      // ───── Animated-card preview helpers (alpine: true mode) ─────────
      lineStyle(i) { return ''; },
      highlightCls(i) {
        return this.highlight.lineIndex === i ? 'pp-card-anim__hl pp-card-anim__hl--on' : 'pp-card-anim__hl';
      },
      hlX(i) {
        if (this.highlight.lineIndex !== i) return 0;
        const line = this.tagline[i];
        if (!line || !line.text) return 0;
        const words = line.text.split(/\s+/);
        const before = words.slice(0, this.highlight.wordIndex).join(' ');
        const stripBaseX = (i % 2 === 0) ? 80 : 200;
        return stripBaseX + 10 + before.length * 11;
      },
      hlY(i) {
        const ys = [70, 115, 160, 205];
        return ys[i] || 0;
      },
      hlW(i) {
        if (this.highlight.lineIndex !== i) return 0;
        const line = this.tagline[i];
        if (!line || !line.text) return 0;
        const words = line.text.split(/\s+/);
        const word = words[this.highlight.wordIndex] || '';
        return Math.max(20, word.length * 11);
      },
      hlFill(i) {
        return this.highlight.lineIndex === i ? HIGHLIGHT_COLORS[this.highlight.color] : 'transparent';
      },

      // ───── Validation (Stage 2 model) ────────────────────────────────
      // Legacy tab names retained for the Plan 8 tab UI (dormant, unused).
      // Stage 2 uses canSubmit() below to gate the Send-panel Add to Cart.
      tabIsValid(name) {
        switch (name) {
          case 'photos':
            return this.photos.length === 4;
          case 'words':
            // Retired — always true so allTabsValid doesn't block legacy callers.
            return true;
          case 'note':
            return true;
          default:
            return false;
        }
      },

      // The address is no longer asked for here (2026-09-03): Send is
      // add-to-cart and Shopify's checkout collects it. Four photos is the
      // whole condition.
      allTabsValid() {
        return this.tabIsValid('photos');
      },

      // Stage 2 submission gate — used by the Send panel's Add to Cart.
      // Everything except photos + shipping address is optional in the
      // new model (topSentence, captions[], bottomNote are all editable
      // but not required).
      canSubmit() {
        // A placeholder has no pixels to bake.
        return this.tabIsValid('photos') && !this.photosLoading();
      },

      /* ── THE FAN-OUT LIST ─────────────────────────────────────────────
         The "button is never off / show what is missing" machinery below is
         the 2026-08-30 design, brought back from the retired Send panel
         (git 70d7193^) and re-pointed at a LIST instead of one address. The
         reasoning is unchanged and worth keeping: a disabled button withholds
         the action without ever saying what is missing, so the customer's
         only move is to hunt. The button always presses; a refused press
         raises recipTried, opens the first incomplete person, marks their
         empty fields and focuses the first one. */

      // A blank person. One place, so the list, the draft and the migration
      // cannot disagree about what an entry is.
      blankRecipient() {
        return { firstName: '', lastName: '', line1: '', line2: '',
                 city: '', state: '', zip: '', country: 'US' };
      },

      /* Preview's way in. Seeds the list with the FIRST person rather than
         opening an empty screen: "who's it for" with nowhere to type is a
         dead end, and the customer pressed a button that promised a form. */
      openRecipients() {
        /* Fetched HERE and not at page load: it is 287 KB, and a customer who
           never sends to more than one person must never pay for it. By the
           time the first ZIP is typed it has long since arrived. */
        const self = this;
        if (window.PPAddress) {
          window.PPAddress.loadZips().then(function () { self._zipTick++; });
        }
        if (!this.recipients.length) {
          this.recipients.push(this.blankRecipient());
          this.recipOpen = 0;
        } else {
          // Come back to the first one still unfinished, or collapse them all.
          this.recipOpen = this.firstIncompleteRecipient();
        }
        track('pp_recipients_opened', { people: this.recipients.length });
        this.setSection('recipients');
      },

      /* ⛔ LEAVING THE STEP IS NOT THE SAME AS ABANDONING IT. Back goes to
         Preview and KEEPS the list, so someone who wanted to check the card
         before typing three addresses does not lose the first two. The list
         is only emptied by removing every person, which is a deliberate act
         with its own button. */
      closeRecipients() { this.setSection('preview'); },

      addRecipient() {
        if (this.recipients.length >= this.recipMax()) return;
        this.recipients.push(this.blankRecipient());
        this.recipOpen = this.recipients.length - 1;
        track('pp_recipient_added', { people: this.recipients.length });
        this.focusFirstRecipientField();
      },

      /* Removing the LAST person empties the list, which puts the card back to
         "one card, address at checkout" — the state it was in before the
         customer ever opened this step. That is the right behaviour and the
         copy on the step says so; it must not silently leave a list of zero
         that still claims to be a fan-out. */
      removeRecipient(i) {
        if (i < 0 || i >= this.recipients.length) return;
        this.recipients.splice(i, 1);
        if (this.recipOpen === i) this.recipOpen = -1;
        else if (this.recipOpen > i) this.recipOpen -= 1;
        track('pp_recipient_removed', { people: this.recipients.length });
        if (!this.recipients.length) this.setSection('preview');
      },

      toggleRecipient(i) { this.recipOpen = this.recipOpen === i ? -1 : i; },

      // Which required fields are still empty for ONE person, in screen order.
      // Screen order is load-bearing: a refused press focuses the first of
      // these, so reordering the markup means reordering SEND_REQUIRED.
      recipMissing(r) {
        r = r || {};
        /* State and a five-digit ZIP are US requirements. Abroad they are
           optional — regions and postcodes vary too much to demand (2026-09-15). */
        const us = recipIsUS(r);
        return SEND_REQUIRED.filter(function (f) {
          if (!us && (f.key === 'state' || f.key === 'zip')) return false;
          const v = String(r[f.key] || '').trim();
          if (!v) return true;
          // ZIP is the one field that can be non-empty and still wrong. A
          // four-digit ZIP is a card that does not arrive, so it counts as
          // missing rather than as a separate kind of error.
          if (f.key === 'zip') return !/^[0-9]{5}$/.test(v);
          return false;
        });
      },

      /* ⛔ MISSING AND WRONG ARE TWO DIFFERENT SENTENCES. "You haven't typed
         the city yet" and "that ZIP isn't a US ZIP code" arrive at different
         moments and want different words, so they are two lists. Mixing them
         tells someone their blank form is wrong.

         `_zipTick` is read here for one reason: the ZIP table loads
         asynchronously, and until it lands PPAddress.problems() cannot answer
         "is this a real ZIP". Touching the counter makes Alpine re-run every
         check the moment it does, so an address typed during the load gets
         judged properly a second later instead of staying provisionally
         clean. Without it the answer is cached at first render and never
         revisited. */
      recipBad(r) {
        this._zipTick;
        const out = window.PPAddress ? window.PPAddress.problems(r || {}).slice() : [];
        /* ⛔ A DESIGN THAT MAY ONLY GO TO SOME COUNTRIES (2026-09-25, Yonatan: "we can sell the
           Winnie the Pooh designs freely in the US, but not in England or Australia"). It is a
           wrong value, said at once and blocking the add like a wrong ZIP - never a silent change
           of the customer's country. First in the list, because no other fix matters until it is. */
        const only = this.designShipsTo();
        if (only) {
          const c = String((r && r.country) || 'US').trim().toUpperCase();
          if (only.indexOf(c) === -1) out.unshift({ key: 'country', msg: this.designShipsToLine() });
        }
        return out;
      },
      /* ⛔ THE ONE PLACE THE VENTURE-LEVEL REFUSAL IS COUNTED. The refusal
         itself is in PPAddress.problems(), which Alpine re-runs on every
         keystroke — tracking there would fire hundreds of times for one
         person. This fires on the select's change event instead: one discrete
         act, one event.

         Deduped per country for the life of the page, because toggling the
         select back and forth is one person changing their mind, not repeated
         demand. Recipient index is deliberately NOT part of the key: two cards
         to the same blocked country is one country we do not serve.

         ⭐ WHY IT EXISTS AT ALL: the EEA/UK/CH block (decisions.md,
         2026-09-25) names its own reversal condition as "real EEA or UK
         demand", and nothing else on the site can see that demand — a person
         who is refused here never reaches checkout, so no order, no
         abandoned cart, no analytics of any other kind records them. */
      onCountryChange(i) {
        const r = this.recipients[i];
        const c = String((r && r.country) || 'US').trim().toUpperCase();
        if (!c || (window.PPAddress && window.PPAddress.shipsTo(c) !== false)) return;
        if (blockedSeen[c]) return;
        blockedSeen[c] = true;
        track('pp_country_blocked', { country: c });
      },

      // The countries the chosen design may be sent to, or null for anywhere (design-picker.js shipsTo).
      designShipsTo() {
        const s = this.card && this.card.design && this.card.design.shipsTo;
        const own = Array.isArray(s) && s.length ? s : null;
        /* A set's countries (pp-set.js: reviewers.com is US only, so the free code is never met by a postage line),
           narrowed by the design's own list, never widened. */
        const set = activeSet();
        const only = set && Array.isArray(set.countries) && set.countries.length && !this.artist && !this.org ? set.countries : null;
        if (!only) return own;
        const both = own ? only.filter(function (c) { return own.indexOf(c) !== -1; }) : only;
        return both.length ? both : only;
      },
      /* The sentence itself is PPAddress.shipsToLine — one copy, shared with
         the cart's address editor. It used to be written out here and again
         in cart-recipients.js, which is how "sent to United Kingdom" survived
         in two places at once. */
      designShipsToLine() {
        const only = this.designShipsTo();
        if (!only) return '';
        return window.PPAddress ? window.PPAddress.shipsToLine(only) : '';
      },

      // Everything a person still needs, in one sentence, most urgent first.
      recipComplete(r) {
        return this.recipMissing(r).length === 0 && this.recipBad(r).length === 0;
      },

      // -1 when every person is finished.
      firstIncompleteRecipient() {
        for (let i = 0; i < this.recipients.length; i++) {
          if (!this.recipComplete(this.recipients[i])) return i;
        }
        return -1;
      },

      // The list is ready when there is at least one person and none of them
      // is half-typed. An empty list is NOT "ready" — it is a different mode.
      recipientsReady() {
        return this.recipients.length > 0 && this.firstIncompleteRecipient() === -1;
      },

      // How this person reads on the envelope, and on the cart line.
      recipLine(r) { return recipToLine(r); },
      recipParts(r) { return recipParts(r); },

      /* The collapsed row's second line — the address WITHOUT the name.
         ⚠️ NOT recipLine(). The row's heading is already the person's name, so
         the full envelope string printed it twice and then, on a phone, the
         ellipsis ate the half that was doing the work: "Miriam Cohen · 1240
         Amsterdam Ave Apt 4B · …" told you nothing you did not have from the
         line above it. Measured on a 390px viewport, 2026-09-08. */
      recipWhere(r) {
        const p = recipParts(r);
        const street = [p.line1, p.line2].filter(Boolean).join(' ');
        const town = [p.city, p.state].filter(Boolean).join(', ');
        return [street, [town, p.zip].filter(Boolean).join(' '), recipCountryLine(r || {})]
                 .filter(Boolean).join('  ·  ');
      },

      // The heading on a collapsed row. Falls back to the position rather than
      // to an empty string, so a row that has not been typed into is still a
      // thing you can point at and open.
      recipTitle(i) {
        const r = this.recipients[i] || {};
        const name = [r.firstName, r.lastName].map(function (s) { return String(s || '').trim(); })
                                              .filter(Boolean).join(' ');
        return name || ('Person ' + (i + 1));
      },

      /* The line above the button, for the person currently open. Names one or
         two missing fields and counts three or more — six field names in a row
         is a list, not a sentence, and nobody reads a list at the moment of
         payment. Silent until they have started OR been refused once: on
         arrival the form is visibly empty, and "6 details still to fill in" is
         only the screen describing itself back to them. */
      recipHint() {
        if (this.recipOpen < 0) return '';
        /* A WRONG value outranks a missing one, and is said even before the
           first refused press: an empty field is a form not finished yet,
           but "93313 is a CA ZIP code, not NY" is a thing they have already
           got wrong and want to know about while the field is still under
           their thumb. */
        const bad = this.recipBad(this.recipients[this.recipOpen]);
        if (bad.length) return bad[0].msg;
        const m = this.recipMissing(this.recipients[this.recipOpen]);
        if (!m.length) return '';
        /* "Started" is any field typed. It was `m.length < SEND_REQUIRED.length`,
           which stopped being true once an address abroad needs fewer fields. */
        const cur = this.recipients[this.recipOpen] || {};
        const started = ['firstName', 'lastName', 'line1', 'line2', 'city', 'state', 'zip']
          .some(function (k) { return String(cur[k] || '').trim(); });
        if (!started && !this.recipTried) return '';
        if (m.length === 1) return 'Still need ' + m[0].label + '.';
        if (m.length === 2) return 'Still need ' + m[0].label + ' and ' + m[1].label + '.';
        return m.length + ' details still to fill in.';
      },

      // Whether one field on one person wears the "you skipped this" mark.
      recipFieldMissing(i, key) {
        /* A WRONG value marks itself immediately; a MISSING one waits for the
           first refused press. Same asymmetry as recipHint, same reason. */
        if (this.recipBad(this.recipients[i]).some(function (f) { return f.key === key; })) return true;
        if (!this.recipTried) return false;
        return this.recipMissing(this.recipients[i]).some(function (f) { return f.key === key; });
      },

      /* ⚠️ THE FOCUS MUST HAPPEN INSIDE THE TAP THAT ASKED FOR IT on iOS, or
         the customer gets a caret in a field with no keyboard under it — the
         same trap addToCart's guard carries. $nextTick is unavoidable here
         because the field does not exist until Alpine has rendered the newly
         opened person, so this is the ONE place that accepts a soft keyboard
         not opening: the row is already scrolled to and visibly empty. */
      focusFirstRecipientField() {
        const self = this;
        this.$nextTick(function () {
          const el = document.querySelector('[data-pp-recip="' + self.recipOpen + '"] input');
          if (el) { try { el.focus(); } catch (e) { /* nothing to do */ } }
        });
      },

      // How many people this card can go to: as many as one cart takes for a
      // customer (RECIP_MAX), and for an artist only the free cards they have left.
      recipMax() {
        const set = activeSet();
        if (set && set.oneCard && !this.artist && !this.org) return 1;   // one card, one person (pp-set.js)
        return this.artist ? Math.max(0, Math.min(RECIP_MAX, this.artist.left)) : RECIP_MAX;
      },

      /* ⭐ ARTIST MODE: the designs. The whole catalogue, as for any customer,
         plus the artist's OWN designs that passed their checks — a free card may
         use either (Daniel, 2026-09-17). Credentials included: artists.picturepops.com
         is same-site, so the artist's session cookie travels. Own designs are offered
         under every occasion; `design=` (from the artist's design page) goes
         straight to photos with that design chosen. */
      async loadArtistDesigns() {
        const a = this.artist;
        let j;
        try {
          const r = await fetch(ARTISTS_ORIGIN + '/api/free-cards/designs', { credentials: 'include' });
          if (r.status === 401) { a.state = 'signin'; return; }
          if (r.status === 403) { a.state = 'terms'; return; }
          if (!r.ok) { a.state = 'error'; return; }
          j = await r.json();
        } catch (e) { a.state = 'error'; return; }
        a.left = j.left;
        if (!j.unlocked) { a.state = 'locked'; return; }
        if (!(j.left > 0)) { a.state = 'none'; return; }
        await this.loadDesigns();
        const own = (j.designs || []).map(function (d) { return Object.assign({}, d, { occasions: [MY_DESIGNS], artist: true }); });
        // A published design of theirs is in the catalogue too; in artist mode it lives under My designs only.
        const ownIds = own.map(function (d) { return d.id; });
        this.designs = own.concat((this.designs || []).filter(function (d) { return ownIds.indexOf(d.id) === -1; }));
        if (a.want && this.designs.some(function (d) { return d.id === a.want || d.artKey === a.want; })) {
          const hit = this.designs.find(function (d) { return d.id === a.want || d.artKey === a.want; });
          if (!this.occasion) { this.occasion = hit.artist ? MY_DESIGNS : 'just-because'; if (this.card) this.card.occasion = this.occasion; }
          this.chooseDesign(hit.id, true);
          if (this.stage === 'entry') this.entryStep = 'photos';
        } else if (a.want) {
          /* A design named in the link that the builder cannot offer (not processed, not passed, not this artist's,
             not in the catalogue): say so (14 Oct). Classic fell back to its grid of occasions. */
          a.state = 'missing';
          return;
        } else {
          this.ensureDesignForOccasion();
        }
        a.state = 'ready';
        /* In B, no design: where cards are chosen, in artist mode (oldEntryAway). */
        const self = this;
        this.$nextTick(function () { self.oldEntryAway(); });
      },

      /* ⭐ ARTIST MODE: the ending. Photos are already baked and on Cloudinary;
         the same properties a cart line would carry go to the artist platform,
         which checks the allowance and creates the $0 order. The platform sets
         _Design and _Design Id itself — the browser's copy is not trusted. */
      // The print art key of the chosen design: the artist's own `artist-…`, or a
      // catalogue entry's artKey, falling back to its name slug exactly as print does.
      artistDesignKey() {
        const id = this.card && this.card.design && this.card.design.id;
        const d = (this.designs || []).find(function (x) { return x.id === id; }) || {};
        const slug = String(d.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        return d.artKey || (/^[a-z0-9-]+$/.test(String(id || '')) ? id : slug);
      },

      async sendArtistCards(photoUrls) {
        const self = this, a = this.artist;
        const cards = this.recipients.map(function (person) { return self.buildCartProperties(photoUrls, person); });
        try {
          const r = await fetch(ARTISTS_ORIGIN + '/api/free-cards', {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ design: this.artistDesignKey(), cards: cards }),
          });
          const j = await r.json().catch(function () { return {}; });
          if (r.status === 401) { a.state = 'signin'; return; }
          if (!r.ok) {
            this.errors._submit = j.error || 'We couldn’t send your cards. Please try again.';
            return;
          }
          a.order = j.order; a.left = j.left; a.state = 'sent';
        } catch (e) {
          this.errors._submit = 'We couldn’t send your cards. Check your connection and try again.';
        } finally {
          this.submitting = false;
        }
      },

      /* ⭐ ORG MODE: the card to make. The business app answers with the order's
         approved design (as a catalogue entry), the note as the order has it for
         this card (names already filled in), and one of three starting points:

           draft   the card as it was last saved here — its photos with their
                   crops and effects, its note, its settings. Reopening a card
                   is editing it, not starting it again.
           photos  pictures someone chose through a photo link, not yet on a
                   card: they go through the ordinary import (handleFiles), so
                   they are decoded, sized and counted exactly as a customer's.
           look    the settings of the order's last saved card (border, fonts,
                   shadow, corners), so card 40 starts where card 39 ended.

         Credentials included: business.picturepops.com is same-site, so the
         session cookie travels. */
      async loadOrgCard() {
        const o = this.org, T = ORG_TOKEN;
        let j;
        try {
          const r = await fetch(T.origin + '/api/builder/' + T.order + '/cards/' + T.card, { credentials: 'include' });
          if (r.status === 401) { o.state = 'signin'; return; }
          j = await r.json().catch(function () { return {}; });
          if (!r.ok) { o.error = j.error || ''; o.state = 'error'; return; }
        } catch (e) { o.state = 'error'; return; }
        o.label = j.card.label || ''; o.orderName = (j.order && j.order.name) || ''; o.back = j.back || '';
        await this.loadDesigns();
        const d = Object.assign({}, j.design, { occasions: [MY_DESIGNS], org: true });
        this.designs = [d].concat((this.designs || []).filter(function (x) { return x.id !== d.id; }));
        this.occasion = MY_DESIGNS;
        if (this.card) this.card.occasion = MY_DESIGNS;
        this.chooseDesign(d.id, true);

        const saved = j.card.draft && typeof j.card.draft === 'object' ? j.card.draft : null;
        this.applyOrgLook((saved && saved.card) || j.card.look || null, !!saved);
        if (this.card && typeof j.card.note === 'string') this.card.bottomNote = j.card.note;

        const urls = (j.card.photos || []).slice(0, 4);
        if (urls.length) {
          try {
            const files = await Promise.all(urls.map(async function (u, i) {
              const r = await fetch(u, { credentials: u.indexOf(T.origin) === 0 ? 'include' : 'omit' });
              if (!r.ok) throw new Error('photo ' + r.status);
              const b = await r.blob();
              // Named by what it is: an iPhone HEIC must reach decodeImageFile() looking like one.
              const ext = ({ 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif', 'image/heic': 'heic',
                             'image/heif': 'heif', 'image/avif': 'avif' })[b.type] || 'jpg';
              return new File([b], 'photo-' + (i + 1) + '.' + ext, { type: b.type || 'image/jpeg' });
            }));
            await this.handleFiles(files, 'org');
          } catch (e) { /* they can still pick photos themselves */ }
        } else if (saved && Array.isArray(saved.photos)) {
          const back = saved.photos.slice(0, 4).filter(function (p) {
            return p && typeof p.dataUrl === 'string' && p.dataUrl.indexOf('data:image/') === 0;
          }).map(function (p) {
            return { dataUrl: p.dataUrl, w: p.w, h: p.h, srcW: p.srcW, srcH: p.srcH,
                     name: p.name || '', size: p.size || 0,
                     cropScale: p.cropScale || 1,
                     panX: p.panX == null ? 50 : p.panX, panY: p.panY == null ? 50 : p.panY,
                     effect: p.effect || 'original', tilt: p.tilt || 0, rot: p.rot || 0 };
          });
          if (back.length) this.acceptPhotos(back);
        }
        if (this.stage === 'entry') this.entryStep = 'photos';
        o.state = 'ready';
      },

      /* The card-wide settings of a saved card, through the same guards
         loadState() gives a draft: a border stop or a face retired since is
         dropped rather than handed to the bake. `all` also restores the words
         (captions, headline) — only for the card's OWN saved state; a look
         borrowed from another card brings its settings, never its words. */
      applyOrgLook(look, all) {
        if (!look || !this.card) return;
        const c = this.card;
        const known = function (k) { return CARD_FONTS.some(function (f) { return f.key === k; }); };
        if (BORDER_STOPS.some(function (b) { return b.key === look.border; })) c.border = look.border;
        if (known(look.noteFont)) c.noteFont = look.noteFont;
        if (known(look.captionFont)) c.captionFont = look.captionFont;
        if (typeof look.fullSize === 'boolean') c.fullSize = look.fullSize;
        if (typeof look.shadow === 'boolean') c.shadow = look.shadow;
        if (look.rounded === true || look.rounded === false || isFinite(parseFloat(look.rounded))) c.rounded = look.rounded;
        if (!all) return;
        if (Array.isArray(look.captions)) c.captions = look.captions.slice(0, 4).map(function (x) { return String(x || ''); });
        if (typeof look.topSentence === 'string') c.topSentence = look.topSentence;
        if (typeof look.noteOwn === 'boolean') c.noteOwn = look.noteOwn;
      },

      /* What the business app keeps so the card can be reopened: the card's
         settings and words, and the photos WITH their pixels (the builder's
         own downscaled copies) and their crops. Everything else about the
         builder — where the person was, what was open — is not the card. */
      orgDraft() {
        const c = this.card || {};
        return {
          v: 1,
          card: {
            border: c.border || BORDER_DEFAULT, noteFont: c.noteFont || CARD_FONT_DEFAULT,
            captionFont: c.captionFont || CARD_FONT_DEFAULT, fullSize: !!c.fullSize,
            shadow: !!c.shadow, rounded: c.rounded == null ? false : c.rounded,
            captions: (c.captions || []).slice(0, 4), topSentence: c.topSentence || '',
            noteOwn: !!c.noteOwn,
          },
          photos: (this.photos || []).slice(0, 4).map(function (p) {
            return { dataUrl: p.dataUrl, w: p.w, h: p.h, srcW: p.srcW, srcH: p.srcH,
                     name: p.name, size: p.size, cropScale: p.cropScale, panX: p.panX, panY: p.panY,
                     effect: p.effect, tilt: p.tilt, rot: p.rot };
          }),
        };
      },

      /* ⭐ ORG MODE: the ending. The same bake and the same upload as addToCart
         (kept in step with it on purpose — what prints is what they saw), then
         buildCartProperties() with no recipient: the business app holds every
         address, and fills in each person's own at order time. */
      async saveOrgCard() {
        const o = this.org, T = ORG_TOKEN;
        this.submitting = true;
        this.errors = {};
        try {
          let blobs, photoUrls;
          try {
            const layout = { border: this.card.border || BORDER_DEFAULT, captionsOn: this.captionsOn(),
                             rounded: this.roundedActive(),
                             // Only read for a cut-out picture (?ppbg=1): what shows behind the person, at every stop.
                             strip: this.designArt('Strip') };
            blobs = await Promise.all(this.photos.map(function (p, i) { return bakePhoto(p, layout, i); }));
          } catch (e) {
            track('pp_upload_failed', { stage: 'bake', reason: String(e && e.message || e), mode: 'org' });
            this.errors._submit = 'We couldn’t prepare your photos. Try again, or swap the picture.';
            return;
          }
          try {
            photoUrls = await Promise.all(blobs.map(cloudinaryUpload));
          } catch (e) {
            track('pp_upload_failed', { stage: 'cloudinary', reason: String(e && e.message || e), mode: 'org' });
            this.errors._submit = 'We couldn’t upload your photos. Check your connection and try again.';
            return;
          }
          const r = await fetch(T.origin + '/api/builder/' + T.order + '/cards/' + T.card, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ properties: this.buildCartProperties(photoUrls, undefined), draft: this.orgDraft() }),
          });
          const j = await r.json().catch(function () { return {}; });
          if (r.status === 401) { o.state = 'signin'; return; }
          if (!r.ok) { this.errors._submit = j.error || 'We couldn’t save the card. Please try again.'; return; }
          window.location.href = j.back || o.back;
        } catch (e) {
          this.errors._submit = 'We couldn’t save the card. Check your connection and try again.';
        } finally {
          this.submitting = false;
        }
      },

      // The CTA's own words. It says what will happen, and the count is the
      // whole point of the screen.
      recipCta() {
        if (this.org) return this.submitting ? 'Saving…' : 'Save to your order';
        const n = this.recipients.length;
        if (this.artist) {
          if (this.submitting) return 'Sending…';
          if (n === 0) return 'Next: who’s it for?';
          return n === 1 ? 'Send free card' : 'Send ' + n + ' free cards';
        }
        if (this.submitting) return 'Adding to your cart…';
        /* Every card is addressed in our form now (2026-09-15), so from
           Preview with nobody yet, the button's next move is the address. */
        if (n === 0) return 'Next: who’s it for?';
        const base = n === 1 ? 'Add to cart' : 'Add ' + n + ' cards to cart';
        /* The card ladder (2026-10-09), behind its theme setting: the order's
           percentage, counting the cards already in the cart — it is per
           order. A percentage, not a price, because a code can still beat it
           at checkout. See assets/pp-ladder.js. */
        const L = window.PPLadder;
        const lbl = L && L.on ? L.label(n + (L.inCartNow || 0)) : '';
        return lbl ? base + ' · ' + lbl : base;
      },

      // The select's options, from pp-address.js — with a one-country fallback
      // for the instant before that deferred script has run.
      countryOptions() {
        return window.PPAddress ? window.PPAddress.countryOptions()
                                : [{ code: 'US', name: 'United States' }];
      },
      recipIsUS(r) { return recipIsUS(r); },
      // Whether any person on the list is abroad — the postage sentence shows.
      recipAbroad() { return this.recipients.some(function (r) { return !recipIsUS(r); }); },

      buildCartProperties(photoUrls, recipient) {
        photoUrls = photoUrls || [];
        const c = this.card || {};
        const props = {
          // VISIBLE — the buyer's last chance to catch a wrong address.
          /* 'Delivery To' was the first property here until 2026-09-03, when
             Send became add-to-cart and Shopify's checkout took over asking.
             It is BACK as of 2026-09-08, but only on a card that is part of a
             fan-out: `recipient` is undefined for the ordinary one-card add,
             and that line carries no address at all — Shopify's checkout is
             still the only place it is asked (print/order.mjs,
             deliveryToFromShipping).

             ⛔ VISIBLE ON PURPOSE, unlike every other property here. It is
             what distinguishes two otherwise identical cards in the cart, in
             the checkout summary and in the confirmation email — and on a
             multi-destination order those are the ONLY places the true
             destinations appear, because Shopify's own shipping address is
             the buyer's. Hiding it behind an underscore would leave the buyer
             paying for three cards with no way to see where any of them go. */

          // HIDDEN — the card's own password. Not fulfilment: this is what
          // makes /pages/card?s=… resolve to THIS order, and it is the only
          // reason a share link can exist without a human minting one. Fresh
          // per add-to-cart, so two cards in one order get two links.
          '_Share': mintShareToken(),

          // HIDDEN — fulfilment only.
          '_Occasion': this.occasion || c.occasion || 'unspecified',
          // The customer-visible design NAME. It prints on the packing slip and
          // the run sheet, and the cart page shows it — but it is NOT what the
          // printer looks the artwork up by any more. See `_Design Id`.
          '_Design': (c.design && (c.design.name || c.design.id)) || 'none',
          // ⭐ THE ART LOOKUP KEY (2026-09-10). The catalogue has always carried
          // an `id` beside the `name` — "shana-tova" / "Shana Tova" — and that
          // id IS the directory under print/art/ and the prefix in the pp-art
          // bucket. Print used to slugify the NAME instead, so RENAMING A DESIGN
          // IN THE THEME EDITOR pointed every order placed afterwards at a
          // directory that does not exist: nothing printed wrong, but the batch
          // was refused whole — other customers' cards with it — days after
          // checkout, while the storefront looked perfect throughout because the
          // web filenames never change.
          //
          // ⛔ `artKey`, NOT `id`, AND THE DIFFERENCE IS NOT COSMETIC. `id` means
          // the art slug in designs.json but Shopify's opaque BLOCK id in a
          // theme-editor entry, so sending `id` would work against the shipped
          // catalogue and send garbage the first time a design was added in the
          // editor — and a garbage key does not degrade gracefully, it makes
          // artSlugsFor() refuse the WHOLE batch. design-picker.js normalises
          // `artKey` per source; see withArtKey() there.
          //
          // Empty is SAFE and is the deliberate default: print reads
          // `designId || slug(design)`, so an empty string is exactly the old
          // name-slug behaviour.
          '_Design Id': (c.design && c.design.artKey) || '',
          // The cart page's thumbnail (2026-09-04): the design's closed
          // product shot, so a line in the cart looks like the card it is.
          // Never printed — print/order.mjs knows it as `Cover` and ignores
          // it. Empty when the design has no shot; the cart falls back to
          // _Photo 1, as it did before.
          '_Cover': this.cartCoverUrl(),
          '_Headline': c.topSentence || '',
          '_Note': c.bottomNote || '',
          // The two card-wide picture settings. Filters and crops travel as
          // PIXELS — bakePhoto() draws them into the uploaded photo — but
          // these two are layout, which the print file has to place, so they
          // travel as words. Read by print/order.mjs.
          // _Layout is unchanged and still says only whether captions print,
          // so an order placed before the border control existed still reads
          // correctly. _Border is the new half — print/order.mjs looks the
          // pair up in MM.borderStops to get the well it must place.
          /* From captionsOn(), not from c.fullSize: with captions no longer
             offered this must say full-size for EVERY order, including one
             built from a draft saved while they were. */
          '_Layout': this.captionsOn() ? 'with-captions' : 'full-size',
          '_Border': c.border || 'classic',
          /* ⭐ THE STRIP FORMAT (2026-10-29): which Part B this card's pictures were baked and cropped for. Print
             (order.mjs `Strip Format`) places, imposes and cuts the card as that; absent means 5x7, every order
             before the 3:4 go-live. Always written, so an order says what it is. */
          '_Strip Format': STRIP_FORMAT,
          '_Shadow': this.shadowActive() ? 'on' : 'off',
          /* The slots whose picture was cut out (Cut background), drawn with no shadow (Daniel, 19 Oct): print reads it
             as `_No Shadow` (order property contract, "No Shadow"). Only while the shadow is on, so it is absent on
             almost every order. */
          ...(this.shadowActive() && this.cutSlots() ? { '_No Shadow': this.cutSlots() } : {}),
          /* ⚠️ ADDITIVE AND OPTIONAL. The corners are already baked white into
             the uploaded picture, so a print run that ignores this property
             still produces the right look. What the property buys is the DROP
             SHADOW following the radius — that is the one picture treatment
             print draws itself rather than receiving baked in.
             See docs/2026-09-08-order-property-contract.md. */
          /* ⛔ `_Rounded` KEEPS ITS on/off SHAPE; THE MILLIMETRES GO BESIDE IT.
             The radius became a 0-10 mm slider on 2026-09-11.

             ⭐ WHY THIS PROPERTY EXISTS, AFTER BEING REMOVED ONCE. The corners
             are baked into the uploaded picture, so print needs nothing to
             print the CARD right — which is why I dropped this and shipped
             without it. Daniel then noticed what that reasoning missed: the
             builder puts the box-shadow on the SAME element that carries
             --pp-photo-radius, and a box-shadow follows its border-radius. So
             the screen shows a shadow matching the chosen radius while paper
             prints one pinned at 2 mm. That is not a cosmetic difference, it
             is the screen lying about the card — the same failure as a note
             that shows in the builder and prints blank.

             ⛔ AND THE NUMBER MUST NOT GO IN `_Rounded`. print/order.mjs maps
             that field and the renderer reads it as on/off; handing it "6.5"
             turns rounding OFF on every order that used it, silently. A live
             property's existing values have to keep meaning what they meant.
             So `_Rounded` answers whether, `_Corner Radius` answers how much.

             Additive and safe to ignore: a print run that never reads this
             still produces the right card, and only the shadow's radius is
             stale until it does. Landed straight on main, the way `_Rounded`
             itself did on 2026-09-10, and recorded in the contract — which is
             the channel between the two sides.

             See docs/2026-09-08-order-property-contract.md. */
          '_Rounded': this.roundedActive() ? 'on' : 'off',
          '_Corner Radius': this.roundedActive()
            ? this.roundedActive().toFixed(1) : '',
          '_Note Font': c.noteFont || CARD_FONT_DEFAULT,
          /* WHOSE WORDS ARE ON THE LOWER PANEL. Only meaningful for designs
             that bring their own message; without it print cannot tell "the
             customer left the design's line alone" from "the customer wrote
             nothing", and those two print differently. */
          '_Note Own': c.noteOwn ? 'yes' : 'no',
          '_Caption Font': c.captionFont || CARD_FONT_DEFAULT,
        };

        /* ── WHERE THIS COPY GOES ─────────────────────────────────────────
           Only on a fan-out line. Two properties, doing two different jobs:

             `Delivery To`  the address as a person writes it, middle-dot
                            joined — the string print/envelope.mjs already
                            knows how to split, and the string the buyer sees.
             `_Ship`        the same address in parts, as compact JSON, so the
                            CART can re-open it in an editor. A one-line string
                            cannot be parsed back into fields reliably (an apt
                            number and a street number look the same), and
                            guessing would silently mangle someone's address.

           ⛔ THE STRING IS THE SOURCE OF TRUTH, THE JSON IS THE CONVENIENCE.
           If the two ever disagree, print reads the string, and the string is
           what the buyer approved. cart-recipients.js rewrites both together
           or neither. */
        if (recipient) {
          const line = recipToLine(recipient);
          if (line) {
            props['Delivery To'] = line;
            props['_Ship'] = JSON.stringify({
              f: recipient.firstName || '', l: recipient.lastName || '',
              a1: recipient.line1 || '', a2: recipient.line2 || '',
              c: recipient.city || '', s: recipient.state || '', z: recipient.zip || '',
              k: String(recipient.country || 'US').toUpperCase(),
            });
            /* ⭐ WHERE IT GOES, AS A FACT (2026-09-15). Print draws the
               international envelope from this, and the checkout Function
               requires one unit of International postage for every card whose
               value is not US. Contract: docs/2026-09-08-order-property-contract.md. */
            props['_Country'] = String(recipient.country || 'US').toUpperCase();
          }
        }

        const captions = c.captions || [];
        // The aspect the picture is actually printed in — the SAME one
        // bakePhoto() crops to, taken from the same function, so the pixel
        // count recorded here describes the crop that was really baked.
        const bakeBox = bakeSizeFor(c.border || BORDER_DEFAULT, !c.fullSize);
        const bakeAspect = bakeBox.w / bakeBox.h;
        for (let i = 0; i < 4; i++) {
          if (photoUrls[i]) props['_Photo ' + (i + 1)] = photoUrls[i];
          // ⛔ THE ONLY HONEST RESOLUTION NUMBER IN THE ORDER.
          //
          // Every uploaded file is exactly the bake size, because bakePhoto()
          // scales the crop up to it. So fulfilment cannot learn anything about
          // sharpness from the delivered image — it has to be told, here, in
          // the customer's own source pixels.
          //
          // Omitted rather than guessed when the draft predates srcW/srcH.
          // print/order.mjs treats absent as "unknown", never as "fine".
          const cp = cropPixels((this.photos || [])[i], bakeAspect);
          if (photoUrls[i] && cp) {
            props['_Src ' + (i + 1)] = cp.w + 'x' + cp.h + ' of ' + cp.srcW + 'x' + cp.srcH;
          }
          // No captions at full size. They are kept in the draft, so turning
          // the layout back on restores them — but a caption in the order that
          // the card does not print is an instruction to a human to print
          // something the customer chose not to have.
          if (captions[i] && !c.fullSize) props['_Caption ' + (i + 1)] = captions[i];
        }
        /* ⭐ THE INSIDE TOP (2026-10-08). Only on a design that offers the choice. `_Top: design` says
           the customer kept the design's own; a layer that did not upload never reaches here
           (addToCart stops first), so `text`/`photo` always carries its `_Photo 5`. The words and
           the look ride along for the record and for support; print draws only the layer. */
        if (this.hasTopChoice()) {
          const tu = this._topUpload, t = c.top || {};
          if (tu && tu.url) {
            props['_Top'] = tu.mode;
            props['_Photo 5'] = tu.url;
            if (tu.src) props['_Src 5'] = tu.src;
            /* The photo smaller than the face, see-through or cut out (24 Oct). All three are already in the pixels of
               `_Photo 5`; these say so. `_Top Photo Width` is the one print reads: the crop's printed width in mm, so
               the sharpness check divides `_Src 5` by the width it really prints at (absent = 98, the whole face, as
               every order before it). The other two are for the record and the run sheet. */
            if (tu.mode === 'photo') {
              if (tu.mmW && tu.mmW < 97.95) props['_Top Photo Width'] = tu.mmW.toFixed(1);
              if (tu.opacity < 1) props['_Top Opacity'] = Math.round(tu.opacity * 100) + '%';
              if (tu.cut) props['_Top Cut'] = 'yes';
            }
            if (tu.mode === 'text') {
              const L = window.PPTop.look(this.topSpec());
              props['_Top Template'] = L.t.key;
              props['_Top Title'] = t.title || '';
              if (!this.topOneLine() && t.subtitle) props['_Top Subtitle'] = t.subtitle;
              props['_Top Style'] = L.title.font + ' / ' + L.accent;
            }
          } else {
            props['_Top'] = 'design';
          }
        }
        return props;
      },

      // The closed product shot of the chosen design, as a full URL — or the
      // flat CoverFront art when a design has no staged shot (designs.json:
      // preview.closed is optional). Empty when nothing is chosen.
      cartCoverUrl() {
        const c = this.card || {};
        const d = c.design;
        if (!d) return '';
        const entry = (this.designs || []).filter(function (x) { return x && x.id === d.id; })[0] || {};
        const p = entry.preview || {}, a = entry.art || {};
        /* The closed shot stands upright; the raw front art is stored upside down for the 3D card (Daniel, 12 Oct:
           "Why is the card upside down here?"). A raw front carries #flip, which every server ignores and the
           builder and the cart read to turn it round. Print never reads _Cover (property contract). */
        if (p.closed) return this.designUrl(p.closed) || '';
        const raw = a.CoverFront ? this.designUrl(a.CoverFront) : d.CoverFront;
        return raw ? raw + '#flip' : '';
      },
      cartCoverFlipped() { return /#flip$/.test(this.cartCoverUrl()); },

      /* buildCheckoutUrl() was here — it pre-filled Shopify checkout's
         shipping address from the Send panel. Gone 2026-09-03 with the
         panel; the cart page comes before checkout now. */
      // A stable id for THIS card's cart line, minted once and kept in memory
      // (so it survives a bfcache back but is fresh for a genuinely new card on
      // a full reload). Lets addToCart replace its own line instead of adding
      // a duplicate. See the dedupe in addToCart.
      _cartKey() {
        if (!this._ppLine) {
          this._ppLine = (window.crypto && crypto.randomUUID)
            ? crypto.randomUUID()
            : 'pp-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
        }
        return this._ppLine;
      },

      async addToCart() {
        // `this` inside the ES5 callbacks below is not the component.
        const self = this;
        if (!this.variantId) {
          this.errors._submit = 'Product unavailable — please reload the page.';
          return;
        }
        // Already in the cart (a browser Back landed the customer back here on
        // a card they have added). Don't add a second copy — take them to it.
        // The flag is cleared the moment they go Back to edit, so a genuine
        // re-add after an edit still works (and de-dupes by _pp_line).
        if (this.cartAdded) { window.location.href = '/cart'; return; }
        /* ⚠️ A REFUSED PRESS IS NOT AN ERROR, AND MUST NOT READ AS ONE.
           This used to set errors._submit — the same channel Cloudinary
           failures and dead carts use — so "you haven't typed the ZIP yet"
           arrived looking exactly like "something broke". It sets no error
           now: it raises sendTried, which turns on the marks and promotes
           the hint line that was already counting. errors._submit is left
           for things that are actually wrong.

           ⚠️ THE FOCUS IS SYNCHRONOUS, and it has to stay that way. This
           runs straight off the button's @click, so the focus happens inside
           the user gesture — iOS only opens the keyboard for a focus that
           does. Move this into a $nextTick or an await and the customer gets
           a caret in a field with no keyboard under it. */
        // Four photos is the only condition now. Preview cannot be reached
        // without them, so this is a guard, not a state the customer sees.
        if (!this.canSubmit()) { this.setSection('photos'); return; }
        // Org mode: no recipients here — the business app has the list.
        if (this.org) { await this.saveOrgCard(); return; }
        /* ⛔ EVERY CARD IS ADDRESSED IN OUR FORM (Daniel, 2026-09-15: "we should
           always collect the address through our own form"). An empty list
           used to be the ordinary one-card add, with Shopify's checkout asking
           for the address; it now opens "Who's it for?" with the first person
           ready to type. Supersedes the opt-in step of 2026-09-08. */
        if (!this.recipients.length) {
          this.openRecipients();
          this.focusFirstRecipientField();
          return;
        }
        /* ⛔ A HALF-TYPED PERSON STOPS THE WHOLE ADD, and says which one.
           Runs BEFORE any await so the focus below happens inside the user
           gesture — iOS only opens the keyboard for a focus that does. */
        if (!this.recipientsReady()) {
          this.recipTried = true;
          this.recipOpen = this.firstIncompleteRecipient();
          if (!this.isSection('recipients')) this.setSection('recipients');
          this.focusFirstRecipientField();
          return;
        }
        this.submitting = true;
        this.errors = {};
        try {
          // Bake each edited panel (effect+crop+border) and upload to Cloudinary; the
          // delivered URLs ride on the order line so the merchant can print.
          // Bake and upload are separated so the failure is diagnostic: baking
          // fails on the customer's own image, uploading fails on our Cloudinary
          // config or their connection. Same message to them, different fix for
          // us — and lumped together the number would say nothing.
          let photoUrls = [];
          let blobs;
          try {
            // .map(bakePhoto) passed the array INDEX as the second argument,
            // which is the layout — photo 0 would have baked to one box and
            // photos 1..3 to another, on every order. The wrapper is what
            // stops that; do not "simplify" it back to .map(bakePhoto).
            const layout = { border: this.card.border || BORDER_DEFAULT, captionsOn: this.captionsOn(),
                             rounded: this.roundedActive(),
                             // Only read for a cut-out picture (?ppbg=1): what shows behind the person, at every stop.
                             strip: this.designArt('Strip') };
            blobs = await Promise.all(this.photos.map(function (p, i) { return bakePhoto(p, layout, i); }));
          } catch (e) {
            track('pp_upload_failed', { stage: 'bake', reason: String(e && e.message || e) });
            this.errors._submit = 'We couldn’t prepare your photos. Try again, or swap the picture.';
            this.submitting = false;
            return;
          }
          try {
            photoUrls = await Promise.all(blobs.map(cloudinaryUpload));
          } catch (e) {
            track('pp_upload_failed', { stage: 'cloudinary', reason: String(e && e.message || e) });
            this.errors._submit = 'We couldn’t upload your photos. Check your connection and try again.';
            this.submitting = false;
            return;
          }
          /* The inside top's layer, baked and uploaded like the photos, before any line is built.
             Its own stage in the failure event, so a broken template is not read as a photo fault. */
          this._topUpload = null;
          if (this.hasTopChoice() && this.topActive()) {
            try {
              this._topUpload = await this.topBakeAndUpload();
            } catch (e) {
              track('pp_upload_failed', { stage: 'inside-top', reason: String(e && e.message || e) });
              this.errors._submit = 'We couldn’t prepare the inside of your card. Try again.';
              this.submitting = false;
              return;
            }
          }
          if (this.artist) { await this.sendArtistCards(photoUrls); return; }
          /* ⛔ REPLACE, NEVER DUPLICATE (Daniel, 2026-09-03). Pressing add
             again after a back-button return must not put a second copy of the
             same card in the cart. Every add carries a stable _pp_line key
             (per card, held in memory so it survives the bfcache); if a line
             with that key is already in the cart, drop it before adding the
             current version. So a re-press is a replace — one line, updated if
             the customer edited — not a duplicate. Shopify would never merge
             these on its own: _Share is fresh per add, so the properties always
             differ. The lookup is best-effort; a failed /cart.js just falls
             through to a plain add. */
          /* ⭐ ONE LINE PER PERSON, AND ONE LINE WHEN THERE IS NO LIST.
             `people` is the list or a single undefined — so the ordinary
             one-card path builds exactly the properties it always did, with
             no address, and the fan-out path differs only in how many times
             this loop runs. There is no separate code path to keep in step.

             ⛔ NOT `quantity: N` ON ONE LINE. Three copies to three people are
             three different envelopes with three different addresses, and a
             line-item property belongs to the LINE, not to a unit of it. The
             print side counts cards through quantity (print/order.mjs,
             linesFromAny) precisely because it cannot tell three copies apart
             — and here they must be told apart. */
          const people = this.recipients;
          const base = this._cartKey();
          const items = people.map(function (person, i) {
            const p = self.buildCartProperties(photoUrls, person);
            /* ⚠️ THE LINE KEY IS PER LINE, NOT PER CARD. It used to be the
               bare card key; three fan-out lines sharing one key would make
               the dedupe below delete two of them on the next press. */
            p['_pp_line'] = base + ':' + i;
            return { id: (activeSet() && activeSet().variant) || self.variantId, quantity: 1, properties: p };
          });
          /* ⛔ REPLACE, NEVER DUPLICATE (Daniel, 2026-09-03). Pressing add
             again after a back-button return must not put a second copy of
             the same card in the cart. Every line this card has ever added
             carries a key beginning `base + ':'`, so ALL of them are dropped
             before the current set goes in — which is what makes a re-press
             after removing a person leave two lines rather than four.
             The bare `base` is matched too: lines added before 2026-09-08
             carry the un-suffixed key.

             ⛔ ONE /cart/update.js, NOT A LOOP OF /cart/change.js.
             A LINE KEY GOES STALE THE MOMENT ANOTHER LINE IS REMOVED.
             Measured on Draft PP, 2026-09-08: with four lines in the cart,
             deleting three of them one at a time by the keys from a single
             /cart.js snapshot removed the first two and answered **HTTP 400**
             on the third — which the catch below swallowed, so a card the
             customer had removed a person from stayed in the cart and stayed
             billable. The symptom was one extra line, and nothing said so.

             `/cart/update.js` takes every key at once and applies them
             together, so no key is read after the cart has moved under it.
             Verified: same four lines, one call, exactly the three intended
             lines gone.

             Still best-effort — a failed read or write falls through to a
             plain add, which duplicates rather than loses, and the cart page
             is where the customer can see and fix that. */
          try {
            const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
            const updates = {};
            let n = 0;
            (cart.items || []).forEach(function (it) {
              const k = it.properties && it.properties['_pp_line'];
              if (k && (k === base || String(k).indexOf(base + ':') === 0)) {
                updates[it.key] = 0;
                n++;
              }
            });
            if (n) {
              const del = await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates: updates }),
              });
              /* Reported rather than swallowed. It does not stop the add — a
                 duplicate is recoverable and a missing card is not — but a
                 silent failure here is what cost the measurement above. */
              if (!del.ok) track('pp_cart_dedupe_failed', { status: del.status, lines: n });
            }
          } catch (e) { /* non-fatal — a plain add is still correct, just not de-duped */ }
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items }),
          });
          if (!res.ok) throw new Error('cart_add_failed_' + res.status);
          track('pp_cart_added', { photos: this.photos.length, cards: items.length });
          /* ⭐ POSTAGE FOLLOWS THE CARDS (assets/pp-postage.js). Awaited so the
             cart page opens already right; a failure here is not fatal — the
             cart page syncs again on load, and the checkout Function refuses a
             mismatch and says why. */
          try { if (window.PPPostage) await window.PPPostage.sync(); } catch (e) { /* the cart page retries */ }
          /* THE DRAFT ENDS WHERE THE CART BEGINS (2026-08-29). Once the line
             item exists, the card is Shopify's to remember: the baked photo
             URLs ride on the order properties, so an abandoned CHECKOUT still
             has the design behind it in the cart. Keeping the draft as well
             would leave "Continue card" lit on every marketing page for
             someone who has already paid.

             The latch goes up BEFORE the clear, because the navigation on the
             next line fires pagehide -> saveNow(). */
          this._draftClosed = true;
          this.cartAdded = true;             // Preview's button now reads "Already added to cart"
          clearDraft();
          /* THE CART, NOT CHECKOUT (decisions.md 2026-09-03). Send is add to
             cart; the cart page is where the next card is sold, and checkout
             is one tap after it. */
          window.location.href = '/cart';
        } catch (e) {
          track('pp_cart_failed', { reason: String(e && e.message || e) });
          this.errors._submit = 'Could not add to cart. Try again or refresh.';
          this.submitting = false;
        }
      },
    });

    // Define `stage` as a real getter AFTER Object.assign so `this.photos`
    // resolves to the merged component (not the inline source object).
    // (Putting `get stage()` inside the Object.assign source caused
    // Object.assign to invoke the getter at copy-time with `this` set to
    // the source literal, where `this.photos` was undefined → TypeError.)
    Object.defineProperty(component, 'stage', {
      enumerable: true,
      configurable: true,
      get() {
        // Photos alone are NOT enough to enter the builder — a design is
        // needed too, or the card is built around a blank cover. Both are
        // asked for in order by the entry flow, so the only way to arrive
        // here half-answered is a save from an older version of the theme;
        // that visitor is put back on the step they are missing.
        // ⚠️ FOUR, not "any". This used to be `length > 0`, which sent the
        // customer into the builder on their first photo and left the card
        // three panels short. The entry step now accepts partial picks and
        // counts them down, so "some photos" is a legitimate resting state
        // and must NOT be mistaken for a finished selection.
        //
        // Safe to tighten because nothing can drop the count once it reaches
        // four: handleFiles() is reachable only from the entry step, and the
        // builder reorders photos but never removes one.
        const ready = this.photos && this.photos.length === 4 && this.designChosen();
        return ready ? 'builder' : 'entry';
      },
    });

    return component;
  }

  window.PP = window.PP || {};
  window.PP.configurator = configuratorComponent;
  window.PP.cropToSquare = cropToSquare;
  window.PP.bakePhoto = bakePhoto;   // for the checks: what a picture bakes to, without an order
  window.PP.photoErrorMessage = photoErrorMessage;
  // The one way anything in this theme reports an event — the design picker
  // goes through it too, so pp_experiments and debug_mode are attached in
  // exactly one place.
  window.PP.track = track;
  // Exported for debugging: tells you what a visitor's dimension value is
  // without having to decode cookies by hand.
  window.PP.experiments = experiments;
  // Read by design-picker.js for its band headings.
  window.PP.OCCASIONS = OCCASIONS;
  window.PP.OCCASION_PAGE = OCCASION_PAGE;
})();
